import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { io } from 'socket.io-client';
import './App.css';

// Import shared contexts (breaks circular dependencies with components)
import { ThemeContext, AuthContext, ToastContext, AgencyContext, MobileNavContext, useTheme, useAuth, useToast, useAgencyContext, useMobileNav } from './contexts/AppContexts';
// Re-export for any external consumers
export { useTheme, useAuth, useToast, useAgencyContext } from './contexts/AppContexts';

// Suppress Leaflet-specific errors that occur during component unmount
// These errors are harmless but show up in React's development error overlay
// In production, these won't be visible to users
if (typeof window !== 'undefined') {
  // Capture errors before React's error boundary
  window.addEventListener('error', function(event) {
    if (event.error && event.error.message && (
      event.error.message.includes('_leaflet_pos') ||
      event.error.message.includes('pos.equals') ||
      (event.error.message.includes('Cannot') && event.error.message.includes('undefined') && 
       event.error.stack && event.error.stack.includes('leaflet'))
    )) {
      // Prevent React from showing the error overlay
      event.stopImmediatePropagation();
      event.preventDefault();
      console.warn('[Leaflet] Suppressed unmount error:', event.error.message);
      return true;
    }
  }, true); // Use capture phase to catch before React
}

// Import Image Compression Utility
import { compressImage as compressImageUtil, processImageFile, IMAGE_CONFIG, formatFileSize } from './utils/imageCompression';

// Import Help System
import { helpContent, fuzzySearch } from './HelpSystem';
import { APP_VERSION_LABEL } from './version';

// Import Analytics Dashboard
import AnalyticsDashboard from './AnalyticsDashboard';
import HierarchicalObjectives from './HierarchicalObjectives';
import DisplayMapping from './DisplayMapping';
import DisplayWeather from './DisplayWeather';
import DisplayBriefing from './DisplayBriefing';
import DisplayICS203 from './DisplayICS203';
import DisplayObjectives from './DisplayObjectives';
import DisplayImages from './DisplayImages';

// Import ICS Organization Chart (Interactive CICS)
import ICSOrganizationChart from './components/ICSOrganizationChart';

// Import IC Briefing Preview
import BriefingPreview from './components/BriefingPreview';
import EntryImageCard from './components/EntryImageCard';

// Import ExCon Builder and Event Builder (HIRA-based)
import ExConBuilder from './components/ExConBuilder';
import EventBuilder from './components/EventBuilder';
import ExConDashboard from './components/ExConDashboard';
import ExerciseLockButton from './components/ExerciseLockButton';
import ExerciseLockedRibbon from './components/ExerciseLockedRibbon';
import useExerciseLock from './hooks/useExerciseLock';
import ExerciseAuditTrail from './components/ExerciseAuditTrail';
import StrikeTeamsModule from './components/StrikeTeamsModule';
import TaskForcesModule from './components/TaskForcesModule';

// Import Sortable Entry List for drag-and-drop reordering
import SortableEntryList from './components/SortableEntryList';

// Import ICS Form hooks for real-time sync
import { useICS216, useICS309, useICS215 } from './hooks/useICSForms';
import * as XLSX from 'xlsx';
import SyncHeader from './components/SyncHeader';
import ResourceTrackerPage from './components/ResourceTrackerPage';
import { QRCodeSVG } from 'qrcode.react';

// Leaflet imports
import { MapContainer as LeafletMapContainer, TileLayer, LayersControl, FeatureGroup, Marker, Popup, Tooltip, Polygon, Polyline, Rectangle, Circle, useMap, useMapEvents } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Immediately patch Leaflet's DomUtil methods after import
// This MUST happen before any map components are rendered
(() => {
  const origSetPosition = L.DomUtil.setPosition;
  L.DomUtil.setPosition = function(el, point) {
    if (!el) return;
    try {
      origSetPosition.call(this, el, point);
    } catch (e) {
      // Silently ignore - this happens during unmount
    }
  };
  
  const origGetPosition = L.DomUtil.getPosition;
  L.DomUtil.getPosition = function(el) {
    if (!el || !el._leaflet_pos) {
      return L.point(0, 0); // Return a proper L.point, not plain object
    }
    try {
      return origGetPosition.call(this, el);
    } catch (e) {
      return L.point(0, 0); // Return a proper L.point, not plain object
    }
  };
})();

// Import leaflet-draw styles
import 'leaflet-draw/dist/leaflet.draw.css';

// MapErrorBoundary - now in its own file, import for local use and re-export
import MapErrorBoundary from './components/MapErrorBoundary';
export { MapErrorBoundary };

// Import shadcn components
import { Button } from './components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { Separator } from './components/ui/separator';
import { ScrollArea } from './components/ui/scroll-area';
import { Input } from './components/ui/input';
import { Label } from './components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Checkbox } from './components/ui/checkbox';
import { Avatar, AvatarFallback, AvatarImage } from './components/ui/avatar';
import { Textarea } from './components/ui/textarea';
import { RadioGroup, RadioGroupItem } from './components/ui/radio-group';
import { Popover, PopoverContent, PopoverTrigger } from './components/ui/popover';
import { 
  CalendarDays, 
  Users, 
  User,
  AlertTriangle, 
  ClipboardList,
  Plus, 
  Printer,
  Settings,
  Home,
  BookOpen,
  Target,
  Shield,
  ArrowLeft,
  Camera,
  Upload,
  Phone,
  Mail,
  MapPin,
  Edit,
  Trash2,
  Save,
  Info,
  FileText,
  Flag,
  Map,
  Trophy,
  CheckCircle,
  Calendar,
  Building,
  Key,
  Radio,
  Headphones,
  MessageSquare,
  ShieldAlert,
  Clock,
  X,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  Star,
  Lightbulb,
  AlertCircle,
  Zap,
  MessageCircle,
  CheckSquare,
  Square,
  PenTool,
  Video,
  Play,
  Link2,
  FileEdit,
  Package,
  Palette,
  DollarSign,
  BookText,
  ExternalLink,
  UserCog,
  UserPlus,
  FileCheck,
  Cloud,
  Heart,
  Coffee,
  Truck,
  Building2,
  BarChart,
  RefreshCw,
  Wifi,
  Trash,
  Plane,
  Image as ImageIcon,
  Book,
  Search,
  HelpCircle,
  Copy,
  ArrowUp,
  ArrowDown,
  Monitor,
  Lock,
  Globe,
  Eye,
  EyeOff,
  Layers,
  Spline,
  Presentation,
  Menu as MenuIcon,
  X as XIcon,
  Sun,
  Moon,
  CloudRain
} from 'lucide-react';

// Import API configuration
import { API_URL, API_BASE_URL } from './config';

// Socket.IO context and components
import { SocketProvider, useSocket } from './contexts/SocketContext';
import OnlinePresence from './components/OnlinePresence';
import { useFieldLock } from './hooks/useFieldLock';

// Import refactored components
import ICSActivityLogForm from './components/ICSActivityLog';
import AfterActionReportSection from './components/AfterActionReport';
import LessonsLearnedForm from './components/LessonsLearned';
import EvaluationReportForm, { calculateOverallRatingHelper } from './components/EvaluationReportForm';
import { calculateHIRARiskScore, getHIRARiskLevel } from './components/HIRAForm';
import ICS215Form from './components/ICS215Form';
import HelpDeskView from './components/HelpDeskView';
import WhitepaperView from './components/WhitepaperView';
import ICS216Form from './components/ICS216Form';
import ICS218Form from './components/ICS218Form';
import ICS220Form from './components/ICS220Form';
import ICS221Form from './components/ICS221Form';
import ICS224Form from './components/ICS224Form';
import ICS225Form from './components/ICS225Form';
import ICS230Form from './components/ICS230Form';
import ICS232Form from './components/ICS232Form';
import ICS233Form from './components/ICS233Form';
import ICS309Form from './components/ICS309Form';
import FoodUnitForm from './components/FoodUnitForm';
import FacilitiesUnitForm from './components/FacilitiesUnitForm';
import CompensationClaimsForm from './components/CompensationClaimsForm';
import CostUnitForm from './components/CostUnitForm';
import TimeUnitForm from './components/TimeUnitForm';
import ProcurementForm from './components/ProcurementForm';
import ICS259Form from './components/ICS259Form';
import ICS259CrewForm from './components/ICS259CrewForm';
import ICS259EquipmentForm from './components/ICS259EquipmentForm';
import ICS259OverheadForm from './components/ICS259OverheadForm';
import ICS259SuppliesForm from './components/ICS259SuppliesForm';
import SafetyOfficerModule from './components/SafetyOfficerModule';
import SituationUnitModule from './components/SituationUnitModule';
import LiaisonOfficerModule from './components/LiaisonOfficerModule';
import ScenarioModule from './components/ScenarioModule';
import IncidentObjectivesModule from './components/IncidentObjectivesModule';
import SafetyModule from './components/SafetyModule';
import ICS204AssignmentList from './components/ICS204AssignmentList';
import AIAssistantWidget from './components/AIAssistantWidget'; // eslint-disable-line no-unused-vars
import WeatherModule from './components/WeatherModule';
import WeatherObservationModule from './components/WeatherObservationModule';
import WXReportModule from './components/WXReportModule';
import SevereWxModule from './components/SevereWxModule';
import ICSDashboardOverview from './components/ICSDashboardOverview';
import ScenarioManagement from './components/ScenarioManagement';
import ScenarioForm from './components/ScenarioForm';
import AgencyManagement from './components/AgencyManagement';
import UserManagement from './components/UserManagement';
import ParticipantForm from './components/ParticipantForm';
import CommsPlanBriefModule from './components/CommsPlanBriefModule';
import ParticipantsList from './components/ParticipantsList';
import PlanEntriesCard from './components/PlanEntriesCard';
import ExerciseDocumentsModal from './components/ExerciseDocumentsModal';
import AIAssistantModal from './components/AIAssistantModal';
import GeneralMessageView from './components/GeneralMessageView';
import ICSGeneralMessageForm from './components/ICSGeneralMessageForm';
import LocationsView from './components/LocationsView';
import TutorialsView from './components/TutorialsView';
import ImageRepository from './components/ImageRepository';
import ResourceForm from './components/ResourceForm';
import ResourcesList from './components/ResourcesList';
import ExerciseListView from './components/ExerciseListView';
import ExerciseDetailView from './components/ExerciseDetailView';
import HIRAForm from './components/HIRAForm';
import ExerciseBuilderWizard from './components/ExerciseBuilderWizard';

// Import ICS form components
import {
  ApprovalRouteForm,
  SafetyMessagePlanForm,
  CommsPlanForm,
  LocationInformationForm,
  MedAidStationForm,
  MedTransportForm,
  HospitalsForm,
  MedProceduresForm
} from './components/ics-forms';
import ICS206MedicalPlan from './components/ICS206MedicalPlan';

// Import custom hooks for ICS forms
import {
  useSafetyMessagePlan,
  useStatusSummary,
  useApprovalRoute,
  useIncidentLocation,
  useIncidentSummary,
  useDecisionSupport,
  useAdditionalDecisionSupport,
  useCommsPlan,
  useMedicalPlan
} from './hooks';

const API = API_URL;

// ICS Standard Marker Icons - Global constant
// ICS_ICONS - now in its own file, import for local use and re-export
import ICS_ICONS from './constants/ICSIcons';
import secureStorage from './utils/secureStorage';
import { useHiraThumbnailCache, useExerciseImageCache } from './utils/hiraThumbnails';
import { writeSanitizedHTML } from './utils/safePrint';
export { ICS_ICONS };

// ── Global Axios auth interceptor ──────────────────────────────────
// Ensures every outgoing request carries the current JWT token, without
// having to remember to attach headers on every call site. Idempotent —
// only installs once even if App.js re-imports during hot reload.
if (!axios.__exrsimAuthInterceptorInstalled) {
  axios.interceptors.request.use((cfg) => {
    try {
      // Only touch same-origin API calls (never third-party requests)
      const url = cfg.url || '';
      const isApiCall = url.startsWith(API) || url.startsWith('/api');
      if (isApiCall) {
        const token = secureStorage.getToken && secureStorage.getToken();
        if (token) {
          cfg.headers = cfg.headers || {};
          if (!cfg.headers.Authorization && !cfg.headers.authorization) {
            cfg.headers.Authorization = `Bearer ${token}`;
          }
        }
      }
    } catch { /* noop */ }
    return cfg;
  });
  axios.__exrsimAuthInterceptorInstalled = true;
}

// ── Global 401 auth-failure interceptor ────────────────────────────
// If any API call returns 401 the token has expired or been revoked.
// Debounced so a transient blip (backend restart during deploy, single
// hiccup on Cloudflare) can't wipe a still-valid session — only real
// repeat 401s trigger a logout.
if (!axios.__exrsimAuthFailInterceptorInstalled) {
  let __sessionExpiredHits = 0;
  let __sessionExpiredWindowStart = 0;
  axios.interceptors.response.use(
    (r) => {
      // Any 2xx response resets the counter (server clearly likes our token)
      __sessionExpiredHits = 0;
      __sessionExpiredWindowStart = 0;
      return r;
    },
    (error) => {
      try {
        const status = error?.response?.status;
        const url = error?.config?.url || '';
        // Don't fire logout on any auth endpoint (including /auth/me — its
        // own catch block already handles token validation results).
        const isAuthEndpoint = /\/auth\//.test(url);
        // Don't fire if the request never carried an Authorization header —
        // that's a "not logged in yet" case, not a "session expired" case.
        const hadAuthHeader = !!(
          error?.config?.headers?.Authorization || error?.config?.headers?.authorization
        );
        if ((status === 401 || status === 403) && !isAuthEndpoint && hadAuthHeader) {
          // Debounce: require 2 hits within 5s before actually logging out.
          const now = Date.now();
          if (now - __sessionExpiredWindowStart > 5000) {
            __sessionExpiredWindowStart = now;
            __sessionExpiredHits = 1;
          } else {
            __sessionExpiredHits += 1;
          }
          if (__sessionExpiredHits >= 2) {
            window.dispatchEvent(new CustomEvent('exrsim:session-expired', { detail: { status, url } }));
            __sessionExpiredHits = 0;
            __sessionExpiredWindowStart = 0;
          }
        }
      } catch { /* noop */ }
      return Promise.reject(error);
    }
  );
  axios.__exrsimAuthFailInterceptorInstalled = true;
}

// ── Global "exercise locked" response interceptor ──────────────────
// If the backend rejects a write with HTTP 423 (exercise locked), show
// a friendly toast so the click never silently fails. The toast module
// is loaded lazily to avoid circular imports.
if (!axios.__exrsimLockedInterceptorInstalled) {
  axios.interceptors.response.use(
    (r) => r,
    async (error) => {
      try {
        if (error?.response?.status === 423) {
          let detail = error?.response?.data?.detail;
          // The data may be a Blob if the caller used responseType:'blob'
          if (!detail && error?.response?.data && typeof error.response.data.text === 'function') {
            try {
              const raw = await error.response.data.text();
              detail = JSON.parse(raw)?.detail || raw;
            } catch { /* noop */ }
          }
          try {
            const { toast } = require('sonner');
            toast.error(detail || 'This exercise is locked — action blocked.', { duration: 5000 });
          } catch { /* noop */ }
        }
      } catch { /* noop */ }
      return Promise.reject(error);
    }
  );
  axios.__exrsimLockedInterceptorInstalled = true;
}


// Global copy to clipboard utility with fallback
const copyToClipboardUtil = (text, onSuccess, onError) => {
  try {
    // Try modern Clipboard API first
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text)
        .then(() => {
          if (onSuccess) onSuccess();
        })
        .catch(() => {
          // Fallback to legacy method
          fallbackCopyUtil(text, onSuccess, onError);
        });
    } else {
      // Use fallback for older browsers or when Clipboard API is blocked
      fallbackCopyUtil(text, onSuccess, onError);
    }
  } catch (err) {
    fallbackCopyUtil(text, onSuccess, onError);
  }
};

const fallbackCopyUtil = (text, onSuccess, onError) => {
  // Create temporary textarea
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  textarea.style.top = '0';
  textarea.style.left = '0';
  document.body.appendChild(textarea);
  
  try {
    // Select and copy
    textarea.focus();
    textarea.select();
    textarea.setSelectionRange(0, text.length);
    const successful = document.execCommand('copy');
    
    if (successful) {
      if (onSuccess) onSuccess();
    } else {
      if (onError) onError();
    }
  } catch (err) {
    if (onError) onError();
  } finally {
    document.body.removeChild(textarea);
  }
};

// Helper function to format time in 24-hour format
const format24HourTime = (date = new Date()) => {
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');
  return `${hours}:${minutes}:${seconds}`;
};

// Theme Context - defined in contexts/AppContexts.js

// Authentication Context - defined in contexts/AppContexts.js

// Toast Notification Context - defined in contexts/AppContexts.js

// Toast Provider Component
const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback((message, type = 'info', duration = 3000, customColor = null, centered = false, requireAcknowledgment = false) => {
    const id = Date.now() + Math.random();
    const toast = { id, message, type, duration, customColor, centered, requireAcknowledgment };
    
    setToasts(prev => [...prev, toast]);

    // Auto remove toast after duration (unless it requires acknowledgment)
    if (duration > 0 && !requireAcknowledgment) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const success = useCallback((message, duration) => showToast(message, 'success', duration), [showToast]);
  const error = useCallback((message, duration) => showToast(message, 'error', duration), [showToast]);
  const warning = useCallback((message, duration) => showToast(message, 'warning', duration), [showToast]);
  const info = useCallback((message, duration) => showToast(message, 'info', duration), [showToast]);

  return (
    <ToastContext.Provider value={{ showToast, success, error, warning, info, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
};

// Toast Container Component
const ToastContainer = ({ toasts, removeToast }) => {
  const centeredToasts = toasts.filter(t => t.centered);
  const regularToasts = toasts.filter(t => !t.centered);
  
  return (
    <>
      {/* Regular toasts - top right */}
      <div className="fixed top-4 right-4 z-50 space-y-2 pointer-events-none">
        {regularToasts.map(toast => (
          <Toast key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
        ))}
      </div>
      
      {/* Centered toasts - middle of screen */}
      {centeredToasts.map(toast => (
        <div key={toast.id} className="fixed inset-0 z-[10005] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <Toast toast={toast} onClose={() => removeToast(toast.id)} centered={true} />
        </div>
      ))}
    </>
  );
};

// Individual Toast Component
const Toast = ({ toast, onClose, centered = false }) => {
  const { type, message, customColor, requireAcknowledgment } = toast;
  
  const getToastStyles = () => {
    // If custom color is provided, use it
    if (customColor) {
      return `${customColor} text-white border-2`;
    }
    
    switch (type) {
      case 'success':
        return 'bg-green-600 border-green-500 text-white';
      case 'error':
        return 'bg-red-600 border-red-500 text-white';
      case 'warning':
        return 'bg-yellow-600 border-yellow-500 text-white';
      case 'info':
      default:
        return 'bg-blue-600 border-blue-500 text-white';
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className="h-5 w-5" />;
      case 'error':
        return <AlertCircle className="h-5 w-5" />;
      case 'warning':
        return <AlertTriangle className="h-5 w-5" />;
      case 'info':
      default:
        return <Info className="h-5 w-5" />;
    }
  };

  // Centered notification (for emergency phase changes)
  if (centered) {
    return (
      <div 
        className={`
          ${getToastStyles()} 
          pointer-events-auto
          flex flex-col items-center gap-4 
          px-8 py-6 
          rounded-xl 
          shadow-2xl 
          border-4
          min-w-[400px] max-w-[600px]
          animate-in zoom-in-95
          transition-all duration-300
        `}
      >
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0">
            {getIcon()}
          </div>
          <div className="flex-1 text-lg font-bold">
            Emergency Phase Change
          </div>
        </div>
        <div className="text-base text-center font-medium">
          {message}
        </div>
        {requireAcknowledgment && (
          <button
            onClick={onClose}
            className="mt-2 px-6 py-3 bg-white/20 hover:bg-white/30 rounded-lg font-semibold transition-colors flex items-center gap-2"
          >
            <CheckCircle className="h-5 w-5" />
            Acknowledge
          </button>
        )}
      </div>
    );
  }

  // Regular notification (top-right)
  return (
    <div 
      className={`
        ${getToastStyles()} 
        pointer-events-auto
        flex items-center gap-3 
        px-4 py-3 
        rounded-lg 
        shadow-lg 
        border-l-4
        min-w-[300px] max-w-[500px]
        animate-in slide-in-from-right
        transition-all duration-300
      `}
    >
      <div className="flex-shrink-0">
        {getIcon()}
      </div>
      <div className="flex-1 text-sm font-medium">
        {message}
      </div>
      <button
        onClick={onClose}
        className="flex-shrink-0 hover:opacity-70 transition-opacity"
        aria-label="Close notification"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

// Theme configurations
const themes = {
  dark: {
    name: 'Dark Theme',
    colors: {
      // Main backgrounds
      primary: 'bg-black',
      secondary: 'bg-gray-900',
      tertiary: 'bg-gray-800',
      quaternary: 'bg-gray-700',
      
      // Text colors
      textPrimary: 'text-white',
      textSecondary: 'text-gray-300',
      textMuted: 'text-gray-400',
      
      // Borders
      border: 'border-gray-700',
      borderLight: 'border-gray-600',
      borderAccent: 'border-orange-500/20',
      
      // Interactive elements
      hover: 'hover:bg-gray-700',
      active: 'bg-orange-500/5',
      
      // Form elements
      input: 'bg-gray-800 border-gray-700 text-white placeholder-gray-400',
      select: 'bg-gray-800 border-gray-700 text-white',
      
      // Cards and containers
      card: 'bg-gray-800 border-gray-700',
      modal: 'bg-gray-900 border-gray-700',
    }
  },
  light: {
    name: 'Light Theme',
    colors: {
      // Main backgrounds
      primary: 'bg-white',
      secondary: 'bg-gray-50',
      tertiary: 'bg-gray-100',
      quaternary: 'bg-gray-200',
      
      // Text colors
      textPrimary: 'text-gray-900',
      textSecondary: 'text-gray-700',
      textMuted: 'text-gray-500',
      
      // Borders
      border: 'border-gray-300',
      borderLight: 'border-gray-200',
      borderAccent: 'border-orange-500/30',
      
      // Interactive elements
      hover: 'hover:bg-gray-100',
      active: 'bg-orange-500/10',
      
      // Form elements
      input: 'bg-white border-gray-300 text-gray-900 placeholder-gray-500',
      select: 'bg-white border-gray-300 text-gray-900',
      
      // Cards and containers
      card: 'bg-white border-gray-300',
      modal: 'bg-white border-gray-300',
    }
  }
};

// Theme Provider Component
const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState('dark');
  
  // Load theme from localStorage on mount
  useEffect(() => {
    const savedTheme = secureStorage.getPreference('exrsim-theme');
    if (savedTheme && themes[savedTheme]) {
      setCurrentTheme(savedTheme);
    }
  }, []);
  
  // Save theme to localStorage when changed
  useEffect(() => {
    secureStorage.setPreference('exrsim-theme', currentTheme);
    // Toggle a class on <html> without stomping on other classes
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(currentTheme);
  }, [currentTheme]);
  
  const switchTheme = (themeName) => {
    if (themes[themeName]) {
      setCurrentTheme(themeName);
    }
  };
  
  const theme = themes[currentTheme];
  
  return (
    <ThemeContext.Provider value={{ currentTheme, theme, switchTheme, themes }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Authentication Provider Component
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load user and token from localStorage on mount
  useEffect(() => {
    const initAuth = async () => {
      const savedToken = secureStorage.getToken();
      const savedUser = secureStorage.getPreference('exrsim-user');
      
      if (savedToken && savedUser && savedUser !== 'undefined') {
        setToken(savedToken);
        try {
          setUser(JSON.parse(savedUser));
          // Verify token is still valid (await completion before setting loading to false)
          await verifyToken(savedToken);
        } catch (error) {
          console.error('Error parsing saved user:', error);
          // Clear invalid data
          secureStorage.removeToken();
          secureStorage.removePreference('exrsim-user');
        }
      }
      setLoading(false);
    };
    
    initAuth();
  }, []);

  // Global session-expired listener — fired by the axios 401 interceptor.
  // Prevents the app from silently sitting with a stale token showing empty
  // dashboards. Force-logs the user out with a clear toast so they know why.
  useEffect(() => {
    const handleSessionExpired = () => {
      // Only act if we currently think we're authenticated
      if (!secureStorage.getToken()) return;
      try {
        const { toast } = require('sonner');
        toast.error('Your session has expired. Please sign in again.', { duration: 6000 });
      } catch { /* noop */ }
      setToken(null);
      setUser(null);
      secureStorage.removeToken();
      secureStorage.removePreference('exrsim-user');
    };
    window.addEventListener('exrsim:session-expired', handleSessionExpired);
    return () => window.removeEventListener('exrsim:session-expired', handleSessionExpired);
  }, []);

  const verifyToken = async (authToken) => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      });
      if (response.data) {
        setUser(response.data);
        secureStorage.setPreference('exrsim-user', JSON.stringify(response.data));
      }
    } catch (error) {
      // Only logout on authentication errors (401/403), not on network errors
      if (error.response && (error.response.status === 401 || error.response.status === 403)) {
        console.error('Token verification failed - authentication error:', error);
        // Token is invalid, clear auth state
        setToken(null);
        setUser(null);
        secureStorage.removeToken();
        secureStorage.removePreference('exrsim-user');
      } else {
        // Network error or server down - keep the user logged in with cached data
        console.warn('Token verification failed - network/server error, keeping cached auth:', error);
      }
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password
      });
      
      const { access_token, user: userData } = response.data;
      
      // Save to state and localStorage
      setToken(access_token);
      setUser(userData);
      secureStorage.setToken(access_token);
      secureStorage.setPreference('exrsim-user', JSON.stringify(userData));
      
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed. Please try again.'
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await axios.post(`${API}/auth/register`, userData);
      
      const { access_token, user: newUser } = response.data;
      
      // Save to state and localStorage
      setToken(access_token);
      setUser(newUser);
      secureStorage.setToken(access_token);
      secureStorage.setPreference('exrsim-user', JSON.stringify(newUser));
      
      return { success: true };
    } catch (error) {
      console.error('Registration error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Registration failed. Please try again.'
      };
    }
  };

  const logout = async () => {
    try {
      // Call backend logout endpoint to update is_logged_in status
      if (token) {
        await fetch(`${API_BASE_URL}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }
    } catch (error) {
      console.error('Logout API call failed:', error);
      // Continue with logout even if API call fails
    } finally {
      // Clear local state and storage
      setToken(null);
      setUser(null);
      secureStorage.removeToken();
      secureStorage.removePreference('exrsim-user');
    }
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    secureStorage.setPreference('exrsim-user', JSON.stringify(updatedUser));
  };

  const isAuthenticated = !!token && !!user;
  
  const hasRole = (roles) => {
    if (!user) return false;
    if (Array.isArray(roles)) {
      return roles.includes(user.role);
    }
    return user.role === roles;
  };

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      isAuthenticated,
      login,
      register,
      logout,
      updateUser,
      hasRole
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Agency Context - defined in contexts/AppContexts.js

const AgencyContextProvider = ({ children }) => {
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [agencies, setAgencies] = useState([]);
  const { user, token } = useAuth();
  const API = API_BASE_URL + '/api';

  // Load selected agency from localStorage
  useEffect(() => {
    const saved = secureStorage.getPreference('selected-agency-context');
    if (saved) {
      try {
        setSelectedAgency(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to parse selected agency:', e);
      }
    }
  }, []);

  // Fetch all agencies when Super Admin logs in
  useEffect(() => {
    if (user?.role === 'Super Admin' && token) {
      fetchAgencies();
    } else {
      // Clear context if not Super Admin
      setSelectedAgency(null);
      setAgencies([]);
    }
  }, [user, token]);

  const fetchAgencies = async () => {
    try {
      const response = await axios.get(`${API}/agencies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgencies(response.data || []);
    } catch (error) {
      console.error('Error fetching agencies:', error);
    }
  };

  const selectAgency = (agency) => {
    setSelectedAgency(agency);
    if (agency) {
      secureStorage.setPreference('selected-agency-context', JSON.stringify(agency));
    } else {
      secureStorage.removePreference('selected-agency-context');
    }
  };

  const clearAgencyContext = () => {
    setSelectedAgency(null);
    secureStorage.removePreference('selected-agency-context');
  };

  return (
    <AgencyContext.Provider value={{
      selectedAgency,
      agencies,
      selectAgency,
      clearAgencyContext,
      isSuperAdmin: user?.role === 'Super Admin'
    }}>
      {children}
    </AgencyContext.Provider>
  );
};

// Validation functions
const validateLatitude = (lat) => {
  if (lat === '' || lat === null || lat === undefined) return true; // Allow empty for optional fields
  
  // Check for proper format: digits + optional decimal + up to 4 decimal places (no sign required)
  const formatRegex = /^-?\d+\.?\d{0,4}$/;
  if (!formatRegex.test(lat.toString())) return false;
  
  const num = parseFloat(lat);
  return !isNaN(num) && num >= -90.0000 && num <= 90.0000;
};

const validateLongitude = (lng) => {
  if (lng === '' || lng === null || lng === undefined) return true; // Allow empty for optional fields
  
  // Check for proper format: digits + optional decimal + up to 4 decimal places (no sign required)
  const formatRegex = /^-?\d+\.?\d{0,4}$/;
  if (!formatRegex.test(lng.toString())) return false;
  
  const num = parseFloat(lng);
  return !isNaN(num) && num >= -180.0000 && num <= 180.0000;
};

const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const validatePhone = (phone) => {
  const phoneRegex = /^\d{3}-\d{3}-\d{4}$/;
  return phoneRegex.test(phone);
};

const formatPhone = (input) => {
  // Remove all non-digit characters
  const digits = input.replace(/\D/g, '');
  
  // Format as XXX-XXX-XXXX
  if (digits.length <= 3) {
    return digits;
  } else if (digits.length <= 6) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  } else {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  }
};

const formatCoordinate = (input, type) => {
  // Remove any non-digit, non-decimal, non-minus characters
  const cleaned = input.replace(/[^0-9.-]/g, '');
  
  // Ensure only one decimal point and one minus sign at the beginning
  let formatted = cleaned;
  const decimalCount = (formatted.match(/\./g) || []).length;
  const minusCount = (formatted.match(/-/g) || []).length;
  
  if (decimalCount > 1) {
    const firstDecimalIndex = formatted.indexOf('.');
    formatted = formatted.substring(0, firstDecimalIndex + 1) + formatted.substring(firstDecimalIndex + 1).replace(/\./g, '');
  }
  
  if (minusCount > 1 || (minusCount === 1 && formatted.indexOf('-') !== 0)) {
    formatted = formatted.replace(/-/g, '');
    if (input.startsWith('-')) {
      formatted = '-' + formatted;
    }
  }
  
  return formatted;
};

// Navigation Component
// Theme Settings Component
const ThemeSettings = ({ onClose }) => {
  const { currentTheme, switchTheme, themes } = useTheme();
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className={`${themes[currentTheme].colors.modal} rounded-lg shadow-xl w-full max-w-md mx-4`}>
        <div className={`flex items-center justify-between p-6 ${themes[currentTheme].colors.borderAccent} border-b`}>
          <h2 className={`text-xl font-bold ${themes[currentTheme].colors.textPrimary}`}>Theme Settings</h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className={`${themes[currentTheme].colors.textMuted} hover:${themes[currentTheme].colors.textPrimary}`}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        
        <div className="p-6 space-y-4">
          <div>
            <Label className={`${themes[currentTheme].colors.textPrimary} text-sm font-medium mb-3 block`}>
              Select Theme
            </Label>
            <div className="space-y-2">
              {Object.entries(themes).map(([themeKey, themeConfig]) => (
                <button
                  key={themeKey}
                  onClick={() => switchTheme(themeKey)}
                  className={`w-full flex items-center justify-between p-3 rounded-lg border-2 transition-all ${
                    currentTheme === themeKey
                      ? 'border-orange-500 bg-orange-500/10'
                      : `${themes[currentTheme].colors.border} ${themes[currentTheme].colors.hover}`
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-4 h-4 rounded-full ${
                      themeKey === 'dark' ? 'bg-gray-800' : 'bg-gray-100'
                    } border-2 ${themes[currentTheme].colors.border}`} />
                    <span className={themes[currentTheme].colors.textPrimary}>
                      {themeConfig.name}
                    </span>
                  </div>
                  {currentTheme === themeKey && (
                    <CheckCircle className="h-4 w-4 text-orange-500" />
                  )}
                </button>
              ))}
            </div>
          </div>
          
          <div className="pt-4 border-t border-gray-600">
            <p className={`text-xs ${themes[currentTheme].colors.textMuted}`}>
              Theme settings are saved automatically and will persist across sessions.
            </p>
          </div>
        </div>
        
        <div className={`flex justify-end p-6 ${themes[currentTheme].colors.borderAccent} border-t`}>
          <Button
            onClick={onClose}
            className="bg-orange-500 hover:bg-orange-600 text-black"
          >
            Done
          </Button>
        </div>
      </div>
    </div>
  );
};

// Glossary Viewer Component
const GlossaryViewer = ({ onClose }) => {
  const { theme } = useTheme();
  const [glossaryTerms, setGlossaryTerms] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLetter, setSelectedLetter] = useState('ALL');
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    loadGlossary();
  }, []);
  
  const loadGlossary = async () => {
    try {
      const response = await axios.get(`${API}/glossary`);
      setGlossaryTerms(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading glossary:', error);
      setLoading(false);
    }
  };
  
  // Filter terms based on search and selected letter
  const filteredTerms = glossaryTerms.filter(term => {
    const matchesSearch = searchTerm === '' || 
      term.term.toLowerCase().includes(searchTerm.toLowerCase()) ||
      term.definition.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesLetter = selectedLetter === 'ALL' || 
      term.term.toUpperCase().startsWith(selectedLetter);
    
    return matchesSearch && matchesLetter;
  });
  
  // Group terms by first letter
  const groupedTerms = filteredTerms.reduce((acc, term) => {
    const firstLetter = term.term[0].toUpperCase();
    if (!acc[firstLetter]) {
      acc[firstLetter] = [];
    }
    acc[firstLetter].push(term);
    return acc;
  }, {});
  
  // Generate alphabet buttons
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
  
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>EXRSIM Glossary</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #f97316; text-align: center; margin-bottom: 30px; }
          .term { margin-bottom: 20px; page-break-inside: avoid; }
          .term-name { font-weight: bold; font-size: 16px; color: #1f2937; margin-bottom: 5px; }
          .term-definition { margin-left: 20px; color: #374151; line-height: 1.6; }
          .letter-section { margin-top: 30px; }
          .letter-heading { font-size: 24px; font-weight: bold; color: #f97316; border-bottom: 2px solid #f97316; padding-bottom: 5px; margin-bottom: 15px; }
          @media print {
            .term { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <h1>EXRSIM Glossary - ICS/Emergency Management Terms</h1>
        ${Object.keys(groupedTerms).sort().map(letter => `
          <div class="letter-section">
            <div class="letter-heading">${letter}</div>
            ${groupedTerms[letter].map(term => `
              <div class="term">
                <div class="term-name">${term.term}</div>
                <div class="term-definition">${term.definition}</div>
              </div>
            `).join('')}
          </div>
        `).join('')}
      </body>
      </html>
    `;
    
    printWindow.document.open();
    writeSanitizedHTML(printWindow.document, printContent);
    printWindow.onload = () => {
      printWindow.print();
    };
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`${theme.colors.modal} rounded-lg w-full max-w-6xl max-h-[90vh] flex flex-col`}>
        {/* Header */}
        <div className={`p-6 border-b ${theme.colors.border} flex justify-between items-center`}>
          <div>
            <h2 className="text-2xl font-bold text-orange-500">Glossary</h2>
            <p className={`text-sm ${theme.colors.textMuted} mt-1`}>
              ICS and Emergency Management Terminology
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={handlePrint}
              className="bg-orange-500 hover:bg-orange-600 text-black"
              size="sm"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print
            </Button>
            <Button
              variant="ghost"
              onClick={onClose}
              className={`${theme.colors.textMuted} hover:${theme.colors.textPrimary}`}
            >
              <X className="h-6 w-6" />
            </Button>
          </div>
        </div>
        
        {/* Search Bar */}
        <div className={`p-4 border-b ${theme.colors.border}`}>
          <Input
            type="text"
            placeholder="Search terms or definitions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`w-full ${theme.colors.input} ${theme.colors.textPrimary}`}
          />
        </div>
        
        {/* Alphabetical Navigation */}
        <div className={`py-6 px-4 border-b ${theme.colors.border}`}>
          <div className="flex gap-2 flex-wrap min-h-[100px]">
            <button
              onClick={() => setSelectedLetter('ALL')}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                selectedLetter === 'ALL'
                  ? 'bg-orange-500 text-black'
                  : `${theme.colors.tertiary} ${theme.colors.textSecondary} hover:bg-orange-500/20`
              }`}
            >
              ALL
            </button>
            {alphabet.map(letter => (
              <button
                key={letter}
                onClick={() => setSelectedLetter(letter)}
                className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                  selectedLetter === letter
                    ? 'bg-orange-500 text-black'
                    : `${theme.colors.tertiary} ${theme.colors.textSecondary} hover:bg-orange-500/20`
                }`}
              >
                {letter}
              </button>
            ))}
          </div>
        </div>
        
        {/* Terms List */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className={`${theme.colors.textMuted} mb-2`}>Loading glossary...</div>
            </div>
          ) : filteredTerms.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className={`h-16 w-16 ${theme.colors.textMuted} mx-auto mb-4`} />
              <p className={`${theme.colors.textSecondary}`}>
                No terms found matching your search.
              </p>
            </div>
          ) : (
            <div className="space-y-8">
              {Object.keys(groupedTerms).sort().map(letter => (
                <div key={letter} id={`letter-${letter}`}>
                  <h3 className="text-2xl font-bold text-orange-500 mb-4 pb-2 border-b-2 border-orange-500">
                    {letter}
                  </h3>
                  <div className="space-y-4">
                    {groupedTerms[letter].map((term, index) => (
                      <div 
                        key={term.term || `glossary-${index}`} 
                        className={`p-4 rounded-lg ${theme.colors.tertiary} border ${theme.colors.border}`}
                      >
                        <h4 className={`text-lg font-bold ${theme.colors.textPrimary} mb-2`}>
                          {term.term}
                        </h4>
                        <p className={`${theme.colors.textSecondary} leading-relaxed`}>
                          {term.definition}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className={`p-4 border-t ${theme.colors.border} flex justify-between items-center`}>
          <p className={`text-sm ${theme.colors.textMuted}`}>
            Total: {filteredTerms.length} term{filteredTerms.length !== 1 ? 's' : ''}
          </p>
          <Button
            onClick={onClose}
            className="bg-orange-500 hover:bg-orange-600 text-black"
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};


// Financial Categories Manager Component (Placeholder)
const FinancialCategoriesManager = ({ onClose }) => {
  const { theme } = useTheme();
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`${theme.colors.modal} rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden`}>
        <div className={`p-6 border-b ${theme.colors.border} flex justify-between items-center`}>
          <h2 className="text-2xl font-bold text-orange-500">Financial Categories Manager</h2>
          <Button
            variant="ghost"
            onClick={onClose}
            className={`${theme.colors.textMuted} hover:${theme.colors.textPrimary}`}
          >
            <X className="h-6 w-6" />
          </Button>
        </div>
        
        <div className="p-6">
          <div className="text-center py-12">
            <DollarSign className={`h-16 w-16 ${theme.colors.textMuted} mx-auto mb-4`} />
            <h3 className={`text-xl font-semibold ${theme.colors.textPrimary} mb-2`}>
              Financial Categories Management
            </h3>
            <p className={`${theme.colors.textMuted} mb-6`}>
              Manage financial categories for exercise budgeting and cost tracking.
            </p>
            <p className={`${theme.colors.textSecondary} text-sm`}>
              This feature will be implemented to manage exercise-related financial categories,
              cost centers, and budget allocation tracking.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Acronyms Manager Component (Placeholder)
const AcronymsManager = ({ onClose }) => {
  const { theme } = useTheme();
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`${theme.colors.modal} rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden`}>
        <div className={`p-6 border-b ${theme.colors.border} flex justify-between items-center`}>
          <h2 className="text-2xl font-bold text-orange-500">Acronyms Manager</h2>
          <Button
            variant="ghost"
            onClick={onClose}
            className={`${theme.colors.textMuted} hover:${theme.colors.textPrimary}`}
          >
            <X className="h-6 w-6" />
          </Button>
        </div>
        
        <div className="p-6">
          <div className="text-center py-12">
            <BookText className={`h-16 w-16 ${theme.colors.textMuted} mx-auto mb-4`} />
            <h3 className={`text-xl font-semibold ${theme.colors.textPrimary} mb-2`}>
              Acronyms & Terminology Management
            </h3>
            <p className={`${theme.colors.textMuted} mb-6`}>
              Manage emergency management acronyms and terminology definitions.
            </p>
            <p className={`${theme.colors.textSecondary} text-sm`}>
              This feature will be implemented to manage emergency management acronyms,
              terminology definitions, and standardized language for exercise documentation.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Definitions Manager Component (Placeholder)
const DefinitionsManager = ({ onClose }) => {
  const { theme } = useTheme();
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`${theme.colors.modal} rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden`}>
        <div className={`p-6 border-b ${theme.colors.border} flex justify-between items-center`}>
          <h2 className="text-2xl font-bold text-orange-500">Definitions Manager</h2>
          <Button
            variant="ghost"
            onClick={onClose}
            className={`${theme.colors.textMuted} hover:${theme.colors.textPrimary}`}
          >
            <X className="h-6 w-6" />
          </Button>
        </div>
        
        <div className="p-6">
          <div className="text-center py-12">
            <BookOpen className={`h-16 w-16 ${theme.colors.textMuted} mx-auto mb-4`} />
            <h3 className={`text-xl font-semibold ${theme.colors.textPrimary} mb-2`}>
              Definitions Management
            </h3>
            <p className={`${theme.colors.textMuted} mb-6`}>
              Manage exercise and emergency management definitions and glossary terms.
            </p>
            <p className={`${theme.colors.textSecondary} text-sm`}>
              This feature will be implemented to manage standardized definitions,
              glossary terms, and terminology used in exercise documentation and training materials.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Quicklinks Manager Component (Placeholder)
const QuicklinksManager = ({ onClose }) => {
  const { theme } = useTheme();
  const [quicklinks, setQuicklinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingQuicklink, setEditingQuicklink] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    url: '',
    description: '',
    category: 'General'
  });
  const [filterCategory, setFilterCategory] = useState('all');
  const [validationError, setValidationError] = useState('');
  
  const categories = ['General', 'Training', 'Reference', 'Tools', 'Emergency', 'Documentation'];
  
  useEffect(() => {
    loadQuicklinks();
  }, [filterCategory]);
  
  const loadQuicklinks = async () => {
    try {
      const url = filterCategory && filterCategory !== 'all'
        ? `${API}/quicklinks?category=${filterCategory}`
        : `${API}/quicklinks`;
      const response = await axios.get(url);
      setQuicklinks(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading quicklinks:', error);
      setLoading(false);
    }
  };
  
  const validateUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };
  
  const handleSave = async () => {
    // Validation
    if (!formData.title.trim()) {
      setValidationError('Title is required');
      return;
    }
    
    if (!formData.url.trim()) {
      setValidationError('URL is required');
      return;
    }
    
    if (!validateUrl(formData.url)) {
      setValidationError('Please enter a valid URL (e.g., https://example.com)');
      return;
    }
    
    setValidationError('');
    
    try {
      if (editingQuicklink) {
        // Update existing quicklink
        await axios.put(`${API}/quicklinks/${editingQuicklink.id}`, formData);
      } else {
        // Create new quicklink
        await axios.post(`${API}/quicklinks`, formData);
      }
      
      // Reset form
      setFormData({ title: '', url: '', description: '', category: 'General' });
      setShowAddForm(false);
      setEditingQuicklink(null);
      
      // Reload quicklinks
      await loadQuicklinks();
    } catch (error) {
      console.error('Error saving quicklink:', error);
      setValidationError(error.response?.data?.detail || 'Failed to save quicklink');
    }
  };
  
  const handleEdit = (quicklink) => {
    setEditingQuicklink(quicklink);
    setFormData({
      title: quicklink.title,
      url: quicklink.url,
      description: quicklink.description || '',
      category: quicklink.category
    });
    setShowAddForm(true);
    setValidationError('');
  };
  
  const handleDelete = async (quicklinkId) => {
    if (!confirm('Are you sure you want to delete this quicklink?')) return;
    
    try {
      await axios.delete(`${API}/quicklinks/${quicklinkId}`);
      await loadQuicklinks();
    } catch (error) {
      console.error('Error deleting quicklink:', error);
      alert('Failed to delete quicklink');
    }
  };
  
  const handleCancel = () => {
    setFormData({ title: '', url: '', description: '', category: 'General' });
    setShowAddForm(false);
    setEditingQuicklink(null);
    setValidationError('');
  };
  
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 10002 }}>
      <div className={`${theme.colors.modal} rounded-lg w-full max-w-6xl max-h-[90vh] flex flex-col`}>
        {/* Header */}
        <div className={`p-6 border-b ${theme.colors.border} flex justify-between items-center`}>
          <div>
            <h2 className="text-2xl font-bold text-orange-500">Quicklinks</h2>
            <p className={`text-sm ${theme.colors.textMuted} mt-1`}>
              Manage quick access links to external resources
            </p>
          </div>
          <Button
            variant="ghost"
            onClick={onClose}
            className={`${theme.colors.textMuted} hover:${theme.colors.textPrimary}`}
          >
            <X className="h-6 w-6" />
          </Button>
        </div>
        
        {/* Add/Edit Form */}
        {showAddForm && (
          <div className={`p-6 border-b ${theme.colors.border} ${theme.colors.tertiary}`}>
            <h3 className={`text-lg font-semibold ${theme.colors.textPrimary} mb-4`}>
              {editingQuicklink ? 'Edit Quicklink' : 'Add New Quicklink'}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className={`${theme.colors.textPrimary} mb-2 block`}>
                  Title <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="e.g., FEMA Website"
                  className={`${theme.colors.input} ${theme.colors.textPrimary}`}
                />
              </div>
              
              <div>
                <Label className={`${theme.colors.textPrimary} mb-2 block`}>
                  Category
                </Label>
                <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                  <SelectTrigger className={`${theme.colors.input} ${theme.colors.textPrimary}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="md:col-span-2">
                <Label className={`${theme.colors.textPrimary} mb-2 block`}>
                  URL <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={formData.url}
                  onChange={(e) => setFormData({...formData, url: e.target.value})}
                  placeholder="https://example.com"
                  className={`${theme.colors.input} ${theme.colors.textPrimary}`}
                />
              </div>
              
              <div className="md:col-span-2">
                <Label className={`${theme.colors.textPrimary} mb-2 block`}>
                  Description (Optional)
                </Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Brief description of the resource..."
                  className={`${theme.colors.input} ${theme.colors.textPrimary}`}
                  rows={2}
                />
              </div>
            </div>
            
            {validationError && (
              <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded text-red-500 text-sm">
                {validationError}
              </div>
            )}
            
            <div className="mt-4 flex gap-2">
              <Button
                onClick={handleSave}
                className="bg-orange-500 hover:bg-orange-600 text-black"
              >
                <Save className="h-4 w-4 mr-2" />
                {editingQuicklink ? 'Update' : 'Add'} Quicklink
              </Button>
              <Button
                onClick={handleCancel}
                variant="outline"
                className={`border-gray-500 ${theme.colors.textSecondary}`}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
        
        {/* Action Bar */}
        {!showAddForm && (
          <div className={`px-6 py-4 border-b ${theme.colors.border} flex justify-between items-center`}>
            <div className="flex items-center gap-4">
              <Label className={`${theme.colors.textPrimary}`}>Filter:</Label>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className={`w-48 ${theme.colors.input} ${theme.colors.textPrimary}`}>
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <Button
              onClick={() => setShowAddForm(true)}
              className="bg-orange-500 hover:bg-orange-600 text-black"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Quicklink
            </Button>
          </div>
        )}
        
        {/* Quicklinks List */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-12">
              <p className={theme.colors.textMuted}>Loading quicklinks...</p>
            </div>
          ) : quicklinks.length === 0 ? (
            <div className="text-center py-12">
              <ExternalLink className={`h-16 w-16 ${theme.colors.textMuted} mx-auto mb-4`} />
              <p className={theme.colors.textSecondary}>No quicklinks added yet</p>
              {!showAddForm && (
                <Button
                  onClick={() => setShowAddForm(true)}
                  className="mt-4 bg-orange-500 hover:bg-orange-600 text-black"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Your First Quicklink
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {quicklinks.map(quicklink => (
                <div 
                  key={quicklink.id}
                  className={`p-4 rounded-lg border ${theme.colors.border} ${theme.colors.tertiary} hover:border-orange-500/50 transition-colors`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <ExternalLink className="h-5 w-5 text-orange-500" />
                        <h4 className={`font-semibold ${theme.colors.textPrimary}`}>
                          {quicklink.title}
                        </h4>
                      </div>
                      <Badge variant="outline" className="bg-orange-500/10 text-orange-500 border-orange-500/30 mb-2">
                        {quicklink.category}
                      </Badge>
                    </div>
                    
                    <div className="flex gap-1 ml-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEdit(quicklink)}
                        className={`${theme.colors.textMuted} hover:text-orange-500`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(quicklink.id)}
                        className={`${theme.colors.textMuted} hover:text-red-500`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {quicklink.description && (
                    <p className={`text-sm ${theme.colors.textSecondary} mb-3`}>
                      {quicklink.description}
                    </p>
                  )}
                  
                  <a
                    href={quicklink.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-orange-500 hover:text-orange-400 underline break-all flex items-center gap-1"
                  >
                    {quicklink.url}
                    <ExternalLink className="h-3 w-3 flex-shrink-0" />
                  </a>
                  
                  <div className={`text-xs ${theme.colors.textMuted} mt-3 pt-3 border-t ${theme.colors.border}`}>
                    Added: {formatDate(quicklink.created_at)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className={`p-4 border-t ${theme.colors.border} flex justify-between items-center`}>
          <p className={`text-sm ${theme.colors.textMuted}`}>
            Total: {quicklinks.length} quicklink{quicklinks.length !== 1 ? 's' : ''}
          </p>
          <Button onClick={onClose} className="bg-orange-500 hover:bg-orange-600 text-black">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

// Position Manager Component (Placeholder)
const PositionManager = ({ onClose }) => {
  const { theme } = useTheme();
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`${theme.colors.modal} rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden`}>
        <div className={`p-6 border-b ${theme.colors.border} flex justify-between items-center`}>
          <h2 className="text-2xl font-bold text-orange-500">Position Manager</h2>
          <Button
            variant="ghost"
            onClick={onClose}
            className={`${theme.colors.textMuted} hover:${theme.colors.textPrimary}`}
          >
            <X className="h-6 w-6" />
          </Button>
        </div>
        
        <div className="p-6">
          <div className="text-center py-12">
            <UserCog className={`h-16 w-16 ${theme.colors.textMuted} mx-auto mb-4`} />
            <h3 className={`text-xl font-semibold ${theme.colors.textPrimary} mb-2`}>
              Position Management
            </h3>
            <p className={`${theme.colors.textMuted} mb-6`}>
              Manage organizational positions, roles, and responsibilities for exercise participants.
            </p>
            <p className={`${theme.colors.textSecondary} text-sm`}>
              This feature will be implemented to manage standardized positions, job titles,
              and role definitions used in emergency response exercises and training scenarios.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Policies and Procedures Manager Component (Placeholder)
const PoliciesProceduresManager = ({ onClose }) => {
  const { theme } = useTheme();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [editingDocument, setEditingDocument] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);
  const [formData, setFormData] = useState({
    description: '',
    category: 'Policy',
    uploaded_by: 'System'
  });
  const [filterCategory, setFilterCategory] = useState('all');
  
  const categories = ['General', 'Command Staff', 'Operations', 'Planning', 'Logistics', 'Finance', 'Administration', 'Policy', 'Safety', 'Training', 'Response', 'Checklist'];
  const allowedExtensions = ['.doc', '.docx', '.pdf', '.xls', '.xlsx', '.ppt', '.pptx'];
  
  useEffect(() => {
    loadDocuments();
  }, [filterCategory]);
  
  const loadDocuments = async () => {
    try {
      const url = filterCategory && filterCategory !== 'all'
        ? `${API}/policies-procedures?category=${filterCategory}`
        : `${API}/policies-procedures`;
      const response = await axios.get(url);
      setDocuments(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error loading documents:', error);
      setLoading(false);
    }
  };
  
  const acceptFile = (file) => {
    if (!file) return false;
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowedExtensions.includes(ext)) {
      alert(`File type not allowed. Please upload: ${allowedExtensions.join(', ')}`);
      return false;
    }
    setSelectedFile(file);
    return true;
  };
  
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && !acceptFile(file)) {
      e.target.value = '';
    }
  };
  
  // ── Drag-and-drop handlers ─────────────────────────────────────────
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (editingDocument) return;
    dragCounter.current += 1;
    if (e.dataTransfer?.items?.length) setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setIsDragging(false);
    }
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);
    if (editingDocument) return;
    const file = e.dataTransfer?.files?.[0];
    if (file) acceptFile(file);
  };
  
  const handleUpload = async () => {
    if (!selectedFile) {
      alert('Please select a file to upload');
      return;
    }
    
    setUploading(true);
    const uploadData = new FormData();
    uploadData.append('file', selectedFile);
    uploadData.append('description', formData.description);
    uploadData.append('category', formData.category);
    uploadData.append('uploaded_by', formData.uploaded_by);
    
    try {
      await axios.post(`${API}/policies-procedures/upload`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Reset form
      setSelectedFile(null);
      setFormData({ description: '', category: 'Policy', uploaded_by: 'System' });
      document.getElementById('file-input').value = '';
      
      // Reload documents
      await loadDocuments();
      setUploading(false);
    } catch (error) {
      console.error('Error uploading file:', error);
      alert(error.response?.data?.detail || 'Failed to upload file');
      setUploading(false);
    }
  };
  
  const _extractBlobErrorMessage = async (error, fallback) => {
    const data = error?.response?.data;
    try {
      if (data && typeof data.text === 'function') {
        const raw = await data.text();
        try { return (JSON.parse(raw)?.detail) || raw || fallback; }
        catch { return raw || fallback; }
      }
      if (typeof data?.detail === 'string') return data.detail;
    } catch { /* noop */ }
    return fallback;
  };
  
  const handleDownload = async (documentId, filename) => {
    try {
      const response = await axios.get(`${API}/policies-procedures/${documentId}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading file:', error);
      alert(await _extractBlobErrorMessage(error, 'Failed to download file'));
    }
  };

  const handleView = async (documentId, filename) => {
    // Open the tab synchronously (still inside the click handler) so popup
    // blockers do not swallow the request; then load content once fetch resolves.
    const win = window.open('', '_blank');
    if (!win) {
      alert('Please allow pop-ups to view files');
      return;
    }
    try {
      win.document.write(
        `<title>Loading ${filename || 'document'}…</title>` +
        `<p style="font-family:system-ui;padding:24px;color:#555">Loading ${filename || 'document'}…</p>`
      );
      const response = await axios.get(`${API}/policies-procedures/${documentId}`, {
        responseType: 'blob'
      });
      const ext = (filename || '').split('.').pop().toLowerCase();
      const mimeFromExt = (
        ext === 'pdf' ? 'application/pdf'
        : ['png','jpg','jpeg','gif','webp'].includes(ext) ? `image/${ext === 'jpg' ? 'jpeg' : ext}`
        : ext === 'svg' ? 'image/svg+xml'
        : ext === 'txt' ? 'text/plain'
        : ext === 'csv' ? 'text/csv'
        : ext === 'html' ? 'text/html'
        : 'application/octet-stream'
      );
      const rawType = response.data.type;
      const mime = (rawType && rawType !== 'application/octet-stream') ? rawType : mimeFromExt;
      const url = window.URL.createObjectURL(new Blob([response.data], { type: mime }));
      win.location.replace(url);
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (error) {
      console.error('Error viewing file:', error);
      const msg = await _extractBlobErrorMessage(error, 'Failed to open file');
      try {
        win.document.body.innerHTML = `<p style="font-family:system-ui;padding:24px;color:#c00">${msg}</p>`;
      } catch { /* noop */ }
      alert(msg);
    }
  };

  const handlePrintDoc = async (documentId, filename) => {
    try {
      const response = await axios.get(`${API}/policies-procedures/${documentId}`, {
        responseType: 'blob'
      });
      const ext = (filename || '').split('.').pop().toLowerCase();
      const mime = response.data.type || (
        ext === 'pdf' ? 'application/pdf'
        : ['png','jpg','jpeg','gif','webp'].includes(ext) ? `image/${ext === 'jpg' ? 'jpeg' : ext}`
        : 'application/octet-stream'
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: mime }));
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.src = url;
      iframe.onload = () => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } catch (e) {
          // Fallback: pop-up the file so the user can print it themselves
          window.open(url, '_blank');
        }
      };
      document.body.appendChild(iframe);
      setTimeout(() => {
        try { document.body.removeChild(iframe); } catch (e) { /* noop */ }
        window.URL.revokeObjectURL(url);
      }, 60000);
    } catch (error) {
      console.error('Error printing file:', error);
      alert(await _extractBlobErrorMessage(error, 'Failed to print file'));
    }
  };
  
  const handleDelete = async (documentId) => {
    if (!confirm('Are you sure you want to delete this document?')) return;
    
    try {
      await axios.delete(`${API}/policies-procedures/${documentId}`);
      await loadDocuments();
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document');
    }
  };
  
  const handleEdit = (document) => {
    setEditingDocument(document);
    setFormData({
      description: document.description || '',
      category: document.category,
      uploaded_by: document.uploaded_by || 'System'
    });
  };
  
  const handleUpdate = async () => {
    if (!editingDocument) return;
    
    try {
      await axios.put(`${API}/policies-procedures/${editingDocument.id}`, {
        description: formData.description,
        category: formData.category
      });
      
      // Reset form
      setEditingDocument(null);
      setFormData({ description: '', category: 'Policy', uploaded_by: 'System' });
      
      // Reload documents
      await loadDocuments();
    } catch (error) {
      console.error('Error updating document:', error);
      alert('Failed to update document');
    }
  };
  
  const handleCancelEdit = () => {
    setEditingDocument(null);
    setFormData({ description: '', category: 'Policy', uploaded_by: 'System' });
  };
  
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };
  
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 10002 }}>
      <div className={`${theme.colors.modal} rounded-lg w-full max-w-6xl max-h-[90vh] flex flex-col`}>
        {/* Header */}
        <div className={`p-6 border-b ${theme.colors.border} flex justify-between items-center`}>
          <div>
            <h2 className="text-2xl font-bold text-orange-500">Policies and Procedures</h2>
            <p className={`text-sm ${theme.colors.textMuted} mt-1`}>
              Upload and manage policy documents
            </p>
          </div>
          <Button
            variant="ghost"
            onClick={onClose}
            className={`${theme.colors.textMuted} hover:${theme.colors.textPrimary}`}
          >
            <X className="h-6 w-6" />
          </Button>
        </div>
        
        {/* Upload/Edit Section */}
        <div className={`p-6 border-b ${theme.colors.border}`}>
          <h3 className={`text-lg font-semibold ${theme.colors.textPrimary} mb-4`}>
            {editingDocument ? 'Edit Document Metadata' : 'Upload Document'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {!editingDocument && (
              <div className="md:col-span-2">
                <Label className={`${theme.colors.textPrimary} mb-2 block`}>File</Label>
                <div
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById('file-input')?.click()}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      document.getElementById('file-input')?.click();
                    }
                  }}
                  data-testid="policies-dropzone"
                  className={`relative cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors ${
                    isDragging
                      ? 'border-orange-500 bg-orange-500/10'
                      : selectedFile
                      ? 'border-green-500/50 bg-green-500/5'
                      : `${theme.colors.border} ${theme.colors.quaternary} hover:border-orange-500/60 hover:bg-orange-500/5`
                  }`}
                >
                  <input
                    id="file-input"
                    type="file"
                    accept=".doc,.docx,.pdf,.xls,.xlsx,.ppt,.pptx"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  {selectedFile ? (
                    <div className="flex items-center justify-center gap-3">
                      <FileText className="h-8 w-8 text-green-400" />
                      <div className="text-left">
                        <div className={`text-sm font-medium ${theme.colors.textPrimary}`}>
                          {selectedFile.name}
                        </div>
                        <div className={`text-xs ${theme.colors.textMuted}`}>
                          {(selectedFile.size / 1024).toFixed(1)} KB · Click to change or drop a new file
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedFile(null);
                          const el = document.getElementById('file-input');
                          if (el) el.value = '';
                        }}
                        className="ml-2 text-gray-400 hover:text-red-400"
                        title="Clear selection"
                        data-testid="policies-dropzone-clear"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2 pointer-events-none">
                      <Upload className={`h-8 w-8 ${isDragging ? 'text-orange-400' : 'text-gray-400'}`} />
                      <div className={`text-sm font-medium ${theme.colors.textPrimary}`}>
                        {isDragging ? 'Drop the file to attach it' : 'Drag & drop a file here, or click to browse'}
                      </div>
                      <div className={`text-xs ${theme.colors.textMuted}`}>
                        Allowed: {allowedExtensions.join(', ')}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {editingDocument && (
              <div className="md:col-span-2">
                <Label className={`${theme.colors.textPrimary} mb-2 block`}>File Name</Label>
                <Input
                  value={editingDocument.original_filename}
                  disabled
                  className={`${theme.colors.input} ${theme.colors.textPrimary} opacity-60`}
                />
                <p className={`text-xs ${theme.colors.textMuted} mt-1`}>
                  File name cannot be changed
                </p>
              </div>
            )}
            
            <div>
              <Label className={`${theme.colors.textPrimary} mb-2 block`}>Category</Label>
              <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                <SelectTrigger className={`${theme.colors.input} ${theme.colors.textPrimary}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="md:col-span-2">
              <Label className={`${theme.colors.textPrimary} mb-2 block`}>Description (Optional)</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Enter document description..."
                className={`${theme.colors.input} ${theme.colors.textPrimary}`}
                rows={2}
              />
            </div>
          </div>
          
          <div className="mt-4 flex justify-end gap-2">
            {editingDocument ? (
              <>
                <Button
                  onClick={handleCancelEdit}
                  variant="outline"
                  className="border-gray-500 text-gray-300"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUpdate}
                  className="bg-blue-500 hover:bg-blue-600 text-white"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Update Document
                </Button>
              </>
            ) : (
              <Button
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
                className="bg-orange-500 hover:bg-orange-600 text-black"
              >
                <Upload className="h-4 w-4 mr-2" />
                {uploading ? 'Uploading...' : 'Upload Document'}
              </Button>
            )}
          </div>
        </div>
        
        {/* Filter Section */}
        <div className={`px-6 py-4 border-b ${theme.colors.border}`}>
          <div className="flex items-center gap-4">
            <Label className={`${theme.colors.textPrimary}`}>Filter by Category:</Label>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className={`w-48 ${theme.colors.input} ${theme.colors.textPrimary}`}>
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Documents List */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-12">
              <p className={theme.colors.textMuted}>Loading documents...</p>
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-12">
              <FileCheck className={`h-16 w-16 ${theme.colors.textMuted} mx-auto mb-4`} />
              <p className={theme.colors.textSecondary}>No documents uploaded yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {documents.map(doc => (
                <div 
                  key={doc.id}
                  className={`p-4 rounded-lg border ${theme.colors.border} ${theme.colors.tertiary} hover:border-orange-500/50 transition-colors`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <FileText className="h-5 w-5 text-orange-500" />
                        <h4 className={`font-semibold ${theme.colors.textPrimary}`}>
                          {doc.original_filename}
                        </h4>
                        <Badge variant="outline" className="bg-orange-500/10 text-orange-500 border-orange-500/30">
                          {doc.category}
                        </Badge>
                      </div>
                      
                      {doc.description && (
                        <p className={`text-sm ${theme.colors.textSecondary} mb-2`}>
                          {doc.description}
                        </p>
                      )}
                      
                      <div className={`text-xs ${theme.colors.textMuted} flex gap-4`}>
                        <span>Size: {formatFileSize(doc.file_size)}</span>
                        <span>Uploaded: {formatDate(doc.uploaded_at)}</span>
                        <span>By: {doc.uploaded_by}</span>
                      </div>
                    </div>
                    
                    <div className="flex gap-2 ml-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(doc)}
                        className="border-blue-500/50 text-blue-500 hover:bg-blue-500/10"
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownload(doc.id, doc.original_filename)}
                        className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
                      >
                        <Save className="h-4 w-4 mr-1" />
                        Download
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleView(doc.id, doc.original_filename)}
                        className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
                        data-testid={`policy-view-${doc.id}`}
                        title="Open in a new tab"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handlePrintDoc(doc.id, doc.original_filename)}
                        className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                        data-testid={`policy-print-${doc.id}`}
                        title="Print this document"
                      >
                        <Printer className="h-4 w-4 mr-1" />
                        Print
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(doc.id)}
                        className="border-red-500/50 text-red-500 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className={`p-4 border-t ${theme.colors.border} flex justify-between items-center`}>
          <p className={`text-sm ${theme.colors.textMuted}`}>
            Total: {documents.length} document{documents.length !== 1 ? 's' : ''}
          </p>
          <Button onClick={onClose} className="bg-orange-500 hover:bg-orange-600 text-black">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

// ICS Dashboard Component
// ICSDashboard moved to components/ICSDashboard.js

// Information Officer Module Component
const InformationOfficerModule = ({ theme, exerciseId, exerciseName }) => {
  const { showToast } = useToast();
  const [infoData, setInfoData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [activeCategory, setActiveCategory] = useState('media-releases');
  const [showBriefing, setShowBriefing] = useState(false);

  const format24HourTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  const [formData, setFormData] = useState({
    category: 'media-releases',
    title: '',
    description: '',
    priority: 'medium',
    status: 'draft',
    timestamp: '',
    contact_info: '',
    notes: '',
    image: ''
  });

  const [errors, setErrors] = useState({});

  const filteredInfoEntries = activeCategory === 'all'
    ? infoData
    : infoData.filter((d) => d.category === activeCategory);

  const toggleSelectInfoEntry = (id) => {
    setSelectedEntries((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const buildInfoReportHtml = (entries, headerLabel) => {
    const rows = (entries || []).map((entry) => {
      const cat = categories[entry.category]?.label || entry.category || 'Entry';
      const ts = format24HourTimestamp(entry.timestamp);
      const body = entry.description || entry.release_content || entry.inquiry_content || entry.post_content || '';
      return `
        <div class="entry">
          <div class="row"><span class="cat">${cat}</span><span class="ts">${ts}</span></div>
          <h3>${entry.title || 'Untitled'}</h3>
          <div class="meta"><span class="pri pri-${entry.priority || 'medium'}">${entry.priority || 'medium'}</span><span class="st">${entry.status || 'draft'}</span></div>
          <p>${body}</p>
        </div>`;
    }).join('');
    return `
      <style>
        body { font-family: -apple-system, Segoe UI, Roboto, sans-serif; padding: 24px; color: #111; }
        h1 { color: #ea580c; margin: 0 0 4px; }
        .sub { color: #444; margin: 0 0 24px; }
        .entry { border: 1px solid #ddd; border-radius: 8px; padding: 12px 16px; margin-bottom: 12px; page-break-inside: avoid; }
        .row { display: flex; justify-content: space-between; font-size: 12px; color: #666; }
        .cat { font-weight: 600; color: #ea580c; }
        h3 { margin: 6px 0; font-size: 16px; }
        .meta { display: flex; gap: 8px; font-size: 11px; margin-bottom: 8px; }
        .pri, .st { padding: 2px 8px; border-radius: 4px; background: #f3f4f6; }
        .pri-urgent { background: #fee2e2; color: #b91c1c; }
        .pri-high { background: #ffedd5; color: #c2410c; }
        .pri-medium { background: #fef9c3; color: #a16207; }
        .pri-low { background: #dcfce7; color: #15803d; }
        p { margin: 0; font-size: 13px; color: #333; white-space: pre-wrap; }
      </style>
      <h1>Information Officer Report — ${headerLabel}</h1>
      <p class="sub">${exerciseName || 'Exercise'} · Generated ${format24HourTimestamp(new Date().toISOString())}</p>
      ${rows || '<p><em>No entries recorded.</em></p>'}
    `;
  };

  const openPrintWindow = (html, title) => {
    const w = window.open('', '_blank', 'width=900,height=1000');
    if (!w) return;
    writeSanitizedHTML(w.document, html);
    w.document.title = title;
    w.focus();
    setTimeout(() => w.print(), 250);
  };

  const handlePrintInformationOfficer = () => {
    openPrintWindow(buildInfoReportHtml(infoData, 'All Entries'), 'Information Officer Report');
  };

  const handlePrintSelectedInformationOfficer = () => {
    const selected = infoData.filter((e) => selectedEntries.includes(e.id));
    openPrintWindow(buildInfoReportHtml(selected, `Selected (${selected.length})`), 'Information Officer — Selected Entries');
  };

  const categories = {
    'media-releases': {
      label: 'Media Releases',
      icon: FileText,
      fields: [
        { key: 'release_title', label: 'Release Title', type: 'text', required: true, placeholder: 'Title of the media release' },
        { key: 'release_content', label: 'Release Content', type: 'textarea', required: true, placeholder: 'Full content of the media release' },
        { key: 'target_audience', label: 'Target Audience', type: 'text', required: false, placeholder: 'Intended audience for this release' },
        { key: 'distribution_channels', label: 'Distribution Channels', type: 'textarea', required: false, placeholder: 'List channels for distribution (TV, Radio, Social Media, etc.)' }
      ]
    },
    'public-inquiries': {
      label: 'Public Inquiries',
      icon: Users,
      fields: [
        { key: 'inquiry_source', label: 'Inquiry Source', type: 'text', required: true, placeholder: 'Source of the inquiry (name, organization)' },
        { key: 'inquiry_content', label: 'Inquiry Content', type: 'textarea', required: true, placeholder: 'Details of the inquiry' },
        { key: 'response_provided', label: 'Response Provided', type: 'textarea', required: false, placeholder: 'Response given to the inquiry' },
        { key: 'follow_up_required', label: 'Follow-up Required', type: 'text', required: false, placeholder: 'Any follow-up actions needed' }
      ]
    },
    'media-contacts': {
      label: 'Media Contacts',
      icon: Phone,
      fields: [
        { key: 'contact_name', label: 'Contact Name', type: 'text', required: true, placeholder: 'Name of media contact' },
        { key: 'organization', label: 'Organization', type: 'text', required: true, placeholder: 'Media organization' },
        { key: 'contact_phone', label: 'Phone Number', type: 'text', required: false, placeholder: 'Contact phone number' },
        { key: 'contact_email', label: 'Email', type: 'text', required: false, placeholder: 'Contact email address' },
        { key: 'beat_coverage', label: 'Beat/Coverage Area', type: 'text', required: false, placeholder: 'Area of coverage or beat' }
      ]
    },
    'social-media': {
      label: 'Social Media Updates',
      icon: Globe,
      fields: [
        { key: 'platform', label: 'Platform', type: 'text', required: true, placeholder: 'Social media platform (Twitter, Facebook, etc.)' },
        { key: 'post_content', label: 'Post Content', type: 'textarea', required: true, placeholder: 'Content of the social media post' },
        { key: 'scheduled_time', label: 'Scheduled Time', type: 'text', required: false, placeholder: 'When to post (if scheduled)' },
        { key: 'engagement_metrics', label: 'Engagement Metrics', type: 'textarea', required: false, placeholder: 'Likes, shares, comments, reach' }
      ]
    }
  };

  const priorityOptions = [
    { value: 'low', label: 'Low', color: 'text-green-500' },
    { value: 'medium', label: 'Medium', color: 'text-yellow-500' },
    { value: 'high', label: 'High', color: 'text-orange-500' },
    { value: 'urgent', label: 'Urgent', color: 'text-red-500' }
  ];

  const statusOptions = [
    { value: 'draft', label: 'Draft', color: 'text-gray-500' },
    { value: 'pending', label: 'Pending Approval', color: 'text-yellow-500' },
    { value: 'approved', label: 'Approved', color: 'text-green-500' },
    { value: 'released', label: 'Released', color: 'text-blue-500' },
    { value: 'archived', label: 'Archived', color: 'text-gray-400' }
  ];

  useEffect(() => {
    loadInfoData();
  }, [exerciseId]);

  const loadInfoData = async () => {
    if (!exerciseId) return;
    try {
      setLoading(true);
      const response = await axios.get(`${API}/information-officer?exercise_id=${exerciseId}`);
      setInfoData(response.data);
    } catch (error) {
      console.error('Error loading information officer data:', error);
      setInfoData([]);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      category: activeCategory,
      title: '',
      description: '',
      priority: 'medium',
      status: 'draft',
      timestamp: new Date().toISOString().slice(0, 16),
      contact_info: '',
      notes: '',
      image: ''
    });
    setErrors({});
    setEditingEntry(null);
  };

  const validateForm = () => {
    const newErrors = {};
    const categoryFields = categories[formData.category]?.fields || [];
    if (!formData.title) newErrors.title = 'Title is required';
    categoryFields.forEach(field => {
      if (field.required && !formData[field.key]) {
        newErrors[field.key] = `${field.label} is required`;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitLocal = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      setLoading(true);
      const entryData = {
        ...formData,
        exercise_id: exerciseId,
        timestamp: formData.timestamp || new Date().toISOString()
      };
      if (editingEntry) {
        await axios.put(`${API}/information-officer/${editingEntry.id}`, entryData);
      } else {
        await axios.post(`${API}/information-officer`, entryData);
      }
      resetForm();
      setShowForm(false);
      loadInfoData();
    } catch (error) {
      console.error('Error saving information officer entry:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (entry) => {
    setFormData({
      category: entry.category || 'media-releases',
      title: entry.title || '',
      description: entry.description || '',
      priority: entry.priority || 'medium',
      status: entry.status || 'draft',
      timestamp: entry.timestamp ? entry.timestamp.slice(0, 16) : '',
      contact_info: entry.contact_info || '',
      notes: entry.notes || '',
      image: entry.image || '',
      ...Object.fromEntries(
        Object.keys(categories).flatMap(cat => 
          categories[cat].fields.map(field => [field.key, entry[field.key] || ''])
        )
      )
    });
    setEditingEntry(entry);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;
    try {
      setLoading(true);
      await axios.delete(`${API}/information-officer/${id}`);
      showToast('Entry deleted', 'success');
      loadInfoData();
    } catch (error) {
      console.error('Error deleting entry:', error);
      const msg = error?.response?.data?.detail || error?.message || 'Failed to delete entry';
      showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const CategoryIcon = categories[activeCategory]?.icon || FileText;

  return (
    <div className="max-w-7xl mx-auto p-6 bg-gray-900 min-h-screen">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-orange-500 mb-2">
              Information Officer
            </h1>
            <p className="text-white text-sm">
              Manage media releases, public inquiries, and communications for {exerciseName || 'this exercise'}
            </p>
          </div>
          <div className="flex space-x-3">
            <Button
              onClick={handlePrintInformationOfficer}
              className="bg-blue-500 hover:bg-blue-600 text-white"
              data-testid="print-info-officer-btn"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print Information Officer Report
            </Button>
            <Button
              onClick={() => exerciseId && setShowBriefing(true)}
              disabled={!exerciseId}
              className="bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50"
              data-testid="ic-briefing-btn"
            >
              <Presentation className="h-4 w-4 mr-2" />
              IC Briefing
            </Button>
            <Button
              onClick={() => { resetForm(); setShowForm(true); }}
              className="bg-blue-500 hover:bg-blue-600 text-white"
              data-testid="info-add-entry-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Entry
            </Button>
            {selectedEntries.length > 0 && (
              <Button
                onClick={handlePrintSelectedInformationOfficer}
                variant="outline"
                className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
                data-testid="print-selected-info-btn"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print Selected ({selectedEntries.length})
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Category Navigation */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-2">
          {Object.entries(categories).map(([key, category]) => {
            const IconComponent = category.icon;
            return (
              <button
                key={key}
                onClick={() => setActiveCategory(key)}
                className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                  activeCategory === key
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                <IconComponent className="h-4 w-4 mr-2" />
                {category.label}
              </button>
            );
          })}
          {/* Show All Button */}
          <button
            onClick={() => setActiveCategory('all')}
            className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
              activeCategory === 'all'
                ? 'bg-orange-500 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
            data-testid="info-show-all-btn"
          >
            <Shield className={`h-4 w-4 mr-2 ${activeCategory === 'all' ? 'text-white' : 'text-red-600'}`} />
            <span>Show All</span>
          </button>
        </div>
      </div>

      {/* IC Briefing Preview Modal */}
      {showBriefing && (
        <BriefingPreview
          exerciseId={exerciseId}
          exerciseName={exerciseName}
          theme={theme}
          onClose={() => setShowBriefing(false)}
        />
      )}

      {/* Form Modal */}
      {showForm && (
        <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
          <CardHeader>
            <CardTitle className="text-orange-500">
              {editingEntry ? 'Edit Entry' : 'New Entry'} - {categories[activeCategory]?.label}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmitLocal} className="space-y-4">
              {/* Entry Image Card */}
              <EntryImageCard
                value={formData.image}
                onChange={(img) => setFormData(prev => ({...prev, image: img}))}
                title="Entry Image"
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-white text-sm font-medium mb-2 block">Title *</label>
                  <Input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}
                    placeholder="Entry title"
                    className={`bg-gray-800 border-gray-600 text-white ${errors.title ? 'border-red-500' : ''}`}
                  />
                  {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
                </div>
                <div>
                  <label className="text-white text-sm font-medium mb-2 block">Priority</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData(prev => ({...prev, priority: e.target.value}))}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-md"
                  >
                    {priorityOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Category-specific fields */}
              {categories[activeCategory]?.fields.map(field => (
                <div key={field.key}>
                  <label className="text-white text-sm font-medium mb-2 block">
                    {field.label} {field.required && '*'}
                  </label>
                  {field.type === 'textarea' ? (
                    <textarea
                      value={formData[field.key] || ''}
                      onChange={(e) => setFormData(prev => ({...prev, [field.key]: e.target.value}))}
                      placeholder={field.placeholder}
                      rows={3}
                      className={`w-full p-3 bg-gray-800 border border-gray-600 rounded text-white ${errors[field.key] ? 'border-red-500' : ''}`}
                    />
                  ) : (
                    <Input
                      type="text"
                      value={formData[field.key] || ''}
                      onChange={(e) => setFormData(prev => ({...prev, [field.key]: e.target.value}))}
                      placeholder={field.placeholder}
                      className={`bg-gray-800 border-gray-600 text-white ${errors[field.key] ? 'border-red-500' : ''}`}
                    />
                  )}
                  {errors[field.key] && <p className="text-red-500 text-sm mt-1">{errors[field.key]}</p>}
                </div>
              ))}

              <div>
                <label className="text-white text-sm font-medium mb-2 block">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({...prev, notes: e.target.value}))}
                  placeholder="Additional notes"
                  rows={2}
                  className="w-full p-3 bg-gray-800 border border-gray-600 rounded text-white"
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit" className="bg-orange-500 hover:bg-orange-600" disabled={loading}>
                  {loading ? 'Saving...' : (editingEntry ? 'Update' : 'Save')}
                </Button>
                <Button type="button" onClick={() => { setShowForm(false); resetForm(); }} variant="outline" className="border-gray-500 text-gray-300">
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Entries List */}
      <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
        <CardHeader className="pb-3">
          <CardTitle className="text-orange-500 text-xl">
            {activeCategory === 'all' ? 'All Entries' : `${categories[activeCategory]?.label} Entries`} ({filteredInfoEntries.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-8 text-gray-400">Loading...</div>
          ) : filteredInfoEntries.length === 0 ? (
            <div className="text-center py-16">
              <Shield className="h-14 w-14 mx-auto mb-4 text-orange-500/70" />
              <p className="text-orange-500 text-xl font-semibold mb-1">
                {activeCategory === 'all' ? 'No Entries Recorded' : `No ${categories[activeCategory]?.label} Entries`}
              </p>
              <p className="text-gray-400 text-sm">
                Start by adding your first {activeCategory === 'all' ? 'entry' : (categories[activeCategory]?.label || '').toLowerCase() + ' entry'} for this exercise.
              </p>
            </div>
          ) : (
            <div className="p-4">
              <SortableEntryList
                entries={filteredInfoEntries}
                setEntries={(newEntries) => {
                  if (activeCategory === 'all') {
                    setInfoData(newEntries);
                  } else {
                    const otherEntries = infoData.filter(d => d.category !== activeCategory);
                    setInfoData([...otherEntries, ...newEntries]);
                  }
                }}
                reorderEndpoint="information-officer/reorder"
                renderEntry={(entry) => (
                  <div className="p-4 hover:bg-gray-800/50 rounded-lg bg-gray-800/30 border border-gray-700">
                    <div className="flex justify-between items-start gap-3">
                      <div className="pt-1">
                        <Checkbox
                          checked={selectedEntries.includes(entry.id)}
                          onCheckedChange={() => toggleSelectInfoEntry(entry.id)}
                          data-testid={`info-entry-select-${entry.id}`}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          {entry.image && (
                            <img
                              src={entry.image}
                              alt="Entry"
                              className="w-[60px] h-[60px] rounded object-cover border border-gray-500 flex-shrink-0"
                              data-testid={`info-entry-image-${entry.id}`}
                            />
                          )}
                          <div>
                            <h4 className="text-white font-semibold">{entry.title}</h4>
                            {activeCategory === 'all' && (
                              <span className="text-xs px-2 py-0.5 rounded bg-orange-500/20 text-orange-300 inline-block mt-1">
                                {categories[entry.category]?.label || entry.category}
                              </span>
                            )}
                          </div>
                        </div>
                        <p className="text-gray-400 text-sm mt-1">{entry.description || entry.release_content || entry.inquiry_content || entry.post_content || 'No description'}</p>
                        <div className="flex gap-2 mt-2">
                          <span className={`text-xs px-2 py-1 rounded ${
                            entry.priority === 'urgent' ? 'bg-red-500/20 text-red-400' :
                            entry.priority === 'high' ? 'bg-orange-500/20 text-orange-400' :
                            entry.priority === 'medium' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-green-500/20 text-green-400'
                          }`}>
                            {entry.priority}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded ${
                            entry.status === 'released' ? 'bg-blue-500/20 text-blue-400' :
                            entry.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                            entry.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-gray-500/20 text-gray-400'
                          }`}>
                            {entry.status}
                          </span>
                          <span className="text-xs text-gray-500">{format24HourTimestamp(entry.timestamp)}</span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={() => handleEdit(entry)} variant="ghost" size="sm" className="text-blue-400 hover:text-blue-300">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button onClick={() => handleDelete(entry.id)} variant="ghost" size="sm" className="text-red-400 hover:text-red-300">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Safety Officer Module Component
const SketchComponent = ({ sketchData, onSketchChange }) => {
  const canvasRef = React.useRef(null);
  const [isDrawing, setIsDrawing] = React.useState(false);
  const [color, setColor] = React.useState('#ff0000');
  const [brushSize, setBrushSize] = React.useState(3);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#1f2937';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Load existing sketch data if available
      if (sketchData) {
        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, 0, 0);
        };
        img.src = sketchData;
      }
    }
  }, []);

  const startDrawing = (e) => {
    setIsDrawing(true);
    draw(e);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    const canvas = canvasRef.current;
    if (canvas && onSketchChange) {
      onSketchChange(canvas.toDataURL());
    }
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = color;
    ctx.lineWidth = brushSize;
    ctx.lineTo(x, y);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#1f2937';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    if (onSketchChange) {
      onSketchChange(null);
    }
  };

  return (
    <div className="space-y-3">
      <label className="text-white text-sm font-medium block">Safety Sketch (Optional)</label>
      <div className="flex items-center gap-4 mb-2">
        <div className="flex items-center gap-2">
          <label className="text-gray-400 text-xs">Color:</label>
          <input 
            type="color" 
            value={color} 
            onChange={(e) => setColor(e.target.value)}
            className="w-8 h-8 rounded cursor-pointer"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-gray-400 text-xs">Size:</label>
          <input 
            type="range" 
            min="1" 
            max="20" 
            value={brushSize}
            onChange={(e) => setBrushSize(parseInt(e.target.value))}
            className="w-20"
          />
          <span className="text-gray-400 text-xs">{brushSize}px</span>
        </div>
        <button
          type="button"
          onClick={clearCanvas}
          className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded"
        >
          Clear
        </button>
      </div>
      <canvas
        ref={canvasRef}
        width={600}
        height={300}
        onMouseDown={startDrawing}
        onMouseUp={stopDrawing}
        onMouseOut={stopDrawing}
        onMouseMove={draw}
        className="border border-gray-600 rounded cursor-crosshair w-full"
        style={{ maxWidth: '100%', height: 'auto' }}
      />
      <p className="text-gray-500 text-xs">Draw safety scenarios, evacuation routes, or hazard locations</p>
    </div>
  );
};


// ─── Self-contained Evaluations Panel for ICS sidebar ───
const ICSEvaluationsPanel = ({ exerciseId, theme }) => {
  const [reports, setReports] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReports = useCallback(async () => {
    if (!exerciseId) return;
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API_BASE_URL}/api/evaluation-reports/exercise/${exerciseId}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setReports(Array.isArray(data) ? data : data.reports || []);
      }
    } catch (e) { console.error('Fetch evaluations error:', e); }
    setLoading(false);
  }, [exerciseId]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const handleDelete = async (reportId) => {
    if (!window.confirm('Delete this evaluation report?')) return;
    try {
      const token = secureStorage.getToken();
      await fetch(`${API_BASE_URL}/api/evaluation-reports/${reportId}`, {
        method: 'DELETE', headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setReports(prev => prev.filter(r => r.id !== reportId));
    } catch (e) { console.error('Delete error:', e); }
  };

  if (showForm) {
    return (
      <EvaluationReportForm
        exerciseId={exerciseId}
        editingReport={editingReport}
        onBack={() => { setShowForm(false); setEditingReport(null); }}
        onSave={() => { setShowForm(false); setEditingReport(null); fetchReports(); }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className={`text-xl font-bold ${theme.colors.textPrimary}`}>Evaluation Reports</h2>
        <Button onClick={() => { setEditingReport(null); setShowForm(true); }} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="add-evaluation-btn">
          <Plus className="h-4 w-4 mr-2" /> New Evaluation
        </Button>
      </div>
      {loading ? (
        <div className={`text-center py-8 ${theme.colors.textMuted}`}>Loading evaluations...</div>
      ) : reports.length === 0 ? (
        <div className={`text-center py-12 ${theme.colors.textMuted}`}>
          <Star className="h-12 w-12 mx-auto mb-4 opacity-30" />
          <p>No evaluation reports yet.</p>
          <p className="text-sm mt-2">Click "New Evaluation" to create your first report.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map((report) => (
            <div key={report.id} className={`${theme.colors.secondary} border ${theme.colors.border} rounded-lg p-4`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className={`font-semibold ${theme.colors.textPrimary}`}>{report.report_title || 'Untitled'}</h3>
                  <p className={`text-sm ${theme.colors.textMuted}`}>
                    {report.evaluator_name && `By ${report.evaluator_name}`}
                    {report.evaluation_date && ` | ${report.evaluation_date}`}
                  </p>
                  {report.summary_of_findings && (
                    <p className={`text-sm mt-2 ${theme.colors.textSecondary} line-clamp-2`}>{report.summary_of_findings}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => { setEditingReport(report); setShowForm(true); }}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300" onClick={() => handleDelete(report.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Self-contained Lessons Learned Panel for ICS sidebar ───
const ICSLessonsLearnedPanel = ({ exerciseId, theme }) => {
  const [lessons, setLessons] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedLessons, setExpandedLessons] = useState(() => new Set());

  const dotmplficcColorMap = {
    'Doctrine': '#3b82f6', 'Organization': '#10b981', 'Training': '#f59e0b',
    'Maint and Equip': '#8b5cf6', 'Personnel': '#06b6d4', 'Leadership': '#ef4444',
    'Facilities': '#84cc16', 'Interoperability': '#f97316', 'Communications': '#ec4899',
    'Command and Control': '#6366f1',
  };

  const fetchLessons = useCallback(async () => {
    if (!exerciseId) return;
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${API_BASE_URL}/api/lessons-learned/exercise/${exerciseId}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setLessons(Array.isArray(data) ? data : data.lessons || []);
        setExpandedLessons(new Set()); // start fully collapsed after any fetch
      }
    } catch (e) { console.error('Fetch lessons error:', e); }
    setLoading(false);
  }, [exerciseId]);

  useEffect(() => { fetchLessons(); }, [fetchLessons]);

  const toggleLessonExpanded = (lessonId) => {
    setExpandedLessons(prev => {
      const next = new Set(prev);
      if (next.has(lessonId)) next.delete(lessonId); else next.add(lessonId);
      return next;
    });
  };
  const expandAll = () => setExpandedLessons(new Set(lessons.map(l => l.id)));
  const collapseAll = () => setExpandedLessons(new Set());

  const handleDelete = async (lessonId) => {
    if (!window.confirm('Delete this lesson learned?')) return;
    try {
      const token = secureStorage.getToken();
      await fetch(`${API_BASE_URL}/api/lessons-learned/${lessonId}`, {
        method: 'DELETE', headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      await fetchLessons();
    } catch (e) { console.error('Delete error:', e); }
  };

  if (showForm) {
    return (
      <LessonsLearnedForm
        exerciseId={exerciseId}
        editingLesson={editingLesson}
        onBack={() => { setShowForm(false); setEditingLesson(null); }}
        onSave={() => { setShowForm(false); setEditingLesson(null); fetchLessons(); }}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className={`text-xl font-bold ${theme.colors.textPrimary}`}>Lessons Learned</h2>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={expandAll}
            disabled={lessons.length === 0}
            className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
            data-testid="ics-lessons-expand-all-btn"
          >
            <ChevronDown className="h-4 w-4 mr-2" /> Expand All
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={collapseAll}
            disabled={lessons.length === 0}
            className="border-gray-500/50 text-gray-300 hover:bg-gray-500/10"
            data-testid="ics-lessons-collapse-all-btn"
          >
            <ChevronRight className="h-4 w-4 mr-2" /> Collapse All
          </Button>
          <Button onClick={() => { setEditingLesson(null); setShowForm(true); }} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="add-lesson-btn">
            <Plus className="h-4 w-4 mr-2" /> New Lesson
          </Button>
        </div>
      </div>
      {loading ? (
        <div className={`text-center py-8 ${theme.colors.textMuted}`}>Loading lessons...</div>
      ) : lessons.length === 0 ? (
        <div className={`text-center py-12 ${theme.colors.textMuted}`}>
          <Lightbulb className="h-12 w-12 mx-auto mb-4 opacity-30" />
          <p>No lessons learned yet.</p>
          <p className="text-sm mt-2">Click "New Lesson" to capture insights.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {lessons.map((lesson) => {
            const isExpanded = expandedLessons.has(lesson.id);
            const priorityColor =
              lesson.priority === 'Pri 1' ? '#ef4444' :
              lesson.priority === 'Pri 2' ? '#f59e0b' :
              lesson.priority === 'Pri 3' ? '#22c55e' : '#3b82f6';
            const statusColor =
              lesson.observation_status === 'worked_well' ? '#16a34a' :
              lesson.observation_status === 'needs_improvement' ? '#dc2626' : '#6b7280';
            return (
              <div
                key={lesson.id}
                className={`${theme.colors.secondary} border ${theme.colors.border} rounded-lg overflow-hidden`}
                style={{ borderLeft: `8px solid ${priorityColor}` }}
                data-testid={`ics-lesson-card-${lesson.id}`}
              >
                <div
                  className="px-6 sm:px-8 py-4 cursor-pointer"
                  onClick={() => toggleLessonExpanded(lesson.id)}
                  data-testid={`ics-lesson-card-header-${lesson.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 flex items-start gap-3 min-w-0">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); toggleLessonExpanded(lesson.id); }}
                        className={`mt-1 flex-shrink-0 ${theme.colors.textMuted} hover:text-orange-400`}
                        data-testid={`ics-lesson-toggle-${lesson.id}`}
                        aria-label={isExpanded ? 'Collapse lesson' : 'Expand lesson'}
                      >
                        {isExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <h3 className={`font-semibold ${theme.colors.textPrimary} truncate`}>{lesson.name || lesson.title || 'Untitled'}</h3>
                        <div className="flex items-center justify-between gap-3 flex-wrap mt-2 text-sm">
                          <div className="flex items-center gap-3 flex-wrap">
                            {typeof lesson.serial_number !== 'undefined' && (
                              <span className={`${theme.colors.textMuted} text-xs`}>Serial #{lesson.serial_number}</span>
                            )}
                            {lesson.priority && (
                              <span
                                className="text-white text-xs font-semibold px-2 py-0.5 rounded"
                                style={{ backgroundColor: priorityColor }}
                              >{lesson.priority}</span>
                            )}
                            {lesson.date && <span className={`${theme.colors.textMuted} text-xs`}>{lesson.date}</span>}
                            {lesson.dotmplficc && (
                              <span
                                className="text-white text-xs font-semibold px-2 py-0.5 rounded"
                                style={{ backgroundColor: dotmplficcColorMap[lesson.dotmplficc] || '#6b7280' }}
                              >{lesson.dotmplficc}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 ml-auto">
                            <span className={`text-xs uppercase tracking-wide ${theme.colors.textMuted}`}>Status</span>
                            <span
                              className="text-white text-xs font-semibold px-2 py-0.5 rounded"
                              style={{ backgroundColor: statusColor }}
                            >
                              {lesson.observation_status === 'worked_well' ? 'Worked Well' :
                               lesson.observation_status === 'needs_improvement' ? 'Needs Improvement' : 'Not Set'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setEditingLesson(lesson); setShowForm(true); }}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-400 hover:text-red-300" onClick={(e) => { e.stopPropagation(); handleDelete(lesson.id); }}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
                {isExpanded && (
                  <div className="px-6 sm:px-8 pb-4 space-y-3 border-t border-gray-700/50">
                    {lesson.references && (
                      <div className="pt-3">
                        <h4 className={`text-sm font-semibold ${theme.colors.textPrimary} mb-1`}>References</h4>
                        <p className={`text-sm ${theme.colors.textSecondary} whitespace-pre-wrap`}>{lesson.references}</p>
                      </div>
                    )}
                    {lesson.issues_observation && (
                      <div>
                        <h4 className={`text-sm font-semibold ${theme.colors.textPrimary} mb-1`}>Issues / Observation</h4>
                        <p className={`text-sm ${theme.colors.textSecondary} whitespace-pre-wrap`}>{lesson.issues_observation}</p>
                      </div>
                    )}
                    {lesson.recommendations && (
                      <div>
                        <h4 className={`text-sm font-semibold ${theme.colors.textPrimary} mb-1`}>Recommendations</h4>
                        <p className={`text-sm ${theme.colors.textSecondary} whitespace-pre-wrap`}>{lesson.recommendations}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


// ICS Main Panel Component - Contains the full ICS sidebar and content rendering
const ICSMainPanel = ({ currentExercise, participants, positions, theme }) => {
  const socketCtx = useSocket();
  const { icsDrawerOpen, setIcsDrawerOpen } = useMobileNav();
  const [activeICSMenu, setActiveICSMenu] = useState('dashboard');
  const [commandStaffExpanded, setCommandStaffExpanded] = useState(false);
  const [operationsExpanded, setOperationsExpanded] = useState(false);
  const [planningExpanded, setPlanningExpanded] = useState(false);
  const [logisticsExpanded, setLogisticsExpanded] = useState(false);
  const [finAdminExpanded, setFinAdminExpanded] = useState(false);
  const [servicesExpanded, setServicesExpanded] = useState(false);
  const [supportExpanded, setSupportExpanded] = useState(false);
  const [groundExpanded, setGroundExpanded] = useState(false);
  const [demobilizationUnitExpanded, setDemobilizationUnitExpanded] = useState(false);
  
  // Additional submenu expansion states
  const [situationExpanded, setSituationExpanded] = useState(false);
  const [resourcesUnitExpanded, setResourcesUnitExpanded] = useState(false);
  const [documentationExpanded, setDocumentationExpanded] = useState(false);
  const [techSpecUnitExpanded, setTechSpecUnitExpanded] = useState(false);
  const [commsUnitExpanded, setCommsUnitExpanded] = useState(false);
  const [medicalUnitExpanded, setMedicalUnitExpanded] = useState(false);
  const [supplyUnitExpanded, setSupplyUnitExpanded] = useState(false);
  const [groundSupportExpanded, setGroundSupportExpanded] = useState(false);
  const [financeExpanded, setFinanceExpanded] = useState(false);
  const [airOpsExpanded, setAirOpsExpanded] = useState(false);
  const [safetyExpanded, setSafetyExpanded] = useState(false);
  const [improvementICSExpanded, setImprovementICSExpanded] = useState(false);
  const [incidentStatusExpanded, setIncidentStatusExpanded] = useState(false);
  const [incidentActionPlanExpanded, setIncidentActionPlanExpanded] = useState(false);
  const [geospatialExpanded, setGeospatialExpanded] = useState(false);
  const [orgPlanningExpanded, setOrgPlanningExpanded] = useState(false);
  const [orgLogisticsExpanded, setOrgLogisticsExpanded] = useState(false);
  const [organizationListExpanded, setOrganizationListExpanded] = useState(false);
  
  // Emergency Phase state
  const [emergencyPhase, setEmergencyPhase] = useState('Prevention');
  const [phaseDropdownOpen, setPhaseDropdownOpen] = useState(false);
  const [canControlPhase, setCanControlPhase] = useState(true); // Allow all users to control for now
  const [userPosition, setUserPosition] = useState(null);
  const [pendingPhaseChange, setPendingPhaseChange] = useState(null); // For confirmation modal
  const [showPhaseConfirmModal, setShowPhaseConfirmModal] = useState(false);
  const [phaseChangeLoading, setPhaseChangeLoading] = useState(false);
  
  const { user } = useAuth();
  const toast = useToast();
  
  // Load emergency phase from current exercise
  useEffect(() => {
    if (currentExercise?.current_emergency_phase) {
      setEmergencyPhase(currentExercise.current_emergency_phase);
    }
  }, [currentExercise?.current_emergency_phase]);
  
  // Poll for phase updates every 10 seconds
  useEffect(() => {
    if (!currentExercise?.id) return;
    
    const pollPhase = async () => {
      try {
        const token = secureStorage.getToken();
        const response = await fetch(`${API_BASE_URL}/api/exercise-builder/${currentExercise.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.current_emergency_phase && data.current_emergency_phase !== emergencyPhase) {
            setEmergencyPhase(data.current_emergency_phase);
          }
        }
      } catch (error) {
        console.error('Error polling phase:', error);
      }
    };
    
    const interval = setInterval(pollPhase, 10000);
    return () => clearInterval(interval);
  }, [currentExercise?.id, emergencyPhase]);

  // Listen for real-time emergency phase changes broadcast from the backend.
  // Show a centered acknowledgment toast to all OTHER users in the exercise.
  useEffect(() => {
    const evt = socketCtx?.phaseChangeEvent;
    if (!evt || !currentExercise?.id) return;
    if (evt.exercise_id && evt.exercise_id !== currentExercise.id) return;

    // Skip the broadcast for the user who triggered the change (they already saw confirm modal + success toast)
    const me = user ? (`${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email) : null;
    const isSelf = me && evt.changed_by_name && me.toLowerCase() === String(evt.changed_by_name).toLowerCase();

    // Always sync local phase state so dropdown reflects the new value
    if (evt.phase && evt.phase !== emergencyPhase) {
      setEmergencyPhase(evt.phase);
    }

    if (!isSelf) {
      const who = evt.changed_by_name || 'A command staff member';
      const pos = evt.changed_by_position ? ` (${evt.changed_by_position})` : '';
      const message = `${who}${pos} has changed the Emergency Phase to "${evt.phase}". Please acknowledge to continue.`;
      // Centered, requires acknowledgement, no auto-dismiss
      toast.showToast(message, 'warning', 0, null, true, true);
    }

    // Clear so we don't re-fire on re-render
    if (typeof socketCtx?.clearPhaseChangeEvent === 'function') {
      socketCtx.clearPhaseChangeEvent();
    }
  }, [socketCtx?.phaseChangeEvent]);
  
  // Handle phase change - shows confirmation modal
  const handlePhaseChange = (newPhase) => {
    if (newPhase === emergencyPhase) {
      setPhaseDropdownOpen(false);
      return;
    }
    setPendingPhaseChange(newPhase);
    setShowPhaseConfirmModal(true);
    setPhaseDropdownOpen(false);
  };
  
  // Confirm and save phase change
  const confirmPhaseChange = async () => {
    if (!pendingPhaseChange || !currentExercise?.id) return;
    
    setPhaseChangeLoading(true);
    try {
      const token = secureStorage.getToken();
      const userName = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email : 'Unknown';
      const userPos = user?.role || 'User';
      
      const response = await fetch(`${API_BASE_URL}/api/exercise-builder/${currentExercise.id}/emergency-phase`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          phase: pendingPhaseChange,
          changed_by_name: userName,
          changed_by_position: userPos
        })
      });
      
      if (response.ok) {
        setEmergencyPhase(pendingPhaseChange);
        toast.success(`Emergency phase changed to ${pendingPhaseChange}`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update phase');
      }
    } catch (error) {
      console.error('Error updating phase:', error);
      toast.error('Failed to update emergency phase');
    } finally {
      setPhaseChangeLoading(false);
      setShowPhaseConfirmModal(false);
      setPendingPhaseChange(null);
    }
  };
  
  // Cancel phase change
  const cancelPhaseChange = () => {
    setShowPhaseConfirmModal(false);
    setPendingPhaseChange(null);
  };
  
  // ========== CUSTOM HOOKS FOR ICS FORMS ==========
  // Safety Message Plan (ICS 208)
  const {
    safetyMessages, setSafetyMessages,
    currentSafetyMessage, setCurrentSafetyMessage,
    editingSafetyMessage, setEditingSafetyMessage,
    addSafetyMessage, editSafetyMessage, cancelEditSafetyMessage, deleteSafetyMessage
  } = useSafetyMessagePlan();

  // Status Summary (ICS 209)
  const {
    statusSummaries, setStatusSummaries,
    currentStatusSummary, setCurrentStatusSummary,
    editingStatusSummary, setEditingStatusSummary,
    addStatusSummary, editStatusSummary, cancelEditStatusSummary, deleteStatusSummary,
    fetchStatusSummaries
  } = useStatusSummary();

  // Expand/collapse tracking for Status Summary entries
  const [expandedStatusSummaries, setExpandedStatusSummaries] = useState(new Set());
  const toggleStatusSummaryExpanded = (id) => {
    setExpandedStatusSummaries((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // Fetch status summaries when exercise changes
  useEffect(() => {
    if (currentExercise?.id) {
      fetchStatusSummaries(currentExercise.id);
    }
  }, [currentExercise?.id, fetchStatusSummaries]);

  // Approval Route
  const {
    approvalRoutes, setApprovalRoutes,
    currentApprovalRoute, setCurrentApprovalRoute,
    editingApprovalRoute, setEditingApprovalRoute,
    addApprovalRoute, editApprovalRoute, cancelEditApprovalRoute, deleteApprovalRoute
  } = useApprovalRoute();

  // Incident Location
  const {
    incidentLocations, setIncidentLocations,
    currentIncidentLocation, setCurrentIncidentLocation,
    editingIncidentLocation, setEditingIncidentLocation,
    locationErrors, setLocationErrors,
    addIncidentLocation, editIncidentLocation, cancelEditIncidentLocation, deleteIncidentLocation
  } = useIncidentLocation();

  // Incident Summary
  const {
    incidentSummaries, setIncidentSummaries,
    currentIncidentSummary, setCurrentIncidentSummary,
    editingIncidentSummary, setEditingIncidentSummary,
    addIncidentSummary, editIncidentSummary, cancelEditIncidentSummary, deleteIncidentSummary,
    updateDamageAssessment
  } = useIncidentSummary();

  // Decision Support
  const {
    decisionSupportEntries, setDecisionSupportEntries,
    currentDecisionSupport, setCurrentDecisionSupport,
    editingDecisionSupport, setEditingDecisionSupport,
    addDecisionSupport, editDecisionSupport, cancelEditDecisionSupport, deleteDecisionSupport,
    calculateTotals, updateCiviliansData, updateRespondersData, updateLifeSafetyThreat, updateProjectedActivity
  } = useDecisionSupport();

  // Additional Decision Support
  const {
    additionalDecisionSupportEntries, setAdditionalDecisionSupportEntries,
    currentAdditionalDecisionSupport, setCurrentAdditionalDecisionSupport,
    editingAdditionalDecisionSupport, setEditingAdditionalDecisionSupport,
    addAdditionalDecisionSupport, editAdditionalDecisionSupport, cancelEditAdditionalDecisionSupport,
    deleteAdditionalDecisionSupport,
    updateCurrentThreatSummary, updateCriticalResourceNeeds
  } = useAdditionalDecisionSupport();

  // Resource Commitment Summary state
  const [exerciseResources, setExerciseResources] = useState([]);
  const [resourceCommitmentEntries, setResourceCommitmentEntries] = useState([]);

  // Resource Commitment functions
  const fetchExerciseResources = async () => {
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(
        `${API_BASE_URL}/api/resources?exercise_id=${currentExercise?.id}`,
        { headers }
      );
      setExerciseResources(response.data || []);
    } catch (error) {
      console.error('Error fetching exercise resources:', error);
      setExerciseResources([]);
    }
  };

  const generateResourceCommitmentSummary = async () => {
    if (exerciseResources.length === 0) {
      alert('No resources found for this exercise.');
      return;
    }

    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // Fetch units for all resources
      const resourcesWithUnits = await Promise.all(
        exerciseResources.map(async (resource) => {
          try {
            const unitsRes = await axios.get(
              `${API_BASE_URL}/api/resources/${resource.id}/units`,
              { headers }
            );
            return { ...resource, units: unitsRes.data || [] };
          } catch {
            return { ...resource, units: [] };
          }
        })
      );

      // Include all resources (with and without units)
      const allResources = resourcesWithUnits;

      if (allResources.length === 0) {
        alert('No resources found for this exercise.');
        return;
      }

      // Fetch agency count from Liaison Officer agency-coordination entries
      let totalAgencies = 0;
      try {
        const agencyRes = await axios.get(
          `${API_BASE_URL}/api/liaison-officer?exercise_id=${currentExercise?.id}`,
          { headers }
        );
        const agencyEntries = (agencyRes.data || []).filter(e => e.category === 'agency-coordination');
        totalAgencies = agencyEntries.length;
      } catch {
        totalAgencies = 0;
      }

      const allUnits = allResources.flatMap(r => r.units);
      const totalPersonnelNeeded = allUnits.reduce((sum, u) => sum + (parseInt(u.personnel_needed) || 0), 0);

      const summary = {
        id: Date.now().toString(),
        generatedAt: new Date().toISOString(),
        totalResources: allResources.length,
        totalUnits: allUnits.length,
        totalAgencies,
        totalQuantityAvailable: allUnits.length,
        totalQuantityNeeded: allResources.reduce((sum, r) => sum + (parseInt(r.quantity_needed) || 0), 0),
        totalPersonnelNeeded,
        resources: allResources
      };

      setResourceCommitmentEntries(prev => [...prev, summary]);
    } catch (error) {
      console.error('Error generating resource commitment summary:', error);
      alert('Error generating summary. Please try again.');
    }
  };

  // Remove a resource commitment entry
  const removeResourceCommitmentEntry = (entryId) => {
    setResourceCommitmentEntries(prev => prev.filter(entry => entry.id !== entryId));
  };

  // Update personnel needed for a specific resource in an entry
  const updatePersonnelNeeded = (entryId, resourceId, value) => {
    setResourceCommitmentEntries(prev => prev.map(entry => {
      if (entry.id === entryId) {
        const updatedResources = entry.resources.map(resource => {
          if (resource.id === resourceId) {
            return { ...resource, personnel_needed: parseInt(value) || 0 };
          }
          return resource;
        });
        // Recalculate total personnel needed
        const totalPersonnelNeeded = updatedResources.reduce((sum, r) => sum + (parseInt(r.personnel_needed) || 0), 0);
        return { ...entry, resources: updatedResources, totalPersonnelNeeded };
      }
      return entry;
    }));
  };

  // Fetch exercise resources when exercise changes
  useEffect(() => {
    if (currentExercise?.id) {
      fetchExerciseResources();
    }
  }, [currentExercise?.id]);

  // ICS 205 Communications Plan
  const {
    ics205Frequencies, setIcs205Frequencies,
    currentIcs205Frequency, setCurrentIcs205Frequency,
    editingIcs205Frequency, setEditingIcs205Frequency,
    loading: ics205Loading,
    lastSaved: ics205LastSaved,
    hasUnsavedChanges: ics205HasUnsavedChanges,
    saveStatus: ics205SaveStatus,
    addIcs205Frequency, editIcs205Frequency, cancelEditIcs205Frequency, deleteIcs205Frequency,
    printIcs205Combined,
    saveFrequencies: saveIcs205Frequencies,
    refreshFrequencies: refreshIcs205Frequencies,
    connectionHealth: ics205ConnectionHealth,
    verifyBackendHealth: verifyIcs205BackendHealth
  } = useCommsPlan(currentExercise);

  // ICS 206 Medical Plan (includes Med Aid Stations, Transport, Hospitals, Procedures)
  const {
    // Medical Aid Stations
    medAidStations, setMedAidStations,
    currentMedAidStation, setCurrentMedAidStation,
    editingMedAidStation, setEditingMedAidStation,
    addMedAidStation, editMedAidStation, cancelEditMedAidStation, deleteMedAidStation,
    saveMedAidStations, loadMedAidStations,
    // Medical Transport
    medTransports, setMedTransports,
    currentMedTransport, setCurrentMedTransport,
    editingMedTransport, setEditingMedTransport,
    addMedTransport, editMedTransport, cancelEditMedTransport, deleteMedTransport,
    saveMedTransport, loadMedTransport,
    // Hospitals
    hospitals, setHospitals,
    currentHospital, setCurrentHospital,
    editingHospital, setEditingHospital,
    hospitalValidation, setHospitalValidation,
    addHospital, editHospital, cancelEditHospital, deleteHospital,
    saveHospitals, loadHospitals,
    // Medical Procedures
    medProcedures, setMedProcedures,
    currentMedProcedure, setCurrentMedProcedure,
    editingMedProcedure, setEditingMedProcedure,
    addMedProcedure, editMedProcedure, cancelEditMedProcedure, deleteMedProcedure,
    saveMedProcedures, loadMedProcedures,
    // Medical Plan Entries (title/from/to/plan)
    medPlanEntries,
    currentMedPlanEntry, setCurrentMedPlanEntry,
    editingMedPlanEntry,
    addMedPlanEntry, editMedPlanEntry, cancelEditMedPlanEntry, deleteMedPlanEntry,
    // Shared sync state
    loading: medicalPlanLoading,
    lastSaved: medicalPlanLastSaved,
    hasUnsavedChanges: medicalPlanHasUnsavedChanges,
    refreshAll: refreshMedicalPlan,
    saveAll: saveMedicalPlan
  } = useMedicalPlan(currentExercise);

  const updateICSMenu = (menuId) => {
    setActiveICSMenu(menuId);
    if (socketCtx?.updateForm) socketCtx.updateForm(menuId);
    // Auto-close mobile drawer after leaf selection
    setIcsDrawerOpen(false);
  };


  const icsMenuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home, textColor: 'text-orange-500' },
    { 
      id: 'command-staff', 
      label: 'Command Staff', 
      icon: UserCog, 
      backgroundColor: 'bg-red-900',
      hasSubmenu: true,
      submenuItems: [
        { id: 'information-officer', label: 'Information Officer', icon: Info, backgroundColor: 'bg-red-500' },
        { id: 'safety-officer', label: 'Safety Officer', icon: Shield, backgroundColor: 'bg-red-500' },
        { id: 'liaison-officer', label: 'Liaison Officer', icon: MessageCircle, backgroundColor: 'bg-red-500' },
        { id: 'organization-chart', label: 'Organization List', icon: Building, backgroundColor: 'bg-red-500' }
      ]
    },
    {
      id: 'operations',
      label: 'Operations',
      icon: Users,
      hasSubmenu: true,
      submenuItems: [
        { 
          id: 'incident-action-plan', 
          label: 'Incident Action Plan', 
          icon: ClipboardList, 
          backgroundColor: 'bg-orange-500',
          hasSubmenu: true,
          submenuItems: [
            { id: 'scenario', label: 'Scenario', icon: FileText, backgroundColor: 'bg-orange-500' },
            { id: 'objectives', label: 'Objectives', icon: Target, backgroundColor: 'bg-orange-500' }
          ]
        },
        { 
          id: 'incident-status-summary', 
          label: 'Incident Status', 
          icon: ClipboardList, 
          backgroundColor: 'bg-orange-500',
          hasSubmenu: true,
          submenuItems: [
            { id: 'incident-header', label: 'Status Summary', icon: FileText, backgroundColor: 'bg-orange-500' },
            { id: 'approve-route', label: 'Approve / Route', icon: CheckCircle, backgroundColor: 'bg-orange-500' },
            { id: 'location-information', label: 'Location Information', icon: MapPin, backgroundColor: 'bg-orange-500' },
            { id: 'incident-summary', label: 'Incident Summary', icon: FileText, backgroundColor: 'bg-orange-500' },
            { id: 'decision-support', label: 'Decision Support', icon: Target, backgroundColor: 'bg-orange-500' },
            { id: 'addtl-decision-supp', label: 'Addt\'l Decision Supp', icon: Plus, backgroundColor: 'bg-orange-500' }
          ]
        },
        { 
          id: 'air-ops', 
          label: 'Air Ops', 
          icon: Cloud, 
          backgroundColor: 'bg-orange-500',
          hasSubmenu: true,
          submenuItems: [
            { id: 'air-ops-summ', label: 'Air Ops Summ.', icon: Cloud, backgroundColor: 'bg-orange-500' }
          ]
        },
        { id: 'assignment-list', label: 'Assignment List', icon: ClipboardList, backgroundColor: 'bg-orange-500' }
      ]
    },
    { 
      id: 'planning', 
      label: 'Planning', 
      icon: Calendar, 
      backgroundColor: 'bg-blue-500',
      hasSubmenu: true,
      submenuItems: [
        { 
          id: 'situation-unit', 
          label: 'Situation Unit', 
          icon: Info, 
          backgroundColor: 'bg-blue-500',
          hasSubmenu: true,
          submenuItems: [
            { id: 'daily-schedule', label: 'Daily Schedule', icon: Calendar, backgroundColor: 'bg-blue-500' }
          ]
        },
        { 
          id: 'resources-unit', 
          label: 'Resources Unit', 
          icon: Users, 
          backgroundColor: 'bg-blue-500',
          hasSubmenu: true,
          submenuItems: [
            { id: 'strike-teams', label: 'Strike Teams', icon: Shield, backgroundColor: 'bg-blue-500' },
            { id: 'task-forces', label: 'Task Forces', icon: Layers, backgroundColor: 'bg-blue-500' },
            { id: 'resources', label: 'Res. Management', icon: Package, backgroundColor: 'bg-blue-500' },
            { id: 'ics-215', label: 'Op Plan Worksheet', icon: FileText, backgroundColor: 'bg-blue-500' },
            { id: 'ics-232', label: 'Resources at Risk', icon: AlertTriangle, backgroundColor: 'bg-blue-500' }
          ]
        },
        { 
          id: 'tech-spec-unit', 
          label: 'Tech Spec Unit', 
          icon: Settings, 
          backgroundColor: 'bg-blue-500',
          hasSubmenu: true,
          submenuItems: [
            { id: 'mapping', label: 'Mapping', icon: Map, backgroundColor: 'bg-blue-500' },
            { id: 'weather', label: 'Weather', icon: Cloud, backgroundColor: 'bg-blue-500' },
            { id: 'wx-report', label: 'WX Report', icon: CloudRain, backgroundColor: 'bg-blue-500' },
            { id: 'severe-wx', label: 'Severe Wx', icon: AlertTriangle, backgroundColor: 'bg-blue-500' }
          ]
        },
        { 
          id: 'documentation-unit', 
          label: 'Document Unit', 
          icon: FileText, 
          backgroundColor: 'bg-blue-500',
          hasSubmenu: true,
          submenuItems: [
            { id: 'ics-2xx-forms', label: 'ICS 2XX Form Series', icon: FileCheck, backgroundColor: 'bg-blue-500' },
            { id: 'activity-log', label: 'Activity Log', icon: Clock, backgroundColor: 'bg-blue-500' },
            { id: 'incident-tracker', label: 'Incident Tracker', icon: Target, backgroundColor: 'bg-blue-500' },
            { id: 'after-action-report', label: 'After Action Report', icon: FileText, backgroundColor: 'bg-blue-500' }
          ]
        },
        { 
          id: 'demobilization-unit', 
          label: 'Demobilize Unit', 
          icon: ArrowLeft, 
          backgroundColor: 'bg-blue-500',
          hasSubmenu: true,
          submenuItems: [
            { id: 'demobile-checkout', label: 'Demobile Checkout', icon: ArrowLeft, backgroundColor: 'bg-blue-500' },
            { id: 'crew-perf-rating', label: 'Crew Perf. Rating', icon: Star, backgroundColor: 'bg-blue-500' },
            { id: 'personnel-perf', label: 'Personnel Perf.', icon: User, backgroundColor: 'bg-blue-500' }
          ]
        }
      ]
    },
    { 
      id: 'logistics', 
      label: 'Logistics', 
      icon: Package, 
      backgroundColor: 'bg-amber-500',
      hasSubmenu: true,
      submenuItems: [
        { 
          id: 'services', 
          label: 'Services', 
          icon: Settings, 
          backgroundColor: 'bg-amber-500',
          hasSubmenu: true,
          submenuItems: [
            { 
              id: 'comms-unit', 
              label: 'Comms Unit', 
              icon: Radio, 
              backgroundColor: 'bg-amber-500',
              hasSubmenu: true,
              submenuItems: [
                { id: 'comms-plan-brief', label: 'Comms Plan', icon: FileText, backgroundColor: 'bg-amber-500' },
                { id: 'comms-plan', label: 'Comms Freqs', icon: FileText, backgroundColor: 'bg-amber-500' },
                { id: 'comms-log', label: 'Comms Log', icon: FileText, backgroundColor: 'bg-amber-500' },
                { id: 'comms-general-message', label: 'General Message', icon: MessageSquare, backgroundColor: 'bg-amber-500' },
                { id: 'comms-list', label: 'Contact List', icon: ClipboardList, backgroundColor: 'bg-amber-500' }
              ]
            },
            { id: 'medical-unit', label: 'Medical Unit', icon: Heart, backgroundColor: 'bg-amber-500' },
            { id: 'food-unit', label: 'Food Unit', icon: Coffee, backgroundColor: 'bg-amber-500' }
          ]
        },
        { 
          id: 'support', 
          label: 'Support', 
          icon: Headphones, 
          backgroundColor: 'bg-amber-500',
          hasSubmenu: true,
          submenuItems: [
            { 
              id: 'supply-unit', 
              label: 'Supply Unit', 
              icon: Package, 
              backgroundColor: 'bg-amber-500',
              hasSubmenu: true,
              submenuItems: [
                { id: 'aircraft-order', label: 'Aircraft Order', icon: Plane, backgroundColor: 'bg-amber-500' },
                { id: 'crews-order', label: 'Crews Order', icon: Users, backgroundColor: 'bg-amber-500' },
                { id: 'equipment-order', label: 'Equipment Order', icon: Package, backgroundColor: 'bg-amber-500' },
                { id: 'overhead-order', label: 'Overhead Order', icon: FileText, backgroundColor: 'bg-amber-500' },
                { id: 'supplies-order', label: 'Supplies Order', icon: Truck, backgroundColor: 'bg-amber-500' }
              ]
            },
            { id: 'facilities-unit', label: 'Facilities Unit', icon: Building, backgroundColor: 'bg-amber-500' }
          ]
        }
      ]
    },
    { 
      id: 'fin-admin', 
      label: 'Fin / Admin', 
      icon: DollarSign, 
      backgroundColor: 'bg-gray-500',
      hasSubmenu: true,
      submenuItems: [
        { id: 'time-unit', label: 'Time Unit', icon: Clock, backgroundColor: 'bg-gray-500' },
        { id: 'procurement-unit', label: 'Procurement Unit', icon: Package, backgroundColor: 'bg-gray-500' },
        { id: 'comp-claims-unit', label: 'Comp and Claims Unit', icon: FileCheck, backgroundColor: 'bg-gray-500' },
        { id: 'cost-unit', label: 'Cost Unit', icon: DollarSign, backgroundColor: 'bg-gray-500' }
      ]
    },
    { 
      id: 'separator-1',
      isSeparator: true
    },
    { 
      id: 'participants', 
      label: 'Participants', 
      icon: Users, 
      backgroundColor: 'bg-orange-500'
    },
    { 
      id: 'images', 
      label: 'Images', 
      icon: ImageIcon, 
      backgroundColor: 'bg-purple-500'
    },
    { 
      id: 'analytics', 
      label: 'Analytics', 
      icon: TrendingUp, 
      backgroundColor: 'bg-green-600'
    },
    { 
      id: 'lessons_learned', 
      label: 'Lessons Learned', 
      icon: Lightbulb, 
      backgroundColor: 'bg-teal-600'
    }
  ];

  // Print function for ICS 208 Combined (Safety Message/Plan)
  const printIcs208Combined = async () => {
    try {
      // Fetch safety data from the API
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(
        `${API_BASE_URL}/api/safety-data?exercise_id=${currentExercise?.id}`,
        { headers }
      );
      
      const safetyData = response.data || [];
      
      if (safetyData.length === 0) {
        alert('No safety data available to print.');
        return;
      }
      
      const printWindow = window.open('', '_blank');
      const printContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>ICS 208 - Safety Message/Plan</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; color: #333; }
            .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 15px; }
            .header h1 { margin: 0 0 10px 0; font-size: 24px; }
            .header h2 { margin: 0; font-size: 18px; color: #666; }
            .section { margin-bottom: 25px; page-break-inside: avoid; }
            .section-title { font-size: 16px; font-weight: bold; background: #f0f0f0; padding: 8px 12px; margin-bottom: 10px; border-left: 4px solid #ea580c; }
            .entry { border: 1px solid #ddd; padding: 15px; margin-bottom: 15px; border-radius: 4px; }
            .entry-header { font-weight: bold; font-size: 14px; margin-bottom: 8px; color: #ea580c; }
            .entry-field { margin: 5px 0; }
            .entry-field label { font-weight: bold; color: #555; }
            .priority-high { color: #dc2626; }
            .priority-medium { color: #f59e0b; }
            .priority-low { color: #16a34a; }
            .footer { margin-top: 30px; border-top: 1px solid #ccc; padding-top: 15px; font-size: 12px; color: #666; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>ICS 208 - SAFETY MESSAGE/PLAN</h1>
            <h2>${currentExercise?.exercise_name || 'Exercise'}</h2>
            <p>Printed: ${new Date().toLocaleString()}</p>
          </div>
          
          ${safetyData.map((entry, index) => `
            <div class="entry">
              <div class="entry-header">${index + 1}. ${entry.title || 'Untitled Entry'}</div>
              <div class="entry-field"><label>Category:</label> ${entry.category || 'N/A'}</div>
              <div class="entry-field"><label>Priority:</label> <span class="priority-${entry.priority || 'medium'}">${(entry.priority || 'medium').toUpperCase()}</span></div>
              <div class="entry-field"><label>Status:</label> ${entry.status || 'N/A'}</div>
              <div class="entry-field"><label>Location:</label> ${entry.location || 'N/A'}</div>
              <div class="entry-field"><label>Description:</label> ${entry.description || 'N/A'}</div>
              ${entry.contact_name ? `<div class="entry-field"><label>Contact:</label> ${entry.contact_name} ${entry.contact_phone ? '- ' + entry.contact_phone : ''}</div>` : ''}
              ${entry.notes ? `<div class="entry-field"><label>Notes:</label> ${entry.notes}</div>` : ''}
              <div class="entry-field"><label>Timestamp:</label> ${entry.timestamp ? new Date(entry.timestamp).toLocaleString() : 'N/A'}</div>
            </div>
          `).join('')}
          
          <div class="footer">
            <p>ICS 208 - Safety Message/Plan | ${currentExercise?.exercise_name || 'Exercise'}</p>
            <p>Total Entries: ${safetyData.length} | Generated by EXRSIM</p>
          </div>
        </body>
        </html>
      `;
      
      writeSanitizedHTML(printWindow.document, printContent);
      printWindow.focus();
      setTimeout(() => printWindow.print(), 500);
    } catch (error) {
      console.error('Error printing ICS 208:', error);
      alert('Error generating print preview. Please try again.');
    }
  };

  const renderICSContent = (participants, positions) => {
    console.log('renderICSContent called with activeICSMenu:', activeICSMenu);
    console.log('Available cases: dashboard, scenario, command-staff, operations, mapping, etc.');
    
    switch (activeICSMenu) {
      case 'dashboard':
        console.log('Dashboard case triggered');
        return <ICSDashboardOverview exerciseId={currentExercise?.id} />;
      case 'incident-action-plan':
        return (
          <div className="p-6">
            <div className={`${theme.colors.card} rounded-lg p-8 border ${theme.colors.border}`}>
              <div className="text-center">
                <ClipboardList className={`h-16 w-16 ${theme.colors.textMuted} mx-auto mb-4`} />
                <h2 className={`text-2xl font-bold text-orange-500 mb-4`}>Incident Action Plan (IAP)</h2>
                <p className={`${theme.colors.textSecondary} mb-6 max-w-2xl mx-auto`}>
                  The Incident Action Plan contains objectives reflecting the overall incident strategy 
                  and specific tactical actions and supporting information for the next operational period.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto mt-8">
                  <div className={`p-6 rounded-lg ${theme.colors.tertiary} border ${theme.colors.border} text-left`}>
                    <h3 className={`font-semibold ${theme.colors.textPrimary} mb-3 text-lg`}>IAP Components</h3>
                    <ul className={`space-y-2 ${theme.colors.textSecondary}`}>
                      <li>• Incident Objectives</li>
                      <li>• Organization Assignment List</li>
                      <li>• Assignment Lists</li>
                      <li>• Communications Plan</li>
                      <li>• Medical Plan</li>
                      <li>• Safety Plan</li>
                    </ul>
                  </div>
                  <div className={`p-6 rounded-lg ${theme.colors.tertiary} border ${theme.colors.border} text-left`}>
                    <h3 className={`font-semibold ${theme.colors.textPrimary} mb-3 text-lg`}>Planning Cycle</h3>
                    <ul className={`space-y-2 ${theme.colors.textSecondary}`}>
                      <li>• Operational Period: 12-24 hours</li>
                      <li>• Prepared by Planning Section</li>
                      <li>• Approved by Incident Commander</li>
                      <li>• Distributed to all sections</li>
                      <li>• Updated each operational period</li>
                    </ul>
                  </div>
                </div>
                <div className={`mt-8 p-4 rounded-lg bg-orange-500/10 border border-orange-500/30`}>
                  <p className={`text-orange-400 text-sm`}>
                    <strong>Note:</strong> The IAP integrates various ICS forms and documents. 
                    Access specific forms through their respective menu sections (Operations, Planning, Logistics, etc.)
                  </p>
                </div>
              </div>
            </div>
          </div>
        );
      case 'scenario':
        return <ScenarioModule theme={theme} exerciseId={currentExercise?.id} exerciseName={currentExercise?.exercise_name} />;
      case 'command-staff':
        return (
          <div className="p-6">
            <div className="text-center py-12">
              <UserCog className={`h-16 w-16 ${theme.colors.textMuted} mx-auto mb-4`} />
              <h2 className={`text-2xl font-bold ${theme.colors.textPrimary} mb-4`}>Command Staff Overview</h2>
              <p className={`${theme.colors.textMuted} mb-6 max-w-2xl mx-auto`}>
                Manage command and general staff positions, responsibilities, and personnel assignments. 
                Access individual staff modules using the submenu options.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto mt-8">
                <button
                  type="button"
                  onClick={() => updateICSMenu('information-officer')}
                  className={`p-4 rounded-lg ${theme.colors.card} border ${theme.colors.border} text-left w-full transition-all hover:-translate-y-1 hover:border-orange-500 hover:shadow-lg hover:shadow-orange-500/20 focus:outline-none focus:ring-2 focus:ring-orange-500`}
                  data-testid="command-staff-info-officer-card"
                  title="Open Information Officer dashboard"
                >
                  <UserCog className="h-8 w-8 text-orange-500 mx-auto mb-2" />
                  <h3 className={`font-semibold ${theme.colors.textPrimary} mb-1 text-center`}>Information Officer</h3>
                  <p className={`text-sm ${theme.colors.textMuted} text-center`}>Manage incident information and communications</p>
                </button>
                <button
                  type="button"
                  onClick={() => updateICSMenu('safety-officer')}
                  className={`p-4 rounded-lg ${theme.colors.card} border ${theme.colors.border} text-left w-full transition-all hover:-translate-y-1 hover:border-orange-500 hover:shadow-lg hover:shadow-orange-500/20 focus:outline-none focus:ring-2 focus:ring-orange-500`}
                  data-testid="command-staff-safety-officer-card"
                  title="Open Safety Officer dashboard"
                >
                  <Shield className="h-8 w-8 text-orange-500 mx-auto mb-2" />
                  <h3 className={`font-semibold ${theme.colors.textPrimary} mb-1 text-center`}>Safety Officer</h3>
                  <p className={`text-sm ${theme.colors.textMuted} text-center`}>Monitor safety conditions and hazard management</p>
                </button>
                <button
                  type="button"
                  onClick={() => updateICSMenu('liaison-officer')}
                  className={`p-4 rounded-lg ${theme.colors.card} border ${theme.colors.border} text-left w-full transition-all hover:-translate-y-1 hover:border-orange-500 hover:shadow-lg hover:shadow-orange-500/20 focus:outline-none focus:ring-2 focus:ring-orange-500`}
                  data-testid="command-staff-liaison-officer-card"
                  title="Open Liaison Officer dashboard"
                >
                  <Users className="h-8 w-8 text-orange-500 mx-auto mb-2" />
                  <h3 className={`font-semibold ${theme.colors.textPrimary} mb-1 text-center`}>Liaison Officer</h3>
                  <p className={`text-sm ${theme.colors.textMuted} text-center`}>Coordinate with external agencies</p>
                </button>
              </div>
            </div>
          </div>
        );
      case 'information-officer':
        return <InformationOfficerModule theme={theme} exerciseId={currentExercise?.id} exerciseName={currentExercise?.exercise_name} />;
      case 'safety-officer':
        return <SafetyOfficerModule theme={theme} exerciseId={currentExercise?.id} exerciseName={currentExercise?.exercise_name} />;
      case 'liaison-officer':
        return <LiaisonOfficerModule theme={theme} exerciseId={currentExercise?.id} exerciseName={currentExercise?.exercise_name} />;
      case 'organization-chart':
        return <ICSOrganizationChart participants={participants} theme={theme} positions={positions} currentExercise={currentExercise} API_URL={API_BASE_URL} />;
      case 'assignment-list':
        return <ICS204AssignmentList theme={theme} currentExercise={currentExercise} API_URL={API_BASE_URL} />;
      case 'objectives':
        return <HierarchicalObjectives theme={theme} exerciseId={currentExercise?.id} exerciseName={currentExercise?.exercise_name} />;
      case 'air-ops':
        return (
          <div className="p-6">
            <div className="text-center py-12">
              <Cloud className={`h-16 w-16 ${theme.colors.textMuted} mx-auto mb-4`} />
              <h2 className={`text-2xl font-bold ${theme.colors.textPrimary} mb-4`}>Air Operations</h2>
              <p className={`${theme.colors.textMuted} mb-6 max-w-2xl mx-auto`}>
                Coordinate and manage air operations including aircraft deployment, flight missions, and airspace coordination for incident response. Use the submenu to access Air Operations modules.
              </p>
            </div>
          </div>
        );
      case 'air-ops-summ':
        return <ICS220Form currentExercise={currentExercise} theme={theme} />;
      case 'incident-status-summary':
        return (
          <div className="p-6">
            <div className="text-center py-12">
              <ClipboardList className={`h-16 w-16 ${theme.colors.textMuted} mx-auto mb-4`} />
              <h2 className={`text-2xl font-bold ${theme.colors.textPrimary} mb-4`}>Status Summary</h2>
              <p className={`${theme.colors.textMuted} mb-6 max-w-2xl mx-auto`}>
                Comprehensive incident status tracking and summary reporting system for operations coordination.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto mt-8">
                {[
                  { id: 'incident-header', title: 'Status Summary', desc: 'Basic incident identification and header information', icon: FileText },
                  { id: 'approve-route', title: 'Approve / Route', desc: 'Approval workflows and routing procedures', icon: CheckCircle },
                  { id: 'location-information', title: 'Location Information', desc: 'Geographic and location-specific incident data', icon: MapPin },
                  { id: 'incident-summary', title: 'Incident Summary', desc: 'Comprehensive incident overview and status', icon: FileText },
                  { id: 'decision-support', title: 'Decision Support', desc: 'Critical decision-making support information', icon: Target },
                  { id: 'addtl-decision-supp', title: 'Additional Decision Support', desc: 'Extended decision support resources and data', icon: Plus }
                ].map(item => {
                  const IconComponent = item.icon;
                  return (
                    <div key={item.id} className={`p-6 rounded-lg ${theme.colors.card} border ${theme.colors.border} hover:bg-orange-500/10 transition-colors cursor-pointer`}
                         onClick={() => updateICSMenu(item.id)}>
                      <IconComponent className="h-12 w-12 text-orange-500 mx-auto mb-4" />
                      <h3 className={`text-xl font-semibold ${theme.colors.textPrimary} mb-3`}>{item.title}</h3>
                      <p className={`${theme.colors.textMuted} text-sm`}>{item.desc}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      case 'incident-header':
        return (
          <div className="p-6 space-y-6 pb-20">
            {/* Status Summary Header */}
            <div className="text-center mb-8">
              <h1 className={`text-3xl font-bold ${theme.colors.textPrimary} mb-2`}>Status Summary</h1>
              <p className={`${theme.colors.textMuted} text-lg`}>
                Incident identification, classification, and comprehensive status reporting
              </p>
            </div>

            {/* Status Summary Form */}
            <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-orange-500">Incident Status Summary (ICS 209)</CardTitle>
                    <CardDescription className={`${theme.colors.textMuted}`}>
                      Enter incident header information, status details, and operational parameters
                    </CardDescription>
                  </div>
                  <Button 
                    onClick={() => {
                      const printWindow = window.open('', '_blank');
                      // Use saved entries if available, otherwise use current form data
                      const headerData = statusSummaries.length > 0 ? statusSummaries : (currentStatusSummary.reportVersion ? [currentStatusSummary] : []);
                      const routeData = approvalRoutes.length > 0 ? approvalRoutes : [];
                      const locationData = incidentLocations.length > 0 ? incidentLocations : [];
                      const summaryData = incidentSummaries.length > 0 ? incidentSummaries : [];
                      const decisionData = decisionSupportEntries.length > 0 ? decisionSupportEntries : [];
                      const addtlDecisionData = additionalDecisionSupportEntries.length > 0 ? additionalDecisionSupportEntries : [];
                      const resourceData = resourceCommitmentEntries.length > 0 ? resourceCommitmentEntries : [];
                      const printContent = `
                        <html>
                          <head>
                            <title>ICS 209 - Incident Status Summary</title>
                            <style>
                              body { font-family: Arial, sans-serif; margin: 20px; color: #333; line-height: 1.6; }
                              .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #f97316; padding-bottom: 15px; }
                              .header h1 { color: #f97316; margin: 0; font-size: 28px; }
                              .section { margin-bottom: 25px; padding: 20px; border: 1px solid #ddd; border-radius: 8px; page-break-inside: avoid; }
                              .section h3 { color: #f97316; margin-top: 0; border-bottom: 2px solid #f97316; padding-bottom: 5px; }
                              .form-data { margin-bottom: 15px; padding: 10px; background-color: #f9fafb; border-left: 3px solid #f97316; }
                              .form-data p { margin: 4px 0; }
                              .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; border-top: 1px solid #ddd; padding-top: 10px; }
                              .empty-note { color: #999; font-style: italic; padding: 10px; }
                            </style>
                          </head>
                          <body>
                            <div class="header">
                              <h1>ICS 209 - Incident Status Summary</h1>
                              <p><strong>Exercise:</strong> ${currentExercise?.exercise_name || 'N/A'}</p>
                              <p><strong>Generated:</strong> ${new Date().toLocaleDateString()} at ${format24HourTime()}</p>
                            </div>
                            
                            <div class="section">
                              <h3>1. Incident Header Information</h3>
                              ${headerData.length > 0 ? headerData.map((entry, index) => `
                                <div class="form-data">
                                  ${headerData.length > 1 ? `<h4>Entry #${index + 1}</h4>` : ''}
                                  <p><strong>Report Version:</strong> ${entry.reportVersion || 'N/A'}</p>
                                  <p><strong>Report Number:</strong> ${entry.reportNumber || 'N/A'}</p>
                                  <p><strong>Incident Commander:</strong> ${entry.incidentCmdrName || 'N/A'}</p>
                                  <p><strong>Incident Management Organization:</strong> ${entry.incidentMgmtOrg || 'N/A'}</p>
                                  <p><strong>Incident Area Size:</strong> ${entry.incidentAreaSize || 'N/A'}</p>
                                  <p><strong>Percent Contained:</strong> ${entry.percentContained || '0'}%</p>
                                  <p><strong>Complexity Level:</strong> ${entry.complexityLevel || entry.incidentComplexity || 'N/A'}</p>
                                  <p><strong>Incident Definition:</strong> ${entry.incidentDefinition || 'N/A'}</p>
                                  <p><strong>Incident Start:</strong> ${entry.startDate || ''} ${entry.startTime || ''}</p>
                                  <p><strong>From:</strong> ${entry.fromDate || ''} ${entry.fromTime || ''}</p>
                                  <p><strong>To:</strong> ${entry.toDate || ''} ${entry.toTime || ''}</p>
                                </div>
                              `).join('') : '<div class="empty-note">No incident header data entered.</div>'}
                            </div>

                            <div class="section">
                              <h3>2. Approval & Routing Information</h3>
                              ${routeData.length > 0 ? routeData.map((entry, index) => `
                                <div class="form-data">
                                  ${routeData.length > 1 ? `<h4>Entry #${index + 1}</h4>` : ''}
                                  <p><strong>Prepared By:</strong> ${entry.preparedByName || 'N/A'} (${entry.preparedByPosition || 'N/A'})</p>
                                  <p><strong>Prepared Date/Time:</strong> ${entry.preparedDate || ''} ${entry.preparedTime || ''}</p>
                                  <p><strong>Submitted Date/Time:</strong> ${entry.submittedDate || ''} ${entry.submittedTime || ''}</p>
                                  <p><strong>Approved By:</strong> ${entry.approvedByName || 'Pending'} (${entry.approvedByPosition || 'N/A'})</p>
                                  <p><strong>Approved Date/Time:</strong> ${entry.approvedDate || ''} ${entry.approvedTime || ''}</p>
                                  <p><strong>Primary Location / Org / Agency sent to:</strong> ${entry.primaryLocation || 'N/A'}</p>
                                </div>
                              `).join('') : '<div class="empty-note">No approval/routing data entered.</div>'}
                            </div>

                            <div class="section">
                              <h3>3. Location Information</h3>
                              ${locationData.length > 0 ? locationData.map((entry, index) => `
                                <div class="form-data">
                                  ${locationData.length > 1 ? `<h4>Entry #${index + 1}</h4>` : ''}
                                  <p><strong>Province/State/Territory:</strong> ${entry.provStateTerritory || 'N/A'}</p>
                                  <p><strong>County/Municipality:</strong> ${entry.countyMunicipality || 'N/A'}</p>
                                  <p><strong>City:</strong> ${entry.city || 'N/A'}</p>
                                  <p><strong>Unit/Other:</strong> ${entry.unitOrOther || 'N/A'}</p>
                                  <p><strong>Jurisdiction:</strong> ${entry.jurisdiction || 'N/A'}</p>
                                  <p><strong>Location Ownership:</strong> ${entry.incidentLocationOwnership || 'N/A'}</p>
                                  <p><strong>Latitude:</strong> ${entry.latitude || 'N/A'}</p>
                                  <p><strong>Longitude:</strong> ${entry.longitude || 'N/A'}</p>
                                  <p><strong>Datum:</strong> ${entry.datum || 'N/A'}</p>
                                  <p><strong>UTM Coordinates:</strong> ${entry.utmCoordinates || 'N/A'}</p>
                                  <p><strong>Additional Electronic Geospatial Data:</strong> ${entry.legalDescription || 'N/A'}</p>
                                  <p><strong>Short Location Description:</strong> ${entry.shortLocationDescription || 'N/A'}</p>
                                </div>
                              `).join('') : '<div class="empty-note">No location data entered.</div>'}
                            </div>

                            <div class="section">
                              <h3>4. Incident Summary</h3>
                              ${summaryData.length > 0 ? summaryData.map((entry, index) => `
                                <div class="form-data">
                                  ${summaryData.length > 1 ? `<h4>Entry #${index + 1}</h4>` : ''}
                                  <p><strong>Significant Events:</strong> ${entry.significantEvents || 'N/A'}</p>
                                  <p><strong>Initial Response:</strong> ${entry.initialResponse || 'N/A'}</p>
                                  <p><strong>Current Actions:</strong> ${entry.currentActions || 'N/A'}</p>
                                  <p><strong>Planned Actions:</strong> ${entry.plannedActions || 'N/A'}</p>
                                  <p><strong>Projected Final Size:</strong> ${entry.projectedFinalSize || 'N/A'}</p>
                                  <p><strong>Estimated Containment:</strong> ${entry.estimatedContainment || 'N/A'}</p>
                                  <p><strong>Anticipated Completion:</strong> ${entry.anticipatedCompletion || 'N/A'}</p>
                                  <p><strong>Remarks:</strong> ${entry.remarks || 'N/A'}</p>
                                </div>
                              `).join('') : '<div class="empty-note">No incident summary data entered.</div>'}
                            </div>

                            <div class="section">
                              <h3>5. Decision Support Information</h3>
                              ${decisionData.length > 0 ? decisionData.map((entry, index) => `
                                <div class="form-data">
                                  ${decisionData.length > 1 ? `<h4>Entry #${index + 1}</h4>` : ''}
                                  <p><strong>Public Status Summary:</strong></p>
                                  ${entry.civilians ? `
                                    <table style="width:100%; border-collapse:collapse; margin:5px 0; font-size:12px;">
                                      <tr><th style="text-align:left; padding:4px; border:1px solid #ddd;">Category</th><th style="padding:4px; border:1px solid #ddd;">This Period</th><th style="padding:4px; border:1px solid #ddd;">Total</th></tr>
                                      <tr><td style="padding:4px; border:1px solid #ddd;">Fatalities</td><td style="padding:4px; border:1px solid #ddd; text-align:center;">${entry.civilians.fatalities?.thisPeriod || 0}</td><td style="padding:4px; border:1px solid #ddd; text-align:center;">${entry.civilians.fatalities?.toDate || 0}</td></tr>
                                      <tr><td style="padding:4px; border:1px solid #ddd;">Injuries</td><td style="padding:4px; border:1px solid #ddd; text-align:center;">${entry.civilians.injuries?.thisPeriod || 0}</td><td style="padding:4px; border:1px solid #ddd; text-align:center;">${entry.civilians.injuries?.toDate || 0}</td></tr>
                                      <tr><td style="padding:4px; border:1px solid #ddd;">Evacuated</td><td style="padding:4px; border:1px solid #ddd; text-align:center;">${entry.civilians.evacuated?.thisPeriod || 0}</td><td style="padding:4px; border:1px solid #ddd; text-align:center;">${entry.civilians.evacuated?.toDate || 0}</td></tr>
                                      <tr><td style="padding:4px; border:1px solid #ddd;">Sheltering</td><td style="padding:4px; border:1px solid #ddd; text-align:center;">${entry.civilians.sheltering?.thisPeriod || 0}</td><td style="padding:4px; border:1px solid #ddd; text-align:center;">${entry.civilians.sheltering?.toDate || 0}</td></tr>
                                    </table>
                                  ` : ''}
                                  <p><strong>Responders Status Summary:</strong></p>
                                  ${entry.responders ? `
                                    <table style="width:100%; border-collapse:collapse; margin:5px 0; font-size:12px;">
                                      <tr><th style="text-align:left; padding:4px; border:1px solid #ddd;">Category</th><th style="padding:4px; border:1px solid #ddd;">This Period</th><th style="padding:4px; border:1px solid #ddd;">Total</th></tr>
                                      <tr><td style="padding:4px; border:1px solid #ddd;">Fatalities</td><td style="padding:4px; border:1px solid #ddd; text-align:center;">${entry.responders.fatalities?.thisPeriod || 0}</td><td style="padding:4px; border:1px solid #ddd; text-align:center;">${entry.responders.fatalities?.toDate || 0}</td></tr>
                                      <tr><td style="padding:4px; border:1px solid #ddd;">Injuries/Illness</td><td style="padding:4px; border:1px solid #ddd; text-align:center;">${entry.responders.injuries?.thisPeriod || 0}</td><td style="padding:4px; border:1px solid #ddd; text-align:center;">${entry.responders.injuries?.toDate || 0}</td></tr>
                                    </table>
                                  ` : ''}
                                  <p><strong>Life Safety & Health Remarks:</strong> ${entry.lifeSafetyHealthRemarks || 'N/A'}</p>
                                  <p><strong>Weather Concerns:</strong> ${entry.weatherConcerns || 'N/A'}</p>
                                  <p><strong>Projected Activity:</strong></p>
                                  <ul>
                                    ${entry.projectedActivity?.twelveHours ? `<li><strong>12 Hours:</strong> ${entry.projectedActivity.twelveHours}</li>` : ''}
                                    ${entry.projectedActivity?.twentyFourHours ? `<li><strong>24 Hours:</strong> ${entry.projectedActivity.twentyFourHours}</li>` : ''}
                                    ${entry.projectedActivity?.fortyEightHours ? `<li><strong>48 Hours:</strong> ${entry.projectedActivity.fortyEightHours}</li>` : ''}
                                    ${entry.projectedActivity?.seventyTwoHours ? `<li><strong>72 Hours:</strong> ${entry.projectedActivity.seventyTwoHours}</li>` : ''}
                                    ${entry.projectedActivity?.afterSeventyTwoHours ? `<li><strong>After 72 Hours:</strong> ${entry.projectedActivity.afterSeventyTwoHours}</li>` : ''}
                                  </ul>
                                </div>
                              `).join('') : '<div class="empty-note">No decision support data entered.</div>'}
                            </div>

                            <div class="section">
                              <h3>6. Additional Decision Support</h3>
                              ${addtlDecisionData.length > 0 ? addtlDecisionData.map((entry, index) => `
                                <div class="form-data">
                                  ${addtlDecisionData.length > 1 ? `<h4>Entry #${index + 1}</h4>` : ''}
                                  <p><strong>Strategic Discussions:</strong> ${entry.strategicDiscussions || 'N/A'}</p>
                                  <p><strong>Planned Actions Next Period:</strong> ${entry.plannedActionsNextPeriod || 'N/A'}</p>
                                  <p><strong>Projected Final Incident Area:</strong> ${entry.projectedFinalIncidentArea || 'N/A'}</p>
                                  <p><strong>Anticipated Completion Date:</strong> ${entry.anticipatedCompletionDate || 'N/A'}</p>
                                  <p><strong>Projected Demobilization Date:</strong> ${entry.projectedDemobilizationDate || 'N/A'}</p>
                                  <p><strong>Estimated Costs to Date:</strong> ${entry.estimatedCostsToDate || 'N/A'}</p>
                                  <p><strong>Projected Final Costs:</strong> ${entry.projectedFinalCosts || 'N/A'}</p>
                                  <p><strong>General Comments:</strong> ${entry.generalComments || 'N/A'}</p>
                                </div>
                              `).join('') : '<div class="empty-note">No additional decision support data entered.</div>'}
                            </div>

                            <div class="section">
                              <h3>7. Resource Commitment Summary</h3>
                              ${resourceData.length > 0 ? resourceData.map((entry, index) => `
                                <div class="form-data">
                                  ${resourceData.length > 1 ? `<h4>Summary #${index + 1}</h4>` : ''}
                                  <div style="display:grid; grid-template-columns:repeat(6,1fr); gap:8px; margin:10px 0; text-align:center;">
                                    <div style="padding:6px; border:1px solid #06b6d4; border-radius:4px; background:#ecfeff;"><div style="font-size:10px; color:#0891b2;"># Agencies</div><div style="font-size:16px; font-weight:bold;">${entry.totalAgencies || 0}</div></div>
                                    <div style="padding:6px; border:1px solid #f97316; border-radius:4px; background:#fff7ed;"><div style="font-size:10px; color:#ea580c;">Types</div><div style="font-size:16px; font-weight:bold;">${entry.totalResources}</div></div>
                                    <div style="padding:6px; border:1px solid #eab308; border-radius:4px; background:#fefce8;"><div style="font-size:10px; color:#ca8a04;">Units</div><div style="font-size:16px; font-weight:bold;">${entry.totalUnits || 0}</div></div>
                                    <div style="padding:6px; border:1px solid #3b82f6; border-radius:4px; background:#eff6ff;"><div style="font-size:10px; color:#2563eb;">Have</div><div style="font-size:16px; font-weight:bold;">${entry.totalQuantityAvailable}</div></div>
                                    <div style="padding:6px; border:1px solid #a855f7; border-radius:4px; background:#faf5ff;"><div style="font-size:10px; color:#9333ea;">Need</div><div style="font-size:16px; font-weight:bold;">${entry.totalQuantityNeeded}</div></div>
                                    <div style="padding:6px; border:1px solid #22c55e; border-radius:4px; background:#f0fdf4;"><div style="font-size:10px; color:#16a34a;"># Personnel</div><div style="font-size:16px; font-weight:bold;">${entry.totalPersonnelNeeded}</div></div>
                                  </div>
                                  <table style="width:100%; border-collapse:collapse; font-size:12px;">
                                    ${entry.resources.map(resource => `
                                      <tr style="background:#f5f5f5; border-bottom:2px solid #f97316;">
                                        <td colspan="3" style="padding:8px; font-weight:bold; color:#f97316;">
                                          ${resource.resource_type}
                                          ${resource.vehicle_equipment_make_model ? ` | ${resource.vehicle_equipment_make_model}` : ''}
                                          ${resource.agency ? ` | ${resource.agency}` : ''}
                                        </td>
                                      </tr>
                                      ${(resource.units || []).length > 0 ? `
                                        <tr style="background:#fafafa;"><td style="padding:4px 8px; font-size:11px; font-weight:bold; color:#666; border:1px solid #ddd;">Unit</td><td style="padding:4px 8px; font-size:11px; font-weight:bold; color:#666; border:1px solid #ddd;">Status</td><td style="padding:4px 8px; font-size:11px; font-weight:bold; color:#666; border:1px solid #ddd; text-align:center;"># Personnel</td></tr>
                                        ${(resource.units || []).map(u => `<tr><td style="padding:4px 8px; border:1px solid #ddd;">${u.unit_name}</td><td style="padding:4px 8px; border:1px solid #ddd;">${u.status || 'Available'}</td><td style="padding:4px 8px; border:1px solid #ddd; text-align:center;">${u.personnel_needed || 0}</td></tr>`).join('')}
                                      ` : '<tr><td colspan="3" style="padding:6px 8px; color:#999; font-style:italic; border:1px solid #ddd;">No individual units</td></tr>'}
                                    `).join('')}
                                  </table>
                                </div>
                              `).join('') : '<div class="empty-note">No resource commitment summary data available.</div>'}
                            </div>

                            <div class="footer">
                              <div><strong>ICS 209 - Incident Status Summary Report</strong></div>
                              <div>Comprehensive status summary including all Status Summary submenu forms</div>
                              <div style="margin-top: 10px;">Powered by EXRSIM - Emergency Response Simulation Platform</div>
                            </div>
                          </body>
                        </html>
                      `;
                      writeSanitizedHTML(printWindow.document, printContent);
                      printWindow.focus();
                      setTimeout(() => {
                        printWindow.print();
                        printWindow.close();
                      }, 250);
                    }}
                    className="bg-blue-500 hover:bg-blue-600 text-white"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Print ICS 209
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Exercise Name and Report Number */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-white text-sm font-medium mb-2 block">Exercise Name</label>
                    <Input
                      type="text"
                      value={currentExercise?.exercise_name || ''}
                      readOnly
                      className="bg-gray-800 border-gray-600 text-white opacity-80"
                    />
                  </div>
                  <div>
                    <label className="text-white text-sm font-medium mb-2 block">Report Number</label>
                    <Input
                      type="number"
                      value={currentStatusSummary.reportNumber}
                      onChange={(e) => setCurrentStatusSummary(prev => ({...prev, reportNumber: parseInt(e.target.value) || 1}))}
                      placeholder="Report sequence number"
                      className="bg-gray-800 border-gray-600 text-white"
                      min="1"
                    />
                  </div>
                </div>

                {/* Report Version */}
                <div>
                  <label className="text-white text-sm font-medium mb-3 block">Report Version *</label>
                  <div className="flex gap-4">
                    {['Initial', 'Update', 'Final'].map(version => (
                      <label key={version} className="flex items-center text-white cursor-pointer">
                        <input
                          type="radio"
                          value={version}
                          checked={currentStatusSummary.reportVersion === version}
                          onChange={(e) => setCurrentStatusSummary(prev => ({...prev, reportVersion: e.target.value}))}
                          className="mr-2 text-orange-500 focus:ring-orange-500"
                        />
                        {version}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Incident Commander and Organization */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-white text-sm font-medium mb-2 block">Incident Cmdr Name *</label>
                    <Input
                      type="text"
                      value={currentStatusSummary.incidentCmdrName}
                      onChange={(e) => setCurrentStatusSummary(prev => ({...prev, incidentCmdrName: e.target.value}))}
                      placeholder="Enter incident commander name"
                      className="bg-gray-800 border-gray-600 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-white text-sm font-medium mb-2 block">Incident Management Organization</label>
                    <Input
                      type="text"
                      value={currentStatusSummary.incidentMgmtOrg}
                      onChange={(e) => setCurrentStatusSummary(prev => ({...prev, incidentMgmtOrg: e.target.value}))}
                      placeholder="Enter managing organization"
                      className="bg-gray-800 border-gray-600 text-white"
                    />
                  </div>
                </div>

                {/* Area Size, Percent Contained, Complexity Level */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-white text-sm font-medium mb-2 block">Incident Area Size</label>
                    <Input
                      type="text"
                      value={currentStatusSummary.incidentAreaSize}
                      onChange={(e) => setCurrentStatusSummary(prev => ({...prev, incidentAreaSize: e.target.value}))}
                      placeholder="eg: Sq Kms or city block"
                      className="bg-gray-800 border-gray-600 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-white text-sm font-medium mb-2 block">Percent Contained</label>
                    <Input
                      type="number"
                      value={currentStatusSummary.percentContained}
                      onChange={(e) => setCurrentStatusSummary(prev => ({...prev, percentContained: e.target.value}))}
                      placeholder="0-100"
                      className="bg-gray-800 border-gray-600 text-white"
                      min="0"
                      max="100"
                    />
                  </div>
                  <div>
                    <label className="text-white text-sm font-medium mb-2 block">Incident Complexity Level</label>
                    <select
                      value={currentStatusSummary.complexityLevel}
                      onChange={(e) => setCurrentStatusSummary(prev => ({...prev, complexityLevel: e.target.value}))}
                      className="w-full p-3 bg-gray-800 border border-gray-600 rounded text-white"
                    >
                      <option value="Light">Light</option>
                      <option value="Average">Average</option>
                      <option value="Difficult">Difficult</option>
                      <option value="Extreme">Extreme</option>
                      <option value="Complex">Complex</option>
                    </select>
                  </div>
                </div>

                {/* Incident Definition */}
                <div>
                  <label className="text-white text-sm font-medium mb-2 block">Incident Definition</label>
                  <textarea
                    value={currentStatusSummary.incidentDefinition}
                    onChange={(e) => setCurrentStatusSummary(prev => ({...prev, incidentDefinition: e.target.value}))}
                    placeholder="Describe the incident type and nature"
                    rows={3}
                    className="w-full p-3 bg-gray-800 border border-gray-600 rounded text-white placeholder-gray-400"
                  />
                </div>

                {/* Dates and Times */}
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <h4 className="text-lg font-semibold text-white">Timing Information</h4>
                    </div>
                    <div className="md:col-span-2">
                      <h4 className="text-lg font-semibold text-white">For Time Period</h4>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-white text-sm font-medium mb-2 block">Incident Start Date/Time (24 hour)</label>
                      <div className="flex gap-2">
                        <Input
                          type="date"
                          value={currentStatusSummary.startDate}
                          onChange={(e) => setCurrentStatusSummary(prev => ({...prev, startDate: e.target.value}))}
                          className="bg-gray-800 border-gray-600 text-white flex-1"
                        />
                        <Input
                          type="text"
                          placeholder="HH:MM"
                          maxLength="5"
                          value={currentStatusSummary.startTime}
                          onChange={(e) => {
                            let v = e.target.value.replace(/[^0-9:]/g, '');
                            if (v.length === 2 && !v.includes(':')) v = v + ':';
                            if (v.length <= 5) setCurrentStatusSummary(prev => ({...prev, startTime: v}));
                          }}
                          className="bg-gray-800 border-gray-600 text-white w-24"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-white text-sm font-medium mb-2 block">From Date/Time (24 hour)</label>
                      <div className="flex gap-2">
                        <Input
                          type="date"
                          value={currentStatusSummary.fromDate}
                          onChange={(e) => setCurrentStatusSummary(prev => ({...prev, fromDate: e.target.value}))}
                          className="bg-gray-800 border-gray-600 text-white flex-1"
                        />
                        <Input
                          type="text"
                          placeholder="HH:MM"
                          maxLength="5"
                          value={currentStatusSummary.fromTime}
                          onChange={(e) => {
                            let v = e.target.value.replace(/[^0-9:]/g, '');
                            if (v.length === 2 && !v.includes(':')) v = v + ':';
                            if (v.length <= 5) setCurrentStatusSummary(prev => ({...prev, fromTime: v}));
                          }}
                          className="bg-gray-800 border-gray-600 text-white w-24"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-white text-sm font-medium mb-2 block">To Date/Time (24 hour)</label>
                      <div className="flex gap-2">
                        <Input
                          type="date"
                          value={currentStatusSummary.toDate}
                          onChange={(e) => setCurrentStatusSummary(prev => ({...prev, toDate: e.target.value}))}
                          className="bg-gray-800 border-gray-600 text-white flex-1"
                        />
                        <Input
                          type="text"
                          placeholder="HH:MM"
                          maxLength="5"
                          value={currentStatusSummary.toTime}
                          onChange={(e) => {
                            let v = e.target.value.replace(/[^0-9:]/g, '');
                            if (v.length === 2 && !v.includes(':')) v = v + ':';
                            if (v.length <= 5) setCurrentStatusSummary(prev => ({...prev, toTime: v}));
                          }}
                          className="bg-gray-800 border-gray-600 text-white w-24"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button onClick={() => addStatusSummary(currentExercise?.id, { emergencyPhase })} className="bg-orange-500 hover:bg-orange-600 text-white">
                    <Plus className="h-4 w-4 mr-2" />
                    {editingStatusSummary ? 'Update Status Summary' : 'Add Status Summary'}
                  </Button>
                  {editingStatusSummary && (
                    <Button onClick={cancelEditStatusSummary} variant="outline" className="border-gray-500 text-gray-300">
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Display Status Summaries */}
            <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-orange-500">Status Summaries ({statusSummaries.length})</CardTitle>
                {statusSummaries.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => setExpandedStatusSummaries(new Set(statusSummaries.map(s => s.id)))}
                      variant="outline"
                      size="sm"
                      className="border-gray-600 text-gray-300 hover:text-orange-400 hover:border-orange-500/50"
                      data-testid="status-summary-expand-all"
                    >
                      Expand All
                    </Button>
                    <Button
                      onClick={() => setExpandedStatusSummaries(new Set())}
                      variant="outline"
                      size="sm"
                      className="border-gray-600 text-gray-300 hover:text-orange-400 hover:border-orange-500/50"
                      data-testid="status-summary-collapse-all"
                    >
                      Collapse All
                    </Button>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {statusSummaries.map(summary => {
                    const isExpanded = expandedStatusSummaries.has(summary.id);
                    return (
                    <div key={summary.id} className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                      <div className="flex items-start gap-3 mb-4">
                        <button
                          type="button"
                          onClick={() => toggleStatusSummaryExpanded(summary.id)}
                          className="mt-1 p-1 rounded hover:bg-gray-700 text-gray-400 hover:text-orange-400 transition-colors"
                          aria-label={isExpanded ? 'Collapse status summary' : 'Expand status summary'}
                          data-testid={`status-summary-toggle-${summary.id}`}
                        >
                          {isExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <div className="text-white font-semibold flex items-center flex-wrap gap-2">
                            <span>{summary.reportVersion || 'Report'} #{summary.reportNumber || '—'}</span>
                            {summary.emergencyPhase && (
                              <span
                                data-testid={`status-summary-phase-${summary.id}`}
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${
                                  summary.emergencyPhase === 'Prevention' ? 'bg-green-500/20 text-green-300 border-green-500/40' :
                                  summary.emergencyPhase === 'Mitigation' ? 'bg-amber-500/20 text-amber-300 border-amber-500/40' :
                                  summary.emergencyPhase === 'Preparedness' ? 'bg-orange-500/20 text-orange-300 border-orange-500/40' :
                                  summary.emergencyPhase === 'Response' ? 'bg-red-900/40 text-red-200 border-red-800/60' :
                                  summary.emergencyPhase === 'Recovery' ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/40' :
                                  'bg-gray-700/60 text-gray-200 border-gray-600'
                                }`}
                              >
                                Phase: {summary.emergencyPhase}
                              </span>
                            )}
                            {summary.incidentCmdrName && (
                              <span className="text-gray-400 font-normal">— IC: {summary.incidentCmdrName}</span>
                            )}
                          </div>
                          {!isExpanded && (
                            <div className="text-gray-400 text-sm mt-1">
                              {summary.complexityLevel || ''}{summary.percentContained ? ` · ${summary.percentContained}% contained` : ''}
                            </div>
                          )}
                        </div>
                      </div>

                      {isExpanded && (
                      <>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div>
                          <div className="text-orange-500 font-semibold mb-1">Report Info</div>
                          <div className="text-white">Version: {summary.reportVersion} #{summary.reportNumber}</div>
                        </div>
                        <div>
                          <div className="text-orange-500 font-semibold mb-1">Incident Commander</div>
                          <div className="text-white">{summary.incidentCmdrName}</div>
                        </div>
                        <div>
                          <div className="text-orange-500 font-semibold mb-1">Management Org</div>
                          <div className="text-white">{summary.incidentMgmtOrg || 'Not specified'}</div>
                        </div>
                        <div>
                          <div className="text-orange-500 font-semibold mb-1">Area Size</div>
                          <div className="text-white">{summary.incidentAreaSize || 'Not specified'}</div>
                        </div>
                        <div>
                          <div className="text-orange-500 font-semibold mb-1">Contained / Completed</div>
                          <div className="text-white">{summary.percentContained}% / {summary.completed || 'Ongoing'}</div>
                        </div>
                        <div>
                          <div className="text-orange-500 font-semibold mb-1">Complexity</div>
                          <div className="text-white">{summary.complexityLevel}</div>
                        </div>
                      </div>
                      
                      {summary.incidentDefinition && (
                        <div className="mt-4">
                          <div className="text-orange-500 font-semibold mb-1">Incident Definition</div>
                          <div className="text-white">{summary.incidentDefinition}</div>
                        </div>
                      )}

                      <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        <div>
                          <div className="text-orange-500 font-semibold mb-1">Start</div>
                          <div className="text-white">{summary.startDate} {summary.startTime}</div>
                        </div>
                        <div>
                          <div className="text-orange-500 font-semibold mb-1">From</div>
                          <div className="text-white">{summary.fromDate} {summary.fromTime}</div>
                        </div>
                        <div>
                          <div className="text-orange-500 font-semibold mb-1">To</div>
                          <div className="text-white">{summary.toDate} {summary.toTime}</div>
                        </div>
                      </div>
                      </>
                      )}

                      <div className="flex gap-2 mt-6">
                        <Button
                          onClick={() => editStatusSummary(summary)}
                          variant="ghost"
                          size="sm"
                          className="text-blue-400 hover:text-blue-300"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => {
                            const printWindow = window.open('', '_blank');
                            const printContent = `
                              <html>
                                <head>
                                  <title>Status Summary Report #${summary.reportNumber}</title>
                                  <style>
                                    body { font-family: Arial, sans-serif; margin: 20px; color: #333; line-height: 1.6; }
                                    .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #f97316; padding-bottom: 15px; }
                                    .header h1 { color: #f97316; margin: 0; font-size: 28px; }
                                    .section { margin-bottom: 25px; }
                                    .field-group { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 15px; }
                                    .field { margin-bottom: 10px; }
                                    .field-label { font-weight: bold; color: #f97316; margin-bottom: 3px; }
                                    .field-value { color: #333; }
                                    .timing-section { background-color: #f9f9f9; padding: 15px; border-radius: 5px; }
                                    .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; border-top: 1px solid #ddd; padding-top: 10px; }
                                  </style>
                                </head>
                                <body>
                                  <div class="header">
                                    <h1>Status Summary Report</h1>
                                    <p><strong>Report:</strong> ${summary.reportVersion} #${summary.reportNumber} | <strong>Generated:</strong> ${new Date().toLocaleDateString()} at ${format24HourTime()}</p>
                                  </div>
                                  
                                  <div class="section">
                                    <h3 style="color: #f97316; border-bottom: 2px solid #f97316; padding-bottom: 5px;">Incident Information</h3>
                                    <div class="field-group">
                                      <div class="field">
                                        <div class="field-label">Incident Commander:</div>
                                        <div class="field-value">${summary.incidentCmdrName}</div>
                                      </div>
                                      <div class="field">
                                        <div class="field-label">Management Organization:</div>
                                        <div class="field-value">${summary.incidentMgmtOrg || 'Not specified'}</div>
                                      </div>
                                      <div class="field">
                                        <div class="field-label">Incident Area Size:</div>
                                        <div class="field-value">${summary.incidentAreaSize || 'Not specified'}</div>
                                      </div>
                                      <div class="field">
                                        <div class="field-label">Complexity Level:</div>
                                        <div class="field-value">${summary.complexityLevel}</div>
                                      </div>
                                    </div>
                                  </div>

                                  <div class="section">
                                    <h3 style="color: #f97316; border-bottom: 2px solid #f97316; padding-bottom: 5px;">Status Information</h3>
                                    <div class="field-group">
                                      <div class="field">
                                        <div class="field-label">Percent Contained:</div>
                                        <div class="field-value">${summary.percentContained}%</div>
                                      </div>
                                      <div class="field">
                                        <div class="field-label">Completion Status:</div>
                                        <div class="field-value">${summary.completed || 'Ongoing'}</div>
                                      </div>
                                    </div>
                                    ${summary.incidentDefinition ? `
                                      <div class="field">
                                        <div class="field-label">Incident Definition:</div>
                                        <div class="field-value">${summary.incidentDefinition}</div>
                                      </div>
                                    ` : ''}
                                  </div>

                                  <div class="section timing-section">
                                    <h3 style="color: #f97316; border-bottom: 2px solid #f97316; padding-bottom: 5px;">Timing Information</h3>
                                    <div class="field-group">
                                      <div class="field">
                                        <div class="field-label">Incident Start:</div>
                                        <div class="field-value">${summary.startDate} ${summary.startTime}</div>
                                      </div>
                                    </div>
                                    <div class="field-group">
                                      <div class="field">
                                        <div class="field-label">From:</div>
                                        <div class="field-value">${summary.fromDate} ${summary.fromTime}</div>
                                      </div>
                                      <div class="field">
                                        <div class="field-label">To:</div>
                                        <div class="field-value">${summary.toDate} ${summary.toTime}</div>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  <div class="footer">
                                    <div><strong>Status Summary Report #${summary.reportNumber}</strong> - ${summary.reportVersion}</div>
                                    <div>Incident Commander: ${summary.incidentCmdrName} | Complexity: ${summary.complexityLevel}</div>
                                    <div style="margin-top: 10px;">Powered by EXRSIM - Emergency Response Simulation Platform</div>
                                  </div>
                                </body>
                              </html>
                            `;
                            writeSanitizedHTML(printWindow.document, printContent);
                            printWindow.focus();
                            setTimeout(() => {
                              printWindow.print();
                              printWindow.close();
                            }, 250);
                          }}
                          variant="ghost"
                          size="sm"
                          className="text-green-400 hover:text-green-300"
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => deleteStatusSummary(summary.id)}
                          variant="ghost"
                          size="sm"
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    );
                  })}
                  {statusSummaries.length === 0 && (
                    <div className="text-center py-8 text-gray-400">
                      No status summaries created yet. Add a status summary above.
                    </div>
                  )}
                </div>

                {statusSummaries.length > 0 && (
                  <div className="mt-6 pt-4 border-t border-gray-700">
                    <Button 
                      onClick={() => {
                        const printWindow = window.open('', '_blank');
                        const printContent = `
                          <html>
                            <head>
                              <title>All Status Summaries Report</title>
                              <style>
                                body { font-family: Arial, sans-serif; margin: 20px; color: #333; line-height: 1.6; }
                                .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #f97316; padding-bottom: 15px; }
                                .header h1 { color: #f97316; margin: 0; font-size: 28px; }
                                .summary-item { margin-bottom: 30px; padding: 20px; border: 1px solid #ddd; border-radius: 8px; page-break-inside: avoid; }
                                .field-group { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 15px; }
                                .field { margin-bottom: 10px; }
                                .field-label { font-weight: bold; color: #f97316; margin-bottom: 3px; }
                                .field-value { color: #333; }
                                .timing-section { background-color: #f9f9f9; padding: 15px; border-radius: 5px; margin-top: 15px; }
                                .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; border-top: 1px solid #ddd; padding-top: 10px; }
                              </style>
                            </head>
                            <body>
                              <div class="header">
                                <h1>Status Summaries Report</h1>
                                <p><strong>Generated:</strong> ${new Date().toLocaleDateString()} at ${format24HourTime()} | <strong>Total Reports:</strong> ${statusSummaries.length}</p>
                              </div>
                              
                              ${statusSummaries.map((summary, index) => `
                                <div class="summary-item">
                                  <h3 style="color: #f97316; margin-top: 0;">Report #${summary.reportNumber} - ${summary.reportVersion}</h3>
                                  <div class="field-group">
                                    <div class="field">
                                      <div class="field-label">Incident Commander:</div>
                                      <div class="field-value">${summary.incidentCmdrName}</div>
                                    </div>
                                    <div class="field">
                                      <div class="field-label">Management Organization:</div>
                                      <div class="field-value">${summary.incidentMgmtOrg || 'Not specified'}</div>
                                    </div>
                                    <div class="field">
                                      <div class="field-label">Area Size:</div>
                                      <div class="field-value">${summary.incidentAreaSize || 'Not specified'}</div>
                                    </div>
                                    <div class="field">
                                      <div class="field-label">Complexity Level:</div>
                                      <div class="field-value">${summary.complexityLevel}</div>
                                    </div>
                                    <div class="field">
                                      <div class="field-label">Percent Contained:</div>
                                      <div class="field-value">${summary.percentContained}%</div>
                                    </div>
                                    <div class="field">
                                      <div class="field-label">Completion Status:</div>
                                      <div class="field-value">${summary.completed || 'Ongoing'}</div>
                                    </div>
                                  </div>
                                  ${summary.incidentDefinition ? `
                                    <div class="field">
                                      <div class="field-label">Incident Definition:</div>
                                      <div class="field-value">${summary.incidentDefinition}</div>
                                    </div>
                                  ` : ''}
                                  <div class="timing-section">
                                    <div class="field-group">
                                      <div class="field">
                                        <div class="field-label">Incident Start:</div>
                                        <div class="field-value">${summary.startDate} ${summary.startTime}</div>
                                      </div>
                                    </div>
                                    <div class="field-group">
                                      <div class="field">
                                        <div class="field-label">From:</div>
                                        <div class="field-value">${summary.fromDate} ${summary.fromTime}</div>
                                      </div>
                                      <div class="field">
                                        <div class="field-label">To:</div>
                                        <div class="field-value">${summary.toDate} ${summary.toTime}</div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              `).join('')}
                              
                              <div class="footer">
                                <div><strong>Status Summaries Report</strong> - Total: ${statusSummaries.length} entries</div>
                                <div>Powered by EXRSIM - Emergency Response Simulation Platform</div>
                              </div>
                            </body>
                          </html>
                        `;
                        writeSanitizedHTML(printWindow.document, printContent);
                        printWindow.focus();
                        setTimeout(() => {
                          printWindow.print();
                          printWindow.close();
                        }, 250);
                      }}
                      variant="outline" 
                      className="border-orange-500 text-orange-500 hover:bg-orange-500/10"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Print All Status Summaries
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        );
      case 'approve-route':
        return (
          <ApprovalRouteForm
            theme={theme}
            positions={positions}
            approvalRoutes={approvalRoutes}
            currentApprovalRoute={currentApprovalRoute}
            setCurrentApprovalRoute={setCurrentApprovalRoute}
            editingApprovalRoute={editingApprovalRoute}
            addApprovalRoute={addApprovalRoute}
            editApprovalRoute={editApprovalRoute}
            cancelEditApprovalRoute={cancelEditApprovalRoute}
            deleteApprovalRoute={deleteApprovalRoute}
            format24HourTime={format24HourTime}
          />
        );
      case 'location-information':
        return (
          <LocationInformationForm
            theme={theme}
            incidentLocations={incidentLocations}
            currentIncidentLocation={currentIncidentLocation}
            setCurrentIncidentLocation={setCurrentIncidentLocation}
            editingIncidentLocation={editingIncidentLocation}
            locationErrors={locationErrors}
            setLocationErrors={setLocationErrors}
            addIncidentLocation={addIncidentLocation}
            editIncidentLocation={editIncidentLocation}
            cancelEditIncidentLocation={cancelEditIncidentLocation}
            deleteIncidentLocation={deleteIncidentLocation}
          />
        );
      case 'incident-summary':
        return (
          <div className="p-6 space-y-6 pb-20">
            {/* Incident Summary Header */}
            <div className="text-center mb-8">
              <h1 className={`text-3xl font-bold ${theme.colors.textPrimary} mb-2`}>Incident Summary</h1>
              <p className={`${theme.colors.textMuted} text-lg`}>
                Comprehensive incident overview, significant events, and damage assessment reporting
              </p>
            </div>

            {/* Incident Summary Form */}
            <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
              <CardHeader>
                <CardTitle className="text-orange-500">Incident Summary Report</CardTitle>
                <CardDescription className={`${theme.colors.textMuted}`}>
                  Document significant events, materials involved, and comprehensive damage assessment information
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Significant Events Section */}
                <div>
                  <label className="text-white text-sm font-medium mb-2 block">
                    Significant Events for this Time Period *
                  </label>
                  <textarea
                    value={currentIncidentSummary.significantEvents}
                    onChange={(e) => setCurrentIncidentSummary(prev => ({...prev, significantEvents: e.target.value}))}
                    placeholder="Describe the significant events that occurred during this operational period..."
                    rows={4}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-md focus:outline-none focus:border-orange-500"
                  />
                </div>

                {/* Primary Materials Section */}
                <div>
                  <label className="text-white text-sm font-medium mb-2 block">
                    Primary Materials or Hazards Involved
                  </label>
                  <textarea
                    value={currentIncidentSummary.primaryMaterials}
                    onChange={(e) => setCurrentIncidentSummary(prev => ({...prev, primaryMaterials: e.target.value}))}
                    placeholder="List the primary materials, hazardous substances, or hazards involved in this incident..."
                    rows={3}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-md focus:outline-none focus:border-orange-500"
                  />
                </div>

                {/* Damage Assessment Grid Section */}
                <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <AlertTriangle className="h-5 w-5 mr-2 text-orange-500" />
                    Damage Assessment Information
                  </h3>
                  
                  {/* Grid Header */}
                  <div className="grid grid-cols-4 gap-4 mb-4">
                    <div className="text-sm font-medium text-orange-400">Structural Summary</div>
                    <div className="text-sm font-medium text-orange-400 text-center"># Threatened - 72 hrs</div>
                    <div className="text-sm font-medium text-orange-400 text-center"># Damaged</div>
                    <div className="text-sm font-medium text-orange-400 text-center"># Destroyed</div>
                  </div>

                  {/* Single Residence Row */}
                  <div className="grid grid-cols-4 gap-4 mb-3 items-center">
                    <div className="text-white font-medium">Single Residence</div>
                    <input
                      type="number"
                      min="0"
                      value={currentIncidentSummary.damageAssessment.singleResidence.threatened}
                      onChange={(e) => updateDamageAssessment('singleResidence', 'threatened', e.target.value)}
                      placeholder="0"
                      className="px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-md focus:outline-none focus:border-orange-500 text-center"
                    />
                    <input
                      type="number"
                      min="0"
                      value={currentIncidentSummary.damageAssessment.singleResidence.damaged}
                      onChange={(e) => updateDamageAssessment('singleResidence', 'damaged', e.target.value)}
                      placeholder="0"
                      className="px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-md focus:outline-none focus:border-orange-500 text-center"
                    />
                    <input
                      type="number"
                      min="0"
                      value={currentIncidentSummary.damageAssessment.singleResidence.destroyed}
                      onChange={(e) => updateDamageAssessment('singleResidence', 'destroyed', e.target.value)}
                      placeholder="0"
                      className="px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-md focus:outline-none focus:border-orange-500 text-center"
                    />
                  </div>

                  {/* Nonresidential/Commercial Row */}
                  <div className="grid grid-cols-4 gap-4 mb-3 items-center">
                    <div className="text-white font-medium">Nonresidential/ Commercial</div>
                    <input
                      type="number"
                      min="0"
                      value={currentIncidentSummary.damageAssessment.nonResidentialCommercial.threatened}
                      onChange={(e) => updateDamageAssessment('nonResidentialCommercial', 'threatened', e.target.value)}
                      placeholder="0"
                      className="px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-md focus:outline-none focus:border-orange-500 text-center"
                    />
                    <input
                      type="number"
                      min="0"
                      value={currentIncidentSummary.damageAssessment.nonResidentialCommercial.damaged}
                      onChange={(e) => updateDamageAssessment('nonResidentialCommercial', 'damaged', e.target.value)}
                      placeholder="0"
                      className="px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-md focus:outline-none focus:border-orange-500 text-center"
                    />
                    <input
                      type="number"
                      min="0"
                      value={currentIncidentSummary.damageAssessment.nonResidentialCommercial.destroyed}
                      onChange={(e) => updateDamageAssessment('nonResidentialCommercial', 'destroyed', e.target.value)}
                      placeholder="0"
                      className="px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-md focus:outline-none focus:border-orange-500 text-center"
                    />
                  </div>

                  {/* Other Minor Structures Row */}
                  <div className="grid grid-cols-4 gap-4 mb-3 items-center">
                    <div className="text-white font-medium">Other Minor Structures</div>
                    <input
                      type="number"
                      min="0"
                      value={currentIncidentSummary.damageAssessment.otherMinorStructures.threatened}
                      onChange={(e) => updateDamageAssessment('otherMinorStructures', 'threatened', e.target.value)}
                      placeholder="0"
                      className="px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-md focus:outline-none focus:border-orange-500 text-center"
                    />
                    <input
                      type="number"
                      min="0"
                      value={currentIncidentSummary.damageAssessment.otherMinorStructures.damaged}
                      onChange={(e) => updateDamageAssessment('otherMinorStructures', 'damaged', e.target.value)}
                      placeholder="0"
                      className="px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-md focus:outline-none focus:border-orange-500 text-center"
                    />
                    <input
                      type="number"
                      min="0"
                      value={currentIncidentSummary.damageAssessment.otherMinorStructures.destroyed}
                      onChange={(e) => updateDamageAssessment('otherMinorStructures', 'destroyed', e.target.value)}
                      placeholder="0"
                      className="px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-md focus:outline-none focus:border-orange-500 text-center"
                    />
                  </div>

                  {/* Other Row */}
                  <div className="grid grid-cols-4 gap-4 items-center">
                    <div className="text-white font-medium">Other</div>
                    <input
                      type="number"
                      min="0"
                      value={currentIncidentSummary.damageAssessment.other.threatened}
                      onChange={(e) => updateDamageAssessment('other', 'threatened', e.target.value)}
                      placeholder="0"
                      className="px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-md focus:outline-none focus:border-orange-500 text-center"
                    />
                    <input
                      type="number"
                      min="0"
                      value={currentIncidentSummary.damageAssessment.other.damaged}
                      onChange={(e) => updateDamageAssessment('other', 'damaged', e.target.value)}
                      placeholder="0"
                      className="px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-md focus:outline-none focus:border-orange-500 text-center"
                    />
                    <input
                      type="number"
                      min="0"
                      value={currentIncidentSummary.damageAssessment.other.destroyed}
                      onChange={(e) => updateDamageAssessment('other', 'destroyed', e.target.value)}
                      placeholder="0"
                      className="px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-md focus:outline-none focus:border-orange-500 text-center"
                    />
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button onClick={addIncidentSummary} className="bg-orange-500 hover:bg-orange-600 text-white">
                    <Plus className="h-4 w-4 mr-2" />
                    {editingIncidentSummary ? 'Update Summary' : 'Add Summary'}
                  </Button>
                  {editingIncidentSummary && (
                    <Button onClick={cancelEditIncidentSummary} variant="outline" className="border-gray-500 text-gray-300">
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Display Incident Summaries */}
            <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
              <CardHeader>
                <CardTitle className="text-orange-500">Incident Summary Records ({incidentSummaries.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {incidentSummaries.map(summary => (
                    <div key={summary.id} className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                      {/* Significant Events */}
                      <div className="mb-6">
                        <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
                          <FileText className="h-4 w-4 mr-2 text-orange-500" />
                          Significant Events
                        </h4>
                        <p className="text-gray-300 text-sm whitespace-pre-wrap">{summary.significantEvents}</p>
                      </div>

                      {/* Primary Materials */}
                      {summary.primaryMaterials && (
                        <div className="mb-6">
                          <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
                            <AlertTriangle className="h-4 w-4 mr-2 text-orange-500" />
                            Primary Materials or Hazards
                          </h4>
                          <p className="text-gray-300 text-sm whitespace-pre-wrap">{summary.primaryMaterials}</p>
                        </div>
                      )}

                      {/* Damage Assessment Grid Display */}
                      <div className="mb-6">
                        <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
                          <Building className="h-4 w-4 mr-2 text-orange-500" />
                          Damage Assessment Information
                        </h4>
                        <div className="bg-gray-700/50 rounded-lg p-4">
                          {/* Grid Header */}
                          <div className="grid grid-cols-4 gap-4 mb-3 text-sm font-medium text-orange-400">
                            <div>Structural Summary</div>
                            <div className="text-center"># Threatened - 72 hrs</div>
                            <div className="text-center"># Damaged</div>
                            <div className="text-center"># Destroyed</div>
                          </div>
                          
                          {/* Data Rows */}
                          {[
                            { label: 'Single Residence', data: summary.damageAssessment.singleResidence },
                            { label: 'Nonresidential/ Commercial', data: summary.damageAssessment.nonResidentialCommercial },
                            { label: 'Other Minor Structures', data: summary.damageAssessment.otherMinorStructures },
                            { label: 'Other', data: summary.damageAssessment.other }
                          ].map(row => (
                            <div key={row.label} className="grid grid-cols-4 gap-4 mb-2 text-sm text-gray-300">
                              <div className="font-medium text-white">{row.label}</div>
                              <div className="text-center">{row.data.threatened || '0'}</div>
                              <div className="text-center">{row.data.damaged || '0'}</div>
                              <div className="text-center">{row.data.destroyed || '0'}</div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex justify-end space-x-2">
                        <Button
                          onClick={() => editIncidentSummary(summary)}
                          size="sm"
                          variant="ghost"
                          className="text-blue-400 hover:text-blue-300"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => {
                            const printWindow = window.open('', '_blank');
                            const totalThreatened = Object.values(summary.damageAssessment).reduce((sum, cat) => sum + (parseInt(cat.threatened) || 0), 0);
                            const totalDamaged = Object.values(summary.damageAssessment).reduce((sum, cat) => sum + (parseInt(cat.damaged) || 0), 0);
                            const totalDestroyed = Object.values(summary.damageAssessment).reduce((sum, cat) => sum + (parseInt(cat.destroyed) || 0), 0);
                            
                            const printContent = `
                              <html>
                                <head>
                                  <title>Incident Summary Report</title>
                                  <style>
                                    body { font-family: Arial, sans-serif; margin: 20px; color: #333; line-height: 1.6; }
                                    .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #f97316; padding-bottom: 15px; }
                                    .header h1 { color: #f97316; margin: 0; font-size: 28px; }
                                    .section { margin-bottom: 25px; padding: 20px; border: 1px solid #ddd; border-radius: 8px; }
                                    .section h3 { color: #f97316; margin-top: 0; border-bottom: 2px solid #f97316; padding-bottom: 5px; }
                                    .field { margin-bottom: 15px; }
                                    .field-label { font-weight: bold; color: #f97316; margin-bottom: 5px; }
                                    .field-value { color: #333; white-space: pre-wrap; }
                                    .damage-grid { width: 100%; border-collapse: collapse; margin-top: 10px; }
                                    .damage-grid th, .damage-grid td { border: 1px solid #ddd; padding: 8px; text-align: center; }
                                    .damage-grid th { background-color: #f97316; color: white; font-weight: bold; }
                                    .damage-grid td:first-child { text-align: left; font-weight: bold; }
                                    .summary-row { background-color: #f5f5f5; font-weight: bold; }
                                    .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; border-top: 1px solid #ddd; padding-top: 10px; }
                                  </style>
                                </head>
                                <body>
                                  <div class="header">
                                    <h1>Incident Summary Report</h1>
                                    <p><strong>Report ID:</strong> ${summary.id} | <strong>Generated:</strong> ${new Date().toLocaleDateString()} at ${format24HourTime()}</p>
                                  </div>
                                  
                                  <div class="section">
                                    <h3>Significant Events for this Time Period</h3>
                                    <div class="field-value">${summary.significantEvents}</div>
                                  </div>

                                  ${summary.primaryMaterials ? `
                                  <div class="section">
                                    <h3>Primary Materials or Hazards Involved</h3>
                                    <div class="field-value">${summary.primaryMaterials}</div>
                                  </div>
                                  ` : ''}

                                  <div class="section">
                                    <h3>Damage Assessment Information</h3>
                                    <table class="damage-grid">
                                      <thead>
                                        <tr>
                                          <th>Structural Summary</th>
                                          <th># Threatened - 72 hrs</th>
                                          <th># Damaged</th>
                                          <th># Destroyed</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        <tr>
                                          <td>Single Residence</td>
                                          <td>${summary.damageAssessment.singleResidence.threatened || '0'}</td>
                                          <td>${summary.damageAssessment.singleResidence.damaged || '0'}</td>
                                          <td>${summary.damageAssessment.singleResidence.destroyed || '0'}</td>
                                        </tr>
                                        <tr>
                                          <td>Nonresidential/ Commercial</td>
                                          <td>${summary.damageAssessment.nonResidentialCommercial.threatened || '0'}</td>
                                          <td>${summary.damageAssessment.nonResidentialCommercial.damaged || '0'}</td>
                                          <td>${summary.damageAssessment.nonResidentialCommercial.destroyed || '0'}</td>
                                        </tr>
                                        <tr>
                                          <td>Other Minor Structures</td>
                                          <td>${summary.damageAssessment.otherMinorStructures.threatened || '0'}</td>
                                          <td>${summary.damageAssessment.otherMinorStructures.damaged || '0'}</td>
                                          <td>${summary.damageAssessment.otherMinorStructures.destroyed || '0'}</td>
                                        </tr>
                                        <tr>
                                          <td>Other</td>
                                          <td>${summary.damageAssessment.other.threatened || '0'}</td>
                                          <td>${summary.damageAssessment.other.damaged || '0'}</td>
                                          <td>${summary.damageAssessment.other.destroyed || '0'}</td>
                                        </tr>
                                        <tr class="summary-row">
                                          <td><strong>TOTAL</strong></td>
                                          <td><strong>${totalThreatened}</strong></td>
                                          <td><strong>${totalDamaged}</strong></td>
                                          <td><strong>${totalDestroyed}</strong></td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>
                                  
                                  <div class="footer">
                                    <div><strong>Incident Summary Report</strong> - Report ID: ${summary.id}</div>
                                    <div>Total Structures Affected: ${totalThreatened + totalDamaged + totalDestroyed} | Generated by ICS Operations</div>
                                    <div style="margin-top: 10px;">Powered by EXRSIM - Emergency Response Simulation Platform</div>
                                  </div>
                                </body>
                              </html>
                            `;
                            writeSanitizedHTML(printWindow.document, printContent);
                            printWindow.focus();
                            setTimeout(() => {
                              printWindow.print();
                              printWindow.close();
                            }, 250);
                          }}
                          size="sm"
                          variant="ghost"
                          className="text-green-400 hover:text-green-300"
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => deleteIncidentSummary(summary.id)}
                          size="sm"
                          variant="ghost"
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {incidentSummaries.length === 0 && (
                    <div className="text-center py-8 text-gray-400">
                      No incident summaries created yet. Add a summary above.
                    </div>
                  )}
                </div>

                {incidentSummaries.length > 0 && (
                  <div className="mt-6 pt-4 border-t border-gray-700">
                    <Button 
                      onClick={() => {
                        const printWindow = window.open('', '_blank');
                        const grandTotalThreatened = incidentSummaries.reduce((sum, summary) => 
                          sum + Object.values(summary.damageAssessment).reduce((catSum, cat) => catSum + (parseInt(cat.threatened) || 0), 0), 0);
                        const grandTotalDamaged = incidentSummaries.reduce((sum, summary) => 
                          sum + Object.values(summary.damageAssessment).reduce((catSum, cat) => catSum + (parseInt(cat.damaged) || 0), 0), 0);
                        const grandTotalDestroyed = incidentSummaries.reduce((sum, summary) => 
                          sum + Object.values(summary.damageAssessment).reduce((catSum, cat) => catSum + (parseInt(cat.destroyed) || 0), 0), 0);
                          
                        const printContent = `
                          <html>
                            <head>
                              <title>All Incident Summaries Report</title>
                              <style>
                                body { font-family: Arial, sans-serif; margin: 20px; color: #333; line-height: 1.6; }
                                .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #f97316; padding-bottom: 15px; }
                                .header h1 { color: #f97316; margin: 0; font-size: 28px; }
                                .summary-item { margin-bottom: 30px; padding: 20px; border: 1px solid #ddd; border-radius: 8px; page-break-inside: avoid; }
                                .section { margin-bottom: 20px; }
                                .section h4 { color: #f97316; margin-bottom: 10px; border-bottom: 1px solid #f97316; padding-bottom: 3px; }
                                .damage-grid { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 12px; }
                                .damage-grid th, .damage-grid td { border: 1px solid #ddd; padding: 6px; text-align: center; }
                                .damage-grid th { background-color: #f97316; color: white; font-weight: bold; }
                                .damage-grid td:first-child { text-align: left; font-weight: bold; }
                                .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; border-top: 1px solid #ddd; padding-top: 10px; }
                              </style>
                            </head>
                            <body>
                              <div class="header">
                                <h1>Incident Summary Report - All Summaries</h1>
                                <p><strong>Generated:</strong> ${new Date().toLocaleDateString()} at ${format24HourTime()} | <strong>Total Summaries:</strong> ${incidentSummaries.length}</p>
                              </div>
                              
                              ${incidentSummaries.map((summary, index) => `
                                <div class="summary-item">
                                  <h3 style="color: #f97316; margin-top: 0;">Incident Summary #${index + 1}</h3>
                                  
                                  <div class="section">
                                    <h4>Significant Events for this Time Period</h4>
                                    <p style="white-space: pre-wrap; margin: 0;">${summary.significantEvents}</p>
                                  </div>

                                  ${summary.primaryMaterials ? `
                                  <div class="section">
                                    <h4>Primary Materials or Hazards Involved</h4>
                                    <p style="white-space: pre-wrap; margin: 0;">${summary.primaryMaterials}</p>
                                  </div>
                                  ` : ''}

                                  <div class="section">
                                    <h4>Damage Assessment Information</h4>
                                    <table class="damage-grid">
                                      <thead>
                                        <tr>
                                          <th>Structural Summary</th>
                                          <th># Threatened - 72 hrs</th>
                                          <th># Damaged</th>
                                          <th># Destroyed</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        <tr><td>Single Residence</td><td>${summary.damageAssessment.singleResidence.threatened || '0'}</td><td>${summary.damageAssessment.singleResidence.damaged || '0'}</td><td>${summary.damageAssessment.singleResidence.destroyed || '0'}</td></tr>
                                        <tr><td>Nonresidential/ Commercial</td><td>${summary.damageAssessment.nonResidentialCommercial.threatened || '0'}</td><td>${summary.damageAssessment.nonResidentialCommercial.damaged || '0'}</td><td>${summary.damageAssessment.nonResidentialCommercial.destroyed || '0'}</td></tr>
                                        <tr><td>Other Minor Structures</td><td>${summary.damageAssessment.otherMinorStructures.threatened || '0'}</td><td>${summary.damageAssessment.otherMinorStructures.damaged || '0'}</td><td>${summary.damageAssessment.otherMinorStructures.destroyed || '0'}</td></tr>
                                        <tr><td>Other</td><td>${summary.damageAssessment.other.threatened || '0'}</td><td>${summary.damageAssessment.other.damaged || '0'}</td><td>${summary.damageAssessment.other.destroyed || '0'}</td></tr>
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              `).join('')}
                              
                              <div style="margin-top: 30px; padding: 20px; border: 2px solid #f97316; border-radius: 8px; background-color: #fff5f5;">
                                <h3 style="color: #f97316; margin-top: 0;">GRAND TOTALS - All Incident Summaries</h3>
                                <p><strong>Total Structures Threatened (72 hrs):</strong> ${grandTotalThreatened}</p>
                                <p><strong>Total Structures Damaged:</strong> ${grandTotalDamaged}</p>
                                <p><strong>Total Structures Destroyed:</strong> ${grandTotalDestroyed}</p>
                                <p><strong>Overall Impact:</strong> ${grandTotalThreatened + grandTotalDamaged + grandTotalDestroyed} structures affected</p>
                              </div>
                              
                              <div class="footer">
                                <div><strong>Comprehensive Incident Summary Report</strong> - Total: ${incidentSummaries.length} summaries</div>
                                <div>Grand Totals: ${grandTotalThreatened} threatened, ${grandTotalDamaged} damaged, ${grandTotalDestroyed} destroyed</div>
                                <div style="margin-top: 10px;">Powered by EXRSIM - Emergency Response Simulation Platform</div>
                              </div>
                            </body>
                          </html>
                        `;
                        writeSanitizedHTML(printWindow.document, printContent);
                        printWindow.focus();
                        setTimeout(() => {
                          printWindow.print();
                          printWindow.close();
                        }, 250);
                      }}
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Print All Summaries
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        );
      case 'decision-support':
        return (
          <div className="p-6 space-y-6 pb-20">
            {/* Decision Support Header */}
            <div className="text-center mb-8">
              <h1 className={`text-3xl font-bold ${theme.colors.textPrimary} mb-2`}>Additional Incident Decision Support Information</h1>
              <p className={`${theme.colors.textMuted} text-lg`}>
                Comprehensive incident decision support data including casualties, threat management, and projected activity
              </p>
            </div>

            {/* Decision Support Form */}
            <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
              <CardHeader>
                <CardTitle className="text-orange-500">Decision Support Data Entry</CardTitle>
                <CardDescription className={`${theme.colors.textMuted}`}>
                  Enter casualty information, threat management status, and incident projections for decision making support
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-8">
                {/* Civilians Grid */}
                <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <Users className="h-5 w-5 mr-2 text-orange-500" />
                    Public Status Summary
                  </h3>
                  
                  {/* Civilians Grid Header */}
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="text-sm font-medium text-orange-400">Civilians</div>
                    <div className="text-sm font-medium text-orange-400 text-center"># During Report Period</div>
                    <div className="text-sm font-medium text-orange-400 text-center"># Total to Date</div>
                  </div>

                  {/* Civilians Data Rows */}
                  {[
                    { key: 'fatalities', label: 'Fatalities' },
                    { key: 'injuriesSick', label: 'Injuries/Sick' },
                    { key: 'trappedNeedRescue', label: 'Trapped/Need Rescue' },
                    { key: 'missing', label: 'Missing' },
                    { key: 'evacuated', label: 'Evacuated' },
                    { key: 'shelteringInPlace', label: 'Sheltering in Place' },
                    { key: 'inTempShelters', label: 'In Temp Shelters' },
                    { key: 'receivedMassImmunizations', label: 'Have Received Mass Immunizations' },
                    { key: 'requireImmunizations', label: 'Require Immunizations' },
                    { key: 'inQuarantine', label: 'In Quarantine' }
                  ].map(row => (
                    <div key={row.key} className="grid grid-cols-3 gap-4 mb-3 items-center">
                      <div className="text-white font-medium">{row.label}</div>
                      <input
                        type="number"
                        min="0"
                        value={currentDecisionSupport.civilians[row.key].reportPeriod}
                        onChange={(e) => updateCiviliansData(row.key, 'reportPeriod', e.target.value)}
                        placeholder="0"
                        className="px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-md focus:outline-none focus:border-orange-500 text-center"
                      />
                      <input
                        type="number"
                        min="0"
                        value={currentDecisionSupport.civilians[row.key].totalToDate}
                        onChange={(e) => updateCiviliansData(row.key, 'totalToDate', e.target.value)}
                        placeholder="0"
                        className="px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-md focus:outline-none focus:border-orange-500 text-center"
                      />
                    </div>
                  ))}

                  {/* Civilians Total Row */}
                  <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-600">
                    <div className="text-orange-400 font-bold">Total</div>
                    <div className="px-3 py-2 bg-orange-500/20 border border-orange-500 text-orange-400 rounded-md text-center font-bold">
                      {calculateTotals(currentDecisionSupport.civilians).reportPeriodTotal}
                    </div>
                    <div className="px-3 py-2 bg-orange-500/20 border border-orange-500 text-orange-400 rounded-md text-center font-bold">
                      {calculateTotals(currentDecisionSupport.civilians).totalToDateSum}
                    </div>
                  </div>
                </div>

                {/* Responders Grid */}
                <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <Shield className="h-5 w-5 mr-2 text-orange-500" />
                    Responders Status Summary
                  </h3>
                  
                  {/* Responders Grid Header */}
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="text-sm font-medium text-orange-400">Responders</div>
                    <div className="text-sm font-medium text-orange-400 text-center"># During Report Period</div>
                    <div className="text-sm font-medium text-orange-400 text-center"># Total to Date</div>
                  </div>

                  {/* Responders Data Rows */}
                  {[
                    { key: 'fatalities', label: 'Fatalities' },
                    { key: 'injuriesSick', label: 'Injuries/Sick' },
                    { key: 'trappedNeedRescue', label: 'Trapped/Need Rescue' },
                    { key: 'missing', label: 'Missing' },
                    { key: 'evacuated', label: 'Evacuated' },
                    { key: 'shelteringInPlace', label: 'Sheltering in Place' },
                    { key: 'inTempShelters', label: 'In Temp Shelters' },
                    { key: 'receivedMassImmunizations', label: 'Have Received Mass Immunizations' },
                    { key: 'requireImmunizations', label: 'Require Immunizations' },
                    { key: 'inQuarantine', label: 'In Quarantine' }
                  ].map(row => (
                    <div key={row.key} className="grid grid-cols-3 gap-4 mb-3 items-center">
                      <div className="text-white font-medium">{row.label}</div>
                      <input
                        type="number"
                        min="0"
                        value={currentDecisionSupport.responders[row.key].reportPeriod}
                        onChange={(e) => updateRespondersData(row.key, 'reportPeriod', e.target.value)}
                        placeholder="0"
                        className="px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-md focus:outline-none focus:border-orange-500 text-center"
                      />
                      <input
                        type="number"
                        min="0"
                        value={currentDecisionSupport.responders[row.key].totalToDate}
                        onChange={(e) => updateRespondersData(row.key, 'totalToDate', e.target.value)}
                        placeholder="0"
                        className="px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-md focus:outline-none focus:border-orange-500 text-center"
                      />
                    </div>
                  ))}

                  {/* Responders Total Row */}
                  <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-600">
                    <div className="text-orange-400 font-bold">Total</div>
                    <div className="px-3 py-2 bg-orange-500/20 border border-orange-500 text-orange-400 rounded-md text-center font-bold">
                      {calculateTotals(currentDecisionSupport.responders).reportPeriodTotal}
                    </div>
                    <div className="px-3 py-2 bg-orange-500/20 border border-orange-500 text-orange-400 rounded-md text-center font-bold">
                      {calculateTotals(currentDecisionSupport.responders).totalToDateSum}
                    </div>
                  </div>
                </div>

                {/* Life Safety Threat Management Grid */}
                <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <AlertTriangle className="h-5 w-5 mr-2 text-orange-500" />
                    Life, Safety, Health Threat Management
                  </h3>
                  
                  {/* Threat Management Grid Header */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="text-sm font-medium text-orange-400">Life, Safety, Health Threat Mgmt</div>
                    <div className="text-sm font-medium text-orange-400 text-center">Check if Active</div>
                  </div>

                  {/* Threat Management Checkboxes */}
                  {[
                    { key: 'noLikelyThreat', label: 'No Likely Threat' },
                    { key: 'potentialThreat', label: 'Potential Threat' },
                    { key: 'massNotificationsInProgress', label: 'Mass Notifications in Progress' },
                    { key: 'massNotificationsCompleted', label: 'Mass Notifications Completed' },
                    { key: 'noEvacuationImminent', label: 'No Evacuation(s) Imminent' },
                    { key: 'planningForEvacuation', label: 'Planning for Evacuation' },
                    { key: 'evacuationInProgress', label: 'Evacuation(S) in Progress' },
                    { key: 'shelterInPlaceInProgress', label: 'Shelter-in-Place in Progress' },
                    { key: 'repopulationInProgress', label: 'Repopulation in Progress' },
                    { key: 'massImmunizationInProgress', label: 'Mass Immunization in Progress' },
                    { key: 'massImmunizationComplete', label: 'Mass Immunization Complete' },
                    { key: 'quarantineInProgress', label: 'Quarantine in Progress' },
                    { key: 'areaRestrictionInEffect', label: 'Area Restriction in Effect' }
                  ].map(row => (
                    <div key={row.key} className="grid grid-cols-2 gap-4 mb-3 items-center">
                      <div className="text-white font-medium">{row.label}</div>
                      <div className="flex justify-center">
                        <input
                          type="checkbox"
                          checked={currentDecisionSupport.lifeSafetyThreatMgmt[row.key]}
                          onChange={(e) => updateLifeSafetyThreat(row.key, e.target.checked)}
                          className="w-5 h-5 text-orange-500 focus:ring-orange-500 focus:ring-2 bg-gray-800 border-gray-600 rounded"
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Remarks and Weather Sections */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-white text-sm font-medium mb-2 block">
                      Life Safety and Health Status/ Threat Remarks
                    </label>
                    <textarea
                      value={currentDecisionSupport.lifeSafetyHealthRemarks}
                      onChange={(e) => setCurrentDecisionSupport(prev => ({...prev, lifeSafetyHealthRemarks: e.target.value}))}
                      placeholder="Enter remarks about life safety and health status/threats..."
                      rows={4}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-md focus:outline-none focus:border-orange-500"
                    />
                  </div>
                  <div>
                    <label className="text-white text-sm font-medium mb-2 block">
                      Weather Concerns
                    </label>
                    <textarea
                      value={currentDecisionSupport.weatherConcerns}
                      onChange={(e) => setCurrentDecisionSupport(prev => ({...prev, weatherConcerns: e.target.value}))}
                      placeholder="Enter current and forecasted weather concerns..."
                      rows={4}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-md focus:outline-none focus:border-orange-500"
                    />
                  </div>
                </div>

                {/* Projected Incident Activity Section */}
                <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <Clock className="h-5 w-5 mr-2 text-orange-500" />
                    Projected Incident Activity Potential, Movement, Escalation or Spread
                  </h3>
                  
                  {[
                    { key: 'twelveHours', label: '12 hours' },
                    { key: 'twentyFourHours', label: '24 hours' },
                    { key: 'fortyEightHours', label: '48 hours' },
                    { key: 'seventyTwoHours', label: '72 hours' },
                    { key: 'afterSeventyTwoHours', label: 'Anticipated after 72 hours' }
                  ].map(period => (
                    <div key={period.key} className="mb-4">
                      <label className="text-white text-sm font-medium mb-2 block">
                        {period.label}
                      </label>
                      <textarea
                        value={currentDecisionSupport.projectedActivity[period.key]}
                        onChange={(e) => updateProjectedActivity(period.key, e.target.value)}
                        placeholder={`Enter projected activity for ${period.label}...`}
                        rows={2}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-md focus:outline-none focus:border-orange-500"
                      />
                    </div>
                  ))}
                </div>

                <div className="flex gap-2">
                  <Button onClick={addDecisionSupport} className="bg-orange-500 hover:bg-orange-600 text-white">
                    <Plus className="h-4 w-4 mr-2" />
                    {editingDecisionSupport ? 'Update Decision Support' : 'Add Decision Support'}
                  </Button>
                  {editingDecisionSupport && (
                    <Button onClick={cancelEditDecisionSupport} variant="outline" className="border-gray-500 text-gray-300">
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Display Decision Support Entries */}
            <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
              <CardHeader>
                <CardTitle className="text-orange-500">Decision Support Records ({decisionSupportEntries.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {decisionSupportEntries.map(entry => (
                    <div key={entry.id} className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                      {/* Civilians Summary Display */}
                      <div className="mb-6">
                        <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
                          <Users className="h-4 w-4 mr-2 text-orange-500" />
                          Civilians Impact Summary
                        </h4>
                        <div className="bg-gray-700/50 rounded-lg p-4">
                          <div className="grid grid-cols-3 gap-4 mb-2 text-sm font-medium text-orange-400">
                            <div>Category</div>
                            <div className="text-center">Report Period</div>
                            <div className="text-center">Total to Date</div>
                          </div>
                          {Object.entries(entry.civilians).map(([key, data]) => (
                            <div key={key} className="grid grid-cols-3 gap-4 mb-1 text-sm text-gray-300">
                              <div className="capitalize">{key.replace(/([A-Z])/g, ' $1').toLowerCase()}</div>
                              <div className="text-center">{data.reportPeriod || '0'}</div>
                              <div className="text-center">{data.totalToDate || '0'}</div>
                            </div>
                          ))}
                          <div className="grid grid-cols-3 gap-4 mt-3 pt-3 border-t border-gray-600 text-sm font-bold text-orange-400">
                            <div>Total</div>
                            <div className="text-center">{calculateTotals(entry.civilians).reportPeriodTotal}</div>
                            <div className="text-center">{calculateTotals(entry.civilians).totalToDateSum}</div>
                          </div>
                        </div>
                      </div>

                      {/* Responders Summary Display */}
                      <div className="mb-6">
                        <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
                          <Shield className="h-4 w-4 mr-2 text-orange-500" />
                          Responders Impact Summary
                        </h4>
                        <div className="bg-gray-700/50 rounded-lg p-4">
                          <div className="grid grid-cols-3 gap-4 mb-2 text-sm font-medium text-orange-400">
                            <div>Category</div>
                            <div className="text-center">Report Period</div>
                            <div className="text-center">Total to Date</div>
                          </div>
                          {Object.entries(entry.responders).map(([key, data]) => (
                            <div key={key} className="grid grid-cols-3 gap-4 mb-1 text-sm text-gray-300">
                              <div className="capitalize">{key.replace(/([A-Z])/g, ' $1').toLowerCase()}</div>
                              <div className="text-center">{data.reportPeriod || '0'}</div>
                              <div className="text-center">{data.totalToDate || '0'}</div>
                            </div>
                          ))}
                          <div className="grid grid-cols-3 gap-4 mt-3 pt-3 border-t border-gray-600 text-sm font-bold text-orange-400">
                            <div>Total</div>
                            <div className="text-center">{calculateTotals(entry.responders).reportPeriodTotal}</div>
                            <div className="text-center">{calculateTotals(entry.responders).totalToDateSum}</div>
                          </div>
                        </div>
                      </div>

                      {/* Threat Management Display */}
                      <div className="mb-6">
                        <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
                          <AlertTriangle className="h-4 w-4 mr-2 text-orange-500" />
                          Active Threat Management
                        </h4>
                        <div className="bg-gray-700/50 rounded-lg p-4">
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {Object.entries(entry.lifeSafetyThreatMgmt)
                              .filter(([_, isActive]) => isActive)
                              .map(([key, _]) => (
                                <div key={key} className="flex items-center text-sm text-green-400">
                                  <CheckCircle className="h-4 w-4 mr-2" />
                                  {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
                                </div>
                              ))}
                            {Object.values(entry.lifeSafetyThreatMgmt).every(val => !val) && (
                              <div className="text-sm text-gray-400 col-span-full">No active threat management activities</div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Remarks and Weather */}
                      {(entry.lifeSafetyHealthRemarks || entry.weatherConcerns) && (
                        <div className="mb-6">
                          <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
                            <FileText className="h-4 w-4 mr-2 text-orange-500" />
                            Remarks and Weather
                          </h4>
                          <div className="space-y-3">
                            {entry.lifeSafetyHealthRemarks && (
                              <div>
                                <div className="text-sm font-medium text-orange-400 mb-1">Life Safety & Health Remarks:</div>
                                <p className="text-sm text-gray-300 whitespace-pre-wrap">{entry.lifeSafetyHealthRemarks}</p>
                              </div>
                            )}
                            {entry.weatherConcerns && (
                              <div>
                                <div className="text-sm font-medium text-orange-400 mb-1">Weather Concerns:</div>
                                <p className="text-sm text-gray-300 whitespace-pre-wrap">{entry.weatherConcerns}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Projected Activity */}
                      {Object.values(entry.projectedActivity).some(val => val.trim()) && (
                        <div className="mb-6">
                          <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
                            <Clock className="h-4 w-4 mr-2 text-orange-500" />
                            Projected Activity Timeline
                          </h4>
                          <div className="space-y-3">
                            {[
                              { key: 'twelveHours', label: '12 Hours' },
                              { key: 'twentyFourHours', label: '24 Hours' },
                              { key: 'fortyEightHours', label: '48 Hours' },
                              { key: 'seventyTwoHours', label: '72 Hours' },
                              { key: 'afterSeventyTwoHours', label: 'After 72 Hours' }
                            ].map(period => (
                              entry.projectedActivity[period.key] && (
                                <div key={period.key}>
                                  <div className="text-sm font-medium text-orange-400 mb-1">{period.label}:</div>
                                  <p className="text-sm text-gray-300 whitespace-pre-wrap">{entry.projectedActivity[period.key]}</p>
                                </div>
                              )
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="flex justify-end space-x-2">
                        <Button
                          onClick={() => {
                            const printWindow = window.open('', '_blank');
                            const activeThreatMgmt = Object.entries(entry.lifeSafetyThreatMgmt)
                              .filter(([_, isActive]) => isActive)
                              .map(([key, _]) => key.replace(/([A-Z])/g, ' $1').toLowerCase())
                              .join(', ') || 'None';
                              
                            const printContent = `
                              <html>
                                <head>
                                  <title>Decision Support Information Report</title>
                                  <style>
                                    body { font-family: Arial, sans-serif; margin: 20px; color: #333; line-height: 1.6; }
                                    .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #f97316; padding-bottom: 15px; }
                                    .header h1 { color: #f97316; margin: 0; font-size: 28px; }
                                    .section { margin-bottom: 25px; padding: 20px; border: 1px solid #ddd; border-radius: 8px; }
                                    .section h3 { color: #f97316; margin-top: 0; border-bottom: 2px solid #f97316; padding-bottom: 5px; }
                                    .data-grid { width: 100%; border-collapse: collapse; margin-top: 10px; }
                                    .data-grid th, .data-grid td { border: 1px solid #ddd; padding: 8px; text-align: center; }
                                    .data-grid th { background-color: #f97316; color: white; font-weight: bold; }
                                    .data-grid td:first-child { text-align: left; font-weight: bold; }
                                    .total-row { background-color: #fff5f5; font-weight: bold; }
                                    .threat-list { columns: 2; column-gap: 20px; }
                                    .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; border-top: 1px solid #ddd; padding-top: 10px; }
                                  </style>
                                </head>
                                <body>
                                  <div class="header">
                                    <h1>Additional Incident Decision Support Information</h1>
                                    <p><strong>Report ID:</strong> ${entry.id} | <strong>Generated:</strong> ${new Date().toLocaleDateString()} at ${format24HourTime()}</p>
                                  </div>
                                  
                                  <div class="section">
                                    <h3>Public Status Summary</h3>
                                    <table class="data-grid">
                                      <thead>
                                        <tr><th>Category</th><th># During Report Period</th><th># Total to Date</th></tr>
                                      </thead>
                                      <tbody>
                                        ${Object.entries(entry.civilians).map(([key, data]) => `
                                          <tr>
                                            <td>${key.replace(/([A-Z])/g, ' $1').toLowerCase()}</td>
                                            <td>${data.reportPeriod || '0'}</td>
                                            <td>${data.totalToDate || '0'}</td>
                                          </tr>
                                        `).join('')}
                                        <tr class="total-row">
                                          <td><strong>TOTAL</strong></td>
                                          <td><strong>${calculateTotals(entry.civilians).reportPeriodTotal}</strong></td>
                                          <td><strong>${calculateTotals(entry.civilians).totalToDateSum}</strong></td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>

                                  <div class="section">
                                    <h3>Responders Status Summary</h3>
                                    <table class="data-grid">
                                      <thead>
                                        <tr><th>Category</th><th># During Report Period</th><th># Total to Date</th></tr>
                                      </thead>
                                      <tbody>
                                        ${Object.entries(entry.responders).map(([key, data]) => `
                                          <tr>
                                            <td>${key.replace(/([A-Z])/g, ' $1').toLowerCase()}</td>
                                            <td>${data.reportPeriod || '0'}</td>
                                            <td>${data.totalToDate || '0'}</td>
                                          </tr>
                                        `).join('')}
                                        <tr class="total-row">
                                          <td><strong>TOTAL</strong></td>
                                          <td><strong>${calculateTotals(entry.responders).reportPeriodTotal}</strong></td>
                                          <td><strong>${calculateTotals(entry.responders).totalToDateSum}</strong></td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  </div>

                                  <div class="section">
                                    <h3>Life, Safety, Health Threat Management</h3>
                                    <div class="threat-list">
                                      ${activeThreatMgmt !== 'None' ? activeThreatMgmt.split(', ').map(threat => `<div>• ${threat}</div>`).join('') : '<div>No active threat management activities</div>'}
                                    </div>
                                  </div>

                                  ${entry.lifeSafetyHealthRemarks || entry.weatherConcerns ? `
                                  <div class="section">
                                    <h3>Remarks and Weather Information</h3>
                                    ${entry.lifeSafetyHealthRemarks ? `
                                      <p><strong>Life Safety & Health Remarks:</strong><br>
                                      ${entry.lifeSafetyHealthRemarks}</p>
                                    ` : ''}
                                    ${entry.weatherConcerns ? `
                                      <p><strong>Weather Concerns:</strong><br>
                                      ${entry.weatherConcerns}</p>
                                    ` : ''}
                                  </div>
                                  ` : ''}

                                  ${Object.values(entry.projectedActivity).some(val => val.trim()) ? `
                                  <div class="section">
                                    <h3>Projected Incident Activity Potential, Movement, Escalation or Spread</h3>
                                    ${[
                                      { key: 'twelveHours', label: '12 Hours' },
                                      { key: 'twentyFourHours', label: '24 Hours' },
                                      { key: 'fortyEightHours', label: '48 Hours' },
                                      { key: 'seventyTwoHours', label: '72 Hours' },
                                      { key: 'afterSeventyTwoHours', label: 'After 72 Hours' }
                                    ].map(period => 
                                      entry.projectedActivity[period.key] ? `
                                        <p><strong>${period.label}:</strong><br>
                                        ${entry.projectedActivity[period.key]}</p>
                                      ` : ''
                                    ).join('')}
                                  </div>
                                  ` : ''}
                                  
                                  <div class="footer">
                                    <div><strong>Decision Support Information Report</strong> - Report ID: ${entry.id}</div>
                                    <div>Total Impact: ${calculateTotals(entry.civilians).reportPeriodTotal + calculateTotals(entry.responders).reportPeriodTotal} affected during report period</div>
                                    <div style="margin-top: 10px;">Powered by EXRSIM - Emergency Response Simulation Platform</div>
                                  </div>
                                </body>
                              </html>
                            `;
                            writeSanitizedHTML(printWindow.document, printContent);
                            printWindow.focus();
                            setTimeout(() => {
                              printWindow.print();
                              printWindow.close();
                            }, 250);
                          }}
                          size="sm"
                          variant="ghost"
                          className="text-green-400 hover:text-green-300"
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => editDecisionSupport(entry)}
                          size="sm"
                          variant="ghost"
                          className="text-blue-400 hover:text-blue-300"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => deleteDecisionSupport(entry.id)}
                          size="sm"
                          variant="ghost"
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {decisionSupportEntries.length === 0 && (
                    <div className="text-center py-8 text-gray-400">
                      No decision support entries created yet. Add an entry above.
                    </div>
                  )}
                </div>

                {decisionSupportEntries.length > 0 && (
                  <div className="mt-6 pt-4 border-t border-gray-700">
                    <Button 
                      onClick={() => {
                        const printWindow = window.open('', '_blank');
                        const grandTotalCivilians = decisionSupportEntries.reduce((sum, entry) => 
                          sum + calculateTotals(entry.civilians).reportPeriodTotal + calculateTotals(entry.civilians).totalToDateSum, 0);
                        const grandTotalResponders = decisionSupportEntries.reduce((sum, entry) => 
                          sum + calculateTotals(entry.responders).reportPeriodTotal + calculateTotals(entry.responders).totalToDateSum, 0);
                          
                        const printContent = `
                          <html>
                            <head>
                              <title>All Decision Support Information Report</title>
                              <style>
                                body { font-family: Arial, sans-serif; margin: 20px; color: #333; line-height: 1.6; }
                                .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #f97316; padding-bottom: 15px; }
                                .header h1 { color: #f97316; margin: 0; font-size: 28px; }
                                .entry-item { margin-bottom: 40px; padding: 20px; border: 1px solid #ddd; border-radius: 8px; page-break-inside: avoid; }
                                .section { margin-bottom: 20px; }
                                .section h4 { color: #f97316; margin-bottom: 10px; border-bottom: 1px solid #f97316; padding-bottom: 3px; }
                                .data-grid { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 12px; }
                                .data-grid th, .data-grid td { border: 1px solid #ddd; padding: 6px; text-align: center; }
                                .data-grid th { background-color: #f97316; color: white; font-weight: bold; }
                                .data-grid td:first-child { text-align: left; font-weight: bold; }
                                .total-row { background-color: #fff5f5; font-weight: bold; }
                                .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; border-top: 1px solid #ddd; padding-top: 10px; }
                              </style>
                            </head>
                            <body>
                              <div class="header">
                                <h1>Decision Support Information Report - All Entries</h1>
                                <p><strong>Generated:</strong> ${new Date().toLocaleDateString()} at ${format24HourTime()} | <strong>Total Entries:</strong> ${decisionSupportEntries.length}</p>
                              </div>
                              
                              ${decisionSupportEntries.map((entry, index) => `
                                <div class="entry-item">
                                  <h3 style="color: #f97316; margin-top: 0;">Decision Support Entry #${index + 1}</h3>
                                  
                                  <div class="section">
                                    <h4>Public Status Summary</h4>
                                    <table class="data-grid">
                                      <thead><tr><th>Category</th><th># During Report Period</th><th># Total to Date</th></tr></thead>
                                      <tbody>
                                        ${Object.entries(entry.civilians).map(([key, data]) => `
                                          <tr><td>${key.replace(/([A-Z])/g, ' $1').toLowerCase()}</td><td>${data.reportPeriod || '0'}</td><td>${data.totalToDate || '0'}</td></tr>
                                        `).join('')}
                                        <tr class="total-row"><td><strong>TOTAL</strong></td><td><strong>${calculateTotals(entry.civilians).reportPeriodTotal}</strong></td><td><strong>${calculateTotals(entry.civilians).totalToDateSum}</strong></td></tr>
                                      </tbody>
                                    </table>
                                  </div>

                                  <div class="section">
                                    <h4>Responders Status Summary</h4>
                                    <table class="data-grid">
                                      <thead><tr><th>Category</th><th># During Report Period</th><th># Total to Date</th></tr></thead>
                                      <tbody>
                                        ${Object.entries(entry.responders).map(([key, data]) => `
                                          <tr><td>${key.replace(/([A-Z])/g, ' $1').toLowerCase()}</td><td>${data.reportPeriod || '0'}</td><td>${data.totalToDate || '0'}</td></tr>
                                        `).join('')}
                                        <tr class="total-row"><td><strong>TOTAL</strong></td><td><strong>${calculateTotals(entry.responders).reportPeriodTotal}</strong></td><td><strong>${calculateTotals(entry.responders).totalToDateSum}</strong></td></tr>
                                      </tbody>
                                    </table>
                                  </div>

                                  <div class="section">
                                    <h4>Active Threat Management</h4>
                                    <p>${Object.entries(entry.lifeSafetyThreatMgmt)
                                      .filter(([_, isActive]) => isActive)
                                      .map(([key, _]) => key.replace(/([A-Z])/g, ' $1').toLowerCase())
                                      .join(', ') || 'None'}</p>
                                  </div>

                                  ${entry.lifeSafetyHealthRemarks || entry.weatherConcerns ? `
                                  <div class="section">
                                    <h4>Remarks and Weather</h4>
                                    ${entry.lifeSafetyHealthRemarks ? `<p><strong>Life Safety Remarks:</strong> ${entry.lifeSafetyHealthRemarks}</p>` : ''}
                                    ${entry.weatherConcerns ? `<p><strong>Weather Concerns:</strong> ${entry.weatherConcerns}</p>` : ''}
                                  </div>
                                  ` : ''}

                                  ${Object.values(entry.projectedActivity).some(val => val.trim()) ? `
                                  <div class="section">
                                    <h4>Projected Activity Timeline</h4>
                                    ${[
                                      { key: 'twelveHours', label: '12 Hours' },
                                      { key: 'twentyFourHours', label: '24 Hours' },
                                      { key: 'fortyEightHours', label: '48 Hours' },
                                      { key: 'seventyTwoHours', label: '72 Hours' },
                                      { key: 'afterSeventyTwoHours', label: 'After 72 Hours' }
                                    ].map(period => 
                                      entry.projectedActivity[period.key] ? `<p><strong>${period.label}:</strong> ${entry.projectedActivity[period.key]}</p>` : ''
                                    ).join('')}
                                  </div>
                                  ` : ''}
                                </div>
                              `).join('')}
                              
                              <div style="margin-top: 30px; padding: 20px; border: 2px solid #f97316; border-radius: 8px; background-color: #fff5f5;">
                                <h3 style="color: #f97316; margin-top: 0;">GRAND TOTALS - All Decision Support Entries</h3>
                                <p><strong>Total Entries Processed:</strong> ${decisionSupportEntries.length}</p>
                                <p><strong>Combined Impact Analysis:</strong> ${grandTotalCivilians + grandTotalResponders} total affected across all reports</p>
                                <p><strong>Active Threat Categories:</strong> ${decisionSupportEntries.reduce((count, entry) => 
                                  count + Object.values(entry.lifeSafetyThreatMgmt).filter(Boolean).length, 0)} total active threat management activities</p>
                              </div>
                              
                              <div class="footer">
                                <div><strong>Comprehensive Decision Support Report</strong> - Total: ${decisionSupportEntries.length} entries</div>
                                <div>Impact Summary: ${grandTotalCivilians} civilians, ${grandTotalResponders} responders affected</div>
                                <div style="margin-top: 10px;">Powered by EXRSIM - Emergency Response Simulation Platform</div>
                              </div>
                            </body>
                          </html>
                        `;
                        writeSanitizedHTML(printWindow.document, printContent);
                        printWindow.focus();
                        setTimeout(() => {
                          printWindow.print();
                          printWindow.close();
                        }, 250);
                      }}
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Print All Decision Support
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        );
      case 'addtl-decision-supp':
        return (
          <div className="p-6 space-y-6 pb-20">
            {/* Additional Decision Support Header */}
            <div className="text-center mb-8">
              <h1 className={`text-3xl font-bold ${theme.colors.textPrimary} mb-2`}>Additional Decision Support Information</h1>
              <p className={`${theme.colors.textMuted} text-lg`}>
                Extended decision support resources including threat analysis, resource planning, and strategic considerations
              </p>
            </div>

            {/* Additional Decision Support Form */}
            <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
              <CardHeader>
                <CardTitle className="text-orange-500">Extended Decision Support Data Entry</CardTitle>
                <CardDescription className={`${theme.colors.textMuted}`}>
                  Enter threat summaries, resource needs, strategic discussions, and planning information for comprehensive decision support
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-8">
                {/* Current Threat Summary Grid */}
                <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <AlertTriangle className="h-5 w-5 mr-2 text-orange-500" />
                    Current Threat Summary
                  </h3>
                  
                  {[
                    { key: 'twelveHours', label: '12 hours' },
                    { key: 'twentyFourHours', label: '24 hours' },
                    { key: 'fortyEightHours', label: '48 hours' },
                    { key: 'seventyTwoHours', label: '72 hours' },
                    { key: 'afterSeventyTwoHours', label: 'After 72 hours' }
                  ].map(period => (
                    <div key={period.key} className="mb-4">
                      <label className="text-white text-sm font-medium mb-2 block">
                        {period.label}
                      </label>
                      <textarea
                        value={currentAdditionalDecisionSupport.currentThreatSummary[period.key]}
                        onChange={(e) => updateCurrentThreatSummary(period.key, e.target.value)}
                        placeholder={`Enter threat summary for ${period.label}...`}
                        rows={2}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-md focus:outline-none focus:border-orange-500"
                      />
                    </div>
                  ))}
                </div>

                {/* Critical Resource Needs Grid */}
                <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <Package className="h-5 w-5 mr-2 text-orange-500" />
                    Critical Resource Needs
                  </h3>
                  
                  {[
                    { key: 'twelveHours', label: '12 hours' },
                    { key: 'twentyFourHours', label: '24 hours' },
                    { key: 'fortyEightHours', label: '48 hours' },
                    { key: 'seventyTwoHours', label: '72 hours' },
                    { key: 'afterSeventyTwoHours', label: 'After 72 hours' }
                  ].map(period => (
                    <div key={period.key} className="mb-4">
                      <label className="text-white text-sm font-medium mb-2 block">
                        {period.label}
                      </label>
                      <textarea
                        value={currentAdditionalDecisionSupport.criticalResourceNeeds[period.key]}
                        onChange={(e) => updateCriticalResourceNeeds(period.key, e.target.value)}
                        placeholder={`Enter resource needs for ${period.label}...`}
                        rows={2}
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-md focus:outline-none focus:border-orange-500"
                      />
                    </div>
                  ))}
                </div>

                {/* Strategic Information Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="text-white text-sm font-medium mb-2 block">
                      Strategic Discussions
                    </label>
                    <textarea
                      value={currentAdditionalDecisionSupport.strategicDiscussions}
                      onChange={(e) => setCurrentAdditionalDecisionSupport(prev => ({...prev, strategicDiscussions: e.target.value}))}
                      placeholder="Enter strategic discussions and considerations..."
                      rows={4}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-md focus:outline-none focus:border-orange-500"
                    />
                  </div>
                  <div>
                    <label className="text-white text-sm font-medium mb-2 block">
                      Planned Actions Next Period
                    </label>
                    <textarea
                      value={currentAdditionalDecisionSupport.plannedActionsNextPeriod}
                      onChange={(e) => setCurrentAdditionalDecisionSupport(prev => ({...prev, plannedActionsNextPeriod: e.target.value}))}
                      placeholder="Enter planned actions for next operational period..."
                      rows={4}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-md focus:outline-none focus:border-orange-500"
                    />
                  </div>
                </div>

                {/* Incident Projections Section */}
                <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <BarChart className="h-5 w-5 mr-2 text-orange-500" />
                    Incident Projections
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="text-white text-sm font-medium mb-2 block">
                        Projected Final Incident Area
                      </label>
                      <input
                        type="text"
                        value={currentAdditionalDecisionSupport.projectedFinalIncidentArea}
                        onChange={(e) => setCurrentAdditionalDecisionSupport(prev => ({...prev, projectedFinalIncidentArea: e.target.value}))}
                        placeholder="Enter projected final incident area..."
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-md focus:outline-none focus:border-orange-500"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-white text-sm font-medium mb-2 block">
                          Anticipated Completion Date
                        </label>
                        <input
                          type="date"
                          value={currentAdditionalDecisionSupport.anticipatedCompletionDate}
                          onChange={(e) => setCurrentAdditionalDecisionSupport(prev => ({...prev, anticipatedCompletionDate: e.target.value}))}
                          className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-md focus:outline-none focus:border-orange-500"
                        />
                      </div>
                      <div>
                        <label className="text-white text-sm font-medium mb-2 block">
                          Projected Demobilization Date
                        </label>
                        <input
                          type="date"
                          value={currentAdditionalDecisionSupport.projectedDemobilizationDate}
                          onChange={(e) => setCurrentAdditionalDecisionSupport(prev => ({...prev, projectedDemobilizationDate: e.target.value}))}
                          className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-md focus:outline-none focus:border-orange-500"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Cost Analysis Section */}
                <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                    <DollarSign className="h-5 w-5 mr-2 text-orange-500" />
                    Cost Analysis
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="text-white text-sm font-medium mb-2 block">
                        Estimated Costs to Date
                      </label>
                      <input
                        type="text"
                        value={currentAdditionalDecisionSupport.estimatedCostsToDate}
                        onChange={(e) => setCurrentAdditionalDecisionSupport(prev => ({...prev, estimatedCostsToDate: e.target.value}))}
                        placeholder="Enter estimated costs to date..."
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-md focus:outline-none focus:border-orange-500"
                      />
                    </div>
                    <div>
                      <label className="text-white text-sm font-medium mb-2 block">
                        Projected Final Costs
                      </label>
                      <input
                        type="text"
                        value={currentAdditionalDecisionSupport.projectedFinalCosts}
                        onChange={(e) => setCurrentAdditionalDecisionSupport(prev => ({...prev, projectedFinalCosts: e.target.value}))}
                        placeholder="Enter projected final costs..."
                        className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-md focus:outline-none focus:border-orange-500"
                      />
                    </div>
                  </div>
                </div>

                {/* General Comments Section */}
                <div>
                  <label className="text-white text-sm font-medium mb-2 block">
                    General Comments
                  </label>
                  <textarea
                    value={currentAdditionalDecisionSupport.generalComments}
                    onChange={(e) => setCurrentAdditionalDecisionSupport(prev => ({...prev, generalComments: e.target.value}))}
                    placeholder="Enter general comments and additional information..."
                    rows={4}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-600 text-white rounded-md focus:outline-none focus:border-orange-500"
                  />
                </div>

                <div className="flex gap-2">
                  <Button onClick={addAdditionalDecisionSupport} className="bg-orange-500 hover:bg-orange-600 text-white">
                    <Plus className="h-4 w-4 mr-2" />
                    {editingAdditionalDecisionSupport ? 'Update Additional Decision Support' : 'Add Additional Decision Support'}
                  </Button>
                  {editingAdditionalDecisionSupport && (
                    <Button onClick={cancelEditAdditionalDecisionSupport} variant="outline" className="border-gray-500 text-gray-300">
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Display Additional Decision Support Entries */}
            <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
              <CardHeader>
                <CardTitle className="text-orange-500">Additional Decision Support Records ({additionalDecisionSupportEntries.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {additionalDecisionSupportEntries.map(entry => (
                    <div key={entry.id} className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                      {/* Current Threat Summary Display */}
                      {Object.values(entry.currentThreatSummary).some(val => val.trim()) && (
                        <div className="mb-6">
                          <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
                            <AlertTriangle className="h-4 w-4 mr-2 text-orange-500" />
                            Current Threat Summary
                          </h4>
                          <div className="space-y-3">
                            {[
                              { key: 'twelveHours', label: '12 Hours' },
                              { key: 'twentyFourHours', label: '24 Hours' },
                              { key: 'fortyEightHours', label: '48 Hours' },
                              { key: 'seventyTwoHours', label: '72 Hours' },
                              { key: 'afterSeventyTwoHours', label: 'After 72 Hours' }
                            ].map(period => (
                              entry.currentThreatSummary[period.key] && (
                                <div key={period.key}>
                                  <div className="text-sm font-medium text-orange-400 mb-1">{period.label}:</div>
                                  <p className="text-sm text-gray-300 whitespace-pre-wrap">{entry.currentThreatSummary[period.key]}</p>
                                </div>
                              )
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Critical Resource Needs Display */}
                      {Object.values(entry.criticalResourceNeeds).some(val => val.trim()) && (
                        <div className="mb-6">
                          <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
                            <Package className="h-4 w-4 mr-2 text-orange-500" />
                            Critical Resource Needs
                          </h4>
                          <div className="space-y-3">
                            {[
                              { key: 'twelveHours', label: '12 Hours' },
                              { key: 'twentyFourHours', label: '24 Hours' },
                              { key: 'fortyEightHours', label: '48 Hours' },
                              { key: 'seventyTwoHours', label: '72 Hours' },
                              { key: 'afterSeventyTwoHours', label: 'After 72 Hours' }
                            ].map(period => (
                              entry.criticalResourceNeeds[period.key] && (
                                <div key={period.key}>
                                  <div className="text-sm font-medium text-orange-400 mb-1">{period.label}:</div>
                                  <p className="text-sm text-gray-300 whitespace-pre-wrap">{entry.criticalResourceNeeds[period.key]}</p>
                                </div>
                              )
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Strategic Information Display */}
                      {(entry.strategicDiscussions || entry.plannedActionsNextPeriod) && (
                        <div className="mb-6">
                          <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
                            <FileText className="h-4 w-4 mr-2 text-orange-500" />
                            Strategic Information
                          </h4>
                          <div className="space-y-3">
                            {entry.strategicDiscussions && (
                              <div>
                                <div className="text-sm font-medium text-orange-400 mb-1">Strategic Discussions:</div>
                                <p className="text-sm text-gray-300 whitespace-pre-wrap">{entry.strategicDiscussions}</p>
                              </div>
                            )}
                            {entry.plannedActionsNextPeriod && (
                              <div>
                                <div className="text-sm font-medium text-orange-400 mb-1">Planned Actions Next Period:</div>
                                <p className="text-sm text-gray-300 whitespace-pre-wrap">{entry.plannedActionsNextPeriod}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Incident Projections Display */}
                      {(entry.projectedFinalIncidentArea || entry.anticipatedCompletionDate || entry.projectedDemobilizationDate) && (
                        <div className="mb-6">
                          <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
                            <BarChart className="h-4 w-4 mr-2 text-orange-500" />
                            Incident Projections
                          </h4>
                          <div className="bg-gray-700/50 rounded-lg p-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                              {entry.projectedFinalIncidentArea && (
                                <div>
                                  <div className="text-orange-400 font-medium">Final Incident Area:</div>
                                  <div className="text-gray-300">{entry.projectedFinalIncidentArea}</div>
                                </div>
                              )}
                              {entry.anticipatedCompletionDate && (
                                <div>
                                  <div className="text-orange-400 font-medium">Completion Date:</div>
                                  <div className="text-gray-300">{entry.anticipatedCompletionDate}</div>
                                </div>
                              )}
                              {entry.projectedDemobilizationDate && (
                                <div>
                                  <div className="text-orange-400 font-medium">Demobilization Date:</div>
                                  <div className="text-gray-300">{entry.projectedDemobilizationDate}</div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Cost Analysis Display */}
                      {(entry.estimatedCostsToDate || entry.projectedFinalCosts) && (
                        <div className="mb-6">
                          <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
                            <DollarSign className="h-4 w-4 mr-2 text-orange-500" />
                            Cost Analysis
                          </h4>
                          <div className="bg-gray-700/50 rounded-lg p-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                              {entry.estimatedCostsToDate && (
                                <div>
                                  <div className="text-orange-400 font-medium">Costs to Date:</div>
                                  <div className="text-gray-300">{entry.estimatedCostsToDate}</div>
                                </div>
                              )}
                              {entry.projectedFinalCosts && (
                                <div>
                                  <div className="text-orange-400 font-medium">Projected Final Costs:</div>
                                  <div className="text-gray-300">{entry.projectedFinalCosts}</div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* General Comments Display */}
                      {entry.generalComments && (
                        <div className="mb-6">
                          <h4 className="text-lg font-semibold text-white mb-3 flex items-center">
                            <MessageSquare className="h-4 w-4 mr-2 text-orange-500" />
                            General Comments
                          </h4>
                          <p className="text-sm text-gray-300 whitespace-pre-wrap bg-gray-700/50 rounded-lg p-4">{entry.generalComments}</p>
                        </div>
                      )}

                      <div className="flex justify-end space-x-2">
                        <Button
                          onClick={() => {
                            const printWindow = window.open('', '_blank');
                            const printContent = `
                              <html>
                                <head>
                                  <title>Additional Decision Support Information Report</title>
                                  <style>
                                    body { font-family: Arial, sans-serif; margin: 20px; color: #333; line-height: 1.6; }
                                    .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #f97316; padding-bottom: 15px; }
                                    .header h1 { color: #f97316; margin: 0; font-size: 28px; }
                                    .section { margin-bottom: 25px; padding: 20px; border: 1px solid #ddd; border-radius: 8px; }
                                    .section h3 { color: #f97316; margin-top: 0; border-bottom: 2px solid #f97316; padding-bottom: 5px; }
                                    .timeline-item { margin-bottom: 15px; padding: 10px; border-left: 3px solid #f97316; background-color: #f9fafb; }
                                    .timeline-label { font-weight: bold; color: #f97316; }
                                    .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }
                                    .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; border-top: 1px solid #ddd; padding-top: 10px; }
                                  </style>
                                </head>
                                <body>
                                  <div class="header">
                                    <h1>Additional Decision Support Information</h1>
                                    <p><strong>Report ID:</strong> ${entry.id} | <strong>Generated:</strong> ${new Date().toLocaleDateString()} at ${format24HourTime()}</p>
                                  </div>
                                  
                                  ${Object.values(entry.currentThreatSummary).some(val => val.trim()) ? `
                                  <div class="section">
                                    <h3>Current Threat Summary</h3>
                                    ${[
                                      { key: 'twelveHours', label: '12 Hours' },
                                      { key: 'twentyFourHours', label: '24 Hours' },
                                      { key: 'fortyEightHours', label: '48 Hours' },
                                      { key: 'seventyTwoHours', label: '72 Hours' },
                                      { key: 'afterSeventyTwoHours', label: 'After 72 Hours' }
                                    ].map(period => 
                                      entry.currentThreatSummary[period.key] ? `
                                        <div class="timeline-item">
                                          <div class="timeline-label">${period.label}:</div>
                                          <p>${entry.currentThreatSummary[period.key]}</p>
                                        </div>
                                      ` : ''
                                    ).join('')}
                                  </div>
                                  ` : ''}

                                  ${Object.values(entry.criticalResourceNeeds).some(val => val.trim()) ? `
                                  <div class="section">
                                    <h3>Critical Resource Needs</h3>
                                    ${[
                                      { key: 'twelveHours', label: '12 Hours' },
                                      { key: 'twentyFourHours', label: '24 Hours' },
                                      { key: 'fortyEightHours', label: '48 Hours' },
                                      { key: 'seventyTwoHours', label: '72 Hours' },
                                      { key: 'afterSeventyTwoHours', label: 'After 72 Hours' }
                                    ].map(period => 
                                      entry.criticalResourceNeeds[period.key] ? `
                                        <div class="timeline-item">
                                          <div class="timeline-label">${period.label}:</div>
                                          <p>${entry.criticalResourceNeeds[period.key]}</p>
                                        </div>
                                      ` : ''
                                    ).join('')}
                                  </div>
                                  ` : ''}

                                  ${entry.strategicDiscussions || entry.plannedActionsNextPeriod ? `
                                  <div class="section">
                                    <h3>Strategic Information</h3>
                                    ${entry.strategicDiscussions ? `<p><strong>Strategic Discussions:</strong><br>${entry.strategicDiscussions}</p>` : ''}
                                    ${entry.plannedActionsNextPeriod ? `<p><strong>Planned Actions Next Period:</strong><br>${entry.plannedActionsNextPeriod}</p>` : ''}
                                  </div>
                                  ` : ''}

                                  ${entry.projectedFinalIncidentArea || entry.anticipatedCompletionDate || entry.projectedDemobilizationDate ? `
                                  <div class="section">
                                    <h3>Incident Projections</h3>
                                    <div class="grid">
                                      ${entry.projectedFinalIncidentArea ? `<p><strong>Final Incident Area:</strong> ${entry.projectedFinalIncidentArea}</p>` : ''}
                                      ${entry.anticipatedCompletionDate ? `<p><strong>Completion Date:</strong> ${entry.anticipatedCompletionDate}</p>` : ''}
                                      ${entry.projectedDemobilizationDate ? `<p><strong>Demobilization Date:</strong> ${entry.projectedDemobilizationDate}</p>` : ''}
                                    </div>
                                  </div>
                                  ` : ''}

                                  ${entry.estimatedCostsToDate || entry.projectedFinalCosts ? `
                                  <div class="section">
                                    <h3>Cost Analysis</h3>
                                    <div class="grid">
                                      ${entry.estimatedCostsToDate ? `<p><strong>Costs to Date:</strong> ${entry.estimatedCostsToDate}</p>` : ''}
                                      ${entry.projectedFinalCosts ? `<p><strong>Projected Final Costs:</strong> ${entry.projectedFinalCosts}</p>` : ''}
                                    </div>
                                  </div>
                                  ` : ''}

                                  ${entry.generalComments ? `
                                  <div class="section">
                                    <h3>General Comments</h3>
                                    <p>${entry.generalComments}</p>
                                  </div>
                                  ` : ''}
                                  
                                  <div class="footer">
                                    <div><strong>Additional Decision Support Report</strong> - Report ID: ${entry.id}</div>
                                    <div style="margin-top: 10px;">Powered by EXRSIM - Emergency Response Simulation Platform</div>
                                  </div>
                                </body>
                              </html>
                            `;
                            writeSanitizedHTML(printWindow.document, printContent);
                            printWindow.focus();
                            setTimeout(() => {
                              printWindow.print();
                              printWindow.close();
                            }, 250);
                          }}
                          size="sm"
                          variant="ghost"
                          className="text-green-400 hover:text-green-300"
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => editAdditionalDecisionSupport(entry)}
                          size="sm"
                          variant="ghost"
                          className="text-blue-400 hover:text-blue-300"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          onClick={() => deleteAdditionalDecisionSupport(entry.id)}
                          size="sm"
                          variant="ghost"
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {additionalDecisionSupportEntries.length === 0 && (
                    <div className="text-center py-8 text-gray-400">
                      No additional decision support entries created yet. Add an entry above.
                    </div>
                  )}
                </div>

                {additionalDecisionSupportEntries.length > 0 && (
                  <div className="mt-6 pt-4 border-t border-gray-700">
                    <Button 
                      onClick={() => {
                        const printWindow = window.open('', '_blank');
                        const printContent = `
                          <html>
                            <head>
                              <title>All Additional Decision Support Information Report</title>
                              <style>
                                body { font-family: Arial, sans-serif; margin: 20px; color: #333; line-height: 1.6; }
                                .header { text-align: center; margin-bottom: 30px; border-bottom: 3px solid #f97316; padding-bottom: 15px; }
                                .header h1 { color: #f97316; margin: 0; font-size: 28px; }
                                .entry-item { margin-bottom: 40px; padding: 20px; border: 1px solid #ddd; border-radius: 8px; page-break-inside: avoid; }
                                .section { margin-bottom: 20px; }
                                .section h4 { color: #f97316; margin-bottom: 10px; border-bottom: 1px solid #f97316; padding-bottom: 3px; }
                                .timeline-item { margin-bottom: 10px; padding: 8px; border-left: 3px solid #f97316; background-color: #f9fafb; }
                                .timeline-label { font-weight: bold; color: #f97316; font-size: 12px; }
                                .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; font-size: 12px; }
                                .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; border-top: 1px solid #ddd; padding-top: 10px; }
                              </style>
                            </head>
                            <body>
                              <div class="header">
                                <h1>Additional Decision Support Information Report - All Entries</h1>
                                <p><strong>Generated:</strong> ${new Date().toLocaleDateString()} at ${format24HourTime()} | <strong>Total Entries:</strong> ${additionalDecisionSupportEntries.length}</p>
                              </div>
                              
                              ${additionalDecisionSupportEntries.map((entry, index) => `
                                <div class="entry-item">
                                  <h3 style="color: #f97316; margin-top: 0;">Additional Decision Support Entry #${index + 1}</h3>
                                  
                                  ${Object.values(entry.currentThreatSummary).some(val => val.trim()) ? `
                                  <div class="section">
                                    <h4>Current Threat Summary</h4>
                                    ${[
                                      { key: 'twelveHours', label: '12 Hours' },
                                      { key: 'twentyFourHours', label: '24 Hours' },
                                      { key: 'fortyEightHours', label: '48 Hours' },
                                      { key: 'seventyTwoHours', label: '72 Hours' },
                                      { key: 'afterSeventyTwoHours', label: 'After 72 Hours' }
                                    ].map(period => 
                                      entry.currentThreatSummary[period.key] ? `
                                        <div class="timeline-item">
                                          <div class="timeline-label">${period.label}:</div>
                                          <p style="margin: 0; font-size: 11px;">${entry.currentThreatSummary[period.key]}</p>
                                        </div>
                                      ` : ''
                                    ).join('')}
                                  </div>
                                  ` : ''}

                                  ${Object.values(entry.criticalResourceNeeds).some(val => val.trim()) ? `
                                  <div class="section">
                                    <h4>Critical Resource Needs</h4>
                                    ${[
                                      { key: 'twelveHours', label: '12 Hours' },
                                      { key: 'twentyFourHours', label: '24 Hours' },
                                      { key: 'fortyEightHours', label: '48 Hours' },
                                      { key: 'seventyTwoHours', label: '72 Hours' },
                                      { key: 'afterSeventyTwoHours', label: 'After 72 Hours' }
                                    ].map(period => 
                                      entry.criticalResourceNeeds[period.key] ? `
                                        <div class="timeline-item">
                                          <div class="timeline-label">${period.label}:</div>
                                          <p style="margin: 0; font-size: 11px;">${entry.criticalResourceNeeds[period.key]}</p>
                                        </div>
                                      ` : ''
                                    ).join('')}
                                  </div>
                                  ` : ''}

                                  ${entry.strategicDiscussions || entry.plannedActionsNextPeriod ? `
                                  <div class="section">
                                    <h4>Strategic Information</h4>
                                    ${entry.strategicDiscussions ? `<p><strong>Strategic Discussions:</strong> ${entry.strategicDiscussions}</p>` : ''}
                                    ${entry.plannedActionsNextPeriod ? `<p><strong>Planned Actions:</strong> ${entry.plannedActionsNextPeriod}</p>` : ''}
                                  </div>
                                  ` : ''}

                                  ${entry.projectedFinalIncidentArea || entry.anticipatedCompletionDate || entry.projectedDemobilizationDate ? `
                                  <div class="section">
                                    <h4>Incident Projections</h4>
                                    <div class="grid">
                                      ${entry.projectedFinalIncidentArea ? `<p><strong>Final Area:</strong> ${entry.projectedFinalIncidentArea}</p>` : ''}
                                      ${entry.anticipatedCompletionDate ? `<p><strong>Completion:</strong> ${entry.anticipatedCompletionDate}</p>` : ''}
                                      ${entry.projectedDemobilizationDate ? `<p><strong>Demobilization:</strong> ${entry.projectedDemobilizationDate}</p>` : ''}
                                    </div>
                                  </div>
                                  ` : ''}

                                  ${entry.estimatedCostsToDate || entry.projectedFinalCosts ? `
                                  <div class="section">
                                    <h4>Cost Analysis</h4>
                                    <div class="grid">
                                      ${entry.estimatedCostsToDate ? `<p><strong>Costs to Date:</strong> ${entry.estimatedCostsToDate}</p>` : ''}
                                      ${entry.projectedFinalCosts ? `<p><strong>Final Costs:</strong> ${entry.projectedFinalCosts}</p>` : ''}
                                    </div>
                                  </div>
                                  ` : ''}

                                  ${entry.generalComments ? `
                                  <div class="section">
                                    <h4>General Comments</h4>
                                    <p>${entry.generalComments}</p>
                                  </div>
                                  ` : ''}
                                </div>
                              `).join('')}
                              
                              <div style="margin-top: 30px; padding: 20px; border: 2px solid #f97316; border-radius: 8px; background-color: #fff5f5;">
                                <h3 style="color: #f97316; margin-top: 0;">SUMMARY - All Additional Decision Support Entries</h3>
                                <p><strong>Total Entries Processed:</strong> ${additionalDecisionSupportEntries.length}</p>
                                <p><strong>Comprehensive Analysis:</strong> Extended decision support covering threat assessment, resource planning, and strategic considerations</p>
                              </div>
                              
                              <div class="footer">
                                <div><strong>Comprehensive Additional Decision Support Report</strong> - Total: ${additionalDecisionSupportEntries.length} entries</div>
                                <div style="margin-top: 10px;">Powered by EXRSIM - Emergency Response Simulation Platform</div>
                              </div>
                            </body>
                          </html>
                        `;
                        writeSanitizedHTML(printWindow.document, printContent);
                        printWindow.focus();
                        setTimeout(() => {
                          printWindow.print();
                          printWindow.close();
                        }, 250);
                      }}
                      className="bg-orange-500 hover:bg-orange-600 text-white"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Print All Additional Decision Support
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        );
      case 'ics-2xx-forms':
        return (
          <div className="p-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-8">
                <FileCheck className={`h-16 w-16 ${theme.colors.textMuted} mx-auto mb-4`} />
                <h2 className={`text-2xl font-bold ${theme.colors.textPrimary} mb-4`}>ICS 2XX Form Series</h2>
                <p className={`${theme.colors.textMuted} mb-6 max-w-2xl mx-auto`}>
                  Access and manage ICS 200 series forms for incident planning, operations, and documentation.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { form: 'ICS 201', title: 'Incident Briefing', desc: 'Initial incident briefing form' },
                  { form: 'ICS 202', title: 'Incident Objectives', desc: 'Incident objectives and strategy' },
                  { form: 'ICS 203', title: 'Organization Assignment List', desc: 'Personnel assignment tracking' },
                  { form: 'ICS 204', title: 'Assignment List', desc: 'Division/Group assignment list' },
                  { form: 'ICS 205', title: 'Incident Radio Communications Plan', desc: 'Communication assignments' },
                  { form: 'ICS 206', title: 'Medical Plan', desc: 'Medical emergency procedures' },
                  { form: 'ICS 208', title: 'Safety Message Plan', desc: 'ICS Safety Plan' },
                  { form: 'ICS 209', title: 'Incident Status Summary', desc: 'ICS Summary Status' },
                  { form: 'ICS 210', title: 'Resource Status Summary', desc: 'Summary of all Resources' },
                  { form: 'ICS 211', title: 'Check-In', desc: 'Personnel Check-In to Event' },
                  { form: 'ICS 213', title: 'General Message', desc: 'General Messaging within ICS' },
                  { form: 'ICS 214', title: 'Activity Log', desc: 'Log of events during event' },
                  { form: 'ICS 215', title: 'Operation Plan Worksheet', desc: 'Develop plan and activities' },
                  { form: 'ICS 216', title: 'Radio Requirement Worksheet', desc: 'Radio equipment and freqs for event' },
                  { form: 'ICS 218', title: 'Support Vehicles / Equip Inventory', desc: 'Inventory of support needs' },
                  { form: 'ICS 220', title: 'Air Operations Summary', desc: 'Inventory of Air Assets and Tasks' },
                  { form: 'ICS 221', title: 'Demobilization on Checkout', desc: 'Departure of Personnel' },
                  { form: 'ICS 224', title: 'Crew Performance Rating', desc: 'Crew rating during event' },
                  { form: 'ICS 225', title: 'Incident Personnel Performance Rating', desc: 'Personnel rating during event' },
                  { form: 'ICS 230', title: 'Daily Meeting Schedule', desc: 'Daily discussion of events and concerns' },
                  { form: 'ICS 232', title: 'Resources at Risk Summary', desc: 'Risk of resources in function and availability' },
                  { form: 'ICS 233', title: 'Incident Open Action Tracker', desc: 'Worksheet for actions still open' },
                  { form: 'ICS 234', title: 'Work Analysis Matrix', desc: 'Analyze your work' },
                  { form: 'ICS 259e', title: 'Resource Order Supplies', desc: 'Order Supplies and Consumables' },
                  { form: 'ICS 309', title: 'Communication Log', desc: 'Capture comms messages' }
                ].map((item) => {
                  // ICS form navigation mapping
                  const icsNavigation = {
                    'ICS 201': 'scenario',
                    'ICS 202': 'objectives', 
                    'ICS 203': 'organization-chart',
                    'ICS 204': 'assignment-list',
                    'ICS 205': 'comms-plan',
                    'ICS 206': 'medical-unit',
                    'ICS 208': 'safety-officer',
                    'ICS 209': 'incident-header',
                    'ICS 210': 'ics-210-resources',
                    'ICS 211': 'participants',
                    'ICS 213': 'comms-general-message',
                    'ICS 214': 'activity-log',
                    'ICS 215': 'ics-215',
                    'ICS 218': 'resources',
                    'ICS 220': 'air-ops-summ',
                    'ICS 221': 'demobile-checkout',
                    'ICS 224': 'crew-perf-rating',
                    'ICS 225': 'personnel-perf',
                    'ICS 230': 'daily-schedule',
                    'ICS 232': 'ics-232',
                    'ICS 233': 'incident-tracker',
                    'ICS 234': 'objectives',
                    'ICS 259e': 'supplies-order',
                    'ICS 309': 'comms-log'
                  };
                  
                  const isClickable = icsNavigation.hasOwnProperty(item.form);
                  
                  const getClickHandler = () => {
                    if (isClickable) {
                      // Special handling for ICS 211 - navigate to Emergency Management > Participants
                      if (item.form === 'ICS 211') {
                        return () => {
                          window.location.href = '#participants';
                        };
                      }
                      return () => updateICSMenu(icsNavigation[item.form]);
                    }
                    return undefined;
                  };
                  
                  const getTooltip = () => {
                    const tooltips = {
                      'ICS 201': 'Click to view ICS 201 - Incident Briefing',
                      'ICS 202': 'Click to view ICS 202 - Incident Objectives',
                      'ICS 203': 'Click to view ICS 203 - Organization Assignment List',
                      'ICS 204': 'Click to view ICS 204 - Assignment List',
                      'ICS 205': 'Click to view ICS 205 - Communications Plan',
                      'ICS 206': 'Click to view ICS 206 - Medical Plan',
                      'ICS 208': 'Click to view ICS 208 - Safety Plan',
                      'ICS 209': 'Click to view ICS 209 - Incident Status Summary',
                      'ICS 210': 'Click to view ICS 210 - Resource Status List',
                      'ICS 211': 'Click to view ICS 211 - Check-In (Participants Form)',
                      'ICS 213': 'Click to view ICS 213 - General Message',
                      'ICS 214': 'Click to view ICS 214 - Activity Log',
                      'ICS 215': 'Click to view ICS 215 - Op Plan Worksheet',
                      'ICS 218': 'Click to view ICS 218 - Resource Management',
                      'ICS 220': 'Click to view ICS 220 - Air Operations Summary',
                      'ICS 221': 'Click to view ICS 221 - Demobilization Checkout',
                      'ICS 224': 'Click to view ICS 224 - Crew Performance Rating',
                      'ICS 225': 'Click to view ICS 225 - Personnel Performance Rating',
                      'ICS 230': 'Click to view ICS 230 - Daily Meeting Schedule',
                      'ICS 232': 'Click to view ICS 232 - Resources at Risk Summary',
                      'ICS 233': 'Click to view ICS 233 - Incident Open Action Tracker',
                      'ICS 234': 'Click to view ICS 234 - Work Analysis Matrix',
                      'ICS 259e': 'Click to view ICS 259e - Resource Order Supplies',
                      'ICS 309': 'Click to view ICS 309 - Communications Log'
                    };
                    return tooltips[item.form] || undefined;
                  };
                  
                  return (
                    <div 
                      key={item.form} 
                      className={`p-4 rounded-lg ${theme.colors.card} border ${theme.colors.border} hover:border-orange-500/50 hover:bg-gray-600 transition-colors ${isClickable ? 'cursor-pointer hover:shadow-md' : ''}`}
                      onClick={getClickHandler()}
                      title={getTooltip()}
                    >
                      <div className="flex items-start space-x-3">
                        <FileText className="h-6 w-6 text-orange-500 mt-1 flex-shrink-0" />
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h3 className={`font-semibold ${theme.colors.textPrimary} mb-1`}>{item.form}</h3>
                            {isClickable && (
                              <ExternalLink className="h-4 w-4 text-orange-500" />
                            )}
                          </div>
                          <h4 className={`text-sm font-medium ${theme.colors.textSecondary} mb-1`}>{item.title}</h4>
                          <p className={`text-xs ${theme.colors.textMuted}`}>{item.desc}</p>
                          {isClickable && (
                            <p className={`text-xs text-orange-600 mt-2 font-medium`}>← Click to access form</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className={`mt-8 p-4 rounded-lg bg-blue-900/20 border border-blue-500/30`}>
                <div className="flex items-center space-x-2 mb-2">
                  <Info className="h-5 w-5 text-blue-400" />
                  <span className="font-semibold text-blue-300">Coming Soon</span>
                </div>
                <p className="text-blue-200 text-sm">
                  Interactive forms with auto-fill, digital signatures, and real-time collaboration features are in development.
                </p>
              </div>
            </div>
          </div>
        );
        
      case 'activity-log':
        return <ICSActivityLogForm currentExercise={currentExercise} />;
        
      case 'participants':
        return <ParticipantsView positions={positions} currentExercise={currentExercise} />;

      case 'images':
        return <ImageRepository currentExercise={currentExercise} theme={theme} />;
      
      case 'analytics':
        return (
          <AnalyticsDashboard 
            exerciseId={currentExercise?.id}
            exerciseName={currentExercise?.exercise_name}
            token={secureStorage.getToken()}
          />
        );

      case 'evaluations':
        return <ICSEvaluationsPanel exerciseId={currentExercise?.id} theme={theme} />;
      case 'lessons_learned':
        return <ICSLessonsLearnedPanel exerciseId={currentExercise?.id} theme={theme} />;
        
      case 'time-unit':
        return <TimeUnitForm currentExercise={currentExercise} theme={theme} />;
      case 'procurement-unit':
        return <ProcurementForm currentExercise={currentExercise} theme={theme} />;
      case 'comp-claims-unit':
        return <CompensationClaimsForm currentExercise={currentExercise} theme={theme} />;
      case 'cost-unit':
        return <CostUnitForm currentExercise={currentExercise} theme={theme} />;
      case 'ics-215':
        return <ICS215Form currentExercise={currentExercise} theme={theme} />;
      case 'ics-210-resources':
        return <ResourcesView positions={positions} currentExercise={currentExercise} />;
      case 'ics-232':
        return <ICS232Form currentExercise={currentExercise} theme={theme} />;
      case 'resources':
        return <ResourcesView positions={positions} currentExercise={currentExercise} />;
      case 'incident-tracker':
        return <ICS233Form currentExercise={currentExercise} theme={theme} />;
      // New Planning submenu items
      case 'resources-unit':
        return (
          <div className="p-6">
            <div className="text-center py-12">
              <Users className={`h-16 w-16 ${theme.colors.textMuted} mx-auto mb-4`} />
              <h2 className={`text-2xl font-bold ${theme.colors.textPrimary} mb-4`}>Resources Unit</h2>
              <p className={`${theme.colors.textMuted} mb-6 max-w-2xl mx-auto`}>
                Track and manage all incident resources including personnel, equipment, and supplies. Use the submenu to access Resources Unit modules.
              </p>
            </div>
          </div>
        );
      case 'strike-teams':
        return <StrikeTeamsModule currentExercise={currentExercise} theme={theme} />;
      case 'task-forces':
        return <TaskForcesModule currentExercise={currentExercise} theme={theme} />;
      case 'aircraft-order':
        return <ICS259Form currentExercise={currentExercise} theme={theme} />;
      case 'crews-order':
        return <ICS259CrewForm currentExercise={currentExercise} theme={theme} />;
      case 'equipment-order':
        return <ICS259EquipmentForm currentExercise={currentExercise} theme={theme} />;
      case 'overhead-order':
        return <ICS259OverheadForm currentExercise={currentExercise} theme={theme} />;
      case 'supplies-order':
        return <ICS259SuppliesForm currentExercise={currentExercise} theme={theme} />;
      case 'tech-spec-unit':
        return (
          <div className="p-6">
            <div className="text-center py-12">
              <Settings className={`h-16 w-16 ${theme.colors.textMuted} mx-auto mb-4`} />
              <h2 className={`text-2xl font-bold ${theme.colors.textPrimary} mb-4`}>Technical Specialist Unit</h2>
              <p className={`${theme.colors.textMuted} mb-6 max-w-2xl mx-auto`}>
                Provide specialized technical expertise and support for incident operations.
              </p>
            </div>
          </div>
        );
      case 'mapping':
        return (
          <div className="h-full">
            <LeafletMapping exerciseId={currentExercise?.id} />
          </div>
        );
      case 'weather':
        return <WeatherModule theme={theme} />;
      case 'wx-obs':
        return <WeatherObservationModule theme={theme} exerciseId={currentExercise?.id} />;
      case 'wx-report':
        return <WXReportModule theme={theme} exerciseId={currentExercise?.id} />;
      case 'severe-wx':
        return <SevereWxModule theme={theme} exerciseId={currentExercise?.id} />;
      // New Services submenu items
      case 'comms-plan-brief':
        return <CommsPlanBriefModule theme={theme} currentExercise={currentExercise} currentPhase={emergencyPhase} />;
      case 'comms-plan':
        return (
          <CommsPlanForm
            theme={theme}
            currentExercise={currentExercise}
            ics205Frequencies={ics205Frequencies}
            currentIcs205Frequency={currentIcs205Frequency}
            setCurrentIcs205Frequency={setCurrentIcs205Frequency}
            editingIcs205Frequency={editingIcs205Frequency}
            loading={ics205Loading}
            lastSaved={ics205LastSaved}
            hasUnsavedChanges={ics205HasUnsavedChanges}
            saveStatus={ics205SaveStatus}
            connectionHealth={ics205ConnectionHealth}
            verifyBackendHealth={verifyIcs205BackendHealth}
            addIcs205Frequency={addIcs205Frequency}
            editIcs205Frequency={editIcs205Frequency}
            cancelEditIcs205Frequency={cancelEditIcs205Frequency}
            deleteIcs205Frequency={deleteIcs205Frequency}
            printIcs205Combined={printIcs205Combined}
            saveFrequencies={saveIcs205Frequencies}
            refreshFrequencies={refreshIcs205Frequencies}
          />
        );
      case 'comms-list':
        // Show only participants assigned to the current exercise (by id or name)
        const exerciseParticipants = participants.filter(p =>
          p.assigned_exercise === currentExercise?.id ||
          p.assigned_exercise === currentExercise?.exercise_name
        );
        
        return (
          <div className="p-6 space-y-6">
            {/* ICS 205A Header */}
            <div className="text-center mb-8">
              <h1 className={`text-3xl font-bold ${theme.colors.textPrimary} mb-2`}>ICS 205A - Communications List</h1>
              <p className={`${theme.colors.textMuted} text-lg`}>
                Contact information for exercise participants involved in incident operations
              </p>
            </div>

            {/* Exercise Participants Display */}
            {exerciseParticipants.length > 0 ? (
              <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
                <CardHeader>
                  <CardTitle className="text-orange-500 flex items-center">
                    <ClipboardList className="h-5 w-5 mr-2" />
                    ICS 205A Exercise Participants ({exerciseParticipants.length} participants)
                  </CardTitle>
                  <CardDescription className={`${theme.colors.textMuted}`}>
                    Participants involved in the current exercise with their contact information
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Table Header */}
                  <div className={`hidden md:grid grid-cols-12 gap-2 p-3 ${theme.colors.secondaryBackground} rounded-t-lg border-b ${theme.colors.border} font-semibold text-sm ${theme.colors.textSecondary}`}>
                    <div className="col-span-2">Name</div>
                    <div className="col-span-2">Position</div>
                    <div className="col-span-2">Organization</div>
                    <div className="col-span-2">Cell Phone</div>
                    <div className="col-span-2">Home Phone</div>
                    <div className="col-span-2">Email</div>
                  </div>
                  {/* Table Rows */}
                  <div className="divide-y divide-gray-700">
                    {exerciseParticipants
                      .sort((a, b) => {
                        const aLastName = a.lastName || a.name?.split(' ').pop() || '';
                        const bLastName = b.lastName || b.name?.split(' ').pop() || '';
                        return aLastName.localeCompare(bLastName);
                      })
                      .map((participant) => (
                        <div key={participant.id} className={`grid grid-cols-1 md:grid-cols-12 gap-2 p-3 hover:${theme.colors.secondaryBackground} transition-colors`}>
                          {/* Mobile: Stacked layout */}
                          <div className="md:hidden space-y-1 p-2 bg-gray-800 rounded-lg">
                            <div className={`font-semibold ${theme.colors.textPrimary}`}>
                              {participant.firstName && participant.lastName 
                                ? `${participant.firstName} ${participant.lastName}`
                                : participant.name || 'Unknown'}
                            </div>
                            <div className={`${theme.colors.textMuted} text-sm`}>{participant.position || '-'}</div>
                            <div className={`${theme.colors.textMuted} text-sm`}>{participant.organization || '-'}</div>
                            <div className={`${theme.colors.textMuted} text-sm`}>{participant.email || '-'}</div>
                            <div className={`${theme.colors.textMuted} text-sm`}>{participant.homePhone || '-'} / {participant.cellPhone || '-'}</div>
                          </div>
                          {/* Desktop: Single row layout */}
                          <div className={`hidden md:block col-span-2 ${theme.colors.textPrimary} font-medium text-sm truncate`} title={participant.firstName && participant.lastName ? `${participant.firstName} ${participant.lastName}` : participant.name || 'Unknown'}>
                            {participant.firstName && participant.lastName 
                              ? `${participant.firstName} ${participant.lastName}`
                              : participant.name || 'Unknown'}
                          </div>
                          <div className={`hidden md:block col-span-2 ${theme.colors.textMuted} text-sm truncate`} title={participant.position || '-'}>
                            {participant.position || '-'}
                          </div>
                          <div className={`hidden md:block col-span-2 ${theme.colors.textMuted} text-sm truncate`} title={participant.organization || '-'}>
                            {participant.organization || '-'}
                          </div>
                          <div className={`hidden md:block col-span-2 ${theme.colors.textMuted} text-sm truncate`} title={participant.cellPhone || '-'}>
                            {participant.cellPhone || '-'}
                          </div>
                          <div className={`hidden md:block col-span-2 ${theme.colors.textMuted} text-sm truncate`} title={participant.homePhone || '-'}>
                            {participant.homePhone || '-'}
                          </div>
                          <div className={`hidden md:block col-span-2 ${theme.colors.textMuted} text-sm truncate`} title={participant.email || '-'}>
                            {participant.email || '-'}
                          </div>
                        </div>
                      ))
                    }
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
                <CardContent className="p-8 text-center">
                  <ClipboardList className={`h-16 w-16 ${theme.colors.textMuted} mx-auto mb-4`} />
                  <h3 className={`text-xl font-semibold ${theme.colors.textPrimary} mb-2`}>No Exercise Participants</h3>
                  <p className={`${theme.colors.textMuted} mb-4`}>
                    No participants are currently marked as "involved in the current exercise."
                  </p>
                  <p className={`${theme.colors.textSecondary} text-sm`}>
                    Add participants through the Exercise Builder → Participants section and check 
                    "Participant is involved in the current exercise" to populate this list.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Plan Entries card moved to Comms Plan menu (CommsPlanBriefModule) */}

            {/* Print ICS 205A Button */}
            <div className="flex gap-4">
              <Button 
                onClick={() => {
                  const printContent = `
                    <html>
                      <head>
                        <title>ICS 205A - Communications List</title>
                        <style>
                          @media print { @page { margin: 0.5in; } }
                          body { font-family: Arial, sans-serif; margin: 0; padding: 20px; background: white; }
                          .header { text-align: center; border-bottom: 2px solid #f97316; padding-bottom: 15px; margin-bottom: 20px; }
                          .title { font-size: 24px; font-weight: bold; color: #f97316; margin-bottom: 5px; }
                          .subtitle { font-size: 14px; color: #666; }
                          .participant-item { padding: 12px; margin: 8px 0; border: 1px solid #e5e7eb; border-radius: 5px; background: #f9fafb; }
                          .participant-name { font-weight: bold; font-size: 16px; color: #374151; }
                          .participant-details { font-size: 12px; color: #6b7280; margin-top: 5px; line-height: 1.4; }
                          .contact-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-top: 8px; }
                          .footer { margin-top: 30px; text-align: center; padding-top: 15px; border-top: 1px solid #e5e7eb; font-size: 12px; color: #6b7280; }
                        </style>
                      </head>
                      <body>
                        <div class="header">
                          <div class="title">ICS 205A - Communications List</div>
                          <div class="subtitle">Exercise Participants Contact Information</div>
                          <div class="subtitle">Generated on ${new Date().toLocaleString()}</div>
                        </div>
                        ${exerciseParticipants
                          .sort((a, b) => {
                            const aLastName = a.lastName || a.name?.split(' ').pop() || '';
                            const bLastName = b.lastName || b.name?.split(' ').pop() || '';
                            return aLastName.localeCompare(bLastName);
                          })
                          .map(participant => `
                            <div class="participant-item">
                              <div class="participant-name">
                                ${participant.firstName && participant.lastName 
                                  ? `${participant.firstName} ${participant.lastName}`
                                  : participant.name || 'Unknown Participant'
                                }
                              </div>
                              <div class="participant-details">
                                <div><strong>Position:</strong> ${participant.position || 'Not assigned'}</div>
                                <div><strong>Organization:</strong> ${participant.organization || 'Not specified'}</div>
                                <div class="contact-grid">
                                  <div><strong>Cell Phone:</strong> ${participant.cellPhone || 'Not provided'}</div>
                                  <div><strong>Home Phone:</strong> ${participant.homePhone || 'Not provided'}</div>
                                  <div><strong>Email:</strong> ${participant.email || 'Not provided'}</div>
                                </div>
                              </div>
                            </div>
                          `).join('')
                        }
                        ${exerciseParticipants.length === 0 ? 
                          '<div style="text-align: center; color: #6b7280; font-style: italic; margin: 30px 0;">No exercise participants available to display.</div>' 
                          : ''
                        }
                        <div class="footer">
                          <div>Total Exercise Participants: ${exerciseParticipants.length}</div>
                          <div>Powered by EXRSIM - Emergency Response Simulation Platform</div>
                        </div>
                      </body>
                    </html>
                  `;
                  const printWindow = window.open('', '_blank');
                  writeSanitizedHTML(printWindow.document, printContent);
                  printWindow.focus();
                  setTimeout(() => {
                    printWindow.print();
                    printWindow.close();
                  }, 250);
                }}
                variant="outline"
                className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print ICS 205A
              </Button>
            </div>
          </div>
        );
      case 'comms-log':
        return <ICS309Form currentExercise={currentExercise} theme={theme} />;
      case 'comms-general-message':
        return <ICSGeneralMessageForm currentExercise={currentExercise} />;
      case 'medical-unit':
        return (
          <ICS206MedicalPlan
            theme={theme}
            currentExercise={currentExercise}
            medAidStations={medAidStations}
            currentMedAidStation={currentMedAidStation}
            setCurrentMedAidStation={setCurrentMedAidStation}
            editingMedAidStation={editingMedAidStation}
            addMedAidStation={addMedAidStation}
            editMedAidStation={editMedAidStation}
            cancelEditMedAidStation={cancelEditMedAidStation}
            deleteMedAidStation={deleteMedAidStation}
            medTransports={medTransports}
            currentMedTransport={currentMedTransport}
            setCurrentMedTransport={setCurrentMedTransport}
            editingMedTransport={editingMedTransport}
            addMedTransport={addMedTransport}
            editMedTransport={editMedTransport}
            cancelEditMedTransport={cancelEditMedTransport}
            deleteMedTransport={deleteMedTransport}
            hospitals={hospitals}
            currentHospital={currentHospital}
            setCurrentHospital={setCurrentHospital}
            editingHospital={editingHospital}
            hospitalValidation={hospitalValidation}
            addHospital={addHospital}
            editHospital={editHospital}
            cancelEditHospital={cancelEditHospital}
            deleteHospital={deleteHospital}
            medPlanEntries={medPlanEntries}
            currentMedPlanEntry={currentMedPlanEntry}
            setCurrentMedPlanEntry={setCurrentMedPlanEntry}
            editingMedPlanEntry={editingMedPlanEntry}
            addMedPlanEntry={addMedPlanEntry}
            editMedPlanEntry={editMedPlanEntry}
            cancelEditMedPlanEntry={cancelEditMedPlanEntry}
            deleteMedPlanEntry={deleteMedPlanEntry}
          />
        );
      case 'food-unit':
        return <FoodUnitForm currentExercise={currentExercise} theme={theme} />;
      // New Support submenu items
      case 'supply-unit':
        return (
          <div className="p-6">
            <div className="text-center py-12">
              <Package className={`h-16 w-16 ${theme.colors.textMuted} mx-auto mb-4`} />
              <h2 className={`text-2xl font-bold ${theme.colors.textPrimary} mb-4`}>Supply Unit</h2>
              <p className={`${theme.colors.textMuted} mb-6 max-w-2xl mx-auto`}>
                Manage procurement, storage, and distribution of supplies and equipment.
              </p>
            </div>
          </div>
        );
      case 'facilities-unit':
        return <FacilitiesUnitForm currentExercise={currentExercise} theme={theme} />;
      case 'demobilization-unit':
        return (
          <div className="p-6">
            <div className="text-center py-12">
              <ArrowLeft className={`h-16 w-16 ${theme.colors.textMuted} mx-auto mb-4`} />
              <h2 className={`text-2xl font-bold ${theme.colors.textPrimary} mb-4`}>Demobilization Unit</h2>
              <p className={`${theme.colors.textMuted} mb-6 max-w-2xl mx-auto`}>
                Manage demobilization processes and resource release. Use the submenu to access Demobilization Unit modules.
              </p>
            </div>
          </div>
        );
      case 'demobile-checkout':
        return <ICS221Form currentExercise={currentExercise} theme={theme} />;
      case 'crew-perf-rating':
        return <ICS224Form currentExercise={currentExercise} theme={theme} />;
      case 'personnel-perf':
        return <ICS225Form currentExercise={currentExercise} theme={theme} />;
      case 'daily-schedule':
        return <ICS230Form currentExercise={currentExercise} theme={theme} />;
      case 'after-action-report':
        return <AfterActionReportSection currentExercise={currentExercise} theme={theme} />;
      default:
        return (
          <div className="p-6">
            <div className="text-center py-12">
              <Shield className={`h-16 w-16 ${theme.colors.textMuted} mx-auto mb-4`} />
              <h2 className={`text-2xl font-bold ${theme.colors.textPrimary} mb-4`}>ICS Dashboard</h2>
              <p className={`${theme.colors.textMuted} mb-6`}>
                Select a module from the sidebar to get started.
              </p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="flex h-full overflow-hidden relative">
        {/* Mobile backdrop for ICS sidebar */}
        {icsDrawerOpen && (
          <button
            aria-label="Close ICS menu"
            onClick={() => setIcsDrawerOpen(false)}
            data-testid="ics-sidebar-mobile-backdrop"
            className="fixed inset-0 z-[999] bg-black/60 md:hidden"
          />
        )}
        {/* ICS Sidebar — drawer on mobile, static on md+ */}
        <div
          data-testid="ics-sidebar"
          className={`
            ${theme.colors.secondary} border-r ${theme.colors.border} flex flex-col
            fixed inset-y-0 left-0 z-[1000] w-72 h-full transition-transform duration-300
            ${icsDrawerOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full'}
            md:relative md:translate-x-0 md:w-64 md:flex-shrink-0
          `}
        >
          {/* Mobile-only close button */}
          <button
            onClick={() => setIcsDrawerOpen(false)}
            className="md:hidden absolute top-3 right-3 z-10 text-gray-400 hover:text-orange-500 p-2"
            aria-label="Close ICS menu"
            data-testid="ics-sidebar-close-btn"
          >
            <XIcon className="h-5 w-5" />
          </button>
          <div className="p-4 flex-shrink-0 pt-12 md:pt-4">
            <h2 className={`text-lg font-semibold ${theme.colors.textPrimary} mb-4`}>
              Incident Command System
            </h2>
          </div>
          <div className="flex-1 overflow-y-auto px-4 pb-4">
            <nav className="space-y-1">
              {icsMenuItems.map((item) => {
                // Handle separator
                if (item.isSeparator) {
                  return (
                    <div key={item.id} className="my-2 border-t border-gray-700"></div>
                  );
                }
                
                const Icon = item.icon;
                const isActive = activeICSMenu === item.id;
                
                return (
                  <div key={item.id} className={item.id === 'improvement' ? 'mb-[100px]' : ''}>
                    <Button
                      variant={isActive ? "secondary" : "ghost"}
                      className={`w-full justify-start text-left ${
                        item.id === 'operations' 
                          ? 'bg-orange-500 text-white hover:bg-orange-600'
                          : item.backgroundColor
                          ? `${item.backgroundColor} text-white hover:opacity-80`
                          : item.textColor
                          ? `${item.textColor} hover:bg-gray-800`
                          : isActive
                            ? `${theme.colors.accent} ${theme.colors.textPrimary}`
                            : `${theme.colors.textSecondary} ${theme.colors.hover}`
                      }`}
                      onClick={() => {
                        if (item.hasSubmenu) {
                          // For items with submenus, just toggle expansion and set active
                          setActiveICSMenu(item.id);
                          if (item.id === 'command-staff') {
                            setCommandStaffExpanded(!commandStaffExpanded);
                          }
                          if (item.id === 'operations') {
                            setOperationsExpanded(!operationsExpanded);
                          }
                          if (item.id === 'planning') {
                            setPlanningExpanded(!planningExpanded);
                          }
                          if (item.id === 'logistics') {
                            setLogisticsExpanded(!logisticsExpanded);
                          }
                          if (item.id === 'fin-admin') {
                            setFinAdminExpanded(!finAdminExpanded);
                          }
                          if (item.id === 'improvement') {
                            setImprovementICSExpanded(!improvementICSExpanded);
                          }
                        } else {
                          // For items without submenus, update the view
                          updateICSMenu(item.id);
                        }
                      }}
                    >
                      <Icon className={`h-4 w-4 mr-3 ${item.textColor || ''} flex-shrink-0`} />
                      <span className={
                        item.id === 'operations' || item.backgroundColor 
                          ? 'text-white' 
                          : item.textColor
                          ? item.textColor
                          : isActive 
                            ? 'text-black font-bold' 
                            : ''
                      }>{item.label}</span>
                      {item.hasSubmenu && item.id === 'command-staff' && (
                        commandStaffExpanded ? (
                          <ChevronDown className="h-4 w-4 ml-auto" />
                        ) : (
                          <ChevronRight className="h-4 w-4 ml-auto" />
                        )
                      )}
                      {item.hasSubmenu && item.id === 'operations' && (
                        operationsExpanded ? (
                          <ChevronDown className="h-4 w-4 ml-auto" />
                        ) : (
                          <ChevronRight className="h-4 w-4 ml-auto" />
                        )
                      )}
                      {item.hasSubmenu && item.id === 'planning' && (
                        planningExpanded ? (
                          <ChevronDown className="h-4 w-4 ml-auto" />
                        ) : (
                          <ChevronRight className="h-4 w-4 ml-auto" />
                        )
                      )}
                      {item.hasSubmenu && item.id === 'logistics' && (
                        logisticsExpanded ? (
                          <ChevronDown className="h-4 w-4 ml-auto" />
                        ) : (
                          <ChevronRight className="h-4 w-4 ml-auto" />
                        )
                      )}
                      {item.hasSubmenu && item.id === 'fin-admin' && (
                        finAdminExpanded ? (
                          <ChevronDown className="h-4 w-4 ml-auto" />
                        ) : (
                          <ChevronRight className="h-4 w-4 ml-auto" />
                        )
                      )}
                      {item.hasSubmenu && item.id === 'improvement' && (
                        improvementICSExpanded ? (
                          <ChevronDown className="h-4 w-4 ml-auto" />
                        ) : (
                          <ChevronRight className="h-4 w-4 ml-auto" />
                        )
                      )}
                    </Button>
                    
                    {/* Command Staff Submenu */}
                    {item.hasSubmenu && item.id === 'command-staff' && commandStaffExpanded && (
                      <div className="ml-4 mt-1 space-y-1">
                        {item.submenuItems?.map((subItem) => {
                          const SubIcon = subItem.icon;
                          const isSubActive = activeICSMenu === subItem.id;
                          
                          return (
                            <Button
                              key={subItem.id}
                              variant="ghost"
                              className={`w-full justify-start text-left text-sm ${
                                subItem.backgroundColor 
                                  ? `${subItem.backgroundColor} text-white hover:opacity-80`
                                  : isSubActive
                                    ? `${theme.colors.accent} ${theme.colors.textPrimary}`
                                    : `${theme.colors.textSecondary} ${theme.colors.hover}`
                              }`}
                              onClick={() => updateICSMenu(subItem.id)}
                            >
                              <SubIcon className="h-4 w-4 mr-3" />
                              <span className={isSubActive ? "text-black font-bold" : "text-white"}>{subItem.label}</span>
                            </Button>
                          );
                        })}
                      </div>
                    )}

                    {/* Operations Submenu */}
                    {item.hasSubmenu && item.id === 'operations' && operationsExpanded && (
                      <div className="ml-4 mt-1 space-y-1">
                        {item.submenuItems?.map((subItem) => {
                          const SubIcon = subItem.icon;
                          const isSubActive = activeICSMenu === subItem.id;
                          
                          return (
                            <div key={subItem.id}>
                              <Button
                                variant="ghost"
                                className={`w-full justify-start text-left text-sm ${
                                  subItem.backgroundColor 
                                    ? `${subItem.backgroundColor} text-white hover:opacity-80`
                                    : isSubActive
                                      ? `${theme.colors.accent} ${theme.colors.textPrimary}`
                                      : `${theme.colors.textMuted} ${theme.colors.hover}`
                                }`}
                                onClick={() => {
                                  if (subItem.hasSubmenu && subItem.id === 'safety') {
                                    setSafetyExpanded(!safetyExpanded);
                                  } else if (subItem.hasSubmenu && subItem.id === 'incident-status-summary') {
                                    setIncidentStatusExpanded(!incidentStatusExpanded);
                                  } else if (subItem.hasSubmenu && subItem.id === 'incident-action-plan') {
                                    setIncidentActionPlanExpanded(!incidentActionPlanExpanded);
                                  } else if (subItem.hasSubmenu && subItem.id === 'air-ops') {
                                    setAirOpsExpanded(!airOpsExpanded);
                                  } else {
                                    updateICSMenu(subItem.id);
                                  }
                                }}
                              >
                                <SubIcon className="h-4 w-4 mr-3" />
                                <span className={isSubActive ? "text-black font-bold" : "text-white"}>{subItem.label}</span>
                                {subItem.hasSubmenu && subItem.id === 'safety' && (
                                  safetyExpanded ? (
                                    <ChevronDown className="h-4 w-4 ml-auto text-white" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4 ml-auto text-white" />
                                  )
                                )}
                                {subItem.hasSubmenu && subItem.id === 'incident-action-plan' && (
                                  incidentActionPlanExpanded ? (
                                    <ChevronDown className="h-4 w-4 ml-auto text-white" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4 ml-auto text-white" />
                                  )
                                )}
                                {subItem.hasSubmenu && subItem.id === 'air-ops' && (
                                  airOpsExpanded ? (
                                    <ChevronDown className="h-4 w-4 ml-auto text-white" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4 ml-auto text-white" />
                                  )
                                )}
                                {subItem.hasSubmenu && subItem.id === 'incident-status-summary' && (
                                  incidentStatusExpanded ? (
                                    <ChevronDown className="h-4 w-4 ml-auto text-white" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4 ml-auto text-white" />
                                  )
                                )}
                              </Button>
                              
                              {/* Safety Nested Submenu */}
                              {subItem.hasSubmenu && subItem.id === 'safety' && safetyExpanded && (
                                <div className="ml-6 mt-1 space-y-1">
                                  {subItem.submenuItems?.map((nestedItem) => {
                                    const NestedIcon = nestedItem.icon;
                                    const isNestedActive = activeICSMenu === nestedItem.id;
                                    
                                    return (
                                      <Button
                                        key={nestedItem.id}
                                        variant="ghost"
                                        className={`w-full justify-start text-left text-xs ${
                                          nestedItem.backgroundColor 
                                            ? `${nestedItem.backgroundColor} text-white hover:opacity-80`
                                            : isNestedActive
                                              ? `${theme.colors.accent} ${theme.colors.textPrimary}`
                                              : `${theme.colors.textMuted} ${theme.colors.hover}`
                                        }`}
                                        onClick={() => updateICSMenu(nestedItem.id)}
                                      >
                                        <NestedIcon className="h-3 w-3 mr-3" />
                                        <span className={isNestedActive ? "text-black font-bold" : "text-white"}>{nestedItem.label}</span>
                                      </Button>
                                    );
                                  })}
                                </div>
                              )}

                              {/* Incident Action Plan Nested Submenu */}
                              {subItem.hasSubmenu && subItem.id === 'incident-action-plan' && incidentActionPlanExpanded && (
                                <div className="ml-6 mt-1 space-y-1">
                                  {subItem.submenuItems?.map((nestedItem) => {
                                    const NestedIcon = nestedItem.icon;
                                    const isNestedActive = activeICSMenu === nestedItem.id;
                                    
                                    return (
                                      <Button
                                        key={nestedItem.id}
                                        variant="ghost"
                                        className={`w-full justify-start text-left text-xs ${
                                          nestedItem.backgroundColor 
                                            ? `${nestedItem.backgroundColor} text-white hover:opacity-80`
                                            : isNestedActive
                                              ? `${theme.colors.accent} ${theme.colors.textPrimary}`
                                              : `${theme.colors.textMuted} ${theme.colors.hover}`
                                        }`}
                                        onClick={() => updateICSMenu(nestedItem.id)}
                                      >
                                        <NestedIcon className="h-3 w-3 mr-3" />
                                        <span className={isNestedActive ? "text-black font-bold" : "text-white"}>{nestedItem.label}</span>
                                      </Button>
                                    );
                                  })}
                                </div>
                              )}

                              {/* Air Ops Nested Submenu */}
                              {subItem.hasSubmenu && subItem.id === 'air-ops' && airOpsExpanded && (
                                <div className="ml-6 mt-1 space-y-1">
                                  {subItem.submenuItems?.map((nestedItem) => {
                                    const NestedIcon = nestedItem.icon;
                                    const isNestedActive = activeICSMenu === nestedItem.id;
                                    
                                    return (
                                      <Button
                                        key={nestedItem.id}
                                        variant="ghost"
                                        className={`w-full justify-start text-left text-xs ${
                                          nestedItem.backgroundColor 
                                            ? `${nestedItem.backgroundColor} text-white hover:opacity-80`
                                            : isNestedActive
                                              ? `${theme.colors.accent} ${theme.colors.textPrimary}`
                                              : `${theme.colors.textMuted} ${theme.colors.hover}`
                                        }`}
                                        onClick={() => updateICSMenu(nestedItem.id)}
                                      >
                                        <NestedIcon className="h-3 w-3 mr-3" />
                                        <span className={isNestedActive ? "text-black font-bold" : "text-white"}>{nestedItem.label}</span>
                                      </Button>
                                    );
                                  })}
                                </div>
                              )}

                              {/* Incident Status Summary Nested Submenu */}
                              {subItem.hasSubmenu && subItem.id === 'incident-status-summary' && incidentStatusExpanded && (
                                <div className="ml-6 mt-1 space-y-1">
                                  {subItem.submenuItems?.map((nestedItem) => {
                                    const NestedIcon = nestedItem.icon;
                                    const isNestedActive = activeICSMenu === nestedItem.id;
                                    
                                    return (
                                      <Button
                                        key={nestedItem.id}
                                        variant="ghost"
                                        className={`w-full justify-start text-left text-xs ${
                                          nestedItem.backgroundColor 
                                            ? `${nestedItem.backgroundColor} text-white hover:opacity-80`
                                            : isNestedActive
                                              ? `${theme.colors.accent} ${theme.colors.textPrimary}`
                                              : `${theme.colors.textMuted} ${theme.colors.hover}`
                                        }`}
                                        onClick={() => updateICSMenu(nestedItem.id)}
                                      >
                                        <NestedIcon className="h-3 w-3 mr-3" />
                                        <span className={isNestedActive ? "text-black font-bold" : "text-white"}>{nestedItem.label}</span>
                                      </Button>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Planning Submenu */}
                    {item.hasSubmenu && item.id === 'planning' && planningExpanded && (
                      <div className="ml-4 mt-1 space-y-1">
                        {item.submenuItems?.map((subItem) => {
                          const SubIcon = subItem.icon;
                          const isSubActive = activeICSMenu === subItem.id;
                          
                          return (
                            <div key={subItem.id}>
                              <Button
                                variant="ghost"
                                className={`w-full justify-start text-left text-sm ${
                                  subItem.backgroundColor 
                                    ? `${subItem.backgroundColor} text-white hover:opacity-80`
                                    : isSubActive
                                      ? `${theme.colors.accent} ${theme.colors.textPrimary}`
                                      : `${theme.colors.textMuted} ${theme.colors.hover}`
                                }`}
                                onClick={() => {
                                  if (subItem.hasSubmenu && subItem.id === 'situation-unit') {
                                    setSituationExpanded(!situationExpanded);
                                  } else if (subItem.hasSubmenu && subItem.id === 'resources-unit') {
                                    setResourcesUnitExpanded(!resourcesUnitExpanded);
                                  } else if (subItem.hasSubmenu && subItem.id === 'geospatial') {
                                    setGeospatialExpanded(!geospatialExpanded);
                                  } else if (subItem.hasSubmenu && subItem.id === 'tech-spec-unit') {
                                    setTechSpecUnitExpanded(!techSpecUnitExpanded);
                                  } else if (subItem.hasSubmenu && subItem.id === 'documentation-unit') {
                                    setDocumentationExpanded(!documentationExpanded);
                                  } else if (subItem.hasSubmenu && subItem.id === 'demobilization-unit') {
                                    setDemobilizationUnitExpanded(!demobilizationUnitExpanded);
                                  } else {
                                    updateICSMenu(subItem.id);
                                  }
                                }}
                              >
                                <SubIcon className="h-4 w-4 mr-3" />
                                <span className={isSubActive ? "text-black font-bold" : "text-white"}>{subItem.label}</span>
                                {subItem.hasSubmenu && subItem.id === 'situation-unit' && (
                                  situationExpanded ? (
                                    <ChevronDown className="h-4 w-4 ml-auto text-white" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4 ml-auto text-white" />
                                  )
                                )}
                                {subItem.hasSubmenu && subItem.id === 'resources-unit' && (
                                  resourcesUnitExpanded ? (
                                    <ChevronDown className="h-4 w-4 ml-auto text-white" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4 ml-auto text-white" />
                                  )
                                )}
                                {subItem.hasSubmenu && subItem.id === 'geospatial' && (
                                  geospatialExpanded ? (
                                    <ChevronDown className="h-4 w-4 ml-auto text-white" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4 ml-auto text-white" />
                                  )
                                )}
                                {subItem.hasSubmenu && subItem.id === 'tech-spec-unit' && (
                                  techSpecUnitExpanded ? (
                                    <ChevronDown className="h-4 w-4 ml-auto text-white" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4 ml-auto text-white" />
                                  )
                                )}
                                {subItem.hasSubmenu && subItem.id === 'documentation-unit' && (
                                  documentationExpanded ? (
                                    <ChevronDown className="h-4 w-4 ml-auto text-white" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4 ml-auto text-white" />
                                  )
                                )}
                                {subItem.hasSubmenu && subItem.id === 'demobilization-unit' && (
                                  demobilizationUnitExpanded ? (
                                    <ChevronDown className="h-4 w-4 ml-auto text-white" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4 ml-auto text-white" />
                                  )
                                )}
                              </Button>
                              
                              {/* Situation Unit Nested Submenu */}
                              {subItem.hasSubmenu && subItem.id === 'situation-unit' && situationExpanded && (
                                <div className="ml-6 mt-1 space-y-1">
                                  {subItem.submenuItems?.map((nestedItem) => {
                                    const NestedIcon = nestedItem.icon;
                                    const isNestedActive = activeICSMenu === nestedItem.id;
                                    
                                    return (
                                      <div key={nestedItem.id}>
                                        <Button
                                          variant="ghost"
                                          className={`w-full justify-start text-left text-xs ${
                                            nestedItem.backgroundColor 
                                              ? `${nestedItem.backgroundColor} text-white hover:opacity-80`
                                              : isNestedActive
                                                ? `${theme.colors.accent} ${theme.colors.textPrimary}`
                                                : `${theme.colors.textMuted} ${theme.colors.hover}`
                                          }`}
                                          onClick={() => updateICSMenu(nestedItem.id)}
                                        >
                                          <NestedIcon className="h-3 w-3 mr-3" />
                                          <span className={isNestedActive ? "text-black font-bold" : "text-white"}>{nestedItem.label}</span>
                                        </Button>
                                        
                                        {/* Geospatial moved to direct Planning submenu */}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              
                              {/* Resources Unit Nested Submenu */}
                              {subItem.hasSubmenu && subItem.id === 'resources-unit' && resourcesUnitExpanded && (
                                <div className="ml-6 mt-1 space-y-1">
                                  {subItem.submenuItems?.map((nestedItem) => {
                                    const NestedIcon = nestedItem.icon;
                                    const isNestedActive = activeICSMenu === nestedItem.id;
                                    
                                    return (
                                      <Button
                                        key={nestedItem.id}
                                        variant="ghost"
                                        className={`w-full justify-start text-left text-xs ${
                                          nestedItem.backgroundColor 
                                            ? `${nestedItem.backgroundColor} text-white hover:opacity-80`
                                            : isNestedActive
                                              ? `${theme.colors.accent} ${theme.colors.textPrimary}`
                                              : `${theme.colors.textMuted} ${theme.colors.hover}`
                                        }`}
                                        onClick={() => updateICSMenu(nestedItem.id)}
                                      >
                                        <NestedIcon className="h-3 w-3 mr-3" />
                                        <span className={isNestedActive ? "text-black font-bold" : "text-white"}>{nestedItem.label}</span>
                                      </Button>
                                    );
                                  })}
                                </div>
                              )}
                              
                              {/* Geospatial Nested Submenu */}
                              {subItem.hasSubmenu && subItem.id === 'geospatial' && geospatialExpanded && (
                                <div className="ml-6 mt-1 space-y-1">
                                  {subItem.submenuItems?.map((nestedItem) => {
                                    const NestedIcon = nestedItem.icon;
                                    const isNestedActive = activeICSMenu === nestedItem.id;
                                    
                                    return (
                                      <Button
                                        key={nestedItem.id}
                                        variant="ghost"
                                        className={`w-full justify-start text-left text-xs ${
                                          nestedItem.backgroundColor 
                                            ? `${nestedItem.backgroundColor} text-white hover:opacity-80`
                                            : isNestedActive
                                              ? `${theme.colors.accent} ${theme.colors.textPrimary}`
                                              : `${theme.colors.textMuted} ${theme.colors.hover}`
                                        }`}
                                        onClick={() => updateICSMenu(nestedItem.id)}
                                      >
                                        <NestedIcon className="h-3 w-3 mr-3" />
                                        <span className={isNestedActive ? "text-black font-bold" : "text-white"}>{nestedItem.label}</span>
                                      </Button>
                                    );
                                  })}
                                </div>
                              )}
                              
                              {/* Tech Spec Unit Nested Submenu */}
                              {subItem.hasSubmenu && subItem.id === 'tech-spec-unit' && techSpecUnitExpanded && (
                                <div className="ml-6 mt-1 space-y-1">
                                  {subItem.submenuItems?.map((nestedItem) => {
                                    const NestedIcon = nestedItem.icon;
                                    const isNestedActive = activeICSMenu === nestedItem.id;
                                    
                                    return (
                                      <Button
                                        key={nestedItem.id}
                                        variant="ghost"
                                        className={`w-full justify-start text-left text-xs ${
                                          nestedItem.backgroundColor 
                                            ? `${nestedItem.backgroundColor} text-white hover:opacity-80`
                                            : isNestedActive
                                              ? `${theme.colors.accent} ${theme.colors.textPrimary}`
                                              : `${theme.colors.textMuted} ${theme.colors.hover}`
                                        }`}
                                        onClick={() => updateICSMenu(nestedItem.id)}
                                      >
                                        <NestedIcon className="h-3 w-3 mr-3" />
                                        <span className={isNestedActive ? "text-black font-bold" : "text-white"}>{nestedItem.label}</span>
                                      </Button>
                                    );
                                  })}
                                </div>
                              )}
                              
                              {/* Document Unit Nested Submenu */}
                              {subItem.hasSubmenu && subItem.id === 'documentation-unit' && documentationExpanded && (
                                <div className="ml-6 mt-1 space-y-1">
                                  {subItem.submenuItems?.map((nestedItem) => {
                                    const NestedIcon = nestedItem.icon;
                                    const isNestedActive = activeICSMenu === nestedItem.id;
                                    
                                    return (
                                      <Button
                                        key={nestedItem.id}
                                        variant="ghost"
                                        className={`w-full justify-start text-left text-xs ${
                                          nestedItem.backgroundColor 
                                            ? `${nestedItem.backgroundColor} text-white hover:opacity-80`
                                            : isNestedActive
                                              ? `${theme.colors.accent} ${theme.colors.textPrimary}`
                                              : `${theme.colors.textMuted} ${theme.colors.hover}`
                                        }`}
                                        onClick={() => updateICSMenu(nestedItem.id)}
                                      >
                                        <NestedIcon className="h-3 w-3 mr-3" />
                                        <span className={isNestedActive ? "text-black font-bold" : "text-white"}>{nestedItem.label}</span>
                                      </Button>
                                    );
                                  })}
                                </div>
                              )}
                              
                              {/* Demobilization Unit Nested Submenu */}
                              {subItem.hasSubmenu && subItem.id === 'demobilization-unit' && demobilizationUnitExpanded && (
                                <div className="ml-6 mt-1 space-y-1">
                                  {subItem.submenuItems?.map((nestedItem) => {
                                    const NestedIcon = nestedItem.icon;
                                    const isNestedActive = activeICSMenu === nestedItem.id;
                                    
                                    return (
                                      <Button
                                        key={nestedItem.id}
                                        variant="ghost"
                                        className={`w-full justify-start text-left text-xs ${
                                          nestedItem.backgroundColor 
                                            ? `${nestedItem.backgroundColor} text-white hover:opacity-80`
                                            : isNestedActive
                                              ? `${theme.colors.accent} ${theme.colors.textPrimary}`
                                              : `${theme.colors.textMuted} ${theme.colors.hover}`
                                        }`}
                                        onClick={() => updateICSMenu(nestedItem.id)}
                                      >
                                        <NestedIcon className="h-3 w-3 mr-3" />
                                        <span className={isNestedActive ? "text-black font-bold" : "text-white"}>{nestedItem.label}</span>
                                      </Button>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Logistics Submenu */}
                    {item.hasSubmenu && item.id === 'logistics' && logisticsExpanded && (
                      <div className="ml-4 mt-1 space-y-1">
                        {item.submenuItems?.map((subItem) => {
                          const SubIcon = subItem.icon;
                          const isSubActive = activeICSMenu === subItem.id;
                          
                          return (
                            <div key={subItem.id}>
                              <Button
                                variant="ghost"
                                className={`w-full justify-start text-left text-sm ${
                                  subItem.backgroundColor 
                                    ? `${subItem.backgroundColor} text-white hover:opacity-80`
                                    : isSubActive
                                      ? `${theme.colors.accent} ${theme.colors.textPrimary}`
                                      : `${theme.colors.textMuted} ${theme.colors.hover}`
                                }`}
                                onClick={() => {
                                  if (subItem.hasSubmenu && subItem.id === 'services') {
                                    setServicesExpanded(!servicesExpanded);
                                  } else if (subItem.hasSubmenu && subItem.id === 'support') {
                                    setSupportExpanded(!supportExpanded);
                                  } else {
                                    updateICSMenu(subItem.id);
                                  }
                                }}
                              >
                                <SubIcon className="h-4 w-4 mr-3" />
                                <span className={isSubActive ? "text-black font-bold" : "text-white"}>{subItem.label}</span>
                                {subItem.hasSubmenu && subItem.id === 'services' && (
                                  servicesExpanded ? (
                                    <ChevronDown className="h-4 w-4 ml-auto text-white" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4 ml-auto text-white" />
                                  )
                                )}
                                {subItem.hasSubmenu && subItem.id === 'support' && (
                                  supportExpanded ? (
                                    <ChevronDown className="h-4 w-4 ml-auto text-white" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4 ml-auto text-white" />
                                  )
                                )}
                              </Button>
                              
                              {/* Services Nested Submenu */}
                              {subItem.hasSubmenu && subItem.id === 'services' && servicesExpanded && (
                                <div className="ml-6 mt-1 space-y-1">
                                  {subItem.submenuItems?.map((nestedItem) => {
                                    const NestedIcon = nestedItem.icon;
                                    const isNestedActive = activeICSMenu === nestedItem.id;
                                    
                                    return (
                                      <div key={nestedItem.id}>
                                        <Button
                                          variant="ghost"
                                          className={`w-full justify-start text-left text-xs ${
                                            nestedItem.backgroundColor 
                                              ? `${nestedItem.backgroundColor} text-white hover:opacity-80`
                                              : isNestedActive
                                                ? `${theme.colors.accent} ${theme.colors.textPrimary}`
                                                : `${theme.colors.textMuted} ${theme.colors.hover}`
                                          }`}
                                          onClick={() => {
                                            if (nestedItem.hasSubmenu && nestedItem.id === 'comms-unit') {
                                              setCommsUnitExpanded(!commsUnitExpanded);
                                            } else {
                                              updateICSMenu(nestedItem.id);
                                            }
                                          }}
                                        >
                                          <NestedIcon className="h-3 w-3 mr-3" />
                                          <span className={isNestedActive ? "text-black font-bold" : "text-white"}>{nestedItem.label}</span>
                                          {nestedItem.hasSubmenu && nestedItem.id === 'comms-unit' && (
                                            commsUnitExpanded ? (
                                              <ChevronDown className="h-3 w-3 ml-auto text-white" />
                                            ) : (
                                              <ChevronRight className="h-3 w-3 ml-auto text-white" />
                                            )
                                          )}
                                        </Button>
                                        
                                        {/* Comms Unit Nested Submenu */}
                                        {nestedItem.hasSubmenu && nestedItem.id === 'comms-unit' && commsUnitExpanded && (
                                          <div className="ml-4 mt-1 space-y-1">
                                            {nestedItem.submenuItems?.map((commsItem) => {
                                              const CommsIcon = commsItem.icon;
                                              const isCommsActive = activeICSMenu === commsItem.id;
                                              
                                              return (
                                                <Button
                                                  key={commsItem.id}
                                                  variant="ghost"
                                                  className={`w-full justify-start text-left text-xs ${
                                                    commsItem.backgroundColor 
                                                      ? `${commsItem.backgroundColor} text-white hover:opacity-80`
                                                      : isCommsActive
                                                        ? `${theme.colors.accent} ${theme.colors.textPrimary}`
                                                        : `${theme.colors.textMuted} ${theme.colors.hover}`
                                                  }`}
                                                  onClick={() => updateICSMenu(commsItem.id)}
                                                >
                                                  <CommsIcon className="h-3 w-3 mr-3" />
                                                  <span className={isCommsActive ? "text-black font-bold" : "text-white"}>{commsItem.label}</span>
                                                </Button>
                                              );
                                            })}
                                          </div>
                                        )}

                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              
                              {/* Support Nested Submenu */}
                              {subItem.hasSubmenu && subItem.id === 'support' && supportExpanded && (
                                <div className="ml-6 mt-1 space-y-1">
                                  {subItem.submenuItems?.map((nestedItem) => {
                                    const NestedIcon = nestedItem.icon;
                                    const isNestedActive = activeICSMenu === nestedItem.id;
                                    
                                    return (
                                      <div key={nestedItem.id}>
                                        <Button
                                          variant="ghost"
                                          className={`w-full justify-start text-left text-xs ${
                                            nestedItem.backgroundColor 
                                              ? `${nestedItem.backgroundColor} text-white hover:opacity-80`
                                              : isNestedActive
                                                ? `${theme.colors.accent} ${theme.colors.textPrimary}`
                                                : `${theme.colors.textMuted} ${theme.colors.hover}`
                                          }`}
                                          onClick={() => {
                                            if (nestedItem.hasSubmenu && nestedItem.id === 'supply-unit') {
                                              setSupplyUnitExpanded(!supplyUnitExpanded);
                                            } else {
                                              updateICSMenu(nestedItem.id);
                                            }
                                          }}
                                        >
                                          <NestedIcon className="h-3 w-3 mr-3" />
                                          <span className={isNestedActive ? "text-black font-bold" : "text-white"}>{nestedItem.label}</span>
                                          {nestedItem.hasSubmenu && nestedItem.id === 'supply-unit' && (
                                            supplyUnitExpanded ? (
                                              <ChevronDown className="h-3 w-3 ml-auto text-white" />
                                            ) : (
                                              <ChevronRight className="h-3 w-3 ml-auto text-white" />
                                            )
                                          )}
                                        </Button>
                                        
                                        {/* Supply Unit Nested Submenu */}
                                        {nestedItem.hasSubmenu && nestedItem.id === 'supply-unit' && supplyUnitExpanded && (
                                          <div className="ml-4 mt-1 space-y-1">
                                            {nestedItem.submenuItems?.map((supplyItem) => {
                                              const SupplyIcon = supplyItem.icon;
                                              const isSupplyActive = activeICSMenu === supplyItem.id;
                                              
                                              return (
                                                <Button
                                                  key={supplyItem.id}
                                                  variant="ghost"
                                                  className={`w-full justify-start text-left text-xs ${
                                                    supplyItem.backgroundColor 
                                                      ? `${supplyItem.backgroundColor} text-white hover:opacity-80`
                                                      : isSupplyActive
                                                        ? `${theme.colors.accent} ${theme.colors.textPrimary}`
                                                        : `${theme.colors.textMuted} ${theme.colors.hover}`
                                                  }`}
                                                  onClick={() => updateICSMenu(supplyItem.id)}
                                                >
                                                  <SupplyIcon className="h-3 w-3 mr-3" />
                                                  <span className={isSupplyActive ? "text-black font-bold" : "text-white"}>{supplyItem.label}</span>
                                                </Button>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Fin/Admin Submenu */}
                    {item.hasSubmenu && item.id === 'fin-admin' && finAdminExpanded && (
                      <div className="ml-4 mt-1 space-y-1">
                        {item.submenuItems?.map((subItem) => {
                          const SubIcon = subItem.icon;
                          const isSubActive = activeICSMenu === subItem.id;
                          
                          return (
                            <Button
                              key={subItem.id}
                              variant="ghost"
                              className={`w-full justify-start text-left text-sm ${
                                subItem.backgroundColor 
                                  ? `${subItem.backgroundColor} text-white hover:opacity-80`
                                  : isSubActive
                                    ? `${theme.colors.accent} ${theme.colors.textPrimary}`
                                    : `${theme.colors.textMuted} ${theme.colors.hover}`
                              }`}
                              onClick={() => updateICSMenu(subItem.id)}
                            >
                              <SubIcon className="h-4 w-4 mr-3" />
                              <span className={isSubActive ? "text-black font-bold" : "text-white"}>{subItem.label}</span>
                            </Button>
                          );
                        })}
                      </div>
                    )}

                    {/* Improvement Submenu */}
                    {item.hasSubmenu && item.id === 'improvement' && improvementICSExpanded && (
                      <div className="ml-4 mt-1 space-y-1">
                        {item.submenuItems?.map((subItem) => {
                          const SubIcon = subItem.icon;
                          const isSubActive = activeICSMenu === subItem.id;
                          
                          return (
                            <Button
                              key={subItem.id}
                              variant="ghost"
                              className={`w-full justify-start text-left text-sm ${
                                subItem.backgroundColor 
                                  ? `${subItem.backgroundColor} text-white hover:opacity-80`
                                  : isSubActive
                                    ? `${theme.colors.accent} ${theme.colors.textPrimary}`
                                    : `${theme.colors.textMuted} ${theme.colors.hover}`
                              }`}
                              onClick={() => updateICSMenu(subItem.id)}
                            >
                              <SubIcon className="h-4 w-4 mr-3" />
                              <span className={isSubActive ? "text-black font-bold" : "text-white"}>{subItem.label}</span>
                            </Button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-h-0 min-w-0 overflow-x-hidden">
          {/* Emergency Phase Navbar with Quick Actions */}
          <div className={`${theme.colors.secondary} border-b ${theme.colors.border} p-3 md:p-4 flex flex-wrap items-center justify-between gap-2 flex-shrink-0`}>
            {/* Quick Action Buttons - Left Side */}
            <div className="flex flex-wrap items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10 min-h-[40px]"
                onClick={() => {
                  console.log('ICS Forms clicked - navigating to ics-2xx-forms');
                  updateICSMenu('ics-2xx-forms');
                }}
                title="ICS Forms"
              >
                <FileText className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">ICS Forms</span>
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10 min-h-[40px]"
                onClick={() => {
                  console.log('Mapping clicked - navigating to mapping');
                  updateICSMenu('mapping');
                }}
                title="Mapping"
              >
                <Map className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Mapping</span>
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                className="border-teal-500/50 text-teal-400 hover:bg-teal-500/10 min-h-[40px]"
                onClick={() => {
                  console.log('Activity Log clicked - navigating to activity-log');
                  updateICSMenu('activity-log');
                }}
                title="Activity Log"
              >
                <Clock className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Activity Log</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10 min-h-[40px]"
                onClick={() => {
                  console.log('Calendar clicked - navigating to daily-schedule');
                  updateICSMenu('daily-schedule');
                }}
                data-testid="ics-topbar-calendar-btn"
                title="Daily Schedule & Calendar (ICS 230)"
              >
                <Calendar className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Calendar</span>
              </Button>
            </div>

            {/* Emergency Phase Dropdown - Center */}
            <div className="relative">
              <div className="relative inline-block">
                <button
                  onClick={() => {
                    if (canControlPhase) {
                      setPhaseDropdownOpen(!phaseDropdownOpen);
                    }
                  }}
                  disabled={!canControlPhase}
                  title={!canControlPhase ? "Only authorized command staff (Incident Commander, Operations/Planning/Logistics/Finance Section Chiefs) or Admins can change the emergency phase" : "Click to change emergency phase"}
                  className={`
                    ${emergencyPhase === 'Prevention' ? 'bg-green-500' : ''}
                    ${emergencyPhase === 'Mitigation' ? 'bg-amber-500' : ''}
                    ${emergencyPhase === 'Preparedness' ? 'bg-orange-500' : ''}
                    ${emergencyPhase === 'Response' ? 'bg-red-900' : ''}
                    ${emergencyPhase === 'Recovery' ? 'bg-amber-500' : ''}
                    ${!canControlPhase ? 'opacity-60 cursor-not-allowed' : 'hover:opacity-90 cursor-pointer'}
                    border-2 border-orange-500 text-white font-semibold px-8 py-3 rounded-lg
                    transition-all duration-200 flex items-center gap-3 min-w-[250px] justify-between
                  `}
                >
                  <span className="text-lg">{emergencyPhase}</span>
                  <div className="flex items-center gap-2">
                    {!canControlPhase && (
                      <Lock className="h-4 w-4 text-orange-300" />
                    )}
                    <ChevronDown className={`h-5 w-5 transition-transform ${phaseDropdownOpen ? 'rotate-180' : ''}`} />
                  </div>
                </button>
                
                {phaseDropdownOpen && canControlPhase && (
                  <div className="absolute top-full mt-2 left-0 right-0 bg-gray-800 border-2 border-orange-500 rounded-lg shadow-xl z-[10010] overflow-hidden">
                    {[
                      { name: 'Prevention', color: 'bg-green-500' },
                      { name: 'Mitigation', color: 'bg-amber-500' },
                      { name: 'Preparedness', color: 'bg-orange-500' },
                      { name: 'Response', color: 'bg-red-900' },
                      { name: 'Recovery', color: 'bg-amber-500' }
                    ].map((phase) => (
                      <button
                        key={phase.name}
                        onClick={() => handlePhaseChange(phase.name)}
                        className={`
                          w-full px-8 py-3 text-left text-white font-medium
                          hover:bg-gray-700 transition-colors flex items-center gap-3
                          ${emergencyPhase === phase.name ? 'bg-gray-700' : ''}
                        `}
                      >
                        <div className={`w-4 h-4 rounded ${phase.color}`}></div>
                        <span>{phase.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Spacer for balance - Right Side */}
            <div className="w-[280px]"></div>
          </div>
          
          {/* Main Content */}
          <main className={`flex-1 min-h-0 ${activeICSMenu === 'mapping' ? 'overflow-hidden' : 'overflow-auto'}`}>
            {renderICSContent(participants, positions)}
          </main>
        </div>
        
        {/* Emergency Phase Change Confirmation Modal */}
        {showPhaseConfirmModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[10020]">
            <div className="bg-gray-800 border-2 border-orange-500 rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
              <div className="text-center mb-6">
                <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 ${
                  pendingPhaseChange === 'Prevention' ? 'bg-green-500' :
                  pendingPhaseChange === 'Mitigation' ? 'bg-amber-500' :
                  pendingPhaseChange === 'Preparedness' ? 'bg-orange-500' :
                  pendingPhaseChange === 'Response' ? 'bg-red-900' :
                  'bg-amber-500'
                }`}>
                  <AlertTriangle className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Confirm Phase Change</h3>
                <p className="text-gray-300">
                  Are you sure you want to change the emergency phase from{' '}
                  <span className="font-semibold text-orange-400">{emergencyPhase}</span> to{' '}
                  <span className="font-semibold text-orange-400">{pendingPhaseChange}</span>?
                </p>
                <p className="text-gray-400 text-sm mt-2">
                  This change will be visible to all users in this exercise.
                </p>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={cancelPhaseChange}
                  disabled={phaseChangeLoading}
                  className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmPhaseChange}
                  disabled={phaseChangeLoading}
                  className={`flex-1 px-4 py-3 text-white rounded-lg font-medium transition-colors disabled:opacity-50 ${
                    pendingPhaseChange === 'Prevention' ? 'bg-green-500 hover:bg-green-600' :
                    pendingPhaseChange === 'Mitigation' ? 'bg-amber-500 hover:bg-amber-600' :
                    pendingPhaseChange === 'Preparedness' ? 'bg-orange-500 hover:bg-orange-600' :
                    pendingPhaseChange === 'Response' ? 'bg-red-700 hover:bg-red-800' :
                    'bg-amber-500 hover:bg-amber-600'
                  }`}
                >
                  {phaseChangeLoading ? 'Updating...' : 'Confirm Change'}
                </button>
              </div>
            </div>
          </div>
        )}
    </div>
  );
};


// TrackerMarkerLayer — uses useMap() to add/update tracker markers via Leaflet API
const TRACKER_ICON_COLORS = {
  fire_truck: '#dc2626', ambulance: '#ffffff', command: '#2563eb',
  heavy_equipment: '#eab308', water_tender: '#06b6d4', rescue: '#f97316',
  hazmat: '#9333ea', helicopter: '#16a34a', boat: '#0d9488',
  utility: '#6b7280', transport: '#4f46e5', police: '#1e3a5f',
};
const TRACKER_ICON_SYMBOLS = {
  fire_truck: '🚒', ambulance: '🚑', command: '📡',
  heavy_equipment: '🚜', water_tender: '💧', rescue: '⛑',
  hazmat: '☢', helicopter: '🚁', boat: '⛵',
  utility: '🔧', transport: '🚌', police: '🚨',
};
const TRACKER_STATUS_COLORS = {
  'Assigned': '#3b82f6', 'En Route': '#f59e0b', 'On Scene': '#22c55e',
  'Available': '#8b5cf6', 'Out of Service': '#ef4444',
};

function TrackerMarkerLayer({ trackers, showTrackers, trails, showTrails, trailMinutes, onMapRef }) {
  const map = useMap();
  const markersRef = useRef({});
  const trailLayersRef = useRef({});

  // Expose map instance to parent
  useEffect(() => {
    if (onMapRef) onMapRef(map);
  }, [map, onMapRef]);

  // Helper: build the divIcon HTML for a tracker
  const buildIconHtml = (tracker) => {
    const bgColor = TRACKER_ICON_COLORS[tracker.vehicle_icon] || '#f97316';
    const symbol = TRACKER_ICON_SYMBOLS[tracker.vehicle_icon] || '\uD83D\uDCCD';
    const statusColor = TRACKER_STATUS_COLORS[tracker.status] || '#6b7280';
    return { bgColor, symbol, statusColor, html: `<div style="position:relative;width:48px;height:48px;display:flex;align-items:center;justify-content:center;">
      <div style="position:absolute;bottom:0;left:50%;width:38px;height:38px;background:${bgColor};border-radius:50% 50% 50% 0;transform:translateX(-50%) rotate(-45deg);border:3px solid ${statusColor};box-shadow:0 2px 10px rgba(0,0,0,0.5);"></div>
      <div style="position:absolute;bottom:7px;left:50%;transform:translateX(-50%);font-size:18px;z-index:1;filter:drop-shadow(0 1px 1px rgba(0,0,0,0.3));">${symbol}</div>
      <div style="position:absolute;top:-18px;left:50%;transform:translateX(-50%);background:rgba(0,0,0,0.85);color:#fff;font-size:10px;font-weight:700;padding:2px 6px;border-radius:3px;white-space:nowrap;">${tracker.callsign || ''}</div>
      <div style="position:absolute;bottom:-4px;right:-4px;width:10px;height:10px;background:${statusColor};border-radius:50%;border:2px solid #fff;box-shadow:0 0 4px ${statusColor};"></div>
    </div>` };
  };

  // Helper: build popup HTML for a tracker
  const buildPopupHtml = (tracker) => {
    const symbol = TRACKER_ICON_SYMBOLS[tracker.vehicle_icon] || '\uD83D\uDCCD';
    const statusColor = TRACKER_STATUS_COLORS[tracker.status] || '#6b7280';
    const speedKmh = tracker.speed ? (tracker.speed * 3.6).toFixed(1) : '0.0';
    const headingStr = tracker.heading ? `${Math.round(tracker.heading)}\u00B0` : 'N/A';
    const lastUpdateStr = tracker.last_update
      ? new Date(tracker.last_update * 1000).toLocaleTimeString()
      : 'N/A';
    return `
      <div style="font-family:system-ui,sans-serif;font-size:13px;line-height:1.5;color:#1f2937;min-width:220px;">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;border-bottom:2px solid #f97316;padding-bottom:6px;">
          <span style="font-size:22px;">${symbol}</span>
          <div>
            <div style="font-weight:700;font-size:15px;">${tracker.callsign || 'Unknown'}</div>
            <div style="color:#6b7280;font-size:11px;">${tracker.resource_type || ''}</div>
          </div>
          <span style="margin-left:auto;background:${statusColor};color:#fff;padding:2px 8px;border-radius:9999px;font-size:11px;font-weight:600;">${tracker.status}</span>
        </div>
        <table style="width:100%;font-size:12px;border-collapse:collapse;">
          ${tracker.identification ? `<tr><td style="color:#6b7280;padding:2px 8px 2px 0;">ID/Name</td><td style="font-weight:500;">${tracker.identification}</td></tr>` : ''}
          ${tracker.vehicle_equipment_make_model ? `<tr><td style="color:#6b7280;padding:2px 8px 2px 0;">Make/Model</td><td style="font-weight:500;">${tracker.vehicle_equipment_make_model}</td></tr>` : ''}
          ${tracker.task_force ? `<tr><td style="color:#6b7280;padding:2px 8px 2px 0;">Task Force</td><td style="font-weight:500;">${tracker.task_force}</td></tr>` : ''}
          ${tracker.strike_team ? `<tr><td style="color:#6b7280;padding:2px 8px 2px 0;">Strike Team</td><td style="font-weight:500;">${tracker.strike_team}</td></tr>` : ''}
          <tr><td style="color:#6b7280;padding:2px 8px 2px 0;">Speed</td><td style="font-weight:500;font-family:monospace;">${speedKmh} km/h</td></tr>
          <tr><td style="color:#6b7280;padding:2px 8px 2px 0;">Heading</td><td style="font-weight:500;font-family:monospace;">${headingStr}</td></tr>
          <tr><td style="color:#6b7280;padding:2px 8px 2px 0;">Coords</td><td style="font-weight:500;font-family:monospace;font-size:11px;">${tracker.lat.toFixed(6)}, ${tracker.lng.toFixed(6)}</td></tr>
          <tr><td style="color:#6b7280;padding:2px 8px 2px 0;">Last Update</td><td style="font-weight:500;">${lastUpdateStr}</td></tr>
          ${tracker.contact_person ? `<tr><td style="color:#6b7280;padding:2px 8px 2px 0;">Contact</td><td style="font-weight:500;">${tracker.contact_person}</td></tr>` : ''}
          ${tracker.contact_phone ? `<tr><td style="color:#6b7280;padding:2px 8px 2px 0;">Phone</td><td style="font-weight:500;"><a href="tel:${tracker.contact_phone}" style="color:#2563eb;">${tracker.contact_phone}</a></td></tr>` : ''}
        </table>
      </div>`;
  };

  useEffect(() => {
    if (!showTrackers) {
      // Hide all markers
      Object.values(markersRef.current).forEach(m => { map.removeLayer(m); });
      markersRef.current = {};
      return;
    }

    const validTrackers = Object.values(trackers).filter(
      t => t.lat && t.lng && !(t.lat === 0 && t.lng === 0)
    );
    const currentIds = new Set(validTrackers.map(t => t.tracker_id));

    // Remove markers that no longer exist
    Object.keys(markersRef.current).forEach(tid => {
      if (!currentIds.has(tid)) {
        map.removeLayer(markersRef.current[tid]);
        delete markersRef.current[tid];
      }
    });

    // Add or update markers
    validTrackers.forEach(tracker => {
      const { html } = buildIconHtml(tracker);
      const icon = L.divIcon({
        html,
        className: 'tracker-live-icon',
        iconSize: [48, 48],
        iconAnchor: [24, 48],
        popupAnchor: [0, -48],
      });
      const popupHtml = buildPopupHtml(tracker);

      const existing = markersRef.current[tracker.tracker_id];
      if (existing) {
        // Update existing marker in-place
        existing.setLatLng([tracker.lat, tracker.lng]);
        existing.setIcon(icon);
        existing.setPopupContent(popupHtml);
      } else {
        // Create new marker
        const marker = L.marker([tracker.lat, tracker.lng], { icon })
          .addTo(map)
          .bindPopup(popupHtml, { maxWidth: 320, minWidth: 250 });
        markersRef.current[tracker.tracker_id] = marker;
      }
    });

    return () => {
      Object.values(markersRef.current).forEach(m => { map.removeLayer(m); });
      markersRef.current = {};
    };
  }, [trackers, showTrackers, map]);

  // ─── Trail rendering (fading polylines) ───
  // Split: trail config ref avoids excessive deps in render effect
  const trailConfigRef = useRef({ trails, showTrails, showTrackers, trackers, trailMinutes });
  trailConfigRef.current = { trails, showTrails, showTrackers, trackers, trailMinutes };

  // Re-render trails when any input changes
  const trailTrigger = JSON.stringify([!!trails, showTrails, showTrackers, Object.keys(trackers || {}).length, trailMinutes]);

  useEffect(() => {
    // Clear old trails
    Object.values(trailLayersRef.current).forEach(layers => {
      layers.forEach(l => map.removeLayer(l));
    });
    trailLayersRef.current = {};

    const { trails: t, showTrails: st, showTrackers: stk, trackers: tk, trailMinutes: tm } = trailConfigRef.current;
    if (!st || !t || !stk) return;

    const now = Date.now() / 1000;
    const windowSec = (tm || 15) * 60;

    Object.entries(t).forEach(([trackerId, points]) => {
      if (!points || points.length < 2) return;

      const tracker = tk[trackerId];
      const baseColor = TRACKER_STATUS_COLORS[tracker?.status] || TRACKER_STATUS_COLORS[points[points.length - 1]?.status] || '#f97316';

      const segmentLayers = [];
      for (let i = 0; i < points.length - 1; i++) {
        const p1 = points[i];
        const p2 = points[i + 1];
        if (!p1.lat || !p1.lng || !p2.lat || !p2.lng) continue;

        const midTime = (p1.timestamp + p2.timestamp) / 2;
        const age = now - midTime;
        const opacity = Math.max(0.1, 1 - (age / windowSec));

        const line = L.polyline(
          [[p1.lat, p1.lng], [p2.lat, p2.lng]],
          { color: baseColor, weight: 4, opacity, lineCap: 'round', lineJoin: 'round' }
        ).addTo(map);
        segmentLayers.push(line);
      }

      points.forEach((p, idx) => {
        if (!p.lat || !p.lng) return;
        const age = now - p.timestamp;
        const opacity = Math.max(0.15, 1 - (age / windowSec));
        const isLast = idx === points.length - 1;
        const dot = L.circleMarker([p.lat, p.lng], {
          radius: isLast ? 0 : 3,
          fillColor: baseColor,
          fillOpacity: opacity,
          color: baseColor,
          weight: 1,
          opacity: opacity * 0.6,
        }).addTo(map);
        segmentLayers.push(dot);
      });

      trailLayersRef.current[trackerId] = segmentLayers;
    });

    return () => {
      Object.values(trailLayersRef.current).forEach(layers => {
        layers.forEach(l => map.removeLayer(l));
      });
      trailLayersRef.current = {};
    };
  }, [trailTrigger, map]);

  return null;
}


// Leaflet Mapping Component
const LeafletMapping = React.memo(({ exerciseId }) => {
  // console.log('LeafletMapping component rendered with exerciseId:', exerciseId); // Removed to reduce re-render overhead
  const { theme } = useTheme();
  const { user } = useAuth();
  const { selectedAgency } = useAgencyContext();
  
  const [mapObjects, setMapObjects] = useState([]);
  const [hiddenObjects, setHiddenObjects] = useState(new Set()); // Track hidden object IDs
  
  // Live GPS tracker state
  const [liveTrackers, setLiveTrackers] = useState({});
  const [showTrackers, setShowTrackers] = useState(true);
  const [trailData, setTrailData] = useState({});
  const [showTrails, setShowTrails] = useState(true);
  const [trailMinutes, setTrailMinutes] = useState(15);
  const [trackerTimeout, setTrackerTimeout] = useState(60);
  const trackerSocketRef = useRef(null);
  const leafletMapInstanceRef = useRef(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedObject, setSelectedObject] = useState(null);
  const [showObjectForm, setShowObjectForm] = useState(false);
  const [editingObject, setEditingObject] = useState(null);
  const [mapInstance, setMapInstance] = useState(null);
  const [drawingLayer, setDrawingLayer] = useState(null);
  const [exerciseData, setExerciseData] = useState(null);
  const [mapReady, setMapReady] = useState(false);
  const [currentZoom, setCurrentZoom] = useState(4);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mouseCoords, setMouseCoords] = useState({ lat: null, lng: null });
  
  // Persist user's selected base layer (satellite, streets, topographical, greyscale)
  const [selectedBaseLayer, setSelectedBaseLayer] = useState(() => {
    return secureStorage.getPreference('map-base-layer') || 'satellite';
  });
  
  // Real-time sync state
  const [lockedObjects, setLockedObjects] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [lastFetchTime, setLastFetchTime] = useState(Date.now());
  
  // Form data for creating/editing map objects
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3388ff',
    image: '',
    type: 'marker', // Add object type to form data
    icon: 'default' // Add icon type
  });

  // Hover modal state
  const [hoveredObject, setHoveredObject] = useState(null);
  const [showHoverModal, setShowHoverModal] = useState(false);
  const [hoverModalPosition, setHoverModalPosition] = useState({ x: 0, y: 0 });
  const [editingInModal, setEditingInModal] = useState(false);
  const [modalFormData, setModalFormData] = useState({
    name: '',
    description: '',
    color: '#3388ff',
    image: '',
    icon: 'default'
  });

  // Clipboard state for copy/paste functionality
  const [copiedObject, setCopiedObject] = useState(null);

  // Drawing state (needs to be declared early for polling useEffect)
  const [drawingMode, setDrawingMode] = useState(null);
  
  // Ref to track drawing mode for setInterval callback (avoids stale closure)
  const drawingModeRef = useRef(null);
  const editingInModalRef = useRef(false);
  const editingInPopupRef = useRef(false);
  const showHoverModalRef = useRef(false);
  
  // Ref to track if initial map view has been set (prevents zoom reset on object add)
  const initialMapViewSetRef = useRef(false);
  
  // Ref to track if component is mounted (prevents map operations after unmount)
  const isMountedRef = useRef(true);

  // Add refs for input fields to debug
  const nameInputRef = useRef(null);
  const descriptionInputRef = useRef(null);
  
  // Ref for FeatureGroup to programmatically add/edit layers
  const featureGroupRef = useRef(null);

  // Track component mount state
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Clear mapRef on unmount to prevent stale reference errors
      if (mapRef.current) {
        try {
          mapRef.current.stop();
        } catch (e) {
          // Ignore errors during cleanup
        }
        mapRef.current = null;
      }
    };
  }, []);

  // ─── Live GPS Tracker Socket Listeners ───
  useEffect(() => {
    if (!exerciseId) return;

    const wsUrl = (API_BASE_URL || '').replace(/\/api\/?$/, '').replace(/\/+$/, '');
    // Fallback: if wsUrl is empty or fails, try window.location.origin
    const fallbackUrl = window.location.origin.replace(/\/api\/?$/, '').replace(/\/+$/, '');

    // REST fetch — tries primary URL first, then fallback
    const fetchTrackers = async () => {
      const urls = [...new Set([`${wsUrl}/api/trackers/${exerciseId}?timeout_minutes=${trackerTimeout}`, `${fallbackUrl}/api/trackers/${exerciseId}?timeout_minutes=${trackerTimeout}`].filter(Boolean))];
      for (const fetchUrl of urls) {
        try {
          const token = secureStorage.getToken();
          const res = await fetch(fetchUrl, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          if (res.ok) {
            const list = await res.json();
            if (Array.isArray(list)) {
              const trackersMap = {};
              list.forEach(t => { trackersMap[t.tracker_id] = t; });
              setLiveTrackers(trackersMap);
              return; // Success — stop trying
            }
          }
        } catch (e) {
          console.warn('[GPS Tracker] REST fetch failed for', fetchUrl, e.message);
        }
      }
    };
    fetchTrackers();

    // Fetch breadcrumb trail data
    const fetchTrails = async () => {
      const urls = [...new Set([`${wsUrl}/api/tracker/trails/${exerciseId}?minutes=${trailMinutes}`, `${fallbackUrl}/api/tracker/trails/${exerciseId}?minutes=${trailMinutes}`].filter(Boolean))];
      for (const fetchUrl of urls) {
        try {
          const token = secureStorage.getToken();
          const res = await fetch(fetchUrl, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          if (res.ok) {
            const data = await res.json();
            if (data && typeof data === 'object') {
              setTrailData(data);
              return;
            }
          }
        } catch (e) {
          // Silent fail — trails are enhancement
        }
      }
    };
    fetchTrails();

    const socket = io(wsUrl || fallbackUrl, {
      path: '/api/ws/socket.io',
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionAttempts: 5,
    });
    trackerSocketRef.current = socket;

    socket.on('connect', () => {
      console.log('[GPS Tracker] Socket connected, joining room', exerciseId);
      socket.emit('join_exercise', {
        exercise_id: exerciseId,
        user_id: user?.id || 'map-viewer',
        user_name: 'Map Viewer',
      });
      socket.emit('get_active_trackers', { exercise_id: exerciseId });
    });

    socket.on('active_trackers', (data) => {
      // Backend sends { trackers: [...] }
      const arr = Array.isArray(data) ? data : (data?.trackers || []);
      const trackersMap = {};
      arr.forEach(t => { trackersMap[t.tracker_id] = t; });
      console.log('[GPS Tracker] Socket received', Object.keys(trackersMap).length, 'trackers');
      setLiveTrackers(trackersMap);
    });

    socket.on('tracker_started', (data) => {
      console.log('[GPS Tracker] Tracker started:', data.callsign || data.tracker_id);
      setLiveTrackers(prev => ({ ...prev, [data.tracker_id]: data }));
    });

    socket.on('tracker_position', (data) => {
      setLiveTrackers(prev => {
        const existing = prev[data.tracker_id];
        // Upsert — add new tracker if tracker_started was missed
        return {
          ...prev,
          [data.tracker_id]: { ...(existing || {}), ...data },
        };
      });
    });

    socket.on('tracker_removed', (data) => {
      console.log('[GPS Tracker] Tracker removed:', data.tracker_id);
      setLiveTrackers(prev => {
        const next = { ...prev };
        delete next[data.tracker_id];
        return next;
      });
    });

    socket.on('connect_error', (err) => {
      console.warn('[GPS Tracker] Socket connection error:', err.message);
    });

    const pollInterval = setInterval(fetchTrackers, 5000);
    const trailPollInterval = setInterval(fetchTrails, 10000);

    return () => {
      clearInterval(pollInterval);
      clearInterval(trailPollInterval);
      socket.emit('leave_exercise', { exercise_id: exerciseId, user_id: user?.id || 'map-viewer' });
      socket.disconnect();
      trackerSocketRef.current = null;
      setLiveTrackers({});
      setTrailData({});
    };
  }, [exerciseId, trailMinutes, trackerTimeout]);

  useEffect(() => {
    if (exerciseId) {
      // Reset the Exercise/Event Center marker refs when exercise changes
      exerciseCenterCreatedRef.current = false;
      exerciseCenterCheckingRef.current = false;
      
      fetchMapObjects();
      fetchExerciseData();
    }
    
    // Add CSS to ensure modals always stay on top
    const modalCSS = `
      .modal-overlay {
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        z-index: 10000 !important;
        pointer-events: auto !important;
      }
      .modal-content {
        position: relative !important;
        z-index: 10001 !important;
        pointer-events: auto !important;
        transform: translateZ(0) !important;
      }
      .leaflet-container {
        z-index: 1 !important;
      }
      .modal-content input[type="text"],
      .modal-content textarea {
        color: #1f2937 !important;
        background-color: #ffffff !important;
        border: 2px solid #d1d5db !important;
        font-size: 14px !important;
        line-height: 1.5 !important;
      }
      .modal-content input[type="text"]:focus,
      .modal-content textarea:focus {
        border-color: #3b82f6 !important;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1) !important;
      }
      .modal-content input[type="text"]::placeholder,
      .modal-content textarea::placeholder {
        color: #6b7280 !important;
        opacity: 1 !important;
      }
      .modal-content input[type="text"],
      .modal-content textarea {
        -webkit-appearance: none !important;
        -moz-appearance: none !important;
        appearance: none !important;
      }
    `;
    
    const styleSheet = document.createElement('style');
    styleSheet.textContent = modalCSS;
    document.head.appendChild(styleSheet);
  }, [exerciseId, selectedAgency]); // Re-fetch when exercise or agency context changes

  // Update map view when exercise data is loaded (only on initial load)
  useEffect(() => {
    if (exerciseData?.latitude && exerciseData?.longitude && mapRef.current && mapReady && !initialMapViewSetRef.current) {
      try {
        const zoomLevel = exerciseData.zoom_level || 8;
        console.log(`🗺️ Setting initial map view to exercise coordinates: ${exerciseData.latitude}, ${exerciseData.longitude} with zoom ${zoomLevel}`);
        // Add small delay to ensure DOM elements are ready
        setTimeout(() => {
          // Double-check everything is still valid before setView
          if (!isMountedRef.current) return;
          if (!mapRef.current) return;
          if (typeof mapRef.current.setView !== 'function') return;
          
          // Additional check - verify map's internal container is still valid
          try {
            const container = mapRef.current.getContainer();
            if (!container || !container.parentNode) {
              console.warn('Map container no longer in DOM, skipping setView');
              return;
            }
          } catch (e) {
            console.warn('Map container check failed:', e.message);
            return;
          }
          
          try {
            mapRef.current.setView([exerciseData.latitude, exerciseData.longitude], zoomLevel, { animate: false });
            initialMapViewSetRef.current = true; // Mark as set to prevent resetting on object add
          } catch (e) {
            console.warn('Map setView error suppressed:', e.message);
          }
        }, 100);
      } catch (error) {
        console.error('Error setting map view:', error);
      }
    }
  }, [exerciseData, mapReady]);

  // Debug modal state changes
  useEffect(() => {
    console.log('🔍 Modal state changed - showHoverModal:', showHoverModal, 'editingInModal:', editingInModal);
    console.log('🔍 modalFormData:', modalFormData);
  }, [showHoverModal, editingInModal, modalFormData]);

  // Create Exercise/Event Center marker when exerciseData is loaded (only once)
  const exerciseCenterCreatedRef = useRef(false);
  const exerciseCenterCheckingRef = useRef(false); // Prevent concurrent checks
  
  useEffect(() => {
    // Only run once when exerciseData becomes available
    if (exerciseData?.latitude && exerciseData?.longitude && 
        !exerciseCenterCreatedRef.current && 
        !exerciseCenterCheckingRef.current &&
        exerciseId) {
      
      exerciseCenterCheckingRef.current = true; // Lock to prevent concurrent execution
      
      // Check backend directly to avoid race conditions with local state
      const checkAndCreateMarker = async () => {
        try {
          // Fetch current map objects from backend to get fresh data
          const response = await fetch(`${API_BASE_URL}/api/map-objects?exercise_id=${exerciseId}`);
          if (response.ok) {
            const currentObjects = await response.json();
            const existingMarker = currentObjects.find(obj => 
              obj.name === 'Exercise/Event Center' && obj.type === 'marker'
            );
            
            if (!existingMarker) {
              console.log('🎯 No Exercise/Event Center marker found in backend, creating one...');
              await createExerciseCenterMarkerAsync(exerciseData.latitude, exerciseData.longitude);
            } else {
              console.log('📍 Exercise/Event Center marker already exists in backend');
            }
          }
        } catch (error) {
          console.error('Error checking for Exercise/Event Center marker:', error);
        } finally {
          exerciseCenterCreatedRef.current = true; // Mark as handled regardless of outcome
          exerciseCenterCheckingRef.current = false; // Release lock
        }
      };
      
      checkAndCreateMarker();
    }
  }, [exerciseData, exerciseId]); // Only depend on exerciseData and exerciseId, not mapObjects
  
  // Keep refs in sync with state for setInterval callback (avoids stale closure)
  useEffect(() => {
    drawingModeRef.current = drawingMode;
    console.log('📌 drawingModeRef updated:', drawingMode);
  }, [drawingMode]);
  
  useEffect(() => {
    editingInModalRef.current = editingInModal;
    console.log('📌 editingInModalRef updated:', editingInModal);
  }, [editingInModal]);
  
  useEffect(() => {
    showHoverModalRef.current = showHoverModal;
    console.log('📌 showHoverModalRef updated:', showHoverModal);
  }, [showHoverModal]);

  // Real-time polling for map objects (every 90 seconds)
  // Uses refs to avoid stale state closure in setInterval callback
  useEffect(() => {
    console.log('🔄 Polling useEffect triggered - exerciseId:', exerciseId);
    
    if (!exerciseId) {
      console.log('⚠️ No exerciseId, skipping polling setup');
      return;
    }
    
    console.log('▶️ Setting up polling interval');
    const pollInterval = setInterval(() => {
      // Check drawing/editing state using refs (avoids stale closure)
      const isDrawing = drawingModeRef.current;
      const isEditingModal = editingInModalRef.current;
      const isEditingPopup = editingInPopupRef.current;
      
      console.log('🔄 Polling tick - drawingMode:', isDrawing, 'editingInModal:', isEditingModal, 'editingInPopup:', isEditingPopup);
      
      if (isDrawing || isEditingModal || isEditingPopup) {
        console.log('⏸️ Polling paused - user is drawing or editing');
        return; // Skip this poll cycle
      }
      
      console.log('📊 Fetching map objects and locks');
      fetchMapObjectsWithSync();
      fetchLocks();
    }, 90000); // Poll every 90 seconds
    
    return () => {
      console.log('🛑 Cleaning up polling interval');
      clearInterval(pollInterval);
    };
  }, [exerciseId]); // Only depend on exerciseId, not drawingMode/editingInModal

  // Auto-dismiss notifications after 5 seconds
  useEffect(() => {
    if (notifications.length > 0) {
      const timer = setTimeout(() => {
        setNotifications(prev => prev.slice(1));
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [notifications]);

  const addNotification = (message, type = 'info') => {
    const notification = {
      id: Date.now(),
      message,
      type // 'info', 'success', 'warning', 'error'
    };
    setNotifications(prev => [...prev, notification]);
  };

  const fetchMapObjectsWithSync = async () => {
    // Skip fetching if user is drawing or editing to prevent interruption
    // Use refs to check current state
    if (drawingModeRef.current || editingInModalRef.current || editingInPopupRef.current) {
      console.log('⏸️ Skipping map objects sync - user is drawing or editing');
      return;
    }
    
    try {
      // Build URL with exercise_id
      let url = `${API_BASE_URL}/api/map-objects?exercise_id=${exerciseId}`;
      
      // If Super Admin has selected a specific agency, add organization_id filter
      if (selectedAgency) {
        url += `&organization_id=${selectedAgency.id}`;
      }
      
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        
        // Detect changes
        const oldIds = new Set(mapObjects.map(obj => obj.id));
        const newIds = new Set(data.map(obj => obj.id));
        
        // Notification system disabled per user request
        // Users found the real-time notifications distracting
        /*
        // Detect added objects
        data.forEach(obj => {
          if (!oldIds.has(obj.id)) {
            if (obj.modified_by && obj.modified_by !== `${user?.first_name} ${user?.last_name}`) {
              addNotification(`📍 ${obj.modified_by || 'Someone'} added "${obj.name}"`, 'info');
            }
          }
        });
        
        // Detect updated objects
        data.forEach(newObj => {
          const oldObj = mapObjects.find(o => o.id === newObj.id);
          if (oldObj && new Date(newObj.updated_at) > new Date(oldObj.updated_at)) {
            if (newObj.modified_by && newObj.modified_by !== `${user?.first_name} ${user?.last_name}`) {
              addNotification(`✏️ ${newObj.modified_by || 'Someone'} updated "${newObj.name}"`, 'success');
            }
          }
        });
        
        // Detect deleted objects
        mapObjects.forEach(oldObj => {
          if (!newIds.has(oldObj.id)) {
            addNotification(`🗑️ "${oldObj.name}" was deleted`, 'warning');
          }
        });
        */
        
        // Only update state if there are actual changes (prevents unnecessary re-renders)
        const hasChanges = 
          oldIds.size !== newIds.size || // Different count
          Array.from(oldIds).some(id => !newIds.has(id)) || // Deleted items
          data.some(newObj => {
            const oldObj = mapObjects.find(o => o.id === newObj.id);
            return !oldObj || JSON.stringify(oldObj) !== JSON.stringify(newObj); // New or updated
          });
        
        if (hasChanges) {
          console.log('📊 Map objects changed, updating state');
          setMapObjects(data);
        } else {
          console.log('✓ No map object changes, skipping state update');
        }
      }
    } catch (error) {
      console.error('Error syncing map objects:', error);
    }
  };

  const fetchLocks = async () => {
    // Skip fetching locks if user is drawing or editing to prevent interruption
    // Use refs to check current state
    if (drawingModeRef.current || editingInModalRef.current) {
      console.log('⏸️ Skipping locks fetch - user is drawing or editing');
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/map-objects/locks/${exerciseId}`);
      if (response.ok) {
        const locks = await response.json();
        const locksMap = {};
        locks.forEach(lock => {
          locksMap[lock.object_id] = {
            locked_by: lock.locked_by,
            locked_at: lock.locked_at
          };
        });
        setLockedObjects(locksMap);
      }
    } catch (error) {
      console.error('Error fetching locks:', error);
    }
  };

  const lockObject = async (objectId) => {
    const userName = `${user?.first_name} ${user?.last_name}`;
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/map-objects/lock/${objectId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ locked_by: userName })
        }
      );
      
      if (response.status === 423) {
        const data = await response.json();
        alert(data.detail);
        return false;
      }
      
      if (response.ok) {
        fetchLocks();
        return true;
      }
    } catch (error) {
      console.error('Error locking object:', error);
    }
    return false;
  };

  const unlockObject = async (objectId) => {
    const userName = `${user?.first_name} ${user?.last_name}`;
    try {
      await fetch(
        `${API_BASE_URL}/api/map-objects/unlock/${objectId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ locked_by: userName })
        }
      );
      fetchLocks();
    } catch (error) {
      console.error('Error unlocking object:', error);
    }
  };

  const fetchMapObjects = async () => {
    try {
      // Build URL with exercise_id
      let url = `${API_BASE_URL}/api/map-objects?exercise_id=${exerciseId}`;
      
      // If Super Admin has selected a specific agency, add organization_id filter
      // This will verify the exercise belongs to that agency
      if (selectedAgency) {
        url += `&organization_id=${selectedAgency.id}`;
      }
      
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        console.log(`📊 Fetched ${data.length} map objects from backend`);
        setMapObjects(data);
      } else {
        console.error('Failed to fetch map objects. Status:', response.status);
      }
    } catch (error) {
      console.error('Error fetching map objects:', error);
    }
  };

  const fetchExerciseData = async () => {
    try {
      const token = secureStorage.getToken();
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch(`${API_BASE_URL}/api/exercise-builder/${exerciseId}`, { headers });
      if (response.ok) {
        const data = await response.json();
        console.log('📍 Fetched exercise data for mapping:', data);
        setExerciseData(data);
        
        // Note: Map view setting is now handled by useEffect with initialMapViewSetRef
        // Note: Exercise/Event Center marker creation is handled by useEffect
      } else {
        console.error('Failed to fetch exercise data. Status:', response.status);
      }
    } catch (error) {
      console.error('Error fetching exercise data:', error);
    }
  };

  const createExerciseCenterMarkerAsync = async (latitude, longitude) => {
    try {
      // Create the marker object
      const markerData = {
        exercise_id: exerciseId,
        type: 'marker',
        name: 'Exercise/Event Center',
        description: 'Main exercise location from Step 1 coordinates',
        color: '#ff4444', // Red color to make it stand out
        geometry: {
          type: 'Point',
          coordinates: [parseFloat(longitude), parseFloat(latitude)]
        },
        image: '',
        icon: 'incident-command-post' // Use ICS icon for Exercise Center
      };
      
      console.log('🎯 Creating Exercise/Event Center marker at:', latitude, longitude);
      
      const response = await fetch(`${API_BASE_URL}/api/map-objects`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(markerData),
      });

      if (response.ok) {
        const newMarker = await response.json();
        console.log('✅ Exercise/Event Center marker created:', newMarker);
        // Add directly to state instead of re-fetching to avoid race conditions
        setMapObjects(prev => [...prev, newMarker]);
      } else {
        console.error('Failed to create Exercise/Event Center marker. Status:', response.status);
      }
    } catch (error) {
      console.error('Error creating Exercise/Event Center marker:', error);
    }
  };

  const handleObjectCreate = async (geoJsonData, objectType) => {
    console.log('🚀 handleObjectCreate called with:', { objectType, geoJsonData });
    
    const objectData = {
      exercise_id: exerciseId,
      type: objectType,
      name: formData.name || `New ${objectType}`,
      description: formData.description || 'Created via drawing tools',
      color: formData.color || '#3388ff',
      geometry: geoJsonData.geometry,
      image: formData.image || '',
      icon: formData.icon || 'default'  // Include icon for markers
    };
    
    // For circles, include the radius property
    if (objectType === 'circle' && geoJsonData.properties?.radius) {
      objectData.radius = geoJsonData.properties.radius;
      console.log('⭕ Added radius to objectData:', objectData.radius);
    }
    
    console.log('📤 Sending to backend:', objectData);

    try {
      const response = await fetch(`${API_BASE_URL}/api/map-objects`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(objectData),
      });

      if (response.ok) {
        const newObject = await response.json();
        console.log('✅ Created map object:', newObject);
        
        // Batch state updates to prevent multiple re-renders
        setMapObjects(prev => [...prev, newObject]);
        
        // Don't reset form or close modal immediately
        // This prevents the screen from re-rendering/resetting view
        // resetForm(); // Commented out to keep user in place
      }
    } catch (error) {
      console.error('Error creating map object:', error);
    }
  };

  const handleObjectUpdate = async (objectId, updates) => {
    const userName = `${user?.first_name} ${user?.last_name}`;
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/map-objects/${objectId}?modified_by=${encodeURIComponent(userName)}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updates),
        }
      );

      if (response.ok) {
        const updatedObject = await response.json();
        setMapObjects(prev => prev.map(obj => obj.id === objectId ? updatedObject : obj));
        setEditingObject(null);
        setShowObjectForm(false);
        setEditingInModal(false);
        setShowHoverModal(false);
        resetForm();
        console.log('✅ Object updated successfully');
      }
    } catch (error) {
      console.error('Error updating map object:', error);
    }
  };

  const handleObjectDelete = async (objectId) => {
    if (!window.confirm('Are you sure you want to delete this map object?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/map-objects/${objectId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        // Remove from local state and refresh from backend to ensure synchronization
        setMapObjects(prev => prev.filter(obj => obj.id !== objectId));
        // Refresh objects from backend to ensure UI is in sync
        await fetchMapObjects();
        setShowHoverModal(false);
        setEditingInModal(false);
        console.log('✅ Object deleted successfully');
      } else {
        console.error('Failed to delete object. Status:', response.status);
        alert('Failed to delete object. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting map object:', error);
      alert('Error deleting object. Please check console for details.');
    }
  };

  // Copy object to clipboard
  const handleCopyObject = (obj) => {
    console.log('📋 Copying object:', obj.name);
    setCopiedObject({
      ...obj,
      id: null, // Clear ID so a new one will be generated on paste
      name: `${obj.name} (Copy)` // Add "(Copy)" to name
    });
    alert(`✅ "${obj.name}" copied!\n\nClick "Paste Object" to create a duplicate.`);
  };

  // Paste copied object (with offset)
  const handlePasteObject = async () => {
    if (!copiedObject) {
      alert('📋 Nothing to paste. Copy an object first by clicking the "Copy" button.');
      return;
    }

    const userName = `${user?.first_name} ${user?.last_name}`;
    
    try {
      // Create offset coordinates (slightly offset from original)
      let pasteGeometry = { ...copiedObject.geometry };
      
      if (copiedObject.type === 'marker' || copiedObject.type === 'circle') {
        // Offset by approximately 100 meters (0.001 degrees)
        const offset = 0.001;
        pasteGeometry.coordinates = [
          copiedObject.geometry.coordinates[0] + offset,
          copiedObject.geometry.coordinates[1] + offset
        ];
      } else if (copiedObject.type === 'polygon' || copiedObject.type === 'polyline' || copiedObject.type === 'line') {
        // Offset all coordinates
        const offset = 0.001;
        if (copiedObject.type === 'polygon') {
          pasteGeometry.coordinates = copiedObject.geometry.coordinates.map(ring =>
            ring.map(coord => [coord[0] + offset, coord[1] + offset])
          );
        } else {
          pasteGeometry.coordinates = copiedObject.geometry.coordinates.map(coord =>
            [coord[0] + offset, coord[1] + offset]
          );
        }
      }

      const newObject = {
        exercise_id: exerciseId,
        organization_id: selectedAgency?.id || null,
        type: copiedObject.type,
        name: copiedObject.name,
        description: copiedObject.description || '',
        geometry: pasteGeometry,
        color: copiedObject.color || '#3388ff',
        image: copiedObject.image || '',
        icon: copiedObject.icon || 'default',
        radius: copiedObject.radius || null,
        created_by: userName
      };

      console.log('📋 Pasting object:', newObject);

      const response = await fetch(`${API_BASE_URL}/api/map-objects`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newObject),
      });

      if (response.ok) {
        const createdObject = await response.json();
        console.log('✅ Object pasted successfully:', createdObject);
        
        // Refresh map objects
        await fetchMapObjects();
        
        // Pan to the new object location
        if (mapRef.current && pasteGeometry.coordinates) {
          let lat, lng;
          if (copiedObject.type === 'polygon') {
            lat = pasteGeometry.coordinates[0][0][1];
            lng = pasteGeometry.coordinates[0][0][0];
          } else if (copiedObject.type === 'polyline' || copiedObject.type === 'line') {
            lat = pasteGeometry.coordinates[0][1];
            lng = pasteGeometry.coordinates[0][0];
          } else {
            lat = pasteGeometry.coordinates[1];
            lng = pasteGeometry.coordinates[0];
          }
          if (isMountedRef.current && mapRef.current) {
            try {
              mapRef.current.setView([lat, lng], mapRef.current.getZoom(), {
                animate: false
              });
            } catch (e) {
              console.warn('Map setView error suppressed:', e.message);
            }
          }
        }

        alert(`✅ "${copiedObject.name}" pasted successfully!`);
      } else {
        console.error('Failed to paste object. Status:', response.status);
        alert('Failed to paste object. Please try again.');
      }
    } catch (error) {
      console.error('Error pasting object:', error);
      alert('Error pasting object. Please check console for details.');
    }
  };

  // React Leaflet Draw event handlers
  const handleDrawCreated = (e) => {
    const { layerType, layer } = e;
    console.log('🎨 Draw created:', layerType, layer);
    
    // Convert layer to GeoJSON
    const geoJson = layer.toGeoJSON();
    console.log('📝 GeoJSON before processing:', geoJson);
    
    // For circles, extract the radius
    if (layerType === 'circle') {
      const radius = layer.getRadius(); // Get radius in meters
      geoJson.properties = geoJson.properties || {};
      geoJson.properties.radius = radius;
      console.log('⭕ Circle radius:', radius);
      console.log('📝 GeoJSON after adding radius:', geoJson);
    }
    
    console.log('💾 Saving drawn object to backend via handleObjectCreate');
    console.log('💾 Object type:', layerType === 'polyline' ? 'line' : layerType);
    
    // Save to backend using existing handler (which will add to mapObjects state)
    try {
      handleObjectCreate(geoJson, layerType === 'polyline' ? 'line' : layerType);
    } catch (error) {
      console.error('❌ Error in handleObjectCreate:', error);
    }
  };

  const handleDrawEdited = async (e) => {
    const { layers } = e;
    console.log('✏️ Draw edited:', layers.getLayers().length, 'layers');
    
    layers.eachLayer(async (layer) => {
      const geoJson = layer.toGeoJSON();
      console.log('🔄 Edited layer:', geoJson);
      
      // First try to get object ID from layer (stored when layer was added)
      let matchingObject = null;
      if (layer.objectId) {
        matchingObject = mapObjects.find(obj => obj.id === layer.objectId);
        console.log('📍 Found object by stored ID:', layer.objectId);
      }
      
      // Fallback: Find matching object by coordinates
      if (!matchingObject) {
        matchingObject = mapObjects.find(obj => {
          if (!obj.geometry) return false;
          
          // Simple coordinate matching for update identification
          if (obj.type === 'marker' && geoJson.geometry.type === 'Point') {
            const [objLng, objLat] = obj.geometry.coordinates;
            const [newLng, newLat] = geoJson.geometry.coordinates;
            return Math.abs(objLat - newLat) < 0.001 && Math.abs(objLng - newLng) < 0.001;
          }
          
          return JSON.stringify(obj.geometry) === JSON.stringify(geoJson.geometry);
        });
      }
      
      if (matchingObject) {
        console.log('✅ Updating object:', matchingObject.id, matchingObject.name);
        
        // Handle circle radius separately
        let updateData = { geometry: geoJson.geometry };
        if (layer.getRadius) {
          updateData.radius = layer.getRadius();
        }
        
        await handleObjectUpdate(matchingObject.id, updateData);
      } else {
        console.warn('⚠️ Could not find matching object for edited layer');
      }
    });
  };

  const handleDrawDeleted = async (e) => {
    const { layers } = e;
    console.log('🗑️ Draw deleted:', layers.getLayers().length, 'layers');
    
    layers.eachLayer(async (layer) => {
      const geoJson = layer.toGeoJSON();
      console.log('❌ Deleted layer:', geoJson);
      
      // First try to get object ID from layer (stored when layer was added)
      let matchingObject = null;
      if (layer.objectId) {
        matchingObject = mapObjects.find(obj => obj.id === layer.objectId);
        console.log('📍 Found object by stored ID:', layer.objectId);
      }
      
      // Fallback: Find matching object by coordinates
      if (!matchingObject) {
        matchingObject = mapObjects.find(obj => {
          if (!obj.geometry) return false;
          return JSON.stringify(obj.geometry) === JSON.stringify(geoJson.geometry);
        });
      }
      
      if (matchingObject) {
        console.log('🗑️ Deleting object:', matchingObject.id, matchingObject.name);
        await handleObjectDelete(matchingObject.id);
      } else {
        console.warn('⚠️ Could not find matching object for deleted layer');
      }
    });
  };

  const getDefaultColorForType = (type) => {
    switch (type) {
      case 'marker': return '#ff0000'; // Red for markers
      case 'line': return '#0000ff'; // Blue for lines  
      case 'polygon': return '#ff8800'; // Orange for polygons
      case 'rectangle': return '#00ff00'; // Green for rectangles
      default: return '#3388ff';
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      color: '#3388ff',
      image: '',
      type: 'marker' // Reset type to default
    });
    setShowObjectForm(false);
    setEditingObject(null);
  };

  const resetModalForm = () => {
    setModalFormData({
      name: '',
      description: '',
      color: '#3388ff',
      image: ''
    });
  };

  const getObjectCoordinates = (obj) => {
    if (!obj.geometry?.coordinates) return [];
    
    switch (obj.type) {
      case 'marker':
        const [lng, lat] = obj.geometry.coordinates;
        return [{ lat: lat.toFixed(6), lng: lng.toFixed(6) }];
      case 'line':
        return obj.geometry.coordinates.map(coord => ({
          lat: coord[1].toFixed(6),
          lng: coord[0].toFixed(6)
        }));
      case 'polygon':
        return obj.geometry.coordinates[0].map(coord => ({
          lat: coord[1].toFixed(6),
          lng: coord[0].toFixed(6)
        }));
      case 'rectangle':
        return obj.geometry.coordinates[0].map(coord => ({
          lat: coord[1].toFixed(6),
          lng: coord[0].toFixed(6)
        }));
      default:
        return [];
    }
  };

  const handleObjectMouseEnter = useCallback((obj, event) => {
    console.log('🔍 Object clicked:', obj.name, 'Modal currently showing:', showHoverModalRef.current);
    
    // Prevent event bubbling that might interfere
    if (event && event.originalEvent) {
      event.originalEvent.stopPropagation();
    }
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }
    
    // Use refs to check current state (avoids stale closure in memoized component)
    if (editingInModalRef.current) {
      console.log('🔍 Already editing in modal, ignoring click');
      return;
    }
    
    if (showHoverModalRef.current) {
      console.log('🔍 Modal already showing, ignoring click');
      return;
    }
    
    console.log('🔍 Setting up modal data for object:', obj.name);
    setHoveredObject(obj);
    
    // Extract coordinates based on object type
    let lat = '', lng = '';
    if (obj.geometry?.coordinates) {
      if (obj.type === 'polygon' && Array.isArray(obj.geometry.coordinates[0])) {
        lat = obj.geometry.coordinates[0][0][1] || '';
        lng = obj.geometry.coordinates[0][0][0] || '';
      } else if (obj.type === 'line' && Array.isArray(obj.geometry.coordinates[0])) {
        lat = obj.geometry.coordinates[0][1] || '';
        lng = obj.geometry.coordinates[0][0] || '';
      } else {
        lat = obj.geometry.coordinates[1] || '';
        lng = obj.geometry.coordinates[0] || '';
      }
    }
    
    setModalFormData({
      name: obj.name || '',
      description: obj.description || '',
      color: obj.color || '#3388ff',
      image: obj.image || '',
      latitude: lat,
      longitude: lng,
      icon: obj.icon || 'default'
    });
    
    // Calculate smart modal position near the marker FIRST
    if (mapRef.current && obj.geometry?.coordinates) {
      const map = mapRef.current;
      let markerLatLng;
      if (obj.type === 'polygon' && Array.isArray(obj.geometry.coordinates[0])) {
        markerLatLng = L.latLng(obj.geometry.coordinates[0][0][1], obj.geometry.coordinates[0][0][0]);
      } else if (obj.type === 'line' && Array.isArray(obj.geometry.coordinates[0])) {
        markerLatLng = L.latLng(obj.geometry.coordinates[0][1], obj.geometry.coordinates[0][0]);
      } else {
        markerLatLng = L.latLng(obj.geometry.coordinates[1], obj.geometry.coordinates[0]);
      }
      
      const markerPoint = map.latLngToContainerPoint(markerLatLng);
      
      // Modal dimensions
      const modalWidth = 400;
      const modalHeight = 500;
      const clearance = 30; // 30px clearance from marker as requested
      const markerSize = 45; // Approximate marker icon size
      
      // Get viewport dimensions
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      let x, y;
      let placement = 'top'; // Default: try TOP first (above marker)
      
      // PRIORITY 1: Try positioning ABOVE the marker (preferred)
      x = markerPoint.x - modalWidth / 2;
      y = markerPoint.y - markerSize / 2 - clearance - modalHeight;
      
      // Check if modal goes off top edge
      if (y < 20) {
        // PRIORITY 2: Try bottom (below marker)
        placement = 'bottom';
        x = markerPoint.x - modalWidth / 2;
        y = markerPoint.y + markerSize / 2 + clearance;
      }
      
      // Check if modal goes off bottom
      if (y + modalHeight > viewportHeight - 20) {
        // PRIORITY 3: Try right side
        placement = 'right';
        x = markerPoint.x + markerSize / 2 + clearance;
        y = markerPoint.y - modalHeight / 2;
      }
      
      // Check if modal goes off right edge
      if (x + modalWidth > viewportWidth - 20) {
        // PRIORITY 4: Try left side
        placement = 'left';
        x = markerPoint.x - markerSize / 2 - clearance - modalWidth;
        y = markerPoint.y - modalHeight / 2;
      }
      
      // Final boundary checks - clamp to screen bounds
      if (x < 20) {
        x = 20;
      }
      if (x + modalWidth > viewportWidth - 20) {
        x = viewportWidth - modalWidth - 20;
      }
      
      if (y < 20) {
        y = 20;
      }
      if (y + modalHeight > viewportHeight - 20) {
        y = viewportHeight - modalHeight - 20;
      }
      
      console.log(`📍 Positioning modal ${placement} of marker at (${x.toFixed(0)}, ${y.toFixed(0)})`);
      
      // Use requestAnimationFrame to ensure position is set before modal shows
      requestAnimationFrame(() => {
        setHoverModalPosition({ x, y });
        // Show modal AFTER position is set
        requestAnimationFrame(() => {
          setShowHoverModal(true);
          console.log('✅ Modal state set to visible');
        });
      });
    } else {
      // Fallback if no geometry available
      setShowHoverModal(true);
      console.log('✅ Modal state set to visible (no position calculation)');
    }
  }, []); // Empty deps since function uses refs which don't need to be in deps

  const handleObjectMouseLeave = () => {
    // Removed auto-close on mouse leave since we're using click-to-open modal
    // Modal should only close when user explicitly closes it or clicks outside
    console.log('🔍 Mouse left object, but modal should stay open');
  };

  const startEditingInModal = async () => {
    console.log('📝 Starting edit mode for object:', hoveredObject?.name);
    
    // Check if object is locked by someone else
    if (hoveredObject && lockedObjects[hoveredObject.id]) {
      const lock = lockedObjects[hoveredObject.id];
      const currentUser = `${user?.first_name} ${user?.last_name}`;
      if (lock.locked_by !== currentUser) {
        alert(`🔒 This object is currently being edited by ${lock.locked_by}. Please wait until they're done.`);
        return;
      }
    }
    
    // Try to lock the object
    if (hoveredObject) {
      const locked = await lockObject(hoveredObject.id);
      if (!locked) {
        return; // Lock failed, don't proceed
      }
      
      const formData = {
        name: hoveredObject.name || '',
        description: hoveredObject.description || '',
        color: hoveredObject.color || '#3388ff',
        image: hoveredObject.image || ''
      };
      
      setModalFormData(formData);
      
      // Set input values directly to avoid controlled component issues
      setTimeout(() => {
        if (nameInputRef.current) {
          nameInputRef.current.value = formData.name;
        }
        if (descriptionInputRef.current) {
          descriptionInputRef.current.value = formData.description;
        }
      }, 100);
    }
    
    // Set edit mode after form data is prepared
    setEditingInModal(true);
    console.log('📝 Edit mode activated');
  };

  const cancelEditingInModal = async () => {
    // Unlock the object
    if (hoveredObject) {
      await unlockObject(hoveredObject.id);
    }
    setEditingInModal(false);
    if (hoveredObject) {
      setModalFormData({
        name: hoveredObject.name || '',
        description: hoveredObject.description || '',
        color: hoveredObject.color || '#3388ff',
        image: hoveredObject.image || ''
      });
    }
  };

  // Removed onChange handlers since we're using uncontrolled inputs

  const saveModalChanges = async () => {
    if (hoveredObject) {
      // Get current values from input refs
      const currentName = nameInputRef.current?.value || modalFormData.name;
      const currentDescription = descriptionInputRef.current?.value || modalFormData.description;
      
      // Update geometry coordinates if changed
      let updatedGeometry = { ...hoveredObject.geometry };
      const newLat = parseFloat(modalFormData.latitude);
      const newLng = parseFloat(modalFormData.longitude);
      let coordinatesChanged = false;
      
      if (!isNaN(newLat) && !isNaN(newLng)) {
        // Check if coordinates actually changed
        let oldLat, oldLng;
        if (hoveredObject.type === 'polygon' && Array.isArray(hoveredObject.geometry.coordinates[0])) {
          oldLat = hoveredObject.geometry.coordinates[0][0][1];
          oldLng = hoveredObject.geometry.coordinates[0][0][0];
        } else if (hoveredObject.type === 'line' && Array.isArray(hoveredObject.geometry.coordinates[0])) {
          oldLat = hoveredObject.geometry.coordinates[0][1];
          oldLng = hoveredObject.geometry.coordinates[0][0];
        } else {
          oldLat = hoveredObject.geometry.coordinates[1];
          oldLng = hoveredObject.geometry.coordinates[0];
        }
        
        coordinatesChanged = (oldLat !== newLat || oldLng !== newLng);
        
        if (hoveredObject.type === 'polygon' && Array.isArray(hoveredObject.geometry.coordinates[0])) {
          // For polygons, update the first point
          updatedGeometry.coordinates = [...hoveredObject.geometry.coordinates];
          updatedGeometry.coordinates[0] = [...hoveredObject.geometry.coordinates[0]];
          updatedGeometry.coordinates[0][0] = [newLng, newLat];
        } else if (hoveredObject.type === 'line' && Array.isArray(hoveredObject.geometry.coordinates[0])) {
          // For lines, update the first point
          updatedGeometry.coordinates = [...hoveredObject.geometry.coordinates];
          updatedGeometry.coordinates[0] = [newLng, newLat];
        } else {
          // For markers and circles, update the center coordinates
          updatedGeometry.coordinates = [newLng, newLat];
        }
      }
      
      const updatedData = {
        ...hoveredObject,
        ...modalFormData,
        name: currentName,
        description: currentDescription,
        geometry: updatedGeometry,
        icon: modalFormData.icon
      };
      
      console.log('💾 Saving modal changes:', updatedData);
      await handleObjectUpdate(hoveredObject.id, updatedData);
      
      // If coordinates changed, refresh map objects and pan to the new location
      if (coordinatesChanged) {
        console.log(`📍 Coordinates changed, refreshing map and panning to new location: ${newLat}, ${newLng}`);
        
        // Refresh map objects to ensure marker position is updated
        await fetchMapObjects();
        
        // Pan the map to the new location
        if (mapRef.current) {
          setTimeout(() => {
            if (!isMountedRef.current || !mapRef.current) return;
            try {
              mapRef.current.setView([newLat, newLng], mapRef.current.getZoom(), {
                animate: false
              });
            } catch (err) {
              console.warn('Map setView error suppressed:', err.message);
            }
          }, 100);
        }
      }
      
      // Unlock the object after saving
      await unlockObject(hoveredObject.id);
      
      // Update the local hoveredObject to reflect changes immediately
      setHoveredObject(prev => ({
        ...prev,
        ...updatedData
      }));
    }
  };

  const handleImageUpload = (event, isModal = false) => {
    console.log('📁 handleImageUpload called, isModal:', isModal);
    const file = event.target.files[0];
    
    if (file) {
      console.log('📁 File details:', file.name, file.type, file.size);
      
      // Check if it's a valid image
      if (!file.type.startsWith('image/')) {
        alert('Please select a valid image file.');
        return;
      }
      
      // Check file size (limit to 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image file is too large. Please select a file smaller than 5MB.');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (e) => {
        console.log('📁 Image loaded, data URL length:', e.target.result.length);
        if (isModal) {
          console.log('📁 Updating modal form data with image');
          setModalFormData(prev => ({ 
            ...prev, 
            image: e.target.result 
          }));
        } else {
          console.log('📁 Updating regular form data with image');
          setFormData(prev => ({ 
            ...prev, 
            image: e.target.result 
          }));
        }
      };
      
      reader.onerror = (error) => {
        console.error('📁 Error reading file:', error);
        alert('Error reading the image file. Please try again.');
      };
      
      reader.readAsDataURL(file);
    } else {
      console.log('📁 No file selected');
    }
  };

  const handleCameraCapture = async (isModal = false) => {
    try {
      // Check if browser supports camera access
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        alert('Camera access is not supported in this browser.');
        return;
      }

      // Request camera access
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } // Use back camera if available
      });

      // Create video element for camera feed
      const video = document.createElement('video');
      video.srcObject = stream;
      video.autoplay = true;
      video.playsInline = true;

      // Create modal for camera interface
      const cameraModal = document.createElement('div');
      cameraModal.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: rgba(0,0,0,0.9);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        z-index: 10001;
      `;

      const videoContainer = document.createElement('div');
      videoContainer.style.cssText = `
        position: relative;
        max-width: 90vw;
        max-height: 70vh;
      `;

      const canvas = document.createElement('canvas');
      const captureBtn = document.createElement('button');
      captureBtn.textContent = 'Capture Photo';
      captureBtn.style.cssText = `
        margin-top: 20px;
        padding: 12px 24px;
        background: #3b82f6;
        color: white;
        border: none;
        border-radius: 6px;
        font-size: 16px;
        cursor: pointer;
      `;

      const cancelBtn = document.createElement('button');
      cancelBtn.textContent = 'Cancel';
      cancelBtn.style.cssText = `
        margin: 20px 0 0 10px;
        padding: 12px 24px;
        background: #6b7280;
        color: white;
        border: none;
        border-radius: 6px;
        font-size: 16px;
        cursor: pointer;
      `;

      video.style.cssText = `
        width: 100%;
        height: 100%;
        object-fit: cover;
        border-radius: 8px;
      `;

      videoContainer.appendChild(video);
      cameraModal.appendChild(videoContainer);
      cameraModal.appendChild(captureBtn);
      cameraModal.appendChild(cancelBtn);
      document.body.appendChild(cameraModal);

      // Handle photo capture
      captureBtn.onclick = () => {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0);
        
        const imageDataURL = canvas.toDataURL('image/jpeg', 0.8);
        
        if (isModal) {
          setModalFormData(prev => ({ ...prev, image: imageDataURL }));
        } else {
          setFormData(prev => ({ ...prev, image: imageDataURL }));
        }

        // Cleanup
        stream.getTracks().forEach(track => track.stop());
        document.body.removeChild(cameraModal);
      };

      // Handle cancel
      cancelBtn.onclick = () => {
        stream.getTracks().forEach(track => track.stop());
        document.body.removeChild(cameraModal);
      };

    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('Failed to access camera. Please check permissions or try uploading a file instead.');
    }
  };

  const getObjectsByCategory = (category) => {
    if (category === 'all') return mapObjects;
    return mapObjects.filter(obj => obj.type === category);
  };

  // Toggle visibility of a map object
  const toggleObjectVisibility = (objectId) => {
    setHiddenObjects(prev => {
      const newSet = new Set(prev);
      if (newSet.has(objectId)) {
        newSet.delete(objectId);
      } else {
        newSet.add(objectId);
      }
      return newSet;
    });
  };

  // Get visible objects for map rendering
  const getVisibleMapObjects = () => {
    return mapObjects.filter(obj => !hiddenObjects.has(obj.id));
  };

  const categories = [
    { id: 'all', label: 'All Objects', count: mapObjects.length },
    { id: 'marker', label: 'Markers', count: mapObjects.filter(obj => obj.type === 'marker').length },
    { id: 'line', label: 'Lines', count: mapObjects.filter(obj => obj.type === 'line').length },
    { id: 'polygon', label: 'Polygons', count: mapObjects.filter(obj => obj.type === 'polygon').length },
    { id: 'rectangle', label: 'Rectangles', count: mapObjects.filter(obj => obj.type === 'rectangle').length },
    { id: 'circle', label: 'Circles', count: mapObjects.filter(obj => obj.type === 'circle').length }
  ];

  // Drawing state and map reference
  const mapRef = useRef(null);
  const drawnItemsRef = useRef(null);
  // drawingMode moved to earlier in component for polling useEffect dependency
  const [pendingObjectData, setPendingObjectData] = useState(null);

  // Fix default marker icons for Leaflet
  useEffect(() => {
    delete L.Icon.Default.prototype._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
      iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
    });
  }, []);

  // Native Leaflet.draw initialization removed - now using React Leaflet Draw

  // Add global functions for popup buttons
  useEffect(() => {
    window.editObject = (objectId) => {
      const obj = mapObjects.find(o => o.id === objectId);
      if (obj) {
        setEditingObject(obj);
        setFormData({
          name: obj.name,
          type: obj.type,
          description: obj.description,
          color: obj.color,
          image: obj.image || ''
        });
        setShowObjectForm(true);
        setEditingInModal(true);
      }
    };

    window.deleteObject = (objectId) => {
      handleObjectDelete(objectId);
    };

    return () => {
      delete window.editObject;
      delete window.deleteObject;
    };
  }, [mapObjects]);

  // Handle map print/capture
  const handleMapPrint = async () => {
    try {
      console.log('Starting map capture for ICS 201...');
      
      // Find the leaflet container
      const mapContainer = document.querySelector('.leaflet-container');
      if (!mapContainer) {
        alert('Map not found. Please ensure the map is fully loaded.');
        return;
      }

      // Use html2canvas to capture the map
      const html2canvas = (await import('html2canvas')).default;
      
      // Hide the print button temporarily during capture
      const printButton = document.querySelector('.absolute.bottom-4.right-4');
      if (printButton) {
        printButton.style.display = 'none';
      }

      const canvas = await html2canvas(mapContainer, {
        useCORS: true,
        allowTaint: true,
        scale: 0.8, // Good quality while keeping file size manageable
        width: mapContainer.offsetWidth,
        height: mapContainer.offsetHeight,
        backgroundColor: '#ffffff'
      });
      
      // Restore print button
      if (printButton) {
        printButton.style.display = 'block';
      }

      const mapScreenshot = canvas.toDataURL('image/png', 0.8);
      
      // Store the screenshot globally so it can be accessed by ICS 201 print function
      window.currentMapScreenshot = mapScreenshot;
      window.mapCaptureTimestamp = new Date().toLocaleString();
      
      console.log('Map captured successfully for ICS 201');
      alert('Map captured successfully! It will be included in the next "Print IAP" report.');
      
    } catch (error) {
      console.error('Error capturing map:', error);
      alert('Error capturing map. Please try again.');
      
      // Restore print button on error
      const printButton = document.querySelector('.absolute.bottom-4.right-4');
      if (printButton) {
        printButton.style.display = 'block';
      }
    }
  };

  // PopupContent Component for Full CRUD Operations in Leaflet Popups
  const PopupContent = ({ obj }) => {
    const [isEditing, setIsEditing] = React.useState(false);
    
    // Helper function to get coordinates based on object type
    const getCoordinates = (obj) => {
      if (!obj.geometry?.coordinates) return { lat: '', lng: '' };
      if (obj.type === 'polygon' && Array.isArray(obj.geometry.coordinates[0])) {
        return {
          lat: obj.geometry.coordinates[0][0][1] || '',
          lng: obj.geometry.coordinates[0][0][0] || ''
        };
      }
      if (obj.type === 'line' && Array.isArray(obj.geometry.coordinates[0])) {
        return {
          lat: obj.geometry.coordinates[0][1] || '',
          lng: obj.geometry.coordinates[0][0] || ''
        };
      }
      return {
        lat: obj.geometry.coordinates[1] || '',
        lng: obj.geometry.coordinates[0] || ''
      };
    };
    
    const [formData, setFormData] = React.useState({
      name: obj.name || '',
      description: obj.description || '',
      color: obj.color || '#3388ff',
      image: obj.image || '',
      latitude: getCoordinates(obj).lat,
      longitude: getCoordinates(obj).lng,
      icon: obj.icon || 'default'
    });
    const [isUploading, setIsUploading] = React.useState(false);

    // Track popup editing state in ref to prevent auto-refresh during editing
    React.useEffect(() => {
      editingInPopupRef.current = isEditing;
      console.log('📝 Popup editing state updated:', isEditing);
    }, [isEditing]);

    // Update form data when obj prop changes (after refresh)
    const objKey = `${obj.name}|${obj.description}|${obj.color}|${obj.image}|${obj.icon}|${JSON.stringify(obj.geometry)}`;
    React.useEffect(() => {
      const coords = getCoordinates(obj);
      setFormData({
        name: obj.name || '',
        description: obj.description || '',
        color: obj.color || '#3388ff',
        image: obj.image || '',
        latitude: coords.lat,
        longitude: coords.lng,
        icon: obj.icon || 'default'
      });
    }, [objKey]); // objKey captures all obj field changes

    const handleSave = async () => {
      try {
        // Update geometry coordinates based on object type
        let updatedGeometry = { ...obj.geometry };
        const newLat = parseFloat(formData.latitude);
        const newLng = parseFloat(formData.longitude);
        let coordinatesChanged = false;
        
        if (!isNaN(newLat) && !isNaN(newLng)) {
          // Check if coordinates actually changed
          let oldLat, oldLng;
          if (obj.type === 'polygon' && Array.isArray(obj.geometry.coordinates[0])) {
            oldLat = obj.geometry.coordinates[0][0][1];
            oldLng = obj.geometry.coordinates[0][0][0];
          } else if (obj.type === 'line' && Array.isArray(obj.geometry.coordinates[0])) {
            oldLat = obj.geometry.coordinates[0][1];
            oldLng = obj.geometry.coordinates[0][0];
          } else {
            oldLat = obj.geometry.coordinates[1];
            oldLng = obj.geometry.coordinates[0];
          }
          
          coordinatesChanged = (oldLat !== newLat || oldLng !== newLng);
          
          if (obj.type === 'polygon' && Array.isArray(obj.geometry.coordinates[0])) {
            // For polygons, update the first point (we could expand this to move the whole polygon)
            updatedGeometry.coordinates = [...obj.geometry.coordinates];
            updatedGeometry.coordinates[0] = [...obj.geometry.coordinates[0]];
            updatedGeometry.coordinates[0][0] = [newLng, newLat];
          } else if (obj.type === 'line' && Array.isArray(obj.geometry.coordinates[0])) {
            // For lines, update the first point
            updatedGeometry.coordinates = [...obj.geometry.coordinates];
            updatedGeometry.coordinates[0] = [newLng, newLat];
          } else {
            // For markers and circles, update the center coordinates
            updatedGeometry.coordinates = [newLng, newLat];
          }
        }
        
        const response = await axios.put(`${API}/map-objects/${obj.id}?exercise_id=${exerciseId}`, {
          name: formData.name,
          description: formData.description,
          color: formData.color,
          image: formData.image,
          icon: formData.icon,
          geometry: updatedGeometry,
          radius: obj.radius // Keep radius for circles
        });
        
        if (response.status === 200) {
          // Store current map state before any updates
          const currentCenter = mapRef.current ? mapRef.current.getCenter() : null;
          const currentZoomLevel = mapRef.current ? mapRef.current.getZoom() : null;
          
          // Exit edit mode first
          setIsEditing(false);
          
          // Force immediate refresh of map objects from backend
          await fetchMapObjects();
          
          // If coordinates changed, pan to new location; otherwise restore view
          if (coordinatesChanged && mapRef.current) {
            console.log(`📍 Coordinates changed, panning map to new location: ${newLat}, ${newLng}`);
            setTimeout(() => {
              if (!isMountedRef.current || !mapRef.current) return;
              try {
                mapRef.current.setView([newLat, newLng], currentZoomLevel, {
                  animate: false
                });
              } catch (err) {
                console.warn('Map setView error suppressed:', err.message);
              }
            }, 300); // Increased timeout to ensure it runs after any other view changes
          } else if (currentCenter && currentZoomLevel && mapRef.current) {
            // Coordinates didn't change, restore the exact view
            console.log(`🔄 Restoring map view after save - zoom: ${currentZoomLevel}, center: ${currentCenter.lat}, ${currentCenter.lng}`);
            setTimeout(() => {
              if (!isMountedRef.current || !mapRef.current) return;
              try {
                mapRef.current.setView(currentCenter, currentZoomLevel, {
                  animate: false // No animation for restore
                });
                console.log(`✅ View restored to zoom: ${currentZoomLevel}`);
              } catch (err) {
                console.warn('Map setView error suppressed:', err.message);
              }
            }, 300); // Increased timeout to ensure it runs after any other view changes
          }
          
          // Show success message after render
          setTimeout(() => {
            alert('Object updated successfully!' + (coordinatesChanged ? ' Map position updated.' : ''));
          }, 300);
        }
      } catch (error) {
        console.error('Error updating object:', error);
        alert('Error updating object. Please try again.');
      }
    };

    const handleDelete = async () => {
      if (window.confirm(`Are you sure you want to delete "${obj.name || 'this object'}"?`)) {
        try {
          const response = await axios.delete(`${API}/map-objects/${obj.id}?exercise_id=${exerciseId}`);
          
          if (response.status === 200) {
            alert('Object deleted successfully!');
            fetchMapObjects();
          }
        } catch (error) {
          console.error('Error deleting object:', error);
          alert('Error deleting object. Please try again.');
        }
      }
    };

    const handleCameraCapture = () => {
      setIsUploading(true);
      
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        alert('Camera access is not supported in this browser.');
        setIsUploading(false);
        return;
      }

      navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        } 
      })
      .then(stream => {
        // Create camera modal
        const cameraModal = document.createElement('div');
        cameraModal.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.9);z-index:10001;display:flex;flex-direction:column;align-items:center;justify-content:center;';
        
        const video = document.createElement('video');
        video.style.cssText = 'max-width:90%;max-height:70vh;';
        video.srcObject = stream;
        video.autoplay = true;
        
        const buttonContainer = document.createElement('div');
        buttonContainer.style.cssText = 'margin-top:20px;display:flex;gap:10px;';
        
        const captureBtn = document.createElement('button');
        captureBtn.textContent = 'Capture Photo';
        captureBtn.style.cssText = 'padding:12px 24px;background:#3b82f6;color:white;border:none;border-radius:6px;cursor:pointer;font-size:16px;';
        
        const cancelBtn = document.createElement('button');
        cancelBtn.textContent = 'Cancel';
        cancelBtn.style.cssText = 'padding:12px 24px;background:#6b7280;color:white;border:none;border-radius:6px;cursor:pointer;font-size:16px;';
        
        captureBtn.onclick = () => {
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          canvas.getContext('2d').drawImage(video, 0, 0);
          
          const imageData = canvas.toDataURL('image/jpeg', 0.8);
          setFormData(prev => ({ ...prev, image: imageData }));
          
          stream.getTracks().forEach(track => track.stop());
          document.body.removeChild(cameraModal);
          setIsUploading(false);
        };
        
        cancelBtn.onclick = () => {
          stream.getTracks().forEach(track => track.stop());
          document.body.removeChild(cameraModal);
          setIsUploading(false);
        };
        
        buttonContainer.appendChild(captureBtn);
        buttonContainer.appendChild(cancelBtn);
        cameraModal.appendChild(video);
        cameraModal.appendChild(buttonContainer);
        document.body.appendChild(cameraModal);
      })
      .catch(err => {
        console.error('Camera error:', err);
        alert('Unable to access camera: ' + err.message);
        setIsUploading(false);
      });
    };

    const handleFileUpload = (e) => {
      const file = e.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFormData(prev => ({ ...prev, image: reader.result }));
        };
        reader.readAsDataURL(file);
      }
    };

    if (isEditing) {
      return (
        <div style={{ minWidth: '280px', maxWidth: '400px', maxHeight: '70vh', overflowY: 'auto' }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: 'bold', position: 'sticky', top: 0, background: 'white', paddingBottom: '8px', zIndex: 10 }}>Edit Map Object</h3>
          
          {/* Name */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Name:</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '14px' }}
            />
          </div>

          {/* Description */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Description:</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '14px', resize: 'vertical' }}
            />
          </div>

          {/* Coordinates */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
              {obj.type === 'circle' ? 'Center Coordinates:' : 
               obj.type === 'polygon' ? 'First Point Coordinates:' : 
               'Coordinates:'}
            </label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '2px', fontSize: '12px', color: '#6b7280' }}>Latitude:</label>
                <input
                  type="number"
                  step="0.000001"
                  value={formData.latitude}
                  onChange={(e) => setFormData(prev => ({ ...prev, latitude: e.target.value }))}
                  placeholder="Enter latitude"
                  style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '14px' }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '2px', fontSize: '12px', color: '#6b7280' }}>Longitude:</label>
                <input
                  type="number"
                  step="0.000001"
                  value={formData.longitude}
                  onChange={(e) => setFormData(prev => ({ ...prev, longitude: e.target.value }))}
                  placeholder="Enter longitude"
                  style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '14px' }}
                />
              </div>
            </div>
            <p style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
              💡 Edit coordinates to precisely position the object on the map
            </p>
          </div>

          {/* Icon Selector (only for markers) */}
          {obj.type === 'marker' && (
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Icon:</label>
              <select
                value={formData.icon}
                onChange={(e) => setFormData(prev => ({ ...prev, icon: e.target.value }))}
                style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '14px', cursor: 'pointer' }}
              >
                {Object.entries(ICS_ICONS).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value.emoji} {value.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Color */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>
              {obj.type === 'marker' ? 'Icon Color:' : 'Color:'}
            </label>
            <input
              type="color"
              value={formData.color}
              onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
              style={{ width: '100%', height: '40px', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer' }}
            />
          </div>

          {/* Image Upload */}
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontSize: '14px', fontWeight: '500' }}>Image:</label>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <button
                onClick={handleCameraCapture}
                disabled={isUploading}
                style={{ flex: 1, padding: '8px', background: '#10b981', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}
              >
                📷 Take Photo
              </button>
              <label style={{ flex: 1, padding: '8px', background: '#6b7280', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', textAlign: 'center' }}>
                📁 Upload
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
              </label>
            </div>
            {formData.image && (
              <img src={formData.image} alt="Preview" style={{ width: '100%', maxHeight: '150px', objectFit: 'cover', borderRadius: '4px', marginTop: '8px' }} />
            )}
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSave();
              }}
              style={{ flex: 1, padding: '10px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}
            >
              💾 Save
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsEditing(false);
                const coords = getCoordinates(obj);
                setFormData({
                  name: obj.name || '',
                  description: obj.description || '',
                  color: obj.color || '#3388ff',
                  image: obj.image || '',
                  latitude: coords.lat,
                  longitude: coords.lng,
                  icon: obj.icon || 'default'
                });
              }}
              style={{ flex: 1, padding: '10px', background: '#6b7280', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}
            >
              ✖ Cancel
            </button>
          </div>
        </div>
      );
    }

    // Read-only view
    return (
      <div style={{ minWidth: '280px', maxWidth: '400px', maxHeight: '70vh', overflowY: 'auto' }}>
        <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: 'bold', position: 'sticky', top: 0, background: 'white', paddingBottom: '8px', zIndex: 10 }}>{obj.name || 'Unnamed Object'}</h3>
        
        {obj.description && (
          <div style={{ marginBottom: '12px' }}>
            <strong style={{ fontSize: '14px' }}>Description:</strong>
            <p style={{ margin: '4px 0', fontSize: '14px', color: '#4b5563' }}>{obj.description}</p>
          </div>
        )}

        {/* Coordinates - Show for all map objects */}
        {obj.geometry?.coordinates && (
          <div style={{ marginBottom: '12px' }}>
            <strong style={{ fontSize: '14px' }}>
              {obj.type === 'circle' ? 'Center Coordinates:' : 
               obj.type === 'polygon' ? 'First Point Coordinates:' : 
               obj.type === 'line' ? 'First Point Coordinates:' :
               'Coordinates:'}
            </strong>
            <div style={{ marginTop: '4px', padding: '8px', background: '#f9fafb', borderRadius: '4px', fontSize: '13px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ color: '#6b7280' }}>Latitude:</span>
                <span style={{ fontWeight: '500', color: '#111827' }}>
                  {obj.type === 'polygon' && Array.isArray(obj.geometry.coordinates[0]) 
                    ? obj.geometry.coordinates[0][0]?.[1]?.toFixed(6) || 'N/A'
                    : obj.type === 'line' && Array.isArray(obj.geometry.coordinates[0])
                    ? obj.geometry.coordinates[0]?.[1]?.toFixed(6) || 'N/A'
                    : obj.geometry.coordinates[1]?.toFixed(6) || 'N/A'}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#6b7280' }}>Longitude:</span>
                <span style={{ fontWeight: '500', color: '#111827' }}>
                  {obj.type === 'polygon' && Array.isArray(obj.geometry.coordinates[0])
                    ? obj.geometry.coordinates[0][0]?.[0]?.toFixed(6) || 'N/A'
                    : obj.type === 'line' && Array.isArray(obj.geometry.coordinates[0])
                    ? obj.geometry.coordinates[0]?.[0]?.toFixed(6) || 'N/A'
                    : obj.geometry.coordinates[0]?.toFixed(6) || 'N/A'}
                </span>
              </div>
              {obj.type === 'circle' && obj.radius && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', paddingTop: '4px', borderTop: '1px solid #e5e7eb' }}>
                  <span style={{ color: '#6b7280' }}>Radius:</span>
                  <span style={{ fontWeight: '500', color: '#111827' }}>{obj.radius.toFixed(2)} meters</span>
                </div>
              )}
            </div>
          </div>
        )}

        <div style={{ marginBottom: '12px' }}>
          <strong style={{ fontSize: '14px' }}>Color:</strong>
          <div style={{ display: 'inline-block', width: '30px', height: '20px', background: obj.color || '#3388ff', border: '1px solid #d1d5db', borderRadius: '4px', marginLeft: '8px', verticalAlign: 'middle' }}></div>
        </div>

        {obj.image && (
          <div style={{ marginBottom: '12px' }}>
            <img src={obj.image} alt={obj.name} style={{ width: '100%', maxHeight: '200px', objectFit: 'cover', borderRadius: '4px' }} />
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsEditing(true);
            }}
            style={{ flex: 1, padding: '10px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}
          >
            ✏️ Edit
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleDelete();
            }}
            style={{ flex: 1, padding: '10px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}
          >
            🗑️ Delete
          </button>
        </div>
      </div>
    );
  };

  const MapContainerComponent = ({ onObjectClick }) => {
    // Component to handle map events and instance (must be inside MapContainer)
    const MapEventHandler = () => {
      const map = useMap();
      
      useEffect(() => {
        if (map && isMountedRef.current) {
          console.log('🗺️ Map instance obtained via useMap');
          mapRef.current = map;
          setMapReady(true);
          
          // Restore saved map view from localStorage (if available)
          const savedLat = secureStorage.getPreference('map-center-lat');
          const savedLng = secureStorage.getPreference('map-center-lng');
          const savedZoom = secureStorage.getPreference('map-zoom');
          
          if (savedLat && savedLng && savedZoom) {
            console.log(`🗺️ Restoring saved map view: ${savedLat}, ${savedLng} @ zoom ${savedZoom}`);
            try {
              map.setView([parseFloat(savedLat), parseFloat(savedLng)], parseFloat(savedZoom), { animate: false });
            } catch (e) {
              console.warn('Map setView error suppressed:', e.message);
            }
          }
          
          // Set initial zoom level
          if (isMountedRef.current) {
            setCurrentZoom(map.getZoom());
          }
          
          // Update zoom level display during zoom animation
          // Use requestAnimationFrame for immediate, smooth updates
          const updateZoomDisplay = () => {
            if (isMountedRef.current && mapRef.current) {
              requestAnimationFrame(() => {
                if (isMountedRef.current && mapRef.current) {
                  try {
                    setCurrentZoom(mapRef.current.getZoom());
                  } catch (e) {
                    // Ignore errors if map is being destroyed
                  }
                }
              });
            }
          };
          
          // Save map position and zoom when they change
          const saveMapView = () => {
            if (!isMountedRef.current || !mapRef.current) return;
            try {
              const center = mapRef.current.getCenter();
              const zoom = mapRef.current.getZoom();
              secureStorage.setPreference('map-center-lat', center.lat.toString());
              secureStorage.setPreference('map-center-lng', center.lng.toString());
              secureStorage.setPreference('map-zoom', zoom.toString());
              
              // Update zoom level display immediately
              if (isMountedRef.current) {
                requestAnimationFrame(() => {
                  if (isMountedRef.current) {
                    setCurrentZoom(zoom);
                  }
                });
              }
            } catch (e) {
              // Ignore errors if map is being destroyed
            }
          };
          
          // Listen for base layer changes to persist user's selection
          const handleBaseLayerChange = (e) => {
            if (!isMountedRef.current) return;
            const layerName = e.name;
            let layerKey = 'satellite';
            if (layerName.includes('Streets')) layerKey = 'streets';
            else if (layerName.includes('Topographical')) layerKey = 'topographical';
            else if (layerName.includes('Greyscale')) layerKey = 'greyscale';
            
            console.log(`🗺️ Base layer changed to: ${layerKey}`);
            secureStorage.setPreference('map-base-layer', layerKey);
            if (isMountedRef.current) {
              setSelectedBaseLayer(layerKey);
            }
          };
          
          map.on('baselayerchange', handleBaseLayerChange);
          
          // Update zoom display during zoom animation (smooth, immediate updates)
          map.on('zoom', updateZoomDisplay);
          map.on('zoomstart', updateZoomDisplay); // Also update when zoom starts
          map.on('moveend', saveMapView);
          map.on('zoomend', saveMapView);
          
          // Listen for Leaflet draw events to update drawingMode state
          map.on('draw:drawstart', (e) => {
            if (!isMountedRef.current) return;
            console.log('✏️ Drawing started:', e.layerType);
            setDrawingMode(e.layerType);
          });
          
          map.on('draw:drawstop', () => {
            if (!isMountedRef.current) return;
            console.log('✏️ Drawing stopped');
            setDrawingMode(null);
          });
          
          map.on('draw:created', () => {
            if (!isMountedRef.current) return;
            console.log('✏️ Drawing created, clearing drawing mode');
            setDrawingMode(null);
          });
          
          map.on('draw:editstart', () => {
            if (!isMountedRef.current) return;
            console.log('✏️ Edit started');
            setDrawingMode('editing');
          });
          
          map.on('draw:editstop', () => {
            if (!isMountedRef.current) return;
            console.log('✏️ Edit stopped');
            setDrawingMode(null);
          });
          
          // Track mouse position for coordinate display
          const handleMouseMove = (e) => {
            if (!isMountedRef.current) return;
            const coords = { lat: e.latlng.lat, lng: e.latlng.lng };
            setMouseCoords(coords);
          };
          
          const handleMouseOut = () => {
            if (!isMountedRef.current) return;
            setMouseCoords({ lat: null, lng: null });
          };
          
          map.on('mousemove', handleMouseMove);
          map.on('mouseout', handleMouseOut);
        }
        
        // Cleanup listeners on unmount
        return () => {
          if (map) {
            // Disable zoom animation to prevent _leaflet_pos errors during unmount
            try {
              // Stop any ongoing animations
              map.stop();
              
              // Disable further animations
              map.options.zoomAnimation = false;
              map.options.fadeAnimation = false;
              map.options.markerZoomAnimation = false;
              
              // Remove all zoom-related event listeners immediately
              map.off('zoomanim');
              map.off('zoomstart');
              map.off('zoomend');
              map.off('zoom');
              
              // Clear the map pane position to prevent stale reference errors
              const mapPane = map.getPane('mapPane');
              if (mapPane && mapPane._leaflet_pos) {
                // Store position as proper L.point to prevent equals() errors
                mapPane._leaflet_pos = L.point(0, 0);
              }
            } catch (e) {
              // Ignore errors during cleanup
            }
            
            map.off('moveend');
            map.off('baselayerchange');
            map.off('draw:drawstart');
            map.off('draw:drawstop');
            map.off('draw:created');
            map.off('draw:editstart');
            map.off('draw:editstop');
            map.off('mousemove');
            map.off('mouseout');
          }
        };
      }, [map]);
      
      return null;
    };
    // Use saved position from localStorage if available, otherwise use exercise coordinates or defaults
    const savedLat = secureStorage.getPreference('map-center-lat');
    const savedLng = secureStorage.getPreference('map-center-lng');
    const savedZoom = secureStorage.getPreference('map-zoom');
    
    // Validate localStorage values before using them
    const parsedLat = savedLat ? parseFloat(savedLat) : NaN;
    const parsedLng = savedLng ? parseFloat(savedLng) : NaN;
    const parsedZoom = savedZoom ? parseFloat(savedZoom) : NaN;
    
    const initialCenter = (!isNaN(parsedLat) && !isNaN(parsedLng))
      ? [parsedLat, parsedLng]
      : (exerciseData?.latitude && exerciseData?.longitude && !isNaN(exerciseData.latitude) && !isNaN(exerciseData.longitude)) 
        ? [exerciseData.latitude, exerciseData.longitude] 
        : [39.8283, -98.5795];
    const initialZoom = (!isNaN(parsedZoom) && parsedZoom >= 1 && parsedZoom <= 20)
      ? parsedZoom
      : (exerciseData?.latitude && exerciseData?.longitude) 
        ? (exerciseData.zoom_level || 8) 
        : 4;

    return (
      <div style={{ height: '100%', width: '100%', position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
        {/* Display Mode Button */}
        <button
          onClick={() => {
            const url = exerciseId ? `#display-mapping?exercise=${exerciseId}` : '#display-mapping';
            window.open(url, '_blank');
          }}
          style={{
            position: 'absolute',
            top: '10px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            padding: '10px 20px',
            backgroundColor: '#ea580c',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '15px',
            fontWeight: '600',
            boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
            whiteSpace: 'nowrap'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#c2410c'}
          onMouseLeave={(e) => e.target.style.backgroundColor = '#ea580c'}
          title="Open in Display Mode"
        >
          <Monitor style={{ width: '18px', height: '18px' }} />
          Display Mode
        </button>
        
        <MapErrorBoundary>
        <LeafletMapContainer
          key={`map-${exerciseId}`} // Stable key based only on exerciseId, not mapObjects
          center={initialCenter}
          zoom={initialZoom}
          style={{ 
            height: '100%', 
            width: '100%',
            position: 'relative',
            zIndex: 1
          }}
          scrollWheelZoom={true}
          doubleClickZoom={true}
          zoomControl={true}
          zoomSnap={0.1}
          zoomDelta={0.5}
          zoomAnimation={false}
          fadeAnimation={false}
          markerZoomAnimation={false}
        >
          {/* Map Event Handler to get map instance */}
          <MapEventHandler />
          
          {/* Layer control for different map types - uses persisted selection */}
          <LayersControl position="topright">
            <LayersControl.BaseLayer checked={selectedBaseLayer === 'satellite'} name="🛰️ Satellite">
              <TileLayer
                attribution='© ESRI'
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              />
            </LayersControl.BaseLayer>
            
            <LayersControl.BaseLayer checked={selectedBaseLayer === 'streets'} name="🗺️ Streets">
              <TileLayer
                attribution='© OpenStreetMap contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
            </LayersControl.BaseLayer>
            
            <LayersControl.BaseLayer checked={selectedBaseLayer === 'topographical'} name="⛰️ Topographical">
              <TileLayer
                attribution='Map data: © OpenStreetMap contributors, SRTM | Map style: © OpenTopoMap (CC-BY-SA)'
                url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
              />
            </LayersControl.BaseLayer>
            
            <LayersControl.BaseLayer checked={selectedBaseLayer === 'greyscale'} name="🏙️ Greyscale">
              <TileLayer
                attribution='© CartoDB'
                url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
              />
            </LayersControl.BaseLayer>
          </LayersControl>

          {/* React Leaflet Draw Integration */}
          <FeatureGroup ref={featureGroupRef}>
            <EditControl
              position="topleft"
              onCreated={handleDrawCreated}
              onEdited={handleDrawEdited}
              onDeleted={handleDrawDeleted}
              draw={{
                polygon: {
                  allowIntersection: false,
                  drawError: {
                    color: '#e1e100',
                    message: '<strong>Drawing error:</strong> Shape edges cannot cross!'
                  },
                  shapeOptions: {
                    color: '#3388ff',
                    weight: 3,
                    fillOpacity: 0.3
                  },
                  showArea: true,
                  showLength: true,
                  metric: true,
                  imperial: false
                },
                polyline: {
                  shapeOptions: {
                    color: '#3388ff',
                    weight: 4,
                    opacity: 0.8
                  },
                  showLength: true,
                  metric: true,
                  imperial: false,
                  feet: false
                },
                rectangle: {
                  shapeOptions: {
                    color: '#3388ff',
                    weight: 3,
                    fillOpacity: 0.3
                  },
                  showArea: true,
                  metric: true,
                  imperial: false
                },
                circle: {
                  shapeOptions: {
                    color: '#3388ff',
                    weight: 3,
                    fillOpacity: 0.3
                  },
                  showRadius: true,
                  metric: true,
                  imperial: false,
                  feet: false
                },
                marker: {
                  icon: new L.Icon({
                    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
                    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
                    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
                    iconSize: [25, 41],
                    iconAnchor: [12, 41],
                    popupAnchor: [1, -34],
                    shadowSize: [41, 41]
                  }),
                  repeatMode: false,
                  zIndexOffset: 2000
                },
                circlemarker: false // Disable circle marker to avoid confusion
              }}
              edit={{
                remove: {
                  title: 'Delete layers'
                },
                edit: {
                  selectedPathOptions: {
                    maintainColor: true,
                    opacity: 0.8,
                    dashArray: '10, 10',
                    fill: true,
                    fillColor: '#fe57a1',
                    fillOpacity: 0.1
                  }
                }
              }}
            />

          {/* Existing map objects from backend - INSIDE FeatureGroup for edit capability */}
          {mapObjects
            .filter((obj) => !hiddenObjects.has(obj.id)) // Filter out hidden objects
            .filter((obj) => {
              // Filter out objects with invalid coordinates
              if (!obj.geometry || !obj.geometry.coordinates) {
                console.warn('Object missing geometry or coordinates:', obj);
                return false;
              }
              
              if (obj.type === 'marker') {
                const [lng, lat] = obj.geometry.coordinates;
                if (typeof lat !== 'number' || typeof lng !== 'number' || 
                    isNaN(lat) || isNaN(lng) || 
                    lat < -90 || lat > 90 || lng < -180 || lng > 180) {
                  console.warn('Invalid marker coordinates:', obj);
                  return false;
                }
              } else if (obj.type === 'polygon') {
                if (!Array.isArray(obj.geometry.coordinates) || 
                    !Array.isArray(obj.geometry.coordinates[0])) {
                  console.warn('Invalid polygon coordinates:', obj);
                  return false;
                }
                // Check each coordinate pair
                for (const coord of obj.geometry.coordinates[0]) {
                  if (!Array.isArray(coord) || coord.length !== 2) {
                    console.warn('Invalid polygon coordinate pair:', coord);
                    return false;
                  }
                  const [lng, lat] = coord;
                  if (typeof lat !== 'number' || typeof lng !== 'number' || 
                      isNaN(lat) || isNaN(lng)) {
                    console.warn('Invalid polygon coordinate values:', coord);
                    return false;
                  }
                }
              } else if (obj.type === 'line') {
                if (!Array.isArray(obj.geometry.coordinates)) {
                  console.warn('Invalid line coordinates:', obj);
                  return false;
                }
                // Check each coordinate pair
                for (const coord of obj.geometry.coordinates) {
                  if (!Array.isArray(coord) || coord.length !== 2) {
                    console.warn('Invalid line coordinate pair:', coord);
                    return false;
                  }
                  const [lng, lat] = coord;
                  if (typeof lat !== 'number' || typeof lng !== 'number' || 
                      isNaN(lat) || isNaN(lng)) {
                    console.warn('Invalid line coordinate values:', coord);
                    return false;
                  }
                }
              } else if (obj.type === 'circle') {
                const [lng, lat] = obj.geometry.coordinates;
                if (typeof lat !== 'number' || typeof lng !== 'number' || 
                    isNaN(lat) || isNaN(lng) || 
                    lat < -90 || lat > 90 || lng < -180 || lng > 180) {
                  console.warn('Invalid circle coordinates:', obj);
                  return false;
                }
                if (!obj.radius || typeof obj.radius !== 'number' || isNaN(obj.radius) || obj.radius <= 0) {
                  console.warn('Invalid or missing circle radius:', obj);
                  return false;
                }
              }
              
              return true;
            })
            .map((obj) => {
            if (obj.type === 'marker' && obj.geometry?.coordinates) {
              const [lng, lat] = obj.geometry.coordinates;
              
              // Get ICS icon data
              const iconType = obj.icon || 'default';
              const iconData = ICS_ICONS[iconType] || ICS_ICONS['default'];
              const iconColor = obj.color || '#3388ff';
              
              // Create custom DivIcon with ICS emoji/icon
              const customIcon = L.divIcon({
                html: `<div style="
                  position: relative;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  width: 45px;
                  height: 45px;
                  cursor: move;
                ">
                  <div style="
                    position: absolute;
                    bottom: 0;
                    left: 50%;
                    transform: translateX(-50%);
                    width: 36px;
                    height: 36px;
                    background-color: ${iconColor};
                    border-radius: 50% 50% 50% 0;
                    transform: translateX(-50%) rotate(-45deg);
                    border: 3px solid white;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.4);
                  "></div>
                  <div style="
                    position: absolute;
                    bottom: 6px;
                    left: 50%;
                    transform: translateX(-50%);
                    font-size: 20px;
                    z-index: 1;
                    filter: drop-shadow(0 1px 1px rgba(0,0,0,0.3));
                  ">${iconData.emoji}</div>
                </div>`,
                className: 'custom-ics-icon',
                iconSize: [45, 45],
                iconAnchor: [22, 45],
                popupAnchor: [0, -45]
              });
              
              return (
                <Marker
                  key={obj.id}
                  position={[lat, lng]}
                  icon={customIcon}
                  draggable={true}
                  eventHandlers={{
                    dragstart: (e) => {
                      console.log(`🖐️ Started dragging marker: ${obj.name}`);
                      // Add semi-transparent effect during drag
                      e.target.setOpacity(0.6);
                    },
                    drag: (e) => {
                      const currentLatLng = e.target.getLatLng();
                      // Optional: could show live coordinates during drag
                      // console.log(`📍 Dragging... Lat: ${currentLatLng.lat.toFixed(6)}, Lng: ${currentLatLng.lng.toFixed(6)}`);
                    },
                    dragend: async (e) => {
                      const newLatLng = e.target.getLatLng();
                      console.log(`📍 Marker dragged to new position:`, newLatLng);
                      console.log(`📍 Object ID: ${obj.id}, Old: [${lat}, ${lng}], New: [${newLatLng.lat}, ${newLatLng.lng}]`);
                      
                      // Restore full opacity
                      e.target.setOpacity(1.0);
                      
                      // Update the marker's coordinates in the backend
                      try {
                        const updatedGeometry = {
                          ...obj.geometry,
                          coordinates: [newLatLng.lng, newLatLng.lat]
                        };
                        
                        await handleObjectUpdate(obj.id, {
                          geometry: updatedGeometry
                        });
                        
                        console.log('✅ Marker position updated successfully');
                        
                        // Show success notification
                        setTimeout(() => {
                          alert(`✅ Marker "${obj.name}" repositioned!\n\nNew coordinates:\nLat: ${newLatLng.lat.toFixed(6)}\nLng: ${newLatLng.lng.toFixed(6)}`);
                        }, 100);
                      } catch (error) {
                        console.error('❌ Error updating marker position:', error);
                        alert('❌ Failed to update marker position. Please try again.');
                        // Refresh to revert to old position
                        await fetchMapObjects();
                      }
                    }
                  }}
                >
                  {/* Hover Tooltip - Read-only Name + Description + Coordinates + Image */}
                  <Tooltip 
                    direction="auto" 
                    offset={[0, -10]} 
                    opacity={0.9}
                    interactive={false}
                    permanent={false}
                  >
                    <div style={{ maxWidth: '200px' }}>
                      <strong>{obj.name || 'Unnamed Object'}</strong>
                      {obj.description && (
                        <>
                          <br />
                          <span style={{ fontSize: '0.9em' }}>{obj.description}</span>
                        </>
                      )}
                      <br />
                      <span style={{ fontSize: '0.85em', color: '#666' }}>
                        Lat: {lat.toFixed(6)}, Lng: {lng.toFixed(6)}
                      </span>
                      {obj.image && (
                        <>
                          <br />
                          <img 
                            src={obj.image} 
                            alt={obj.name || 'Object'} 
                            style={{ 
                              width: '100%', 
                              maxHeight: '120px', 
                              objectFit: 'cover', 
                              borderRadius: '4px', 
                              marginTop: '8px' 
                            }} 
                          />
                        </>
                      )}
                    </div>
                  </Tooltip>
                  
                  {/* Click Popup - Full CRUD */}
                  <Popup 
                    maxWidth={420} 
                    minWidth={300}
                    maxHeight={600}
                    closeButton={true}
                    autoClose={false}
                    closeOnEscapeKey={true}
                    keepInView={true}
                    autoPan={true}
                    autoPanPadding={[80, 80]}
                  >
                    <PopupContent obj={obj} key={obj.id} />
                  </Popup>
                </Marker>
              );
            } else if (obj.type === 'polygon' && obj.geometry?.coordinates) {
              try {
                const coords = obj.geometry.coordinates[0].map(coord => [coord[1], coord[0]]);
                return (
                  <Polygon
                    key={obj.id}
                    positions={coords}
                    color={obj.color || '#3388ff'}
                    fillColor={obj.color || '#3388ff'}
                    fillOpacity={0.3}
                    eventHandlers={{
                      add: (e) => {
                        // Store object ID on the layer for edit/delete operations
                        e.target.objectId = obj.id;
                        e.target.objectData = obj;
                      }
                    }}
                  >
                    {/* Hover Tooltip - Read-only Name + Description + Image */}
                    <Tooltip 
                      direction="auto" 
                      offset={[0, 0]} 
                      opacity={0.9}
                      interactive={false}
                      permanent={false}
                    >
                      <div style={{ maxWidth: '200px' }}>
                        <strong>{obj.name || 'Unnamed Object'}</strong>
                        {obj.description && (
                          <>
                            <br />
                            <span style={{ fontSize: '0.9em' }}>{obj.description}</span>
                          </>
                        )}
                        {obj.image && (
                          <>
                            <br />
                            <img 
                              src={obj.image} 
                              alt={obj.name || 'Object'} 
                              style={{ 
                                width: '100%', 
                                maxHeight: '120px', 
                                objectFit: 'cover', 
                                borderRadius: '4px', 
                                marginTop: '8px' 
                              }} 
                            />
                          </>
                        )}
                      </div>
                    </Tooltip>
                    
                    {/* Click Popup - Full CRUD */}
                    <Popup 
                      maxWidth={420} 
                      minWidth={300}
                      maxHeight={600}
                      closeButton={true}
                      autoClose={false}
                      closeOnEscapeKey={true}
                      keepInView={true}
                      autoPan={true}
                      autoPanPadding={[80, 80]}
                    >
                      <PopupContent obj={obj} key={obj.id} />
                    </Popup>
                  </Polygon>
                );
              } catch (error) {
                console.warn('Error rendering polygon:', obj, error);
                return null;
              }
            } else if (obj.type === 'line' && obj.geometry?.coordinates) {
              try {
                const coords = obj.geometry.coordinates.map(coord => [coord[1], coord[0]]);
                return (
                  <Polyline
                    key={obj.id}
                    positions={coords}
                    color={obj.color || '#3388ff'}
                    weight={4}
                    eventHandlers={{
                      add: (e) => {
                        // Store object ID on the layer for edit/delete operations
                        e.target.objectId = obj.id;
                        e.target.objectData = obj;
                      }
                    }}
                  >
                    {/* Hover Tooltip - Read-only Name + Description + Image */}
                    <Tooltip 
                      direction="auto" 
                      offset={[0, 0]} 
                      opacity={0.9}
                      interactive={false}
                      permanent={false}
                    >
                      <div style={{ maxWidth: '200px' }}>
                        <strong>{obj.name || 'Unnamed Object'}</strong>
                        {obj.description && (
                          <>
                            <br />
                            <span style={{ fontSize: '0.9em' }}>{obj.description}</span>
                          </>
                        )}
                        {obj.image && (
                          <>
                            <br />
                            <img 
                              src={obj.image} 
                              alt={obj.name || 'Object'} 
                              style={{ 
                                width: '100%', 
                                maxHeight: '120px', 
                                objectFit: 'cover', 
                                borderRadius: '4px', 
                                marginTop: '8px' 
                              }} 
                            />
                          </>
                        )}
                      </div>
                    </Tooltip>
                    
                    {/* Click Popup - Full CRUD */}
                    <Popup 
                      maxWidth={420} 
                      minWidth={300}
                      maxHeight={600}
                      closeButton={true}
                      autoClose={false}
                      closeOnEscapeKey={true}
                      keepInView={true}
                      autoPan={true}
                      autoPanPadding={[80, 80]}
                    >
                      <PopupContent obj={obj} key={obj.id} />
                    </Popup>
                  </Polyline>
                );
              } catch (error) {
                console.warn('Error rendering polyline:', obj, error);
                return null;
              }
            } else if (obj.type === 'rectangle' && obj.geometry?.coordinates) {
              try {
                const coords = obj.geometry.coordinates[0].map(coord => [coord[1], coord[0]]);
                const bounds = [
                  [Math.min(...coords.map(c => c[0])), Math.min(...coords.map(c => c[1]))],
                  [Math.max(...coords.map(c => c[0])), Math.max(...coords.map(c => c[1]))]
                ];
                return (
                  <Rectangle
                    key={obj.id}
                    bounds={bounds}
                    color={obj.color || '#3388ff'}
                    fillColor={obj.color || '#3388ff'}
                    fillOpacity={0.3}
                    eventHandlers={{
                      add: (e) => {
                        // Store object ID on the layer for edit/delete operations
                        e.target.objectId = obj.id;
                        e.target.objectData = obj;
                      }
                    }}
                  >
                    {/* Hover Tooltip - Read-only Name + Description + Image */}
                    <Tooltip 
                      direction="auto" 
                      offset={[0, 0]} 
                      opacity={0.9}
                      interactive={false}
                      permanent={false}
                    >
                      <div style={{ maxWidth: '200px' }}>
                        <strong>{obj.name || 'Unnamed Object'}</strong>
                        {obj.description && (
                          <>
                            <br />
                            <span style={{ fontSize: '0.9em' }}>{obj.description}</span>
                          </>
                        )}
                        {obj.image && (
                          <>
                            <br />
                            <img 
                              src={obj.image} 
                              alt={obj.name || 'Object'} 
                              style={{ 
                                width: '100%', 
                                maxHeight: '120px', 
                                objectFit: 'cover', 
                                borderRadius: '4px', 
                                marginTop: '8px' 
                              }} 
                            />
                          </>
                        )}
                      </div>
                    </Tooltip>
                    
                    {/* Click Popup - Full CRUD */}
                    <Popup 
                      maxWidth={420} 
                      minWidth={300}
                      maxHeight={600}
                      closeButton={true}
                      autoClose={false}
                      closeOnEscapeKey={true}
                      keepInView={true}
                      autoPan={true}
                      autoPanPadding={[80, 80]}
                    >
                      <PopupContent obj={obj} key={obj.id} />
                    </Popup>
                  </Rectangle>
                );
              } catch (error) {
                console.warn('Error rendering rectangle:', obj, error);
                return null;
              }
            } else if (obj.type === 'circle' && obj.geometry?.coordinates) {
              try {
                const [lng, lat] = obj.geometry.coordinates;
                const radius = obj.radius || 1000;
                
                console.log('⭕ Rendering circle in Mapping:', { id: obj.id, name: obj.name, lat, lng, radius });
                
                // Validate coordinates and radius
                if (!lat || !lng || isNaN(lat) || isNaN(lng) || !radius || isNaN(radius) || radius <= 0) {
                  console.warn('❌ Invalid circle data in Mapping:', obj);
                  return null;
                }
                
                return (
                  <Circle
                    key={`mapping-circle-${obj.id}`}
                    center={[parseFloat(lat), parseFloat(lng)]}
                    radius={parseFloat(radius)}
                    pathOptions={{ 
                      color: obj.color || '#3388ff', 
                      fillColor: obj.color || '#3388ff', 
                      fillOpacity: 0.2,
                      weight: 3
                    }}
                    eventHandlers={{
                      add: (e) => {
                        // Store object ID on the layer for edit/delete operations
                        e.target.objectId = obj.id;
                        e.target.objectData = obj;
                      }
                    }}
                  >
                    {/* Hover Tooltip */}
                    <Tooltip 
                      direction="auto" 
                      offset={[0, 0]} 
                      opacity={0.9}
                      interactive={false}
                      permanent={false}
                    >
                      <div style={{ maxWidth: '200px' }}>
                        <strong>{obj.name || 'Unnamed Circle'}</strong>
                        {obj.description && (
                          <>
                            <br />
                            <span style={{ fontSize: '0.9em' }}>{obj.description}</span>
                          </>
                        )}
                        <br />
                        <span style={{ fontSize: '0.85em', color: '#666' }}>Radius: {Math.round(radius)}m</span>
                      </div>
                    </Tooltip>
                    
                    {/* Click Popup */}
                    <Popup 
                      maxWidth={420} 
                      minWidth={300}
                      maxHeight={600}
                      closeButton={true}
                      autoClose={false}
                      closeOnEscapeKey={true}
                      keepInView={true}
                      autoPan={true}
                      autoPanPadding={[80, 80]}
                    >
                      <PopupContent obj={obj} key={obj.id} />
                    </Popup>
                  </Circle>
                );
              } catch (error) {
                console.warn('Error rendering circle:', obj, error);
                return null;
              }
            }
            return null;
          }).filter(Boolean)} {/* Filter out null values */}
          </FeatureGroup>

          {/* ─── Live GPS Resource Tracker Markers ─── */}
          <TrackerMarkerLayer trackers={liveTrackers} showTrackers={showTrackers} trails={trailData} showTrails={showTrails} trailMinutes={trailMinutes} onMapRef={(m) => { leafletMapInstanceRef.current = m; }} />

        </LeafletMapContainer>
        </MapErrorBoundary>
        
        {/* Map Print Button */}
        <div className="absolute bottom-4 right-4 z-[1000]">
          <Button
            onClick={handleMapPrint}
            className="bg-orange-500 hover:bg-orange-600 text-white shadow-lg"
            size="sm"
          >
            <Printer className="h-4 w-4 mr-2" />
            Capture for ICS 201
          </Button>
        </div>

        {/* Enhanced CRUD Modal with Image Upload */}
        {showHoverModal && hoveredObject && (
          <div 
            className="fixed inset-0 modal-overlay"
            style={{ zIndex: 9998, backgroundColor: 'transparent', pointerEvents: 'none' }}
            onClick={(e) => {
              console.log('🔍 Modal overlay clicked, editingInModal:', editingInModal);
              if (!editingInModal && e.target === e.currentTarget) {
                console.log('🔍 Closing modal via overlay click');
                setShowHoverModal(false);
                setHoveredObject(null);
              }
            }}
          >
            <div 
              className="bg-white rounded-lg shadow-2xl p-6 max-w-md w-full max-h-[80vh] overflow-y-auto modal-content"
              style={{
                position: 'fixed',
                left: `${hoverModalPosition.x}px`,
                top: `${hoverModalPosition.y}px`,
                zIndex: 9999,
                pointerEvents: 'auto',
                border: '2px solid #3b82f6',
                boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
                width: '400px'
              }}
              onClick={(e) => {
                console.log('🔍 Modal content clicked');
                e.stopPropagation();
              }}
            >
              {/* Arrow pointer pointing down to marker */}
              <div
                style={{
                  position: 'absolute',
                  bottom: '-12px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '0',
                  height: '0',
                  borderLeft: '12px solid transparent',
                  borderRight: '12px solid transparent',
                  borderTop: '12px solid #3b82f6',
                  zIndex: 10000
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  bottom: '-9px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '0',
                  height: '0',
                  borderLeft: '10px solid transparent',
                  borderRight: '10px solid transparent',
                  borderTop: '10px solid white',
                  zIndex: 10001
                }}
              />
              <div className="space-y-4">
                {/* Header */}
                <div className="flex justify-between items-start">
                  <h3 className="text-xl font-semibold text-gray-900">
                    {editingInModal ? 'Edit Map Object' : 'Map Object Details'}
                  </h3>
                  <button
                    onClick={() => {
                      setShowHoverModal(false);
                      setHoveredObject(null);
                      setEditingInModal(false);
                    }}
                    className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                  >
                    ×
                  </button>
                </div>

                {/* Object Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name:</label>
                  {editingInModal ? (
                    <input
                      ref={nameInputRef}
                      type="text"
                      defaultValue={modalFormData.name || ''}
                      className="w-full p-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 bg-white text-gray-900 placeholder-gray-500"
                      style={{
                        color: '#1f2937 !important',
                        backgroundColor: '#ffffff !important',
                        fontSize: '14px'
                      }}
                      placeholder="Enter object name"
                      autoComplete="off"
                    />
                  ) : (
                    <p className="text-gray-900 bg-gray-50 p-2 rounded">
                      {hoveredObject.name || 'Unnamed Object'}
                    </p>
                  )}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description:</label>
                  {editingInModal ? (
                    <textarea
                      ref={descriptionInputRef}
                      defaultValue={modalFormData.description || ''}
                      className="w-full p-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 bg-white text-gray-900 placeholder-gray-500 resize-none"
                      style={{
                        color: '#1f2937 !important',
                        backgroundColor: '#ffffff !important',
                        fontSize: '14px',
                        minHeight: '80px'
                      }}
                      rows="3"
                      placeholder="Enter object description"
                      autoComplete="off"
                    />
                  ) : (
                    <p className="text-gray-700 bg-gray-50 p-2 rounded">
                      {hoveredObject.description || 'No description'}
                    </p>
                  )}
                </div>

                {/* Coordinates - Show for all map objects */}
                {hoveredObject && hoveredObject.geometry?.coordinates && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {hoveredObject.type === 'circle' ? 'Center Coordinates:' : 
                       hoveredObject.type === 'polygon' ? 'First Point Coordinates:' : 
                       'Coordinates:'}
                    </label>
                    {editingInModal ? (
                      <div>
                        <div className="flex gap-2 mb-2">
                          <div className="flex-1">
                            <label className="block text-xs text-gray-600 mb-1">Latitude:</label>
                            <input
                              type="number"
                              step="0.000001"
                              value={modalFormData.latitude || ''}
                              onChange={(e) => setModalFormData(prev => ({ ...prev, latitude: e.target.value }))}
                              placeholder="Enter latitude"
                              className="w-full p-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 bg-white text-gray-900"
                            />
                          </div>
                          <div className="flex-1">
                            <label className="block text-xs text-gray-600 mb-1">Longitude:</label>
                            <input
                              type="number"
                              step="0.000001"
                              value={modalFormData.longitude || ''}
                              onChange={(e) => setModalFormData(prev => ({ ...prev, longitude: e.target.value }))}
                              placeholder="Enter longitude"
                              className="w-full p-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 bg-white text-gray-900"
                            />
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 italic">💡 Edit coordinates to precisely position the object</p>
                      </div>
                    ) : (
                      <div className="bg-gray-50 p-3 rounded space-y-1">
                        <div className="flex justify-between">
                          <span className="text-gray-600 text-sm">Latitude:</span>
                          <span className="text-gray-900 font-medium">
                            {hoveredObject.type === 'polygon' && Array.isArray(hoveredObject.geometry.coordinates[0])
                              ? hoveredObject.geometry.coordinates[0][0]?.[1]?.toFixed(6) || 'N/A'
                              : hoveredObject.type === 'line' && Array.isArray(hoveredObject.geometry.coordinates[0])
                              ? hoveredObject.geometry.coordinates[0]?.[1]?.toFixed(6) || 'N/A'
                              : hoveredObject.geometry.coordinates[1]?.toFixed(6) || 'N/A'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600 text-sm">Longitude:</span>
                          <span className="text-gray-900 font-medium">
                            {hoveredObject.type === 'polygon' && Array.isArray(hoveredObject.geometry.coordinates[0])
                              ? hoveredObject.geometry.coordinates[0][0]?.[0]?.toFixed(6) || 'N/A'
                              : hoveredObject.type === 'line' && Array.isArray(hoveredObject.geometry.coordinates[0])
                              ? hoveredObject.geometry.coordinates[0]?.[0]?.toFixed(6) || 'N/A'
                              : hoveredObject.geometry.coordinates[0]?.toFixed(6) || 'N/A'}
                          </span>
                        </div>
                        {hoveredObject.type === 'circle' && hoveredObject.radius && (
                          <div className="flex justify-between pt-1 border-t border-gray-200">
                            <span className="text-gray-600 text-sm">Radius:</span>
                            <span className="text-gray-900 font-medium">{hoveredObject.radius.toFixed(2)} meters</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Icon Selector (only for markers) */}
                {hoveredObject && hoveredObject.type === 'marker' && editingInModal && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Icon:</label>
                    <select
                      value={modalFormData.icon || 'default'}
                      onChange={(e) => setModalFormData(prev => ({ ...prev, icon: e.target.value }))}
                      className="w-full p-2 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 bg-white text-gray-900"
                    >
                      {Object.entries(ICS_ICONS).map(([key, value]) => (
                        <option key={key} value={key}>
                          {value.emoji} {value.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Color */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {hoveredObject && hoveredObject.type === 'marker' ? 'Icon Color:' : 'Color:'}
                  </label>
                  {editingInModal ? (
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={modalFormData.color}
                        onChange={(e) => setModalFormData(prev => ({ ...prev, color: e.target.value }))}
                        className="h-10 w-20 rounded border border-gray-300"
                      />
                      <span className="text-sm text-gray-600">{modalFormData.color}</span>
                    </div>
                  ) : (
                    <div className="flex items-center bg-gray-50 p-2 rounded">
                      <div 
                        className="w-6 h-6 rounded mr-3" 
                        style={{ backgroundColor: hoveredObject.color || '#3388ff' }}
                      />
                      <span className="text-gray-700">
                        {hoveredObject.color || '#3388ff'}
                      </span>
                    </div>
                  )}
                </div>

                {/* Image Upload Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Image:</label>
                  {editingInModal ? (
                    <div className="space-y-3">
                      {/* File Upload */}
                      <div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            console.log('📁 File selected:', e.target.files[0]);
                            handleImageUpload(e, true);
                          }}
                          className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                      </div>
                      
                      {/* Camera Button */}
                      <div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            console.log('📷 Take Photo button clicked - launching camera interface');
                            handleCameraCapture(true);
                          }}
                          className="w-full bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded text-sm font-medium flex items-center justify-center gap-2"
                        >
                          <Camera className="h-4 w-4" />
                          Take Photo
                        </button>
                      </div>
                      
                      {/* Image Preview */}
                      {modalFormData.image && (
                        <div className="relative">
                          <img 
                            src={modalFormData.image} 
                            alt="Object" 
                            className="w-full h-32 object-cover rounded border"
                          />
                          <button
                            type="button"
                            onClick={() => setModalFormData(prev => ({ ...prev, image: '' }))}
                            className="absolute top-1 right-1 bg-red-600 hover:bg-red-700 text-white rounded-full w-6 h-6 text-xs flex items-center justify-center"
                          >
                            ×
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-gray-50 p-2 rounded">
                      {hoveredObject.image ? (
                        <img 
                          src={hoveredObject.image} 
                          alt="Object" 
                          className="w-full h-32 object-cover rounded"
                        />
                      ) : (
                        <p className="text-gray-500 text-center py-4">No image</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Coordinates Display */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Coordinates:</label>
                  <div className="bg-gray-50 p-2 rounded max-h-24 overflow-y-auto">
                    {getObjectCoordinates(hoveredObject).map((coord, index) => (
                      <div key={`coord-${coord.lat}-${coord.lng}`} className="text-xs text-gray-600 font-mono">
                        Point {index + 1}: {coord.lat}°N, {coord.lng}°E
                      </div>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-2 pt-4 border-t border-gray-200">
                  {editingInModal ? (
                    <>
                      <button
                        onClick={saveModalChanges}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded font-medium"
                      >
                        Save Changes
                      </button>
                      <button
                        onClick={cancelEditingInModal}
                        className="flex-1 bg-gray-400 hover:bg-gray-500 text-white py-2 px-4 rounded font-medium"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={startEditingInModal}
                        className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded font-medium"
                      >
                        Edit Object
                      </button>
                      {hoveredObject.type === 'marker' && hoveredObject.name !== 'Exercise/Event Center' && (
                        <button
                          onClick={() => {
                            handleCopyObject(hoveredObject);
                            setShowHoverModal(false);
                          }}
                          className="bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded font-medium"
                          title="Copy this marker"
                        >
                          Copy
                        </button>
                      )}
                      <button
                        onClick={() => {
                          setShowHoverModal(false);
                          handleObjectDelete(hoveredObject.id);
                        }}
                        className="bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded font-medium"
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex h-full">
      {/* Sidebar for object management */}
      <div className={`${sidebarCollapsed ? 'w-12' : 'w-80'} ${theme.colors.secondary} border-r ${theme.colors.border} flex flex-col h-full transition-all duration-300 relative z-[1001]`}>
        {/* Collapse/Expand Toggle Button */}
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="absolute top-4 -right-3 z-10 bg-orange-600 hover:bg-orange-700 text-white rounded-full p-1 shadow-lg transition-all"
          title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {sidebarCollapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </button>

        {!sidebarCollapsed && (
          <>
            {/* Create Object Panel Removed - Use Leaflet Draw Tools Only */}

            {/* Manual Entry Button Removed - Use draw tools only */}

            <div className="p-4 border-b border-gray-700">
              <div className="flex justify-between items-center mb-4">
                <h2 className={`text-lg font-semibold ${theme.colors.textPrimary}`}>Map Objects</h2>
                {copiedObject && (
                  <button
                    onClick={handlePasteObject}
                    className="flex items-center gap-1 px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded font-medium"
                    title={`Paste "${copiedObject.name}"`}
                  >
                    <ClipboardList className="h-4 w-4" />
                    Paste
                  </button>
                )}
              </div>
          
          {/* Category filters */}
          <div className="space-y-2">
            {categories.map(category => (
              <button
                key={category.id}
                onClick={() => {
                  console.log('Category clicked:', category.id);
                  setSelectedCategory(category.id);
                }}
                className={`w-full text-left px-3 py-2 rounded text-sm ${
                  selectedCategory === category.id
                    ? 'bg-blue-600 text-white'
                    : `${theme.colors.textMuted} hover:bg-gray-700`
                }`}
              >
                <span>{category.label}</span>
                <span className="float-right bg-gray-600 text-white px-2 py-0.5 rounded-full text-xs">
                  {category.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Objects list */}
        <div className="flex-1 overflow-y-auto p-4">
          {getObjectsByCategory(selectedCategory).length === 0 ? (
            <div className="text-center py-8">
              <p className={`${theme.colors.textMuted} text-sm`}>
                No {selectedCategory === 'all' ? 'objects' : selectedCategory} found
              </p>
              <p className={`${theme.colors.textMuted} text-xs mt-2`}>
                Click "Add Map Object" above to create one
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {getObjectsByCategory(selectedCategory).map(obj => (
                <div
                  key={obj.id}
                  className={`p-3 rounded border ${theme.colors.border} cursor-pointer hover:bg-gray-700 ${hiddenObjects.has(obj.id) ? 'opacity-50' : ''}`}
                  onClick={() => {
                    console.log('📋 Sidebar object clicked:', obj.name);
                    // Show the same modal as clicking map objects
                    setHoveredObject(obj);
                    setModalFormData({
                      name: obj.name || '',
                      description: obj.description || '',
                      color: obj.color || '#3388ff',
                      image: obj.image || ''
                    });
                    setShowHoverModal(true);
                    setSelectedObject(obj);
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center flex-1">
                      <div
                        className="w-4 h-4 rounded mr-2"
                        style={{ backgroundColor: obj.color, opacity: hiddenObjects.has(obj.id) ? 0.5 : 1 }}
                      ></div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className={`font-medium ${theme.colors.textPrimary} text-sm`}>{obj.name}</p>
                          {lockedObjects[obj.id] && (
                            <span className="flex items-center gap-1 text-xs text-red-400" title={`Locked by ${lockedObjects[obj.id].locked_by}`}>
                              <Lock className="h-3 w-3" />
                              {lockedObjects[obj.id].locked_by}
                            </span>
                          )}
                        </div>
                        <p className={`text-xs ${theme.colors.textMuted} capitalize`}>{obj.type}</p>
                      </div>
                    </div>
                    <div className="flex space-x-1">
                      {/* Visibility toggle button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleObjectVisibility(obj.id);
                        }}
                        className={`p-1 ${hiddenObjects.has(obj.id) ? 'text-gray-500 hover:text-gray-400' : 'text-yellow-400 hover:text-yellow-300'}`}
                        title={hiddenObjects.has(obj.id) ? 'Show on map' : 'Hide from map'}
                      >
                        {hiddenObjects.has(obj.id) ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                      </button>
                      {obj.type === 'marker' && obj.name !== 'Exercise/Event Center' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopyObject(obj);
                          }}
                          className="p-1 text-green-400 hover:text-green-300"
                          title="Copy marker"
                        >
                          <Copy className="h-3 w-3" />
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingObject(obj);
                          setFormData({
                            name: obj.name,
                            type: obj.type,
                            description: obj.description,
                            color: obj.color,
                            image: obj.image || ''
                          });
                          setShowObjectForm(true);
                          setEditingInModal(true);
                          setShowHoverModal(false);
                        }}
                        className="p-1 text-blue-400 hover:text-blue-300"
                      >
                        <Edit className="h-3 w-3" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleObjectDelete(obj.id);
                        }}
                        className="p-1 text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                  {obj.description && (
                    <p className={`text-xs ${theme.colors.textMuted} mt-1 line-clamp-2`}>
                      {obj.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
          </>
        )}
        
        {/* Collapsed state - show icon only */}
        {sidebarCollapsed && (
          <div className="flex flex-col items-center py-4 space-y-4">
            <div className="text-gray-400 transform -rotate-90 whitespace-nowrap text-sm font-semibold">
              Map Objects
            </div>
            <div className="text-orange-500 text-xs font-bold">
              {mapObjects.length}
            </div>
          </div>
        )}
      </div>

      {/* Main map area */}
      <div className="flex-1 relative" style={{ minHeight: '400px' }}>
        <MapErrorBoundary>
          {useMemo(() => <MapContainerComponent onObjectClick={handleObjectMouseEnter} />, [
            exerciseId, 
            mapReady,
            mapObjects, // Re-added to show objects, map won't remount as long as there's no key change
            hiddenObjects, // Re-render when visibility changes
            handleObjectMouseEnter // Include handler to ensure event handlers are updated
          ])}
        </MapErrorBoundary>
        
        {/* Zoom Level Display - Outside memoized component so it can update independently */}
        <div
          style={{
            position: 'absolute',
            bottom: '30px',
            left: '10px',
            zIndex: 1000,
            padding: '8px 12px',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            color: '#1f2937',
            border: '2px solid #3b82f6',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '600',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            backdropFilter: 'blur(4px)',
            pointerEvents: 'none'
          }}
          title="Current map zoom level (updates in real-time)"
        >
          <span style={{ fontSize: '16px' }}>🔍</span>
          <span>Zoom: {currentZoom.toFixed(1)}</span>
        </div>
        
        {/* Mouse Coordinate Display - Bottom Center of Map - Outside memoized component */}
        <div 
          id="map-coord-display-react"
          style={{
            position: 'absolute',
            bottom: '80px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1000,
            background: 'rgba(17, 24, 39, 0.95)',
            color: 'white',
            padding: '8px 16px',
            borderRadius: '8px',
            fontFamily: 'monospace',
            fontSize: '13px',
            border: '1px solid #fb923c',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            pointerEvents: 'none'
          }}
        >
          <span style={{ color: '#fb923c' }}>Lat:</span>{' '}
          {mouseCoords.lat !== null ? mouseCoords.lat.toFixed(4) : '------'}
          <span style={{ color: '#6b7280', margin: '0 8px' }}>|</span>
          <span style={{ color: '#fb923c' }}>Lng:</span>{' '}
          {mouseCoords.lng !== null ? mouseCoords.lng.toFixed(4) : '------'}
        </div>

        {/* Live Tracker Toggle — top-right of map, below Display Mode */}
        <div
          style={{
            position: 'absolute',
            top: '56px',
            right: '60px',
            zIndex: 1000,
            pointerEvents: 'auto',
          }}
          data-testid="tracker-debug-area"
        >
          {/* Always show tracker toggle — muted when no active trackers */}
          <div className="flex gap-2">
            <Button
              onClick={() => setShowTrackers(prev => !prev)}
              className={`${
                Object.keys(liveTrackers).length > 0
                  ? (showTrackers ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-500 hover:bg-gray-600')
                  : 'bg-gray-700 hover:bg-gray-600 opacity-80'
              } text-white shadow-lg`}
              size="sm"
              data-testid="tracker-toggle-btn"
            >
              <Radio className="h-4 w-4 mr-2" />
              {Object.keys(liveTrackers).length > 0
                ? `${showTrackers ? 'Trackers' : 'Trackers Off'} (${Object.keys(liveTrackers).length})`
                : 'Trackers (0)'}
            </Button>
            {showTrackers && Object.keys(liveTrackers).length > 0 && (
              <Button
                onClick={() => {
                  if (leafletMapInstanceRef.current) {
                    const validTrackers = Object.values(liveTrackers).filter(
                      t => t.lat && t.lng && !(t.lat === 0 && t.lng === 0)
                    );
                    if (validTrackers.length > 0) {
                      const bounds = L.latLngBounds(validTrackers.map(t => [t.lat, t.lng]));
                      leafletMapInstanceRef.current.flyToBounds(bounds, { padding: [80, 80], maxZoom: 14, duration: 1 });
                    }
                  }
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white shadow-lg"
                size="sm"
                data-testid="tracker-locate-btn"
              >
                <MapPin className="h-4 w-4" />
              </Button>
            )}
          </div>
          {/* Trail & Timeout controls — visible when trackers are active */}
          {showTrackers && Object.keys(liveTrackers).length > 0 && (
            <div className="flex gap-2 mt-2 flex-wrap">
              <Button
                onClick={() => setShowTrails(prev => !prev)}
                className={`${showTrails ? 'bg-orange-600 hover:bg-orange-700' : 'bg-gray-500 hover:bg-gray-600'} text-white shadow-lg`}
                size="sm"
                data-testid="trail-toggle-btn"
              >
                <Spline className="h-4 w-4 mr-2" />
                {showTrails ? 'Trails' : 'Trails Off'}
              </Button>
              {showTrails && (
                <select
                  value={trailMinutes}
                  onChange={(e) => setTrailMinutes(Number(e.target.value))}
                  className="bg-gray-800 text-white text-xs border border-gray-600 rounded px-2 py-1 shadow-lg cursor-pointer"
                  style={{ height: '32px' }}
                  data-testid="trail-duration-select"
                >
                  <option value={15}>15 min</option>
                  <option value={30}>30 min</option>
                  <option value={60}>1 hour</option>
                  <option value={1440}>All (24h)</option>
                </select>
              )}
              <select
                value={trackerTimeout}
                onChange={(e) => setTrackerTimeout(Number(e.target.value))}
                className="bg-gray-800 text-white text-xs border border-gray-600 rounded px-2 py-1 shadow-lg cursor-pointer"
                style={{ height: '32px' }}
                data-testid="tracker-timeout-select"
                title="How long inactive trackers remain visible"
              >
                <option value={15}>Visible: 15 min</option>
                <option value={30}>Visible: 30 min</option>
                <option value={60}>Visible: 1 hour</option>
                <option value={120}>Visible: 2 hours</option>
                <option value={1440}>Visible: 24 hours</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Real-Time Notifications - REMOVED per user request */}
      {/* These notifications were showing when other users added/edited map objects */}
      {/* 
      <div className="fixed top-20 right-4 z-50 space-y-2" style={{ maxWidth: '400px' }}>
        {notifications.map((notif) => (
          <div
            key={notif.id}
            className={`p-4 rounded-lg shadow-lg border-l-4 ${
              notif.type === 'success' ? 'bg-green-900 border-green-500 text-green-100' :
              notif.type === 'warning' ? 'bg-yellow-900 border-yellow-500 text-yellow-100' :
              notif.type === 'error' ? 'bg-red-900 border-red-500 text-red-100' :
              'bg-blue-900 border-blue-500 text-blue-100'
            } animate-slide-in`}
          >
            <div className="flex items-start justify-between">
              <p className="text-sm font-medium">{notif.message}</p>
              <button
                onClick={() => setNotifications(prev => prev.filter(n => n.id !== notif.id))}
                className="ml-4 text-white hover:text-gray-300"
              >
                ×
              </button>
            </div>
          </div>
        ))}
      </div>
      */}

      {/* Object form modal - Always rendered for debugging */}
      {showObjectForm && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center modal-overlay"
          style={{ 
            zIndex: 9999,
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={(e) => {
            console.log('🎯 Modal overlay clicked');
            if (e.target === e.currentTarget) {
              console.log('🎯 Closing modal via overlay click');
              resetForm();
            }
          }}
        >
          <div 
            className="bg-white dark:bg-gray-800 rounded-lg p-6 w-96 max-w-md mx-4 shadow-2xl border-2 border-blue-500 modal-content"
            style={{ 
              zIndex: 10000,
              position: 'relative',
              backgroundColor: 'white',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}
            onClick={(e) => {
              console.log('🎯 Modal content clicked');
              e.stopPropagation();
            }}
          >
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {editingObject ? 'Edit Map Object' : 'Add Map Object'}
                </h3>
                {/* Location coordinates removed with Create Object panel */}
              </div>
              <button
                onClick={resetForm}
                className="text-3xl text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white bg-gray-100 hover:bg-gray-200 rounded-full w-8 h-8 flex items-center justify-center"
              >
                ×
              </button>
            </div>
            
            <form onSubmit={(e) => e.preventDefault()} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full p-3 rounded border-2 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Enter object name"
                  autoFocus
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Object Type</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                  className="w-full p-3 rounded border-2 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="marker">📍 Marker (Point)</option>
                  <option value="line">📏 Line</option>
                  <option value="polygon">🔶 Polygon</option>
                  <option value="rectangle">⬜ Rectangle</option>
                  <option value="circle">⭕ Circle</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full p-3 rounded border-2 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  rows={3}
                  placeholder="Enter description"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Color</label>
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                  className="w-full h-12 rounded border-2 border-gray-300 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Image</label>
                
                {/* Image Preview */}
                {formData.image && (
                  <div className="mb-3 relative">
                    <img 
                      src={formData.image} 
                      alt="Preview" 
                      className="w-full h-32 object-cover rounded border-2 border-gray-300"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, image: '' }))}
                      className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600"
                      title="Remove image"
                    >
                      ×
                    </button>
                  </div>
                )}
                
                {/* File Input */}
                <div className="flex flex-col gap-2">
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        // Convert to base64
                        const reader = new FileReader();
                        reader.onloadend = () => {
                          setFormData(prev => ({ ...prev, image: reader.result }));
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    className="hidden"
                    id="map-image-upload"
                  />
                  
                  <div className="flex gap-2">
                    <label
                      htmlFor="map-image-upload"
                      className="flex-1 cursor-pointer bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded text-center transition-colors flex items-center justify-center gap-2"
                    >
                      <Upload className="h-4 w-4" />
                      Upload Image
                    </label>
                    
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        console.log('📷 Take Photo button clicked - launching camera interface');
                        handleCameraCapture(false); // false = not for modal, for form data
                      }}
                      className="flex-1 bg-green-500 hover:bg-green-600 text-white font-medium py-2 px-4 rounded transition-colors flex items-center justify-center gap-2"
                    >
                      <Camera className="h-4 w-4" />
                      Take Photo
                    </button>
                  </div>
                  
                  <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                    Upload an image or capture with your device camera
                  </p>
                </div>
              </div>

              <div className="flex space-x-3 pt-6">
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    try {
                      if (editingObject) {
                        console.log('Updating existing object:', editingObject.id);
                        handleObjectUpdate(editingObject.id, formData);
                      } else {
                        // Creation removed - use Leaflet draw tools only
                        alert('Use the Leaflet draw tools to create new objects.');
                      }
                    } catch (error) {
                      console.error('Error saving object:', error);
                      alert('Error saving object. Please check console for details.');
                    }
                  }}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg shadow-lg transition-all duration-200 transform hover:scale-105"
                >
                  {editingObject ? 'Update Object' : 'Save Object'}
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    resetForm();
                  }}
                  className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-6 rounded-lg shadow-lg transition-all duration-200"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
});

// ICS Dashboard Overview Component
const UserProfile = ({ onClose }) => {
  const { theme } = useTheme();
  const { user: currentUser, token, setUser } = useAuth();
  const toast = useToast();
  const API = API_BASE_URL + '/api';
  const [loading, setLoading] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(currentUser?.totp_enabled || false);
  const [showQR, setShowQR] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  
  useEffect(() => {
    setTwoFactorEnabled(currentUser?.totp_enabled || false);
  }, [currentUser]);

  const handleEnable2FA = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/setup-2fa`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQrCode(response.data.qr_code);
      setSecret(response.data.manual_entry_key);
      setShowQR(true);
      toast.success('Scan the QR code with your authenticator app');
    } catch (error) {
      console.error('Error enabling 2FA:', error);
      toast.error('Failed to enable 2FA: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify-2fa`, 
        { code: verificationCode },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      // Update user state
      const updatedUser = { ...currentUser, totp_enabled: true };
      secureStorage.setPreference('exrsim-user', JSON.stringify(updatedUser));
      if (setUser) setUser(updatedUser);
      
      setTwoFactorEnabled(true);
      setShowQR(false);
      setVerificationCode('');
      toast.success('2FA enabled successfully!');
    } catch (error) {
      console.error('Error verifying 2FA:', error);
      toast.error('Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    if (!window.confirm('Are you sure you want to disable Two-Factor Authentication? This will make your account less secure.')) {
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/disable-2fa`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update user state
      const updatedUser = { ...currentUser, totp_enabled: false };
      secureStorage.setPreference('exrsim-user', JSON.stringify(updatedUser));
      if (setUser) setUser(updatedUser);
      
      setTwoFactorEnabled(false);
      toast.success('2FA disabled successfully');
    } catch (error) {
      console.error('Error disabling 2FA:', error);
      toast.error('Failed to disable 2FA: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 p-4">
      <div className={`${theme.colors.tertiary} border ${theme.colors.border} rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto relative z-[10000]`}>
        {/* Header */}
        <div className="p-6 border-b border-gray-700">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-white flex items-center">
                <User className="h-6 w-6 mr-2 text-orange-500" />
                User Profile & Security
              </h2>
              <p className="text-sm text-gray-400 mt-1">Manage your account settings and security</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-white">
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* User Info */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white">Account Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-sm text-gray-400">Email</label>
                <p className="text-white">{currentUser?.email}</p>
              </div>
              <div>
                <label className="text-sm text-gray-400">Name</label>
                <p className="text-white">{currentUser?.first_name} {currentUser?.last_name}</p>
              </div>
              <div>
                <label className="text-sm text-gray-400">Role</label>
                <Badge className="bg-orange-500">{currentUser?.role}</Badge>
              </div>
            </CardContent>
          </Card>

          {/* 2FA Settings */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <Shield className="h-5 w-5 mr-2 text-blue-400" />
                Two-Factor Authentication (2FA)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-900 rounded-lg">
                <div>
                  <p className="text-white font-semibold">Status</p>
                  <p className="text-sm text-gray-400">
                    {twoFactorEnabled ? 'Two-Factor Authentication is enabled' : 'Two-Factor Authentication is disabled'}
                  </p>
                </div>
                <Badge className={twoFactorEnabled ? 'bg-green-600' : 'bg-gray-600'}>
                  {twoFactorEnabled ? 'Enabled' : 'Disabled'}
                </Badge>
              </div>

              {!twoFactorEnabled && !showQR && (
                <div className="space-y-3">
                  <p className="text-gray-300 text-sm">
                    Add an extra layer of security to your account. You'll need to enter a code from your authenticator app when logging in.
                  </p>
                  <Button 
                    onClick={handleEnable2FA} 
                    disabled={loading}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    {loading ? 'Setting up...' : 'Enable 2FA'}
                  </Button>
                </div>
              )}

              {showQR && (
                <div className="space-y-4">
                  <div className="bg-white p-4 rounded-lg">
                    <img src={qrCode} alt="QR Code" className="mx-auto" />
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-300">Manual entry key:</p>
                    <code className="block bg-gray-900 p-3 rounded text-sm text-blue-400 break-all">{secret}</code>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm text-gray-300">Enter 6-digit verification code:</label>
                    <input
                      type="text"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="000000"
                      className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-center text-2xl tracking-widest"
                      maxLength="6"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={handleVerify2FA} 
                      disabled={loading || verificationCode.length !== 6}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      Verify & Enable
                    </Button>
                    <Button 
                      onClick={() => {
                        setShowQR(false);
                        setVerificationCode('');
                      }} 
                      variant="outline"
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {twoFactorEnabled && (
                <div className="space-y-3">
                  <div className="flex items-start space-x-2 p-3 bg-green-900/20 border border-green-700 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-400 mt-0.5" />
                    <div>
                      <p className="text-green-400 font-semibold text-sm">Protected</p>
                      <p className="text-gray-300 text-xs">Your account is secured with Two-Factor Authentication</p>
                    </div>
                  </div>
                  <Button 
                    onClick={handleDisable2FA} 
                    disabled={loading}
                    variant="outline"
                    className="w-full border-red-500 text-red-500 hover:bg-red-500/10"
                  >
                    {loading ? 'Disabling...' : 'Disable 2FA'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-700 flex justify-end">
          <Button onClick={onClose} variant="outline">
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// Agency Management Component (Super Admin Only)
// ============================================================================

const Navigation = ({ currentExercise = null, activeMenu = 'dashboard' }) => {
  const { theme, currentTheme, switchTheme } = useTheme();
  const { user, logout } = useAuth();
  const { selectedAgency, agencies, selectAgency, clearAgencyContext, isSuperAdmin } = useAgencyContext();
  const API = API_BASE_URL + '/api';
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showAgencyDropdown, setShowAgencyDropdown] = useState(false);
  const [userAgencyName, setUserAgencyName] = useState(null);
  const [showLocationManager, setShowLocationManager] = useState(false);
  const [showThemeSettings, setShowThemeSettings] = useState(false);
  const [showFinancialCategories, setShowFinancialCategories] = useState(false);
  const [showAcronyms, setShowAcronyms] = useState(false);
  const [showDefinitions, setShowDefinitions] = useState(false);
  const [showQuicklinks, setShowQuicklinks] = useState(false);
  const [showPositionManager, setShowPositionManager] = useState(false);
  const [showPoliciesProcedures, setShowPoliciesProcedures] = useState(false);
  const [showExerciseDocuments, setShowExerciseDocuments] = useState(false);
  const [showAIAssistant, setShowAIAssistant] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showAgencyManagement, setShowAgencyManagement] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [helpSearchQuery, setHelpSearchQuery] = useState('');
  const [selectedHelpTopic, setSelectedHelpTopic] = useState(null);
  const [helpActiveTab, setHelpActiveTab] = useState('quick-start'); // quick-start, forms, roles, faq, glossary
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);
  const [glossaryTerms, setGlossaryTerms] = useState([]);
  const [glossaryLoading, setGlossaryLoading] = useState(false);
  const [faqCategory, setFaqCategory] = useState('all');

  // Close contact modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showContactModal && !event.target.closest('.contact-modal-container')) {
        setShowContactModal(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showContactModal]);

  // Keyboard shortcut for help (? key)
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.key === '?' && !event.ctrlKey && !event.metaKey && !event.altKey) {
        // Only trigger if not in an input field
        const target = event.target;
        if (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA') {
          event.preventDefault();
          setShowHelpModal(true);
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, []);

  // Fetch user's agency name
  useEffect(() => {
    const fetchUserAgency = async () => {
      console.log('🏢 Fetching user agency. User:', user);
      
      if (!user) {
        console.log('❌ No user found');
        return;
      }
      
      // Check if agency name is already in user object
      if (user.organization_name) {
        console.log('✅ Agency name found in user object:', user.organization_name);
        setUserAgencyName(user.organization_name);
        return;
      }
      
      console.log('User organization_id:', user.organization_id);
      
      if (user.organization_id) {
        try {
          const token = secureStorage.getToken();
          const headers = token ? { Authorization: `Bearer ${token}` } : {};
          
          // Fetch user's own agency using the new endpoint
          console.log('📡 Fetching user\'s agency from API...');
          const response = await axios.get(`${API}/my-agency`, { headers });
          console.log('✅ Agency fetched:', response.data);
          
          if (response.data && response.data.name) {
            setUserAgencyName(response.data.name);
            console.log('✅ Set userAgencyName to:', response.data.name);
          } else {
            console.log('❌ No agency name in response');
          }
        } catch (error) {
          console.error('❌ Error fetching user agency:', error);
          console.log('Error details:', error.response?.data);
        }
      } else {
        console.log('❌ User has no organization_id');
      }
    };
    
    fetchUserAgency();
  }, [user]);

  // Fetch glossary terms when glossary tab is opened
  useEffect(() => {
    const fetchGlossary = async () => {
      if (showHelpModal && helpActiveTab === 'glossary' && glossaryTerms.length === 0) {
        setGlossaryLoading(true);
        try {
          const response = await axios.get(`${API}/glossary`);
          setGlossaryTerms(response.data);
        } catch (error) {
          console.error('Error loading glossary:', error);
        } finally {
          setGlossaryLoading(false);
        }
      }
    };
    fetchGlossary();
  }, [showHelpModal, helpActiveTab]);

  // Mobile drawer toggle from context (opens the app Sidebar)
  const { setMainDrawerOpen, icsDrawerOpen, setIcsDrawerOpen } = useMobileNav();
  const isIcsRoute = activeMenu === 'ics';

  return (
    <nav className={`${theme.colors.primary} border-b ${theme.colors.borderAccent} px-3 md:px-6 py-3 md:py-4`}>
      <div className="flex items-center justify-between gap-2">
        {/* Mobile hamburger — opens either the app Sidebar or ICS Sidebar */}
        <button
          type="button"
          onClick={() => {
            if (isIcsRoute) setIcsDrawerOpen(!icsDrawerOpen);
            else setMainDrawerOpen(true);
          }}
          className="md:hidden inline-flex items-center justify-center w-11 h-11 rounded-lg text-orange-500 hover:bg-orange-500/10"
          aria-label="Open menu"
          data-testid="nav-hamburger-btn"
        >
          <MenuIcon className="h-6 w-6" />
        </button>
        <div className="flex items-center space-x-4 md:space-x-8">
          <div className="relative contact-modal-container">
            <button 
              onClick={() => setShowContactModal(!showContactModal)} 
              className="flex items-center group hover:scale-105 transition-transform"
            >
              {/* EXRSIM Logo Image */}
              <img 
                src="https://customer-assets.emergentagent.com/job_crisis-trainer/artifacts/v9rilei7_EXRSIM_Small.jpg"
                alt="EXRSIM Emergency Training"
                className="h-9 md:h-12 w-auto object-contain cursor-pointer"
              />
            </button>
            
            {/* Contact Modal */}
            {showContactModal && (
              <div
                className="fixed left-4 md:left-24 top-16 md:top-20 w-80 bg-black border-2 border-orange-500 rounded-lg shadow-lg p-4"
                style={{ zIndex: 10020 }}
                data-testid="exrsim-logo-contact-modal"
              >
                <div className="space-y-2 text-sm text-center">
                  <p className="text-orange-500">If you want more information, please email:</p>
                  <p className="text-white">info@exrsim.com</p>
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-2 md:space-x-4 flex-wrap">
            {currentExercise ? (
              <button
                className={`text-orange-500 hover:text-orange-400 transition-colors ${activeMenu === 'manage' ? 'font-bold' : 'font-medium'}`}
                onClick={() => window.location.href = `#manage?exercise=${currentExercise.id}`}
                data-testid="nav-exercise-link"
              >
                Exercise
              </button>
            ) : (
              <Link 
                to="/exercises" 
                className={`text-orange-500 hover:text-orange-400 transition-colors ${activeMenu === 'exercises' ? 'font-bold' : 'font-medium'}`}
              >
                Exercise
              </Link>
            )}
            {currentExercise && (
              <>
                {/* Arrow between buttons pointing to non-active button */}
                <div className="flex items-center">
                  {activeMenu === 'exercises' ? (
                    <ChevronRight className="h-4 w-4 text-white" />
                  ) : activeMenu === 'ics' || activeMenu === 'manage' ? (
                    <ChevronLeft className="h-4 w-4 text-white" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-white" />
                  )}
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    className={`text-orange-500 hover:text-orange-400 transition-colors cursor-pointer ${activeMenu === 'ics' ? 'font-bold' : 'font-medium'}`}
                    onClick={() => window.location.href = `#ics?exercise=${currentExercise.id}`}
                    title={currentExercise.exercise_name}
                    data-testid="nav-ics-link"
                  >
                    ICS
                  </button>
                  <span className="hidden md:inline text-white text-sm font-medium">
                    {currentExercise.exercise_name}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
        
        {/* Center - Agency Name Display (for all users) */}
        <div className="flex-1 flex items-center justify-center">
          {userAgencyName && !isSuperAdmin && (
            <div className="hidden sm:flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-600/20 to-purple-600/20 border border-blue-500/30 rounded-lg">
              <Building2 className="h-5 w-5 text-blue-400" />
              <span className="text-white font-semibold text-lg">{userAgencyName}</span>
            </div>
          )}
        </div>
        
        {/* Online Presence */}
        <div className="hidden md:block">
          <OnlinePresence />
        </div>
        
        {/* Right side navigation — hidden on mobile (moved to bottom sheet) */}
        <div className="hidden md:flex items-center space-x-4">
          {/* Agency Context Selector (Super Admin only) */}
          {isSuperAdmin && (
            <div className="relative">
              <Button 
                variant="outline" 
                size="sm" 
                className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                onClick={() => setShowAgencyDropdown(!showAgencyDropdown)}
              >
                <Building2 className="h-4 w-4 mr-2" />
                {selectedAgency ? selectedAgency.name : 'All Agencies'}
                <ChevronDown className="h-3 w-3 ml-2" />
              </Button>
              
              {/* Agency Dropdown */}
              {showAgencyDropdown && (
                <div className={`absolute right-0 mt-2 w-72 ${theme.colors.tertiary} border ${theme.colors.border} rounded-md shadow-lg z-[10001] max-h-96 overflow-y-auto`}>
                  <div className="py-1">
                    <div className="px-4 py-3 border-b border-gray-700">
                      <p className="text-sm text-purple-400 font-semibold">View as Agency</p>
                      <p className="text-xs text-gray-400 mt-1">Select an agency to filter data</p>
                    </div>
                    
                    {/* All Agencies Option */}
                    <button
                      className={`flex items-center w-full px-4 py-2 text-sm ${theme.colors.textSecondary} ${theme.colors.hover} transition-colors ${!selectedAgency ? 'bg-purple-500/20 text-purple-400' : ''}`}
                      onClick={() => {
                        clearAgencyContext();
                        setShowAgencyDropdown(false);
                      }}
                    >
                      <Globe className="h-4 w-4 mr-3" />
                      <span className="font-medium">All Agencies</span>
                      {!selectedAgency && <CheckCircle className="h-4 w-4 ml-auto text-purple-400" />}
                    </button>
                    
                    <div className="border-t border-gray-700 my-1"></div>
                    
                    {/* Individual Agencies */}
                    {agencies.map((agency) => (
                      <button
                        key={agency.id}
                        className={`flex items-center w-full px-4 py-2 text-sm ${theme.colors.textSecondary} ${theme.colors.hover} transition-colors ${selectedAgency?.id === agency.id ? 'bg-purple-500/20 text-purple-400' : ''}`}
                        onClick={() => {
                          selectAgency(agency);
                          setShowAgencyDropdown(false);
                        }}
                      >
                        <Building2 className="h-4 w-4 mr-3" />
                        <div className="flex-1 text-left">
                          <div className="font-medium text-white">{agency.name}</div>
                          <div className="text-xs text-gray-500">Code: {agency.registration_code}</div>
                        </div>
                        {selectedAgency?.id === agency.id && <CheckCircle className="h-4 w-4 ml-2 text-purple-400" />}
                      </button>
                    ))}
                    
                    {agencies.length === 0 && (
                      <div className="px-4 py-6 text-center text-gray-500 text-sm">
                        No agencies created yet
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* User dropdown menu */}
          <div className="relative">
            <Button 
              variant="outline" 
              size="sm" 
              className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
              onClick={() => setShowUserDropdown(!showUserDropdown)}
            >
              <User className="h-4 w-4 mr-2" />
              {user?.first_name} {user?.last_name}
              <ChevronDown className="h-3 w-3 ml-2" />
            </Button>
            
            {/* User Dropdown */}
            {showUserDropdown && (
              <div className={`absolute right-0 mt-2 w-60 ${theme.colors.tertiary} border ${theme.colors.border} rounded-md shadow-lg z-50`}>
                <div className="py-1">
                  <div className="px-4 py-3 border-b border-gray-700">
                    <p className="text-sm text-gray-400">Signed in as</p>
                    <p className="text-sm font-medium text-white">{user?.email}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <Badge className="bg-orange-600 text-white">{user?.role}</Badge>
                      {user?.agency_name && (
                        <Badge className="bg-blue-600 text-white text-xs">{user?.agency_name}</Badge>
                      )}
                    </div>
                  </div>
                  <button
                    className={`flex items-center w-full px-4 py-2 text-sm ${theme.colors.textSecondary} ${theme.colors.hover} hover:text-red-400 transition-colors`}
                    onClick={() => {
                      logout();
                      setShowUserDropdown(false);
                    }}
                  >
                    <X className="h-4 w-4 mr-3" />
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Help Button */}
          <Button 
            variant="outline" 
            size="sm" 
            className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
            onClick={() => setShowHelpModal(true)}
            title="Help & Documentation"
          >
            <span className="text-lg font-bold">?</span>
          </Button>

          {/* Theme toggle (desktop) */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => switchTheme(currentTheme === 'dark' ? 'light' : 'dark')}
            className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
            title={`Switch to ${currentTheme === 'dark' ? 'light' : 'dark'} theme`}
            data-testid="nav-theme-toggle-btn"
          >
            {currentTheme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>

          {/* Settings dropdown menu */}
          <div className="relative">
            <Button 
              variant="outline" 
              size="sm" 
              className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
              onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}
            >
              <Settings className="h-4 w-4 mr-2" />
              Settings
              <ChevronDown className="h-3 w-3 ml-2" />
            </Button>
          
          {/* Dropdown Menu */}
          {showSettingsDropdown && (
            <div className={`absolute right-0 mt-2 w-60 ${theme.colors.tertiary} border ${theme.colors.border} rounded-md shadow-lg z-[10001]`}>
              <div className="py-1">
                {/* User Management - Admin Only */}
                {(user?.role === 'Super Admin' || user?.role === 'Agency Admin') && (
                  <>
                    <div className="border-t border-gray-700 my-1"></div>
                    <button
                      className={`flex items-center w-full px-4 py-2 text-sm ${theme.colors.textSecondary} ${theme.colors.hover} hover:text-orange-400 transition-colors`}
                      onClick={() => {
                        setShowUserManagement(true);
                        setShowSettingsDropdown(false);
                      }}
                    >
                      <UserCog className="h-4 w-4 mr-3" />
                      User Management
                    </button>
                  </>
                )}
                
                {/* Agency Management - Super Admin (all agencies) + Agency Admin (own agency, backup/restore) */}
                {(user?.role === 'Super Admin' || user?.role === 'Agency Admin') && (
                  <>
                    <button
                      className={`flex items-center w-full px-4 py-2 text-sm ${theme.colors.textSecondary} ${theme.colors.hover} hover:text-orange-400 transition-colors`}
                      onClick={() => {
                        setShowAgencyManagement(true);
                        setShowSettingsDropdown(false);
                      }}
                      data-testid="settings-agency-management"
                    >
                      <Building className="h-4 w-4 mr-3" />
                      {user?.role === 'Super Admin' ? 'Agency Management' : 'Agency Backup & Restore'}
                    </button>
                    <div className="border-t border-gray-700 my-1"></div>
                  </>
                )}
                <button
                  className={`flex items-center w-full px-4 py-2 text-sm ${theme.colors.textSecondary} ${theme.colors.hover} hover:text-orange-400 transition-colors`}
                  onClick={() => {
                    setShowQuicklinks(true);
                    setShowSettingsDropdown(false);
                  }}
                >
                  <ExternalLink className="h-4 w-4 mr-3" />
                  Quicklinks
                </button>
                <button
                  className={`flex items-center w-full px-4 py-2 text-sm ${theme.colors.textSecondary} ${theme.colors.hover} hover:text-orange-400 transition-colors`}
                  onClick={() => {
                    setShowPoliciesProcedures(true);
                    setShowSettingsDropdown(false);
                  }}
                >
                  <FileCheck className="h-4 w-4 mr-3" />
                  Policies and Procedures
                </button>
                <button
                  className={`flex items-center w-full px-4 py-2 text-sm ${theme.colors.textSecondary} ${theme.colors.hover} hover:text-orange-400 transition-colors ${!currentExercise ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={!currentExercise}
                  onClick={() => {
                    if (!currentExercise) return;
                    setShowExerciseDocuments(true);
                    setShowSettingsDropdown(false);
                  }}
                  data-testid="settings-exercise-documents-btn"
                  title={currentExercise ? '' : 'Select an exercise first'}
                >
                  <FileText className="h-4 w-4 mr-3" />
                  Exercise Documents
                </button>
                <button
                  className={`flex items-center w-full px-4 py-2 text-sm ${theme.colors.textSecondary} ${theme.colors.hover} hover:text-orange-400 transition-colors ${!currentExercise ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={!currentExercise}
                  onClick={() => {
                    if (!currentExercise) return;
                    setShowAIAssistant(true);
                    setShowSettingsDropdown(false);
                  }}
                  data-testid="settings-ai-assistant-btn"
                  title={currentExercise ? 'Canadian ICS AI Advisor (Claude Sonnet 4.5)' : 'Select an exercise first'}
                >
                  <Shield className="h-4 w-4 mr-3" />
                  AI Assistant
                </button>
              </div>
            </div>
          )}
          </div>

        {/* Mobile-only compact right-side controls */}
        <div className="flex md:hidden items-center gap-1 ml-auto">
          <button
            onClick={() => switchTheme(currentTheme === 'dark' ? 'light' : 'dark')}
            className="inline-flex items-center justify-center w-11 h-11 rounded-lg text-orange-500 hover:bg-orange-500/10"
            aria-label="Toggle theme"
            data-testid="nav-mobile-theme-toggle-btn"
          >
            {currentTheme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>
          <button
            onClick={() => setShowUserDropdown(!showUserDropdown)}
            className="relative inline-flex items-center justify-center w-11 h-11 rounded-lg text-orange-500 hover:bg-orange-500/10"
            aria-label="User menu"
            data-testid="nav-mobile-user-btn"
          >
            <User className="h-5 w-5" />
          </button>
          <button
            onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}
            className="inline-flex items-center justify-center w-11 h-11 rounded-lg text-orange-500 hover:bg-orange-500/10"
            aria-label="Settings menu"
            data-testid="nav-mobile-settings-btn"
          >
            <Settings className="h-5 w-5" />
          </button>
        </div>
        </div>
      </div>

      {/* Location Manager Modal */}
      {showLocationManager && (
        <LocationManager onClose={() => setShowLocationManager(false)} exerciseId={currentExercise?.id} />
      )}

      {/* Financial Categories Modal */}
      {showFinancialCategories && (
        <FinancialCategoriesManager onClose={() => setShowFinancialCategories(false)} />
      )}

      {/* Acronyms Modal */}
      {showAcronyms && (
        <AcronymsManager onClose={() => setShowAcronyms(false)} />
      )}

      {/* Definitions Modal */}
      {showDefinitions && (
        <DefinitionsManager onClose={() => setShowDefinitions(false)} />
      )}

      {/* Quicklinks Modal */}
      {showQuicklinks && (
        <QuicklinksManager onClose={() => setShowQuicklinks(false)} />
      )}

      {/* Position Manager Modal */}
      {showPositionManager && (
        <PositionManager onClose={() => setShowPositionManager(false)} />
      )}

      {/* Policies and Procedures Modal */}
      {showPoliciesProcedures && (
        <PoliciesProceduresManager onClose={() => setShowPoliciesProcedures(false)} />
      )}

      {/* Exercise Documents Modal — per-exercise file library */}
      <ExerciseDocumentsModal
        open={showExerciseDocuments}
        onClose={() => setShowExerciseDocuments(false)}
        currentExercise={currentExercise}
      />

      {/* AI Assistant Modal — Claude Sonnet 4.5 (Canadian ICS advisor) */}
      <AIAssistantModal
        open={showAIAssistant}
        onClose={() => setShowAIAssistant(false)}
        currentExercise={currentExercise}
        currentView={activeMenu}
      />

      {/* Theme Settings Modal */}
      {showThemeSettings && (
        <ThemeSettings onClose={() => setShowThemeSettings(false)} />
      )}
      
      {/* User Profile Modal */}
      {showUserProfile && (
        <UserProfile onClose={() => setShowUserProfile(false)} />
      )}

      {/* User Management Modal */}
      {showUserManagement && (
        <UserManagement onClose={() => setShowUserManagement(false)} />
      )}
      
      {/* Agency Management Modal */}
      {showAgencyManagement && (
        <AgencyManagement onClose={() => setShowAgencyManagement(false)} />
      )}

      {/* Enhanced Help & Documentation Modal */}
      {showHelpModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[9999] p-4" onClick={() => setShowHelpModal(false)}>
          <div 
            className="bg-gray-900 rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                  <Book className="h-6 w-6" />
                  EXRSIM Help & Documentation
                </h2>
                <p className="text-blue-100 text-sm mt-1" data-testid="help-modal-version-label">Your complete guide to emergency management excellence &middot; {APP_VERSION_LABEL}</p>
              </div>
              <button
                onClick={() => setShowHelpModal(false)}
                className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Search Bar */}
            <div className="p-4 bg-gray-800 border-b border-gray-700">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search documentation, FAQs, forms, roles..."
                  value={helpSearchQuery}
                  onChange={(e) => setHelpSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Tabs */}
            <div className="bg-gray-800 border-b border-gray-700 px-4">
              <div className="flex space-x-1 overflow-x-auto">
                <button
                  onClick={() => setHelpActiveTab('quick-start')}
                  className={`px-4 py-3 font-medium text-sm whitespace-nowrap transition-colors ${
                    helpActiveTab === 'quick-start'
                      ? 'text-blue-400 border-b-2 border-blue-400'
                      : 'text-gray-400 hover:text-gray-200'
                  }`}
                >
                  Quick Start
                </button>
                <button
                  onClick={() => setHelpActiveTab('forms')}
                  className={`px-4 py-3 font-medium text-sm whitespace-nowrap transition-colors ${
                    helpActiveTab === 'forms'
                      ? 'text-blue-400 border-b-2 border-blue-400'
                      : 'text-gray-400 hover:text-gray-200'
                  }`}
                >
                  <FileText className="inline h-4 w-4 mr-1" />
                  ICS Forms
                </button>
                <button
                  onClick={() => setHelpActiveTab('roles')}
                  className={`px-4 py-3 font-medium text-sm whitespace-nowrap transition-colors ${
                    helpActiveTab === 'roles'
                      ? 'text-blue-400 border-b-2 border-blue-400'
                      : 'text-gray-400 hover:text-gray-200'
                  }`}
                >
                  <Users className="inline h-4 w-4 mr-1" />
                  Role Guides
                </button>
                <button
                  onClick={() => setHelpActiveTab('faq')}
                  className={`px-4 py-3 font-medium text-sm whitespace-nowrap transition-colors ${
                    helpActiveTab === 'faq'
                      ? 'text-blue-400 border-b-2 border-blue-400'
                      : 'text-gray-400 hover:text-gray-200'
                  }`}
                >
                  <HelpCircle className="inline h-4 w-4 mr-1" />
                  FAQ
                </button>
                <button
                  onClick={() => setHelpActiveTab('glossary')}
                  className={`px-4 py-3 font-medium text-sm whitespace-nowrap transition-colors ${
                    helpActiveTab === 'glossary'
                      ? 'text-blue-400 border-b-2 border-blue-400'
                      : 'text-gray-400 hover:text-gray-200'
                  }`}
                >
                  <Book className="inline h-4 w-4 mr-1" />
                  Glossary
                </button>
              </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto p-6 bg-gray-900">
              
              {/* Quick Start Tab */}
              {helpActiveTab === 'quick-start' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl font-bold text-white mb-4">Getting Started with EXRSIM</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 hover:border-blue-500 transition-colors cursor-pointer"
                           onClick={() => { setHelpActiveTab('faq'); setFaqCategory('Getting Started'); }}>
                        <h4 className="font-semibold text-blue-400 mb-2">1. Create Your First Exercise</h4>
                        <p className="text-gray-300 text-sm">Navigate to Exercises &rarr; Create New Exercise &rarr; Follow the 17-step wizard (Scenario, Purpose, Scope, Goals &amp; Objectives, Injections, Exercise Sites, Standard of Conduct, Mapping, and more). Auto-saves every 60 s.</p>
                      </div>
                      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 hover:border-orange-500 transition-colors cursor-pointer"
                           onClick={() => { setHelpActiveTab('roles'); setSelectedRole('exercise-controller'); }}>
                        <h4 className="font-semibold text-orange-400 mb-2">2. ExCon Builder</h4>
                        <p className="text-gray-300 text-sm">17-step wizard for Exercise Controllers with auto-populated ExCon Team and injection management</p>
                      </div>
                      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 hover:border-green-500 transition-colors cursor-pointer"
                           onClick={() => { setHelpActiveTab('faq'); setFaqCategory('ExCon & Builders'); }}>
                        <h4 className="font-semibold text-green-400 mb-2">3. Event Builder</h4>
                        <p className="text-gray-300 text-sm">Streamlined 5-step wizard for real-world emergencies: Info, Scenario, Orgs, Mapping, Activate ICS</p>
                      </div>
                      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 hover:border-purple-500 transition-colors cursor-pointer"
                           onClick={() => { setHelpActiveTab('faq'); setFaqCategory('ExCon & Builders'); }}>
                        <h4 className="font-semibold text-purple-400 mb-2">4. ExCon Dashboard</h4>
                        <p className="text-gray-300 text-sm">Centralized injection management with real-time alerts, actual responses, and auto-refresh</p>
                      </div>
                      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 hover:border-blue-500 transition-colors cursor-pointer"
                           onClick={() => { setHelpActiveTab('forms'); setSelectedForm('ics-201'); }}>
                        <h4 className="font-semibold text-blue-400 mb-2">5. Complete ICS 201</h4>
                        <p className="text-gray-300 text-sm">Start with Incident Briefing form within first operational period</p>
                      </div>
                      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 hover:border-blue-500 transition-colors cursor-pointer"
                           onClick={() => setHelpActiveTab('roles')}>
                        <h4 className="font-semibold text-blue-400 mb-2">6. Assign Roles & Build IAP</h4>
                        <p className="text-gray-300 text-sm">Use ICS 203 for personnel and create your IAP with ICS 202, 204, 205, 206</p>
                      </div>
                      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 hover:border-teal-500 transition-colors cursor-pointer"
                           onClick={() => { setHelpActiveTab('faq'); setFaqCategory('Analytics'); }}>
                        <h4 className="font-semibold text-teal-400 mb-2">7. Analytics Dashboard</h4>
                        <p className="text-gray-300 text-sm">View Resource Allocation and Personnel Distribution charts with interactive tooltips inside each exercise</p>
                      </div>
                      <div className="bg-gray-800 rounded-lg p-4 border border-gray-700 hover:border-yellow-500 transition-colors cursor-pointer"
                           onClick={() => { setHelpActiveTab('faq'); setFaqCategory('Help Desk'); }}>
                        <h4 className="font-semibold text-yellow-400 mb-2">8. Help Desk</h4>
                        <p className="text-gray-300 text-sm">Submit private support tickets from the sidebar. Super Admins manage, respond, and print reports</p>
                      </div>
                    </div>
                  </div>

                  {/* Access Control Info */}
                  <div className="bg-gradient-to-r from-orange-900/30 to-red-900/30 rounded-lg p-6 border border-orange-800">
                    <h4 className="font-semibold text-orange-300 mb-3 flex items-center gap-2">
                      <Users className="h-5 w-5" />
                      ExCon Access Control
                    </h4>
                    <div className="space-y-2 text-sm text-gray-300">
                      <p>The ExCon Dashboard and injection data are restricted to ExCon Team members:</p>
                      <p>&#8226; <strong className="text-orange-300">Exercise Director</strong> &mdash; Full ExCon access</p>
                      <p>&#8226; <strong className="text-orange-300">Exercise Evaluator</strong> &mdash; Full ExCon access</p>
                      <p>&#8226; <strong className="text-orange-300">Exercise Observer</strong> &mdash; Full ExCon access</p>
                      <p>&#8226; <strong className="text-orange-300">Super Admin</strong> &mdash; Full access to all features</p>
                      <p className="text-gray-400 mt-2">Non-ExCon participants will not see injections, the ExCon Dashboard, or Events stat cards.</p>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-blue-900/30 to-indigo-900/30 rounded-lg p-6 border border-blue-800">
                    <h4 className="font-semibold text-blue-300 mb-3 flex items-center gap-2">
                      <Info className="h-5 w-5" />
                      Need Help?
                    </h4>
                    <div className="space-y-2 text-sm text-gray-300">
                      <p>&#8226; Look for <HelpCircle className="inline h-4 w-4 text-blue-400" /> icons throughout the app for context-specific help</p>
                      <p>&#8226; Use the search bar above to quickly find answers</p>
                      <p>&#8226; Check <span className="text-blue-400 cursor-pointer" onClick={() => setHelpActiveTab('roles')}>Role Guides</span> for position-specific workflows (including ExCon)</p>
                      <p>&#8226; Browse <span className="text-blue-400 cursor-pointer" onClick={() => setHelpActiveTab('forms')}>ICS Forms</span> for detailed form instructions (including ICS 259 and unit forms)</p>
                      <p>&#8226; See <span className="text-orange-400 cursor-pointer" onClick={() => { setHelpActiveTab('faq'); setFaqCategory('ExCon & Builders'); }}>ExCon & Builders FAQ</span> for ExCon Dashboard, Event Builder, and RBAC</p>
                      <p>&#8226; Visit <span className="text-yellow-400 cursor-pointer" onClick={() => { setHelpActiveTab('faq'); setFaqCategory('Help Desk'); }}>Help Desk FAQ</span> for support ticket guidance</p>
                      <p>&#8226; Explore <span className="text-teal-400 cursor-pointer" onClick={() => { setHelpActiveTab('faq'); setFaqCategory('Analytics'); }}>Analytics</span>, <span className="text-green-400 cursor-pointer" onClick={() => { setHelpActiveTab('faq'); setFaqCategory('Evaluations & Improvement'); }}>Evaluations</span>, and <span className="text-pink-400 cursor-pointer" onClick={() => { setHelpActiveTab('faq'); setFaqCategory('Resource Management'); }}>Resources</span> FAQs</p>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold text-white mb-3">Most Viewed Topics</h4>
                    <div className="space-y-2">
                      {helpContent.faq.slice(0, 5).map(item => (
                        <div key={item.id} 
                             className="bg-gray-800 rounded p-3 border border-gray-700 hover:border-blue-500 transition-colors cursor-pointer"
                             onClick={() => { setHelpActiveTab('faq'); setHelpSearchQuery(item.question); }}>
                          <p className="text-blue-400 text-sm font-medium">{item.question}</p>
                          <p className="text-gray-400 text-xs mt-1">{item.category}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* ICS Forms Tab */}
              {helpActiveTab === 'forms' && (
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-white mb-4">ICS Forms Reference</h3>
                  
                  {selectedForm ? (
                    <div>
                      <button
                        onClick={() => setSelectedForm(null)}
                        className="text-blue-400 hover:text-blue-300 mb-4 flex items-center gap-2"
                      >
                        ← Back to all forms
                      </button>
                      
                      {(() => {
                        const form = helpContent.icsForms[selectedForm];
                        return (
                          <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                            <div className="flex justify-between items-start mb-4">
                              <div>
                                <h4 className="text-2xl font-bold text-white">{/^\d+$/.test(form.number) ? `ICS ${form.number}` : form.number === 'LOG' ? 'Logistics' : form.number === 'FIN' ? 'Finance/Admin' : form.number}</h4>
                                <p className="text-xl text-blue-400 mt-1">{form.name}</p>
                              </div>
                              <span className="bg-blue-600 text-white px-3 py-1 rounded text-sm">{/^\d+$/.test(form.number) ? `#${form.number}` : form.number}</span>
                            </div>
                            
                            <div className="space-y-4">
                              <div>
                                <h5 className="font-semibold text-gray-300 mb-2">Purpose</h5>
                                <p className="text-gray-400">{form.purpose}</p>
                              </div>
                              
                              <div>
                                <h5 className="font-semibold text-gray-300 mb-2">When to Use</h5>
                                <p className="text-gray-400">{form.when}</p>
                              </div>
                              
                              <div>
                                <h5 className="font-semibold text-gray-300 mb-2">Who Completes</h5>
                                <p className="text-gray-400">{form.who}</p>
                              </div>
                              
                              <div>
                                <h5 className="font-semibold text-gray-300 mb-2">Required Fields</h5>
                                <ul className="list-disc list-inside text-gray-400 space-y-1">
                                  {form.required.map((field, idx) => (
                                    <li key={`item-${idx}`}>{field}</li>
                                  ))}
                                </ul>
                              </div>
                              
                              <div>
                                <h5 className="font-semibold text-gray-300 mb-2">Best Practices & Tips</h5>
                                <ul className="space-y-2">
                                  {form.tips.map((tip, idx) => (
                                    <li key={`item-${idx}`} className="flex items-start gap-2">
                                      <CheckSquare className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                                      <span className="text-gray-400 text-sm">{tip}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              
                              <div>
                                <h5 className="font-semibold text-gray-300 mb-2">Related Forms</h5>
                                <div className="flex flex-wrap gap-2">
                                  {form.relatedForms.map((relForm, idx) => (
                                    <span key={`item-${idx}`} className="bg-gray-700 text-blue-400 px-3 py-1 rounded text-sm hover:bg-gray-600 cursor-pointer"
                                          onClick={() => {
                                            const formKey = Object.keys(helpContent.icsForms).find(
                                              key => helpContent.icsForms[key].number === relForm.replace('ICS ', '')
                                            );
                                            if (formKey) setSelectedForm(formKey);
                                          }}>
                                      {relForm}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {Object.entries(helpContent.icsForms)
                        .filter(([key, form]) => {
                          if (!helpSearchQuery) return true;
                          const searchLower = helpSearchQuery.toLowerCase();
                          return form.number.includes(searchLower) ||
                                 form.name.toLowerCase().includes(searchLower) ||
                                 form.purpose.toLowerCase().includes(searchLower);
                        })
                        .map(([key, form]) => (
                        <div
                          key={key}
                          onClick={() => setSelectedForm(key)}
                          className="bg-gray-800 rounded-lg p-4 border border-gray-700 hover:border-blue-500 transition-colors cursor-pointer"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="text-lg font-bold text-white">{/^\d+$/.test(form.number) ? `ICS ${form.number}` : form.number === 'LOG' ? 'Logistics' : form.number === 'FIN' ? 'Finance/Admin' : form.number}</h4>
                            <FileText className="h-5 w-5 text-blue-400" />
                          </div>
                          <p className="text-blue-400 font-medium text-sm mb-2">{form.name}</p>
                          <p className="text-gray-400 text-xs line-clamp-2">{form.purpose}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Role Guides Tab */}
              {helpActiveTab === 'roles' && (
                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-white mb-4">ICS Role Workflows & Checklists</h3>
                  
                  {selectedRole ? (
                    <div>
                      <button
                        onClick={() => setSelectedRole(null)}
                        className="text-blue-400 hover:text-blue-300 mb-4 flex items-center gap-2"
                      >
                        ← Back to all roles
                      </button>
                      
                      {(() => {
                        const role = helpContent.roleWorkflows[selectedRole];
                        return (
                          <div className="space-y-6">
                            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                              <h4 className="text-2xl font-bold text-white mb-2">{role.title}</h4>
                              <p className="text-gray-400 mb-4">{role.description}</p>
                              
                              <div className="mb-6">
                                <h5 className="font-semibold text-gray-300 mb-3">Key Responsibilities</h5>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                  {role.responsibilities.map((resp, idx) => (
                                    <div key={`item-${idx}`} className="flex items-start gap-2 bg-gray-700/30 p-2 rounded">
                                      <CheckSquare className="h-4 w-4 text-green-400 mt-0.5 flex-shrink-0" />
                                      <span className="text-gray-300 text-sm">{resp}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                              
                              <div className="mb-6">
                                <h5 className="font-semibold text-gray-300 mb-3">Key Forms</h5>
                                <div className="flex flex-wrap gap-2">
                                  {role.keyForms.map((form, idx) => (
                                    <span key={`item-${idx}`} className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-500 cursor-pointer"
                                          onClick={() => {
                                            setHelpActiveTab('forms');
                                            const formKey = Object.keys(helpContent.icsForms).find(
                                              key => helpContent.icsForms[key].number === form.replace('ICS ', '')
                                            );
                                            if (formKey) setSelectedForm(formKey);
                                          }}>
                                      {form}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>

                            {/* Workflow */}
                            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                              <h5 className="font-semibold text-white mb-4">Workflow by Phase</h5>
                              <div className="space-y-4">
                                {role.workflow.map((phase, idx) => (
                                  <div key={`item-${idx}`} className="border-l-4 border-blue-500 pl-4">
                                    <h6 className="font-semibold text-blue-400 mb-2">{phase.phase}</h6>
                                    <ul className="space-y-2">
                                      {phase.tasks.map((task, taskIdx) => (
                                        <li key={`${phase.phase}-task-${taskIdx}`} className="flex items-start gap-2">
                                          <span className="text-blue-400 mt-1">→</span>
                                          <span className="text-gray-300 text-sm">{task}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Checklist */}
                            <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
                              <h5 className="font-semibold text-white mb-4">Role-Specific Checklist</h5>
                              <div className="space-y-2">
                                {role.checklist.map((item, idx) => (
                                  <label key={`item-${idx}`} className="flex items-start gap-3 p-3 bg-gray-700/30 rounded hover:bg-gray-700/50 cursor-pointer">
                                    <input type="checkbox" className="mt-1 h-4 w-4 text-blue-600 rounded" />
                                    <span className="text-gray-300 text-sm">{item.task}</span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {Object.entries(helpContent.roleWorkflows)
                        .filter(([key, role]) => {
                          if (!helpSearchQuery) return true;
                          const searchLower = helpSearchQuery.toLowerCase();
                          return role.title.toLowerCase().includes(searchLower) ||
                                 role.description.toLowerCase().includes(searchLower);
                        })
                        .map(([key, role]) => (
                        <div
                          key={key}
                          onClick={() => setSelectedRole(key)}
                          className="bg-gray-800 rounded-lg p-4 border border-gray-700 hover:border-blue-500 transition-colors cursor-pointer"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <Users className="h-6 w-6 text-blue-400" />
                            <span className="bg-blue-600 text-white text-xs px-2 py-1 rounded">{role.responsibilities.length} duties</span>
                          </div>
                          <h4 className="text-lg font-bold text-white mb-2">{role.title}</h4>
                          <p className="text-gray-400 text-sm line-clamp-2">{role.description}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* FAQ Tab */}
              {helpActiveTab === 'faq' && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-white">Frequently Asked Questions</h3>
                    <select
                      value={faqCategory}
                      onChange={(e) => setFaqCategory(e.target.value)}
                      className="bg-gray-800 border border-gray-700 text-white px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="all">All Categories</option>
                      <option value="Getting Started">Getting Started</option>
                      <option value="ExCon & Builders">ExCon & Builders</option>
                      <option value="ICS Forms">ICS Forms</option>
                      <option value="ICS Concepts">ICS Concepts</option>
                      <option value="Resources">Resources</option>
                      <option value="Navigation">Navigation</option>
                      <option value="Technical">Technical</option>
                      <option value="Help Desk">Help Desk</option>
                      <option value="Analytics">Analytics</option>
                      <option value="Evaluations & Improvement">Evaluations & Improvement</option>
                      <option value="Resource Management">Resource Management</option>
                      <option value="Logistics Forms">Logistics Forms</option>
                      <option value="Mapping & Tracking">Mapping & Tracking</option>
                    </select>
                  </div>

                  <div className="space-y-3">
                    {fuzzySearch(helpSearchQuery, helpContent.faq, ['question', 'answer', 'category'])
                      .filter(item => faqCategory === 'all' || item.category === faqCategory)
                      .map(item => (
                        <div key={item.id} className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                          <div className="flex items-start gap-3">
                            <HelpCircle className="h-5 w-5 text-blue-400 mt-1 flex-shrink-0" />
                            <div className="flex-1">
                              <h4 className="font-semibold text-white mb-2">{item.question}</h4>
                              <p className="text-gray-400 text-sm mb-2">{item.answer}</p>
                              <span className="inline-block bg-blue-900/30 text-blue-400 text-xs px-2 py-1 rounded">{item.category}</span>
                            </div>
                          </div>
                        </div>
                    ))}
                  </div>

                  {fuzzySearch(helpSearchQuery, helpContent.faq, ['question', 'answer', 'category']).length === 0 && (
                    <div className="text-center py-12">
                      <HelpCircle className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                      <p className="text-gray-400">No FAQs found matching your search.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Glossary Tab - Enhanced with Index, Sorting & Search */}
              {helpActiveTab === 'glossary' && (
                <div className="space-y-4">
                  {/* Header with Stats */}
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-bold text-white">ICS/Emergency Management Glossary</h3>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-gray-400">
                        {fuzzySearch(helpSearchQuery, glossaryTerms, ['term', 'definition']).length} of {glossaryTerms.length} terms
                      </span>
                    </div>
                  </div>
                  
                  {glossaryLoading ? (
                    <div className="text-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-3"></div>
                      <p className="text-gray-400">Loading glossary...</p>
                    </div>
                  ) : (
                    <>
                      {/* Alphabetical Index Navigation */}
                      <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-semibold text-gray-400 uppercase">Quick Jump:</span>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {['ALL', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'].map((letter) => {
                            const letterTerms = letter === 'ALL' 
                              ? glossaryTerms 
                              : glossaryTerms.filter(t => t.term.toUpperCase().startsWith(letter));
                            const searchedTerms = fuzzySearch(helpSearchQuery, letterTerms, ['term', 'definition']);
                            const hasTerms = searchedTerms.length > 0;
                            
                            return (
                              <button
                                key={letter}
                                onClick={() => {
                                  if (hasTerms) {
                                    const element = document.getElementById(`glossary-letter-${letter}`);
                                    if (element) {
                                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                    }
                                  }
                                }}
                                disabled={!hasTerms}
                                className={`px-2 py-1 text-xs rounded transition-colors ${
                                  hasTerms
                                    ? 'bg-blue-600 hover:bg-blue-500 text-white cursor-pointer'
                                    : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                                }`}
                                title={hasTerms ? `Jump to ${letter} (${searchedTerms.length} terms)` : `No terms starting with ${letter}`}
                              >
                                {letter}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Glossary Terms - Grouped by Letter */}
                      <div className="space-y-6 max-h-[600px] overflow-y-auto pr-2">
                        {['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'].map((letter) => {
                          const letterTerms = glossaryTerms.filter(t => t.term.toUpperCase().startsWith(letter));
                          const searchedTerms = fuzzySearch(helpSearchQuery, letterTerms, ['term', 'definition']);
                          
                          if (searchedTerms.length === 0) return null;
                          
                          return (
                            <div key={letter} id={`glossary-letter-${letter}`} className="scroll-mt-4">
                              {/* Letter Header */}
                              <div className="sticky top-0 bg-gray-900 py-2 mb-3 border-b-2 border-blue-500 z-10">
                                <h4 className="text-2xl font-bold text-blue-400">{letter}</h4>
                                <span className="text-xs text-gray-400">{searchedTerms.length} term{searchedTerms.length !== 1 ? 's' : ''}</span>
                              </div>
                              
                              {/* Terms Grid */}
                              <div className="grid grid-cols-1 gap-3 pl-4">
                                {searchedTerms.sort((a, b) => a.term.localeCompare(b.term)).map((item, idx) => (
                                  <div key={`item-${idx}`} className="bg-gray-800 rounded-lg p-4 border border-gray-700 hover:border-blue-500 transition-colors">
                                    <h5 className="font-bold text-blue-400 mb-2 text-lg">{item.term}</h5>
                                    <p className="text-gray-300 text-sm leading-relaxed">{item.definition}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {fuzzySearch(helpSearchQuery, glossaryTerms, ['term', 'definition']).length === 0 && (
                        <div className="text-center py-12">
                          <Book className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                          <p className="text-gray-400">No glossary terms found matching "{helpSearchQuery}"</p>
                          <button
                            onClick={() => setHelpSearchQuery('')}
                            className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded text-sm"
                          >
                            Clear Search
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

// Agency Context Banner - Shows when Super Admin is viewing as specific agency
const AgencyContextBanner = () => {
  const { theme } = useTheme();
  const { selectedAgency, clearAgencyContext, isSuperAdmin } = useAgencyContext();
  
  if (!isSuperAdmin || !selectedAgency) {
    return null;
  }
  
  return (
    <div className="bg-purple-600 border-b-2 border-purple-400 px-6 py-2">
      <div className="flex items-center justify-between max-w-7xl mx-auto">
        <div className="flex items-center space-x-3">
          <Building2 className="h-5 w-5 text-white" />
          <div>
            <span className="text-white font-semibold">Viewing as: {selectedAgency.name}</span>
            <span className="text-purple-200 text-sm ml-3">
              (Super Admin Mode - Only seeing data for this agency)
            </span>
          </div>
        </div>
        <button
          onClick={clearAgencyContext}
          className="flex items-center space-x-2 px-4 py-1 bg-white/20 hover:bg-white/30 rounded-md transition-colors text-white text-sm"
        >
          <X className="h-4 w-4" />
          <span>View All Agencies</span>
        </button>
      </div>
    </div>
  );
};

// Location Manager Component
const LocationManager = ({ onClose, exerciseId }) => {
  const { theme } = useTheme();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    latitude: '',
    longitude: '',
    contact_person: '',
    contact_phone: ''
  });
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    fetchMapMarkers();
  }, [exerciseId]);

  const fetchMapMarkers = async () => {
    try {
      const url = exerciseId 
        ? `${API}/map-objects?type=marker&exercise_id=${exerciseId}`
        : `${API}/map-objects?type=marker`;
      const response = await axios.get(url);
      setLocations(response.data.map(marker => ({ id: marker.id, name: marker.name })));
    } catch (error) {
      console.error('Error fetching map markers:', error);
      setLocations([]);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Location name is required';
    }
    
    if (!formData.description.trim()) {
      errors.description = 'Description is required';
    }
    
    if (formData.latitude && !validateLatitude(formData.latitude)) {
      errors.latitude = 'Please enter a valid latitude (-90.0000 to 90.0000)';
    }
    
    if (formData.longitude && !validateLongitude(formData.longitude)) {
      errors.longitude = 'Please enter a valid longitude (-180.0000 to 180.0000)';
    }
    
    if (formData.contact_phone && !validatePhone(formData.contact_phone)) {
      errors.contact_phone = 'Please enter phone in format 123-456-7890';
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmitLocal = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    try {
      const locationData = {
        ...formData,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null
      };

      if (editingLocation) {
        const response = await axios.put(`${API}/locations/${editingLocation.id}`, locationData);
        setLocations(prev => prev.map(loc => loc.id === editingLocation.id ? response.data : loc));
      } else {
        const response = await axios.post(`${API}/locations`, locationData);
        setLocations(prev => [...prev, response.data]);
      }
      
      resetForm();
    } catch (error) {
      console.error('Error saving location:', error);
      alert('Error saving location. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (location) => {
    setEditingLocation(location);
    setFormData({
      name: location.name || '',
      description: location.description || '',
      address: location.address || '',
      latitude: location.latitude?.toString() || '',
      longitude: location.longitude?.toString() || '',
      contact_person: location.contact_person || '',
      contact_phone: location.contact_phone || ''
    });
    setShowAddForm(true);
  };

  const handleDelete = async (locationId) => {
    if (window.confirm('Are you sure you want to delete this location?')) {
      try {
        await axios.delete(`${API}/locations/${locationId}`);
        setLocations(prev => prev.filter(loc => loc.id !== locationId));
      } catch (error) {
        console.error('Error deleting location:', error);
        alert('Error deleting location. Please try again.');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      address: '',
      latitude: '',
      longitude: '',
      contact_person: '',
      contact_phone: ''
    });
    setEditingLocation(null);
    setShowAddForm(false);
    setValidationErrors({});
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e.target.value);
    setFormData(prev => ({ ...prev, contact_phone: formatted }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`${theme.colors.modal} rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden`}>
        <div className={`p-6 border-b ${theme.colors.border} flex justify-between items-center`}>
          <h2 className="text-2xl font-bold text-orange-500">Location Manager</h2>
          <Button
            variant="ghost"
            onClick={onClose}
            className={`${theme.colors.textMuted} hover:${theme.colors.textPrimary}`}
          >
            <X className="h-6 w-6" />
          </Button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          {!showAddForm ? (
            <>
              <div className="flex justify-between items-center mb-6">
                <p className="text-gray-400">Manage exercise locations like Command Post, Hospital, etc.</p>
                <Button
                  onClick={() => setShowAddForm(true)}
                  className="bg-orange-500 hover:bg-orange-600 text-black font-semibold"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Location
                </Button>
              </div>

              {loading ? (
                <div className="space-y-4">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={`el-${i}`} className="bg-gray-700 rounded-lg p-4 animate-pulse">
                      <div className="h-4 bg-gray-600 rounded w-1/4 mb-2"></div>
                      <div className="h-3 bg-gray-600 rounded w-3/4"></div>
                    </div>
                  ))}
                </div>
              ) : locations.length === 0 ? (
                <div className="text-center py-12">
                  <MapPin className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-300 mb-2">No Locations Yet</h3>
                  <p className="text-gray-500 mb-4">Add your first exercise location to get started.</p>
                  <Button
                    onClick={() => setShowAddForm(true)}
                    className="bg-orange-500 hover:bg-orange-600 text-black font-semibold"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add First Location
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {locations.map((location) => (
                    <Card key={location.id} className={`${theme.colors.tertiary} ${theme.colors.border}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className={`text-lg font-semibold ${theme.colors.textPrimary} mb-2`}>{location.name}</h3>
                            <p className={`${theme.colors.textSecondary} mb-3`}>{location.description}</p>
                            
                            <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 text-sm ${theme.colors.textMuted}`}>
                              {location.address && (
                                <div className="flex items-center space-x-2">
                                  <MapPin className="h-4 w-4 text-orange-500" />
                                  <span>{location.address}</span>
                                </div>
                              )}
                              
                              {location.contact_person && (
                                <div className="flex items-center space-x-2">
                                  <Users className="h-4 w-4 text-orange-500" />
                                  <span>{location.contact_person}</span>
                                </div>
                              )}
                              
                              {location.contact_phone && (
                                <div className="flex items-center space-x-2">
                                  <Phone className="h-4 w-4 text-orange-500" />
                                  <span>{location.contact_phone}</span>
                                </div>
                              )}
                              
                              {location.latitude && location.longitude && (
                                <div className="flex items-center space-x-2">
                                  <Map className="h-4 w-4 text-orange-500" />
                                  <span>{location.latitude}, {location.longitude}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex space-x-2 ml-4">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEdit(location)}
                              className="border-gray-600 text-gray-300 hover:text-orange-500 hover:border-orange-500/50"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(location.id)}
                              className="border-red-600 text-red-400 hover:text-red-300 hover:border-red-500/50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </>
          ) : (
            <form onSubmit={handleSubmitLocal} className="space-y-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className={`text-xl font-semibold ${theme.colors.textPrimary}`}>
                  {editingLocation ? 'Edit Location' : 'Add New Location'}
                </h3>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={resetForm}
                  className={`${theme.colors.textMuted} hover:${theme.colors.textPrimary}`}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back to List
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name" className={theme.colors.textSecondary}>Location Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className={theme.colors.input}
                    placeholder="Command Post"
                    required
                  />
                  {validationErrors.name && (
                    <p className="text-red-500 text-sm mt-1">{validationErrors.name}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="contact_person" className={theme.colors.textSecondary}>Contact Person</Label>
                  <Input
                    id="contact_person"
                    value={formData.contact_person}
                    onChange={(e) => setFormData(prev => ({ ...prev, contact_person: e.target.value }))}
                    className={theme.colors.input}
                    placeholder="John Smith"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="description" className={theme.colors.textSecondary}>Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className={theme.colors.input}
                  placeholder="Primary command and control center for emergency operations"
                  rows={3}
                  required
                />
                {validationErrors.description && (
                  <p className="text-red-500 text-sm mt-1">{validationErrors.description}</p>
                )}
              </div>

              <div>
                <Label htmlFor="address" className="text-gray-300">Address</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  className="bg-gray-700 border-gray-600 text-white"
                  placeholder="123 Emergency Way, City, Province"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="latitude" className="text-gray-300">Latitude</Label>
                  <Input
                    id="latitude"
                    value={formData.latitude}
                    onChange={(e) => setFormData(prev => ({ ...prev, latitude: e.target.value }))}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="45.1234"
                  />
                  {validationErrors.latitude && (
                    <p className="text-red-500 text-sm mt-1">{validationErrors.latitude}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="longitude" className="text-gray-300">Longitude</Label>
                  <Input
                    id="longitude"
                    value={formData.longitude}
                    onChange={(e) => setFormData(prev => ({ ...prev, longitude: e.target.value }))}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="-97.0000"
                  />
                  {validationErrors.longitude && (
                    <p className="text-red-500 text-sm mt-1">{validationErrors.longitude}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="contact_phone" className="text-gray-300">Contact Phone</Label>
                  <Input
                    id="contact_phone"
                    value={formData.contact_phone}
                    onChange={handlePhoneChange}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="123-456-7890"
                  />
                  {validationErrors.contact_phone && (
                    <p className="text-red-500 text-sm mt-1">{validationErrors.contact_phone}</p>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-4 pt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={resetForm}
                  disabled={loading}
                  className="border-gray-600 text-gray-300 hover:bg-gray-700"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-orange-500 hover:bg-orange-600 text-black font-semibold"
                >
                  {loading ? 'Saving...' : (editingLocation ? 'Update Location' : 'Add Location')}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// Sidebar Component
const Sidebar = ({ onMenuSelect, activeMenu, user }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [canAccessExcon, setCanAccessExcon] = useState(false);

  // Check if user can access ExCon Dashboard
  useEffect(() => {
    if (user?.role === 'Super Admin') {
      setCanAccessExcon(true);
      return;
    }
    if (!user) return;

    const checkAccess = async () => {
      try {
        const token = secureStorage.getToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/participants`, { headers });
        const participants = response.data || [];
        const exconPositions = ['exercise director', 'exercise evaluator', 'exercise observer'];
        const userFullName = `${user.first_name || ''} ${user.last_name || ''}`.trim().toLowerCase();
        const userEmail = (user.email || '').toLowerCase();

        const hasExconRole = participants.some(p => {
          const pName = (p.name || '').toLowerCase();
          const pEmail = (p.email || '').toLowerCase();
          const pPosition = (p.position || '').toLowerCase();
          return (pEmail === userEmail || pName === userFullName) && exconPositions.includes(pPosition);
        });
        setCanAccessExcon(hasExconRole);
      } catch (error) {
        console.error('Error checking ExCon access:', error);
        setCanAccessExcon(false);
      }
    };
    checkAccess();
  }, [user]);
  
  const allMenuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'hira', label: 'HIRA', icon: AlertTriangle },
    { id: 'excon-dashboard', label: 'ExCon Dashboard', icon: Zap, requiresExcon: true },
    { id: 'exercises', label: 'Exercises', icon: Target },
    { id: 'participants', label: 'Participants', icon: Users },
    { id: 'tutorials', label: 'Tutorials', icon: Video, hidden: true },
    { id: 'separator-helpdesk', label: '', icon: null },
    { id: 'helpdesk', label: 'Help Desk', icon: HelpCircle },
    { id: 'whitepaper', label: 'Whitepaper', icon: FileText },
    { id: 'privacy-policy', label: 'Privacy Policy', icon: Shield, href: `${process.env.REACT_APP_BACKEND_URL || ''}/api/privacy-policy`, external: true },
    { id: 'licence-agreement', label: 'Licence Agreement', icon: FileText, href: `${process.env.REACT_APP_BACKEND_URL || ''}/api/licence-agreement`, external: true }
  ];

  const menuItems = allMenuItems.filter(item => (!item.requiresExcon || canAccessExcon) && !item.hidden);

  // Mobile drawer state (below md breakpoint); on desktop we use the local collapse toggle
  const { mainDrawerOpen, setMainDrawerOpen } = useMobileNav();

  const handleMenuClick = (id) => {
    onMenuSelect(id);
    // Auto-close the drawer on mobile after a selection
    setMainDrawerOpen(false);
  };

  return (
    <>
      {/* Mobile backdrop */}
      {mainDrawerOpen && (
        <button
          aria-label="Close menu"
          onClick={() => setMainDrawerOpen(false)}
          data-testid="sidebar-mobile-backdrop"
          className="fixed inset-0 z-[999] bg-black/60 md:hidden"
        />
      )}
      <aside
        data-testid="app-sidebar"
        className={`
          bg-gray-900 border-r border-orange-500/20 h-full transition-transform duration-300
          md:relative md:translate-x-0 md:flex md:flex-col md:h-auto
          fixed inset-y-0 left-0 z-[1000] w-72
          ${mainDrawerOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full'}
          md:${isCollapsed ? 'w-16' : 'w-64'}
          ${isCollapsed ? 'md:w-16' : 'md:w-64'}
        `}
      >
        {/* Close (mobile only) */}
        <button
          onClick={() => setMainDrawerOpen(false)}
          className="absolute top-3 right-3 z-10 text-gray-400 hover:text-orange-500 md:hidden p-2"
          aria-label="Close menu"
          data-testid="sidebar-close-btn"
        >
          <XIcon className="h-5 w-5" />
        </button>

        {/* Collapse/Expand Toggle Button (desktop only) */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="absolute top-4 -right-3 z-10 bg-orange-600 hover:bg-orange-700 text-white rounded-full p-1 shadow-lg transition-all hidden md:block"
          title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? (
            <ChevronRight className="h-4 w-4" />
          ) : (
            <ChevronLeft className="h-4 w-4" />
          )}
        </button>

        <ScrollArea className="h-full">
          <div className="p-4 pt-12 md:pt-4">
            {!isCollapsed && (
              <h3 className="text-orange-500 font-semibold mb-4 text-sm uppercase tracking-wider">
                Emergency Management
              </h3>
            )}
            <div className="space-y-2">
              {menuItems.map((item) => {
                if (item.id.startsWith('separator')) {
                  return <div key={item.id} className="border-t border-gray-700 my-2"></div>;
                }
                if (item.external && item.href) {
                  return (
                    <a
                      key={item.id}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setMainDrawerOpen(false)}
                      className={`w-full flex items-center ${isCollapsed ? 'md:justify-center' : 'space-x-3'} px-3 py-3 md:py-2 rounded-lg transition-colors text-left min-h-[44px] text-gray-300 hover:text-orange-500 hover:bg-gray-800`}
                      data-testid={`sidebar-${item.id}`}
                      title={isCollapsed ? item.label : `Open ${item.label} in a new tab`}
                    >
                      <item.icon className="h-5 w-5 flex-shrink-0" />
                      <span className={`font-medium ${isCollapsed ? 'md:hidden' : ''}`}>{item.label}</span>
                    </a>
                  );
                }
                return (
                  <button
                    key={item.id}
                    onClick={() => handleMenuClick(item.id)}
                    className={`w-full flex items-center ${isCollapsed ? 'md:justify-center' : 'space-x-3'} px-3 py-3 md:py-2 rounded-lg transition-colors text-left min-h-[44px] ${
                      activeMenu === item.id
                        ? 'bg-orange-500/20 text-orange-500 border border-orange-500/30'
                        : 'text-gray-300 hover:text-orange-500 hover:bg-gray-800'
                    }`}
                    data-testid={`sidebar-${item.id}`}
                    title={isCollapsed ? item.label : ''}
                  >
                    <item.icon className="h-5 w-5 flex-shrink-0" />
                    <span className={`font-medium ${isCollapsed ? 'md:hidden' : ''}`}>{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </ScrollArea>
      </aside>
    </>
  );
};

// Dashboard Component
// Time validation and formatting functions
const validateTimeFormat = (timeStr) => {
  if (!timeStr || timeStr.trim() === '') return true; // Allow empty for optional fields
  
  // Check format: 24-hour format HH:MM or H:MM (0-23 hours, 0-59 minutes)
  const timeRegex = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])$/;
  return timeRegex.test(timeStr.trim());
};

const formatTimeInput = (timeStr) => {
  if (!timeStr || timeStr.trim() === '') return '';
  
  // Remove any non-digit characters except colon
  let cleaned = timeStr.trim().replace(/[^\d:]/g, '');
  
  // If only digits, auto-format with colon
  if (/^\d{3,4}$/.test(cleaned)) {
    // HHMM or HMM format - insert colon
    if (cleaned.length === 4) {
      cleaned = cleaned.substring(0, 2) + ':' + cleaned.substring(2);
    } else if (cleaned.length === 3) {
      cleaned = cleaned.substring(0, 1) + ':' + cleaned.substring(1);
    }
  }
  
  // If it's already in valid 24-hour format (HH:MM or H:MM), validate and format
  if (/^\d{1,2}:\d{2}$/.test(cleaned)) {
    const [hour, minute] = cleaned.split(':');
    const hourNum = parseInt(hour);
    const minuteNum = parseInt(minute);
    
    // Validate hour (0-23) and minute (0-59)
    if (hourNum >= 0 && hourNum <= 23 && minuteNum >= 0 && minuteNum <= 59) {
      // Ensure zero-padding for single digit hours and minutes
      const paddedHour = hourNum.toString().padStart(2, '0');
      const paddedMinute = minuteNum.toString().padStart(2, '0');
      return `${paddedHour}:${paddedMinute}`;
    }
  }
  
  return timeStr; // Return original if can't format
};

const timeStringToDisplay = (timeStr) => {
  if (!timeStr || timeStr === '') return '';
  // Keep 24-hour format for display
  return timeStr;
};

const displayTimeToApiFormat = (timeStr) => {
  if (!timeStr || timeStr.trim() === '') return '';
  // Keep 24-hour format for API
  return formatTimeInput(timeStr);
};
const Dashboard = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const { selectedAgency } = useAgencyContext();
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null); // { status, message }
  const [expandedExercises, setExpandedExercises] = useState(new Set()); // exercise ids currently expanded
  const [searchQuery, setSearchQuery] = useState('');
  const isExpanded = (id) => expandedExercises.has(id);
  const toggleExpanded = (id, e) => {
    if (e) e.stopPropagation();
    setExpandedExercises((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const filteredExercises = React.useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return exercises;
    return exercises.filter((ex) => {
      const fields = [ex.exercise_name, ex.exercise_type, ex.exercise_description, ex.location];
      return fields.some((f) => (f || '').toString().toLowerCase().includes(q));
    });
  }, [exercises, searchQuery]);
  // Lazy-loaded HIRA thumbnails keyed by hira_id → data URI, used as an
  // avatar fallback when an exercise doesn't have its own image.
  const hiraThumbCache = useHiraThumbnailCache(exercises.map((e) => e.hira_id).filter(Boolean));
  // Lazy-loaded exercise images (list endpoint strips them; this hook
  // re-hydrates each row's own uploaded image via a dedicated endpoint).
  const exImageCache = useExerciseImageCache(exercises);

  useEffect(() => {
    fetchExercises();
  }, [selectedAgency]); // Re-fetch when agency context changes

  const fetchExercises = async () => {
    setFetchError(null);
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // Add organization_id filter if Super Admin has selected an agency
      let url = `${API}/exercise-builder`;
      if (selectedAgency) {
        url += `?organization_id=${selectedAgency.id}`;
      }

      const response = await axios.get(url, { headers });
      setExercises(response.data);
    } catch (error) {
      console.error('Error fetching exercises:', error);
      console.error('Error details:', error.response?.data);
      const status = error?.response?.status;
      // 401/403 is handled globally (session expired) — no need for an inline banner.
      if (status !== 401 && status !== 403) {
        const detail = error?.response?.data?.detail || error?.message || 'Could not reach the server';
        setFetchError({
          status: status || 0,
          message: typeof detail === 'string' ? detail : 'Could not reach the server',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    // Handle both time formats
    if (timeString && timeString.includes(':')) {
      return timeString;
    }
    return timeString || 'Not set';
  };

  const printDashboard = () => {
    const currentDateTime = new Date().toLocaleString();
    const printContent = `
      <html>
        <head>
          <title>Exercise Dashboard Summary</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; line-height: 1.6; }
            .header { border-bottom: 3px solid #f97316; margin-bottom: 30px; padding-bottom: 15px; }
            .section { margin-bottom: 25px; }
            .exercise-card { 
              border: 1px solid #ddd; 
              padding: 15px; 
              margin-bottom: 15px; 
              border-radius: 8px;
              page-break-inside: avoid;
            }
            .exercise-title { font-size: 18px; font-weight: bold; margin-bottom: 8px; color: #333; }
            .exercise-type { 
              background: #f97316; 
              color: white; 
              padding: 2px 8px; 
              border-radius: 4px; 
              font-size: 12px; 
              display: inline-block; 
              margin-bottom: 8px;
            }
            .exercise-description { color: #666; margin-bottom: 8px; }
            .exercise-details { font-size: 14px; color: #555; }
            .footer { 
              position: fixed; 
              bottom: 20px; 
              left: 20px; 
              right: 20px; 
              text-align: center; 
              font-size: 12px; 
              color: #666; 
              border-top: 1px solid #ddd; 
              padding-top: 10px; 
            }
            .summary-stats {
              display: flex;
              gap: 20px;
              margin-bottom: 20px;
              flex-wrap: wrap;
            }
            .stat-item {
              background: #f5f5f5;
              padding: 10px 15px;
              border-radius: 5px;
              text-align: center;
              min-width: 120px;
            }
            .stat-number { font-size: 24px; font-weight: bold; color: #f97316; }
            .stat-label { font-size: 12px; color: #666; }
            @media print { 
              @page { 
                margin: 20px 20px 80px 20px; 
                size: A4;
              }
              body { 
                margin: 0; 
                padding: 20px 20px 60px 20px; 
                font-size: 12px;
                line-height: 1.4;
              } 
              .no-print { display: none; }
              .footer { 
                position: fixed; 
                bottom: 20px; 
                left: 20px; 
                right: 20px;
                height: 40px;
                z-index: 999;
              }
              .report-item { 
                page-break-inside: avoid; 
                page-break-after: auto;
                margin-bottom: 20px;
              }
              .section-title { 
                page-break-after: avoid;
              }
              .assessment-grid { 
                page-break-inside: avoid;
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Exercise Dashboard Summary</h1>
            <p>Complete overview of emergency training exercises</p>
          </div>

          <div class="section">
            <div class="summary-stats">
              <div class="stat-item">
                <div class="stat-number">${exercises.length}</div>
                <div class="stat-label">Total Exercises</div>
              </div>
              <div class="stat-item">
                <div class="stat-number">${exercises.filter(ex => ex.exercise_type === 'Full Scale Exercise').length}</div>
                <div class="stat-label">Full Scale</div>
              </div>
              <div class="stat-item">
                <div class="stat-number">${exercises.filter(ex => ex.exercise_type === 'Table Top').length}</div>
                <div class="stat-label">Table Top</div>
              </div>
              <div class="stat-item">
                <div class="stat-number">${exercises.filter(ex => ex.exercise_type === 'Functional').length}</div>
                <div class="stat-label">Functional</div>
              </div>
            </div>
          </div>

          <div class="section">
            <h2>Exercise Details</h2>
            ${exercises.length > 0 ? 
              exercises.map(exercise => `
                <div class="exercise-card">
                  <div class="exercise-title">${exercise.exercise_name || 'Untitled Exercise'}</div>
                  <div class="exercise-type">${exercise.exercise_type || 'N/A'}</div>
                  <div class="exercise-description">${exercise.exercise_description || 'No description available'}</div>
                  <div class="exercise-details">
                    <strong>Start Date:</strong> ${exercise.start_date || 'Not set'} | 
                    <strong>End Date:</strong> ${exercise.end_date || 'Not set'} | 
                    <strong>Location:</strong> ${exercise.location || 'Not specified'}
                  </div>
                  ${exercise.latitude && exercise.longitude ? `
                    <div class="exercise-details">
                      <strong>Coordinates:</strong> Latitude ${exercise.latitude}°, Longitude ${exercise.longitude}° | <strong>Zoom Level:</strong> ${exercise.zoom_level || 8}
                    </div>
                  ` : ''}
                  <div class="exercise-details">
                    <strong>Goals:</strong> ${exercise.goals?.length || 0} | 
                    <strong>Objectives:</strong> ${exercise.objectives?.length || 0} | 
                    <strong>Events:</strong> ${exercise.events?.length || 0} | 
                    <strong>Organizations:</strong> ${exercise.organizations?.length || 0}
                  </div>
                </div>
              `).join('') 
              : '<div class="exercise-card">No exercises created yet.</div>'
            }
          </div>

          <div class="footer">
            <p>Generated on: ${currentDateTime} | Powered by EXRSIM</p>
          </div>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    writeSanitizedHTML(printWindow.document, printContent);
    printWindow.focus();
    printWindow.print();
  };
  return (
    <div className="p-4 md:p-6 space-y-6">
      
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className={`text-2xl md:text-3xl font-bold ${theme.colors.textPrimary} mb-1 md:mb-2`}>Exercise Dashboard</h1>
          <p className={`${theme.colors.textMuted} text-sm md:text-base`}>Manage your emergency training exercises</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setExpandedExercises(new Set(filteredExercises.map((e) => e.id)))}
            className={`${theme.colors.border} ${theme.colors.textSecondary} hover:text-orange-400 hover:border-orange-500/50 text-xs min-h-[36px]`}
            data-testid="dashboard-expand-all"
          >
            Expand All
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setExpandedExercises(new Set())}
            className={`${theme.colors.border} ${theme.colors.textSecondary} hover:text-orange-400 hover:border-orange-500/50 text-xs min-h-[36px]`}
            data-testid="dashboard-collapse-all"
          >
            Collapse All
          </Button>
          <Button 
            variant="outline" 
            className={`${theme.colors.border} ${theme.colors.textSecondary} ${theme.colors.hover} hover:text-orange-500 min-h-[44px]`}
            onClick={() => printDashboard()}
            title="Print Exercises Summary"
          >
            <Printer className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Print Summary</span>
          </Button>
          <Button 
            className="bg-orange-500 hover:bg-orange-600 text-black font-semibold min-h-[44px]"
            onClick={() => { window.location.hash = '#hira'; }}
            data-testid="new-exercise-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            New HIRA
          </Button>
        </div>
      </div>

      {/* Search bar + result counter */}
      {!loading && exercises.length > 0 && (
        <div className="space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search exercises by name, description, type or location…"
              className="pl-9 pr-9 bg-gray-800 border-gray-700 text-white placeholder-gray-500"
              data-testid="dashboard-search-input"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-orange-400 p-1"
                title="Clear search"
                data-testid="dashboard-search-clear"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <p className={`text-xs sm:text-sm ${theme.colors.textMuted}`} data-testid="dashboard-search-counter">
            Showing {filteredExercises.length} of {exercises.length} exercise{exercises.length !== 1 ? 's' : ''}{searchQuery ? ` matching "${searchQuery}"` : ''}
          </p>
        </div>
      )}

      <div className="flex flex-col gap-4">
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <Card key={`policy-${i}`} className={`${theme.colors.tertiary} ${theme.colors.border}`}>
              <CardHeader>
                <div className={`h-4 ${theme.colors.quaternary} rounded animate-pulse mb-2`}></div>
                <div className={`h-3 ${theme.colors.quaternary} rounded animate-pulse w-2/3`}></div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className={`h-3 ${theme.colors.quaternary} rounded animate-pulse`}></div>
                  <div className={`h-3 ${theme.colors.quaternary} rounded animate-pulse w-3/4`}></div>
                </div>
              </CardContent>
            </Card>
          ))
        ) : fetchError ? (
          <div>
            <Card className={`${theme.colors.tertiary} border-red-500/40 border-dashed`}>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <AlertTriangle className="h-12 w-12 text-red-400 mb-4" />
                <h3 className={`text-lg font-semibold ${theme.colors.textSecondary} mb-2`}>Couldn't load exercises</h3>
                <p className={`${theme.colors.textMuted} text-center mb-4 max-w-md`}>
                  {fetchError.status ? `Server responded with HTTP ${fetchError.status}. ` : ''}{fetchError.message}
                </p>
                <Button
                  className="bg-orange-500 hover:bg-orange-600 text-black font-semibold"
                  onClick={() => { setLoading(true); fetchExercises(); }}
                  data-testid="dashboard-retry-btn"
                >
                  Retry
                </Button>
              </CardContent>
            </Card>
          </div>
        ) : exercises.length === 0 ? (
          <div>
            <Card className={`${theme.colors.tertiary} ${theme.colors.border} border-dashed`}>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Shield className={`h-12 w-12 ${theme.colors.textMuted} mb-4`} />
                <h3 className={`text-lg font-semibold ${theme.colors.textSecondary} mb-2`}>No Exercises Yet</h3>
                <p className={`${theme.colors.textMuted} text-center mb-4`}>
                  Create exercises through HIRA using the ExCon or Event buttons.
                </p>
                <Button 
                  className="bg-orange-500 hover:bg-orange-600 text-black font-semibold"
                  onClick={() => window.location.href = '#hira'}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Go to HIRA
                </Button>
              </CardContent>
            </Card>
          </div>
        ) : filteredExercises.length === 0 ? (
          <div>
            <Card className={`${theme.colors.tertiary} ${theme.colors.border} border-dashed`}>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Search className={`h-10 w-10 ${theme.colors.textMuted} mb-3`} />
                <p className={`${theme.colors.textMuted} text-center`}>
                  No exercises match &ldquo;{searchQuery}&rdquo;.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className={`mt-3 ${theme.colors.border} ${theme.colors.textSecondary} hover:text-orange-400`}
                  onClick={() => setSearchQuery('')}
                  data-testid="dashboard-search-reset"
                >
                  Clear search
                </Button>
              </CardContent>
            </Card>
          </div>
        ) : (
          filteredExercises.map((exercise) => {
            const expanded = isExpanded(exercise.id);
            return (
            <Card 
              key={exercise.id} 
              className={`${theme.colors.tertiary} ${theme.colors.border} hover:border-orange-500/50 transition-colors cursor-pointer`}
              data-testid={`exercise-card-${exercise.id}`}
              onClick={() => window.location.href = `#manage?exercise=${exercise.id}`}
            >
              <CardHeader className={expanded ? '' : 'pb-3'}>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={(e) => toggleExpanded(exercise.id, e)}
                    className="flex-shrink-0 text-gray-400 hover:text-orange-400"
                    title={expanded ? 'Collapse' : 'Expand'}
                    data-testid={`exercise-toggle-${exercise.id}`}
                  >
                    {expanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                  </button>
                  {/* Left: exercise image (own via lazy cache → linked HIRA thumb → icon) */}
                  {(exercise.exercise_image || exImageCache[exercise.id]) ? (
                    <div
                      className={`${expanded ? 'w-12 h-12' : 'w-10 h-10'} rounded ${theme.colors.quaternary} overflow-hidden flex-shrink-0`}
                      data-testid={`dashboard-exercise-own-thumb-${exercise.id}`}
                    >
                      <img src={exercise.exercise_image || exImageCache[exercise.id]} alt={exercise.exercise_name} className="w-full h-full object-cover" />
                    </div>
                  ) : hiraThumbCache[exercise.hira_id] ? (
                    <div
                      className={`${expanded ? 'w-12 h-12' : 'w-10 h-10'} rounded ${theme.colors.quaternary} overflow-hidden flex-shrink-0`}
                      data-testid={`dashboard-exercise-hira-thumb-${exercise.id}`}
                    >
                      <img src={hiraThumbCache[exercise.hira_id]} alt={exercise.exercise_name} className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <div className={`${expanded ? 'w-12 h-12' : 'w-10 h-10'} rounded ${theme.colors.quaternary} flex-shrink-0 flex items-center justify-center`}>
                      <Shield className="h-5 w-5 text-gray-500" />
                    </div>
                  )}
                  {/* Middle: name + pill on first line, remainder inline when collapsed */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <CardTitle className={`${theme.colors.textPrimary} text-lg truncate`}>{exercise.exercise_name}</CardTitle>
                      <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30 flex-shrink-0">
                        {exercise.exercise_type}
                      </Badge>
                      {!expanded && (exercise.location || exercise.start_date) && (
                        <span className={`text-xs ${theme.colors.textMuted} truncate`}>
                          {exercise.location ? `${exercise.location}` : ''}
                          {exercise.location && exercise.start_date ? ' · ' : ''}
                          {exercise.start_date ? `Start ${formatDate(exercise.start_date)}${exercise.start_time ? ` ${formatTime(exercise.start_time)}` : ''}` : ''}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Right: lock icon far right */}
                  <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    <ExerciseLockButton
                      exercise={exercise}
                      user={user}
                      size="sm"
                      testIdPrefix="dashboard-exercise-lock"
                      onChanged={fetchExercises}
                    />
                  </div>
                </div>
                {expanded && (
                  <CardDescription className={`${theme.colors.textMuted} mt-2`}>
                    {exercise.exercise_description}
                  </CardDescription>
                )}
              </CardHeader>
              {expanded && (
                <CardContent className="pt-0">
                  <div className={`space-y-2 text-sm ${theme.colors.textMuted}`}>
                    <div className="flex items-center space-x-2">
                      <MapPin className="h-4 w-4" />
                      <span>{exercise.location || 'Location not set'}</span>
                    </div>
                    {exercise.latitude && exercise.longitude && (
                      <div className="flex items-center space-x-2">
                        <MapPin className="h-4 w-4" />
                        <span>Coordinates: {exercise.latitude}°, {exercise.longitude}° | Zoom: {exercise.zoom_level || 8}</span>
                      </div>
                    )}
                    <div className="flex items-center space-x-2">
                      <CalendarDays className="h-4 w-4" />
                      <span>Start: {formatDate(exercise.start_date)} at {formatTime(exercise.start_time)}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CalendarDays className="h-4 w-4" />
                      <span>End: {formatDate(exercise.end_date)} at {formatTime(exercise.end_time)}</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 pt-3 border-t border-gray-700 text-xs text-gray-500">
                    <span>Created: {exercise.created_at ? new Date(exercise.created_at).toLocaleDateString() : 'N/A'}</span>
                    {exercise.updated_at && (
                      <span>Modified: {new Date(exercise.updated_at).toLocaleDateString()}</span>
                    )}
                  </div>
                </CardContent>
              )}
            </Card>
            );
          })
        )}
      </div>
    </div>
  );
};

// Participant Management Components
const ParticipantsView = ({ positions = [], currentExercise = null }) => {
  const [view, setView] = useState('list'); // 'list', 'add', 'edit'
  const [editingParticipant, setEditingParticipant] = useState(null);

  const handleAddNew = () => {
    setEditingParticipant(null);
    setView('add');
  };

  const handleEdit = (participant) => {
    setEditingParticipant(participant);
    setView('edit');
  };

  const handleBack = () => {
    setView('list');
    setEditingParticipant(null);
  };

  const handleSave = () => {
    setView('list');
    setEditingParticipant(null);
    // The list will automatically refresh via useEffect
  };

  if (view === 'add' || view === 'edit') {
    return (
      <ParticipantForm
        onBack={handleBack}
        onSave={handleSave}
        editingParticipant={editingParticipant}
        positions={positions}
        exerciseId={currentExercise?.id}
      />
    );
  }

  return (
    <ParticipantsList
      onAddNew={handleAddNew}
      onEdit={handleEdit}
      positions={positions}
    />
  );
};

// General Message Management
const ResourcesView = ({ positions = [], currentExercise = null }) => {
  const [view, setView] = useState('list'); // 'list', 'add', 'edit'
  const [editingResource, setEditingResource] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Listen for edit resource requests from ICS interface
  useEffect(() => {
    const handleEditFromICS = (event) => {
      const { resource } = event.detail;
      if (resource) {
        setEditingResource(resource);
        setView('edit');
      }
    };

    window.addEventListener('editResourceFromICS', handleEditFromICS);
    
    return () => {
      window.removeEventListener('editResourceFromICS', handleEditFromICS);
    };
  }, []);

  const handleAddNew = () => {
    setEditingResource(null);
    setView('add');
  };

  const handleEdit = (resource) => {
    setEditingResource(resource);
    setView('edit');
  };

  const handleBack = () => {
    setView('list');
    setEditingResource(null);
  };

  const handleSave = () => {
    setView('list');
    setEditingResource(null);
    // Trigger a refresh of the resources list to get updated data
    setRefreshTrigger(prev => prev + 1);
  };

  if (view === 'add' || view === 'edit') {
    return (
      <ResourceForm
        onBack={handleBack}
        onSave={handleSave}
        editingResource={editingResource}
        positions={positions}
        exerciseId={currentExercise?.id}
      />
    );
  }

  return (
    <ResourcesList
      onAddNew={handleAddNew}
      onEdit={handleEdit}
      refreshTrigger={refreshTrigger}
      currentExercise={currentExercise}
      positions={positions}
    />
  );
};

// Locations View - Full CRUD for managing check-in locations
const InlineExerciseEditor = ({ exerciseId, initialStep, returnTo, onClose }) => {
  const [editingExercise, setEditingExercise] = useState(null);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();

  useEffect(() => {
    const fetchExercise = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/exercise-builder/${exerciseId}`, { headers });
        setEditingExercise(response.data);
      } catch (error) {
        console.error('Error fetching exercise for editing:', error);
      } finally {
        setLoading(false);
      }
    };
    if (exerciseId) fetchExercise();
  }, [exerciseId, token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  const handleBack = () => {
    if (returnTo === 'excon-dashboard') {
      window.location.hash = '#excon-dashboard';
    } else {
      onClose();
    }
  };

  return (
    <ExerciseBuilderWizard 
      onBack={handleBack}
      editingExercise={editingExercise}
      initialStep={initialStep}
      returnTo={returnTo}
      LeafletMapping={LeafletMapping}
      MSELView={MSELView}
    />
  );
};


const ExerciseView = ({ editingExerciseId, editingStep, editingReturnTo, onClearEditing }) => {
  if (editingExerciseId) {
    return (
      <InlineExerciseEditor 
        exerciseId={editingExerciseId} 
        initialStep={editingStep} 
        returnTo={editingReturnTo}
        onClose={onClearEditing}
      />
    );
  }
  return <ExerciseListView onEditExercise={(exerciseId) => {
    window.location.hash = `#builder?exercise=${exerciseId}`;
  }} />;
};

// MSEL Management Components
const normaliseMSELScenarioTime = (raw) => {
  if (!raw) return '';
  const s = String(raw).trim();
  // Modern canonical: YYYY-MM-DDTHH:MM (with optional trailing seconds / timezone)
  const isoMatch = s.match(/^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})/);
  if (isoMatch) return `${isoMatch[1]}T${isoMatch[2]}:${isoMatch[3]}`;
  // Bare time formats: HHMM or HH:MM → use today's date so the picker has a value
  const bareTime = s.match(/^(\d{2}):?(\d{2})$/);
  if (bareTime) {
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, '0');
    const d = String(today.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}T${bareTime[1]}:${bareTime[2]}`;
  }
  // Try native Date parse as a last resort
  const dt = new Date(s);
  if (!Number.isNaN(dt.getTime())) {
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const d = String(dt.getDate()).padStart(2, '0');
    const hh = String(dt.getHours()).padStart(2, '0');
    const mm = String(dt.getMinutes()).padStart(2, '0');
    return `${y}-${m}-${d}T${hh}:${mm}`;
  }
  return '';
};

const MSELForm = ({ onBack, onSave, editingEvent = null, exerciseId }) => {
  const toast = useToast();
  const { token } = useAuth();
  // Lazy init from editingEvent so Radix Selects (Event Type, Inject Mode, From, To)
  // see the correct value on their first mount instead of "" → later setFormData.
  const [formData, setFormData] = useState(() => ({
    event_number: editingEvent?.event_number || 1,
    msel_name: editingEvent?.msel_name || '',
    scenario_time: normaliseMSELScenarioTime(editingEvent?.scenario_time),
    event_type: editingEvent?.event_type || '',
    inject_mode: editingEvent?.inject_mode || '',
    from_entity: editingEvent?.from_entity || '',
    to_entity: editingEvent?.to_entity || '',
    message: editingEvent?.message || '',
    expected_response: editingEvent?.expected_response || '',
    objective_capability_task: editingEvent?.objective_capability_task || '',
    notes: editingEvent?.notes || ''
  }));
  const [loading, setLoading] = useState(false);

  const eventTypes = [
    'Player Inject',
    'Media Inject',
    'Discussion Question',
    'Information Update',
    'Decision Point',
    'Time Advance',
    'Scenario Development',
    'Resource Request',
    'Communication Test',
    'Evaluation Checkpoint'
  ];

  const injectModes = [
    'Phone Call',
    'Radio Communication',
    'Email/Message',
    'Face-to-Face',
    'Written Document',
    'Media Broadcast',
    'Social Media',
    'Emergency Alert System',
    'Controller Briefing',
    'Automated System'
  ];

  const controllerRoles = [
    'Exercise Controller',
    'Lead Controller',
    'Safety Officer',
    'Evaluator',
    'SIMCELL Controller',
    'Communications Controller',
    'Media Controller',
    'Logistics Controller',
    'Technical Controller',
    'Observer Controller'
  ];

  const playerRoles = [
    'Incident Commander',
    'Operations Section',
    'Planning Section',
    'Logistics Section',
    'Finance/Administration Section',
    'Safety Officer',
    'Public Information Officer',
    'Liaison Officer',
    'All Players',
    'Emergency Operations Center',
    'Field Teams',
    'Media Representatives'
  ];

  useEffect(() => {
    if (!editingEvent && exerciseId) {
      // Auto-count: fetch existing events and set event_number to max + 1
      (async () => {
        try {
          const headers = token ? { Authorization: `Bearer ${token}` } : {};
          const r = await axios.get(`${API}/msel/${exerciseId}`, { headers });
          const existing = Array.isArray(r.data) ? r.data : [];
          const maxNum = existing.reduce((m, ev) => {
            const n = parseInt(ev.event_number, 10);
            return Number.isFinite(n) && n > m ? n : m;
          }, 0);
          setFormData(prev => ({ ...prev, event_number: maxNum + 1 }));
        } catch (err) {
          console.warn('MSELForm auto-count fetch failed, defaulting to 1:', err?.message || err);
        }
      })();
    }
  }, [editingEvent, exerciseId, token]);

  const handleSubmitLocal = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    console.log('Submitting MSEL event...');
    console.log('Exercise ID:', exerciseId);
    console.log('Form Data:', formData);
    
    try {
      // Add exercise_id to the data being sent
      const dataToSend = {
        ...formData,
        exercise_id: exerciseId || ''
      };
      
      console.log('Data to send:', dataToSend);
      
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };
      
      if (editingEvent) {
        console.log('Updating existing event:', editingEvent.id);
        const response = await axios.put(`${API}/msel/event/${editingEvent.id}`, dataToSend, config);
        console.log('Update response:', response.data);
        toast.success('MSEL event updated successfully!');
      } else {
        console.log('Creating new event at:', `${API}/msel`);
        const response = await axios.post(`${API}/msel`, dataToSend, config);
        console.log('Create response:', response.data);
        toast.success('MSEL event created successfully!');
      }
      onSave();
    } catch (error) {
      console.error('Error saving MSEL event:', error);
      console.error('Error response:', error.response?.data);
      toast.error(`Error saving MSEL event: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <Button 
          variant="ghost" 
          onClick={onBack}
          className="text-gray-400 hover:text-orange-500 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to MSEL List
        </Button>
        <h1 className="text-3xl font-bold text-orange-500 mb-2">
          {editingEvent ? 'Edit MSEL Event' : 'Add New MSEL Event'}
        </h1>
        <p className="text-gray-400">Master Sequence Event List - Exercise event planning and tracking</p>
      </div>

      <form onSubmit={handleSubmitLocal} className="space-y-6">
        {/* Event Identification */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-orange-500">Event Identification</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="event_number" className="text-gray-300">Event Number *</Label>
                <Input
                  id="event_number"
                  type="number"
                  min="1"
                  value={formData.event_number}
                  readOnly
                  className="bg-gray-700 border-gray-600 text-white cursor-not-allowed opacity-90"
                  data-testid="msel-event-number"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Auto-numbered — increments from the last event in this exercise</p>
              </div>
              <div />
              <div>
                <Label htmlFor="scenario_date" className="text-gray-300">Scenario Date *</Label>
                <Input
                  type="date"
                  id="scenario_date"
                  value={(formData.scenario_time || '').split('T')[0] || ''}
                  onChange={(e) => {
                    const newDate = e.target.value;
                    const existingTime = (formData.scenario_time || '').split('T')[1] || '';
                    setFormData(prev => ({ ...prev, scenario_time: newDate ? `${newDate}T${existingTime}` : '' }));
                  }}
                  className="bg-gray-700 border-gray-600 text-white"
                  data-testid="msel-scenario-date"
                  required
                />
              </div>
              <div>
                <Label htmlFor="scenario_time_only" className="text-gray-300">Scenario Time (24-hour, HH:MM) *</Label>
                <Input
                  type="text"
                  id="scenario_time_only"
                  value={((formData.scenario_time || '').split('T')[1] || '').slice(0, 5)}
                  onChange={(e) => {
                    // Accept only digits and a single colon; keep at most HH:MM
                    let v = e.target.value.replace(/[^0-9:]/g, '').slice(0, 5);
                    const existingDate = (formData.scenario_time || '').split('T')[0] || '';
                    setFormData(prev => ({ ...prev, scenario_time: existingDate ? `${existingDate}T${v}` : (v ? `T${v}` : '') }));
                  }}
                  placeholder="08:00"
                  pattern="^([01]\d|2[0-3]):[0-5]\d$"
                  maxLength={5}
                  className="bg-gray-700 border-gray-600 text-white"
                  data-testid="msel-scenario-time"
                  required
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Event Classification */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-orange-500">Event Classification</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="msel_name" className="text-gray-300">Name of MSEL *</Label>
              <Input
                id="msel_name"
                type="text"
                value={formData.msel_name}
                onChange={(e) => setFormData(prev => ({ ...prev, msel_name: e.target.value }))}
                placeholder="give a name to describe the event e.g.: protest violence increasing, officer missing, de-escalation"
                className="bg-gray-700 border-gray-600 text-white"
                data-testid="msel-name-input"
                required
              />
            </div>
            <div>
              <Label htmlFor="event_type" className="text-gray-300">Event Type</Label>
              <Select value={formData.event_type} onValueChange={(value) => setFormData(prev => ({ ...prev, event_type: value }))}>
                <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                  <SelectValue placeholder="Select event type" />
                </SelectTrigger>
                <SelectContent className="bg-gray-700 border-gray-600">
                  {eventTypes.map((type) => (
                    <SelectItem key={type} value={type} className="text-white focus:bg-gray-600">
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="inject_mode" className="text-gray-300">Inject Mode</Label>
              <Select value={formData.inject_mode} onValueChange={(value) => setFormData(prev => ({ ...prev, inject_mode: value }))}>
                <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                  <SelectValue placeholder="Select inject mode" />
                </SelectTrigger>
                <SelectContent className="bg-gray-700 border-gray-600">
                  {injectModes.map((mode) => (
                    <SelectItem key={mode} value={mode} className="text-white focus:bg-gray-600">
                      {mode}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Communication Details */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-orange-500">Communication Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="from_entity" className="text-gray-300">From (Controller/Entity)</Label>
              <div className="space-y-2">
                <Select value={formData.from_entity} onValueChange={(value) => setFormData(prev => ({ ...prev, from_entity: value }))}>
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                    <SelectValue placeholder="Select controller role" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-700 border-gray-600">
                    {controllerRoles.map((role) => (
                      <SelectItem key={role} value={role} className="text-white focus:bg-gray-600">
                        {role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-400">Or manually edit the field above to enter a custom value</p>
              </div>
            </div>

            <div>
              <Label htmlFor="to_entity" className="text-gray-300">To (Player/Group)</Label>
              <div className="space-y-2">
                <Select value={formData.to_entity} onValueChange={(value) => setFormData(prev => ({ ...prev, to_entity: value }))}>
                  <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                    <SelectValue placeholder="Select player role" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-700 border-gray-600">
                    {playerRoles.map((role) => (
                      <SelectItem key={role} value={role} className="text-white focus:bg-gray-600">
                        {role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-400">Or manually edit the field above to enter a custom value</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Event Content */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-orange-500">Event Content & Objectives</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="message" className="text-gray-300">Message *</Label>
              <Textarea
                id="message"
                value={formData.message}
                onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                className="bg-gray-700 border-gray-600 text-white"
                rows={4}
                placeholder="The core content of the inject - information, request, or call to action"
                required
              />
            </div>

            <div>
              <Label htmlFor="expected_response" className="text-gray-300">Expected Participant Response</Label>
              <Textarea
                id="expected_response"
                value={formData.expected_response}
                onChange={(e) => setFormData(prev => ({ ...prev, expected_response: e.target.value }))}
                className="bg-gray-700 border-gray-600 text-white"
                rows={3}
                placeholder="The anticipated action or reaction from the player"
              />
            </div>

            <div>
              <Label htmlFor="objective_capability_task" className="text-gray-300">Objective, Capability, and/or Critical Task</Label>
              <Textarea
                id="objective_capability_task"
                value={formData.objective_capability_task}
                onChange={(e) => setFormData(prev => ({ ...prev, objective_capability_task: e.target.value }))}
                className="bg-gray-700 border-gray-600 text-white"
                rows={3}
                placeholder="The specific exercise objective or critical task this event tests"
              />
            </div>

            <div>
              <Label htmlFor="notes" className="text-gray-300">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                className="bg-gray-700 border-gray-600 text-white"
                rows={2}
                placeholder="Additional controller information or details"
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end space-x-4 pt-6">
          <Button type="button" variant="outline" onClick={onBack} disabled={loading}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            className="bg-orange-500 hover:bg-orange-600 text-black font-semibold"
            disabled={loading}
          >
            {loading ? 'Saving...' : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {editingEvent ? 'Update Event' : 'Save Event'}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

const MSELList = ({ onAddNew, onEdit, exerciseId }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedEvents, setExpandedEvents] = useState(new Set());
  const { token } = useAuth();

  const toggleEventExpanded = (id) => {
    setExpandedEvents((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    fetchEvents();
  }, [exerciseId]);

  const fetchEvents = async () => {
    try {
      // Fetch events filtered by exercise_id if provided
      const endpoint = exerciseId ? `${API}/msel/${exerciseId}` : `${API}/msel`;
      console.log('Fetching MSEL events from:', endpoint);
      const response = await axios.get(endpoint);
      setEvents(response.data);
      console.log('Loaded MSEL events:', response.data.length);
    } catch (error) {
      console.error('Error fetching MSEL events:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteEvent = async (eventId) => {
    if (window.confirm('Are you sure you want to delete this MSEL event?')) {
      try {
        const config = token ? { headers: { Authorization: `Bearer ${token}` } } : {};
        await axios.delete(`${API}/msel/event/${eventId}`, config);
        setEvents(prev => prev.filter(e => e.id !== eventId));
        console.log('MSEL event deleted successfully');
      } catch (error) {
        console.error('Error deleting MSEL event:', error);
        alert('Error deleting MSEL event. Please try again.');
      }
    }
  };

  const toggleCompletion = async (event) => {
    try {
      const updatedEvent = {
        ...event,
        completed: !event.completed,
        actual_time: !event.completed ? format24HourTime() : null
      };
      await axios.put(`${API}/msel/event/${event.id}`, updatedEvent);
      setEvents(prev => prev.map(e => e.id === event.id ? updatedEvent : e));
    } catch (error) {
      console.error('Error updating event completion:', error);
    }
  };

  const getEventTypeColor = (eventType) => {
    const colors = {
      'Player Inject': 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      'Media Inject': 'bg-purple-500/20 text-purple-300 border-purple-500/30',
      'Discussion Question': 'bg-green-500/20 text-green-300 border-green-500/30',
      'Information Update': 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
      'Decision Point': 'bg-red-500/20 text-red-300 border-red-500/30'
    };
    return colors[eventType] || 'bg-gray-500/20 text-gray-300 border-gray-500/30';
  };

  // Sort events by event number
  const sortedEvents = [...events].sort((a, b) => a.event_number - b.event_number);

  const escapeHTML = (s) => String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  const handlePrintMSEL = async () => {
    if (!sortedEvents.length) {
      alert('There are no MSEL events to print.');
      return;
    }
    // Try to fetch the exercise name for a nicer header (optional)
    let exerciseName = '';
    if (exerciseId) {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const r = await axios.get(`${API}/exercise-builder/${exerciseId}`, { headers });
        exerciseName = r.data?.exercise_name || '';
      } catch (err) {
        console.warn('Print MSEL: could not fetch exercise name:', err?.message || err);
      }
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow pop-ups to print the MSEL report.');
      return;
    }

    const formatScenarioTime = (v) => {
      if (!v) return 'Not Set';
      try {
        return new Date(v).toLocaleString([], {
          month: 'short', day: 'numeric', year: 'numeric',
          hour: '2-digit', minute: '2-digit', hour12: false
        });
      } catch { return String(v); }
    };

    const rowsHTML = sortedEvents.map((event) => `
      <div class="event">
        <div class="event-head">
          <div class="event-num">#${escapeHTML(event.event_number)}</div>
          <div class="event-title">
            <h2>Event #${escapeHTML(event.event_number)}${event.msel_name ? ` — ${escapeHTML(event.msel_name)}` : ''}</h2>
            <div class="badges">
              <span class="badge badge-type">${escapeHTML(event.event_type || 'No Event Type')}</span>
              <span class="badge badge-mode">${escapeHTML(event.inject_mode || 'No Inject Mode')}</span>
              ${event.completed ? '<span class="badge badge-done">Completed</span>' : ''}
            </div>
          </div>
        </div>
        <table class="event-table">
          <tbody>
            <tr><th>Scenario Date/Time</th><td>${escapeHTML(formatScenarioTime(event.scenario_time))}</td></tr>
            <tr><th>From → To</th><td>${escapeHTML(event.from_entity || 'Not Set')} → ${escapeHTML(event.to_entity || 'Not Set')}</td></tr>
            <tr><th>Message</th><td>${escapeHTML(event.message || '')}</td></tr>
            <tr><th>Expected Response</th><td>${escapeHTML(event.expected_response || '')}</td></tr>
            <tr><th>Objective / Task</th><td>${escapeHTML(event.objective_capability_task || '')}</td></tr>
            ${event.notes ? `<tr><th>Controller Notes</th><td>${escapeHTML(event.notes)}</td></tr>` : ''}
            ${event.completed && event.actual_time ? `<tr><th>Completed At</th><td>${escapeHTML(event.actual_time)}</td></tr>` : ''}
            ${event.created_at ? `<tr><th>Created</th><td>${escapeHTML(new Date(event.created_at).toLocaleString())}</td></tr>` : ''}
          </tbody>
        </table>
      </div>
    `).join('');

    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>MSEL Report${exerciseName ? ' — ' + escapeHTML(exerciseName) : ''}</title>
          <style>
            * { box-sizing: border-box; }
            body { font-family: Arial, Helvetica, sans-serif; color: #111; margin: 24px; }
            h1 { color: #ea580c; margin: 0 0 4px 0; font-size: 22px; }
            .subtitle { color: #555; margin-bottom: 16px; font-size: 13px; }
            .meta { color: #333; font-size: 12px; margin-bottom: 20px; }
            .event { border: 1px solid #ccc; border-radius: 6px; padding: 12px 16px; margin-bottom: 14px; page-break-inside: avoid; }
            .event-head { display: flex; align-items: flex-start; gap: 12px; margin-bottom: 8px; }
            .event-num { flex: 0 0 auto; width: 42px; height: 42px; border-radius: 50%; background: #fff2e6; color: #c2410c; display: inline-flex; align-items: center; justify-content: center; font-weight: 700; font-size: 14px; }
            .event-title { flex: 1 1 auto; }
            .event-title h2 { margin: 0 0 4px 0; font-size: 16px; color: #111; }
            .badges { display: flex; flex-wrap: wrap; gap: 6px; }
            .badge { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 11px; border: 1px solid #ddd; background: #f7f7f7; color: #333; }
            .badge-type { background: #eef4ff; color: #1d4ed8; border-color: #c7d7fe; }
            .badge-mode { background: #f3f4f6; color: #374151; border-color: #d1d5db; }
            .badge-done { background: #e7f7ec; color: #0f766e; border-color: #bbe6cd; }
            table.event-table { width: 100%; border-collapse: collapse; font-size: 12px; margin-top: 6px; }
            table.event-table th, table.event-table td { text-align: left; vertical-align: top; padding: 6px 8px; border: 1px solid #e5e7eb; }
            table.event-table th { width: 180px; background: #f9fafb; color: #444; font-weight: 600; }
            .footer { color: #666; font-size: 11px; margin-top: 8px; text-align: right; }
            @media print { body { margin: 12mm; } .event { break-inside: avoid; } }
          </style>
        </head>
        <body>
          <h1>Master Scenario Events List (MSEL)</h1>
          ${exerciseName ? `<div class="subtitle">Exercise: ${escapeHTML(exerciseName)}</div>` : ''}
          <div class="meta">Total events: ${sortedEvents.length} &nbsp;|&nbsp; Printed: ${escapeHTML(new Date().toLocaleString([], { hour12: false }))}</div>
          ${rowsHTML}
          <div class="footer">EXRSIM — MSEL Report</div>
        </body>
      </html>
    `;

    writeSanitizedHTML(printWindow.document, printContent);
    printWindow.focus();
    setTimeout(() => printWindow.print(), 300);
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-orange-500 mb-2">MSEL - Master Sequence Event List</h1>
          <p className="text-gray-400">Manage exercise event timeline and inject sequences</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            onClick={() => setExpandedEvents(new Set(events.map(e => e.id)))}
            variant="outline"
            className="border-gray-600 text-gray-300 hover:text-orange-400 hover:border-orange-500/50"
            data-testid="msel-expand-all"
          >
            Expand All
          </Button>
          <Button
            onClick={() => setExpandedEvents(new Set())}
            variant="outline"
            className="border-gray-600 text-gray-300 hover:text-orange-400 hover:border-orange-500/50"
            data-testid="msel-collapse-all"
          >
            Collapse All
          </Button>
          <Button
            onClick={handlePrintMSEL}
            variant="outline"
            className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
            data-testid="print-msel-btn"
          >
            <Printer className="h-4 w-4 mr-2" />
            Print MSEL
          </Button>
          <Button 
            onClick={onAddNew}
            className="bg-orange-500 hover:bg-orange-600 text-black font-semibold"
            data-testid="add-msel-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add MSEL Event
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={`msel-${i}`} className="bg-gray-800 border-gray-700">
              <CardContent className="p-4">
                <div className="space-y-2">
                  <div className="h-4 bg-gray-700 rounded animate-pulse w-1/4"></div>
                  <div className="h-3 bg-gray-700 rounded animate-pulse w-2/3"></div>
                  <div className="h-3 bg-gray-700 rounded animate-pulse w-1/2"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : events.length === 0 ? (
        <Card className="bg-gray-800 border-gray-700 border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ClipboardList className="h-12 w-12 text-gray-500 mb-4" />
            <h3 className="text-lg font-semibold text-gray-300 mb-2">No MSEL Events Yet</h3>
            <p className="text-gray-500 text-center mb-4">
              Start planning your exercise by creating sequence events and inject timelines.
            </p>
            <Button 
              onClick={onAddNew}
              className="bg-orange-500 hover:bg-orange-600 text-black font-semibold"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create First MSEL Event
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-400">
              Showing {sortedEvents.length} event{sortedEvents.length !== 1 ? 's' : ''} (sorted by event number)
            </p>
          </div>
          {sortedEvents.map((event) => {
            const isExpanded = expandedEvents.has(event.id);
            return (
            <Card key={event.id} className={`bg-gray-800 border-gray-700 hover:border-orange-500/50 transition-colors ${event.completed ? 'opacity-75' : ''}`}>
              <CardContent className="p-6">
                <div className="flex items-start space-x-4">
                  {/* Expand / collapse chevron */}
                  <button
                    type="button"
                    onClick={() => toggleEventExpanded(event.id)}
                    className="flex-shrink-0 mt-1 p-1 rounded hover:bg-gray-700 text-gray-400 hover:text-orange-400 transition-colors"
                    aria-label={isExpanded ? 'Collapse event details' : 'Expand event details'}
                    data-testid={`msel-event-toggle-${event.id}`}
                  >
                    {isExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                  </button>
                  {/* Event Number Badge */}
                  <div className="flex-shrink-0">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${
                      event.completed ? 'bg-green-500/20 text-green-300' : 'bg-orange-500/20 text-orange-300'
                    }`}>
                      #{event.event_number}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center flex-wrap gap-3 mb-2">
                      <h3 className="text-lg font-semibold text-white" data-testid={`msel-event-title-${event.id}`}>
                        Event #{event.event_number}
                        {event.msel_name && (
                          <span className="text-orange-300 font-normal ml-2">— {event.msel_name}</span>
                        )}
                      </h3>
                      <Badge className={getEventTypeColor(event.event_type)}>
                        {event.event_type || 'No Event Type'}
                      </Badge>
                      {isExpanded && (
                        <Badge className="bg-gray-600/20 text-gray-300 border-gray-600/30">
                          {event.inject_mode || 'No Inject Mode'}
                        </Badge>
                      )}
                      {event.completed && (
                        <Badge className="bg-green-500/20 text-green-300 border-green-500/30">
                          Completed
                        </Badge>
                      )}
                    </div>

                    <div className="mb-3 space-y-1">
                      <div>
                        <span className="text-orange-400 font-medium text-sm">Scenario Date/Time:</span>
                        <span className="text-gray-300 ml-2" data-testid={`msel-event-time-${event.id}`}>{event.scenario_time ? new Date(event.scenario_time).toLocaleString([], { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }) : 'Not Set'}</span>
                      </div>
                      {isExpanded && (
                        <div>
                          <span className="text-blue-400 font-medium text-sm">From → To:</span>
                          <span className="text-gray-300 ml-2">{event.from_entity || 'Not Set'} → {event.to_entity || 'Not Set'}</span>
                        </div>
                      )}
                    </div>

                    {isExpanded && (
                    <>
                    <div className="mb-3">
                      <span className="text-green-400 font-medium text-sm">Message:</span>
                      <p className="text-gray-300 text-sm mt-1">{event.message}</p>
                    </div>

                    <div className="mb-3">
                      <span className="text-purple-400 font-medium text-sm">Expected Response:</span>
                      <p className="text-gray-300 text-sm mt-1">{event.expected_response}</p>
                    </div>

                    <div className="mb-3">
                      <span className="text-yellow-400 font-medium text-sm">Objective/Task:</span>
                      <p className="text-gray-300 text-sm mt-1">{event.objective_capability_task}</p>
                    </div>

                    {event.notes && (
                      <div className="mb-3 p-3 bg-gray-700/50 rounded">
                        <span className="text-cyan-400 font-medium text-sm">Controller Notes:</span>
                        <p className="text-gray-300 text-sm mt-1">{event.notes}</p>
                      </div>
                    )}

                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>Created: {new Date(event.created_at).toLocaleString()}</span>
                      {event.completed && event.actual_time && (
                        <span>Completed at: {event.actual_time}</span>
                      )}
                    </div>
                    </>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col space-y-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleCompletion(event)}
                      className={event.completed 
                        ? "border-green-600 text-green-300 hover:text-green-200 hover:border-green-500/50"
                        : "border-orange-600 text-orange-300 hover:text-orange-200 hover:border-orange-500/50"
                      }
                    >
                      {event.completed ? 'Mark Pending' : 'Mark Complete'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onEdit(event)}
                      className="border-gray-600 text-gray-300 hover:text-orange-500 hover:border-orange-500/50"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => deleteEvent(event.id)}
                      className="border-red-600 text-red-400 hover:text-red-300 hover:border-red-500/50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

const MSELView = ({ exerciseId }) => {
  const [view, setView] = useState('list'); // 'list', 'add', 'edit'
  const [editingEvent, setEditingEvent] = useState(null);

  const handleAddNew = () => {
    setEditingEvent(null);
    setView('add');
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setView('edit');
  };

  const handleBack = () => {
    setView('list');
    setEditingEvent(null);
  };

  const handleSave = () => {
    setView('list');
    setEditingEvent(null);
    // The list will automatically refresh via useEffect
  };

  if (view === 'add' || view === 'edit') {
    return (
      <MSELForm
        key={editingEvent?.id || 'new-msel'}
        onBack={handleBack}
        onSave={handleSave}
        editingEvent={editingEvent}
        exerciseId={exerciseId}
      />
    );
  }

  return (
    <MSELList
      onAddNew={handleAddNew}
      onEdit={handleEdit}
      exerciseId={exerciseId}
    />
  );
};

// HIRA Management Components
// Memoized RadioSection to prevent unnecessary re-renders
const RadioSection = React.memo(({ title, levels, value, onChange, required = true, scrollRef }) => {
  const handleChange = (val) => {
    // Capture scroll position
    if (scrollRef) {
      scrollRef.current = window.pageYOffset || document.documentElement.scrollTop;
    }
    onChange(parseInt(val));
  };
  
  return (
    <Card className="bg-gray-800 border-gray-700">
      <CardHeader>
        <CardTitle className="text-orange-500 flex items-center">
          {title}
          {required && <span className="text-red-400 ml-1">*</span>}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <RadioGroup value={value.toString()} onValueChange={handleChange}>
          {levels.map((level) => (
            <div 
              key={level.level} 
              className="flex items-start space-x-3 py-2"
              onMouseDown={(e) => {
                // Prevent focus that causes scroll
                if (scrollRef) {
                  scrollRef.current = window.pageYOffset || document.documentElement.scrollTop;
                }
              }}
            >
              <RadioGroupItem 
                value={level.level.toString()} 
                id={`${title}-${level.level}`}
              />
              <div className="flex-1">
                <Label 
                  htmlFor={`${title}-${level.level}`} 
                  className="text-white font-medium cursor-pointer"
                >
                  Level {level.level} - {level.label}
                </Label>
                <p className="text-sm text-gray-400 mt-1">{level.description}</p>
              </div>
            </div>
          ))}
        </RadioGroup>
      </CardContent>
    </Card>
  );
});

const HIRAList = ({ onAddNew, onEdit, onExCon, onEvent }) => {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedEntries, setExpandedEntries] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  // Lazy-loaded thumbnail cache keyed by entry.id → data URI
  const [thumbCache, setThumbCache] = useState({});
  // Super-Admin-only: list of agencies and the target we're bulk-assigning to
  const [agencies, setAgencies] = useState([]);
  const [bulkTargetOrgId, setBulkTargetOrgId] = useState('');
  const [bulkAssigning, setBulkAssigning] = useState(false);
  const [showMissingImages, setShowMissingImages] = useState(false);
  const [missingImageEntries, setMissingImageEntries] = useState([]);
  const [loadingMissing, setLoadingMissing] = useState(false);
  const isSuperAdmin = user?.role === 'Super Admin';

  // Fetch a thumbnail for every entry that has an image but no cache entry yet.
  useEffect(() => {
    const missing = entries.filter((e) => e.has_image && !thumbCache[e.id]);
    if (missing.length === 0) return;
    const token = secureStorage.getToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    let cancelled = false;
    (async () => {
      for (const entry of missing) {
        try {
          const { data } = await axios.get(`${API}/hira/${entry.id}/thumbnail`, { headers });
          if (cancelled) return;
          if (data && data.image) {
            setThumbCache((prev) => ({ ...prev, [entry.id]: data.image }));
          }
        } catch (err) {
          // Swallow individual thumbnail failures; the fallback icon still renders.
        }
      }
    })();
    return () => { cancelled = true; };
  }, [entries]);

  const toggleExpanded = (id) => {
    setExpandedEntries((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    fetchEntries();
  }, []);

  // Super Admin only: fetch agencies for the bulk-assign selector.
  useEffect(() => {
    if (!isSuperAdmin) return;
    const token = secureStorage.getToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    (async () => {
      try {
        const { data } = await axios.get(`${API}/agencies`, { headers });
        setAgencies(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error fetching agencies for HIRA bulk-assign:', err);
      }
    })();
  }, [isSuperAdmin]);

  // Super Admin only: fetch cross-agency list of HIRA entries missing hazard image.
  const fetchMissingImages = React.useCallback(async () => {
    if (!isSuperAdmin) return;
    const token = secureStorage.getToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    setLoadingMissing(true);
    try {
      const { data } = await axios.get(`${API}/hira/admin/missing-images`, { headers });
      setMissingImageEntries(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching HIRA missing-images report:', err);
      setMissingImageEntries([]);
    } finally {
      setLoadingMissing(false);
    }
  }, [isSuperAdmin]);
  useEffect(() => { fetchMissingImages(); }, [fetchMissingImages]);

  const handleBulkAssignUnassigned = async () => {
    if (!bulkTargetOrgId) {
      alert('Please pick a target agency first.');
      return;
    }
    const target = agencies.find((a) => a.id === bulkTargetOrgId);
    const unassignedCount = entries.filter((e) => !e.organization_id).length;
    if (!window.confirm(
      `Assign ${unassignedCount} unassigned HIRA ${unassignedCount === 1 ? 'entry' : 'entries'} to "${target?.name || bulkTargetOrgId}"? This makes them visible to that agency's users.`
    )) return;
    try {
      setBulkAssigning(true);
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const { data } = await axios.put(
        `${API}/hira/admin/bulk-assign-unassigned`,
        { organization_id: bulkTargetOrgId },
        { headers },
      );
      alert(data.message || `Assigned ${data.modified} entries`);
      await fetchEntries();
    } catch (err) {
      console.error('Bulk assign failed:', err);
      alert(err.response?.data?.detail || 'Bulk assign failed');
    } finally {
      setBulkAssigning(false);
    }
  };

  const fetchEntries = async () => {
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/hira`, { headers });
      setEntries(response.data);
    } catch (error) {
      console.error('Error fetching HIRA entries:', error);
      console.error('Error details:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const deleteEntry = async (entryId) => {
    if (window.confirm('Are you sure you want to delete this HIRA entry?')) {
      try {
        const token = secureStorage.getToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        await axios.delete(`${API}/hira/${entryId}`, { headers });
        setEntries(prev => prev.filter(e => e.id !== entryId));
      } catch (error) {
        console.error('Error deleting HIRA entry:', error);
        console.error('Error details:', error.response?.data);
        alert('Error deleting HIRA entry. Please try again.');
      }
    }
  };

  const getRiskLevel = (_frequency, _impact, entry) => {
    // Use shared HIRA risk algorithm — accepts the full entry so trend
    // checkboxes are included. Callers passing (frequency, impact) alone
    // still work: we synthesise a minimal entry.
    if (entry && typeof entry === 'object') return getHIRARiskLevel(entry);
    const synth = {
      frequency: _frequency,
      fatalities: _impact, injuries: 0, evacuation: 0, property_damage: 0,
      change_in_frequency: [], change_in_vulnerability: [],
    };
    return getHIRARiskLevel(synth);
  };

  // Sort entries by full risk score (highest risk first)
  const filteredEntries = entries.filter((e) => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    return (
      (e.name || '').toLowerCase().includes(q) ||
      (e.description || '').toLowerCase().includes(q) ||
      (e.disaster_type || '').toLowerCase().includes(q) ||
      (e.notes || '').toLowerCase().includes(q)
    );
  });
  const sortedEntries = [...filteredEntries].sort((a, b) => {
    const sa = calculateHIRARiskScore(a).total;
    const sb = calculateHIRARiskScore(b).total;
    return sb - sa;
  });

  const getInitials = (name) => {
    return name.split(' ').map(word => word.charAt(0)).join('').toUpperCase();
  };

  const escapeHtml = (str = '') =>
    String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const handlePrintReport = () => {
    if (!entries.length) {
      alert('No HIRA entries to print yet.');
      return;
    }
    const win = window.open('', '_blank');
    if (!win) {
      alert('Please allow pop-ups to print the HIRA report.');
      return;
    }
    const rows = sortedEntries.map((e, idx) => {
      const s = calculateHIRARiskScore(e);
      const lvl = getHIRARiskLevel(e);
      const yesFreq = Array.isArray(e.change_in_frequency) ? e.change_in_frequency.filter(v => v === true).length : 0;
      const yesVuln = Array.isArray(e.change_in_vulnerability) ? e.change_in_vulnerability.filter(v => v === true).length : 0;
      return `
        <div class="entry">
          <div class="entry-header">
            <div>
              <div class="entry-title">${idx + 1}. ${escapeHtml(e.name || 'Untitled')}</div>
              <div class="entry-subtitle">${escapeHtml(e.disaster_type || '')}${e.latitude && e.longitude ? ` &middot; ${escapeHtml(e.latitude)}, ${escapeHtml(e.longitude)}` : ''}</div>
            </div>
            <div class="risk-badge risk-${lvl.level.toLowerCase()}">
              ${lvl.level} &middot; ${s.total}
            </div>
          </div>
          ${e.description ? `<p class="desc">${escapeHtml(e.description)}</p>` : ''}
          <div class="grid">
            <div><span class="k">Frequency</span><span class="v">${e.frequency ?? 0}</span></div>
            <div><span class="k">Fatalities</span><span class="v">${e.fatalities ?? 0}</span></div>
            <div><span class="k">Injuries</span><span class="v">${e.injuries ?? 0}</span></div>
            <div><span class="k">Evacuation</span><span class="v">${e.evacuation ?? 0}</span></div>
            <div><span class="k">Property Damage</span><span class="v">${e.property_damage ?? 0}</span></div>
            <div><span class="k">Critical Infra.</span><span class="v">${e.critical_infrastructure ?? 0}</span></div>
            <div><span class="k">Environmental</span><span class="v">${e.environmental_damage ?? 0}</span></div>
            <div><span class="k">Business/Financial</span><span class="v">${e.business_financial_impact ?? 0}</span></div>
            <div><span class="k">Psychosocial</span><span class="v">${e.psychosocial_impact ?? 0}</span></div>
          </div>
          <div class="score-row">
            <div><span class="k">Impact</span><span class="v">${s.base}</span><span class="hint">sum of frequency + all 8 impact levels</span></div>
            <div><span class="k">Trend</span><span class="v">+${s.trend}</span><span class="hint">${s.yesCount} Yes across 8 change checkboxes (${yesFreq} freq &middot; ${yesVuln} vuln)</span></div>
            <div><span class="k">Total</span><span class="v total">${s.total}</span></div>
          </div>
        </div>
      `;
    }).join('');

    const counts = sortedEntries.reduce((acc, e) => {
      const l = getHIRARiskLevel(e).level.toLowerCase();
      acc[l] = (acc[l] || 0) + 1;
      return acc;
    }, {});

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>HIRA Report</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 32px; color: #222; }
            h1 { color: #ea580c; text-align: center; border-bottom: 3px solid #ea580c; padding-bottom: 8px; }
            .subtitle { text-align: center; color: #666; font-size: 13px; margin: 4px 0 20px; }
            .summary { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 24px; }
            .summary .cell { border: 1px solid #ddd; padding: 10px; border-radius: 6px; text-align: center; }
            .summary .cell .label { font-size: 11px; color: #666; text-transform: uppercase; }
            .summary .cell .value { font-size: 22px; font-weight: 600; }
            .entry { border: 1px solid #ddd; border-radius: 8px; padding: 16px; margin-bottom: 14px; page-break-inside: avoid; }
            .entry-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; }
            .entry-title { font-weight: 700; color: #ea580c; font-size: 15px; }
            .entry-subtitle { color: #777; font-size: 12px; margin-top: 2px; }
            .risk-badge { padding: 4px 10px; border-radius: 6px; font-weight: 700; font-size: 12px; white-space: nowrap; }
            .risk-critical { background: #fecaca; color: #991b1b; border: 1px solid #fca5a5; }
            .risk-high { background: #fed7aa; color: #9a3412; border: 1px solid #fdba74; }
            .risk-medium { background: #fef08a; color: #854d0e; border: 1px solid #fde047; }
            .risk-low { background: #bbf7d0; color: #14532d; border: 1px solid #86efac; }
            .desc { margin: 10px 0; color: #333; font-size: 13px; }
            .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px 16px; font-size: 12px; margin-top: 10px; padding-top: 10px; border-top: 1px dashed #eee; }
            .grid div { display: flex; justify-content: space-between; }
            .k { color: #666; }
            .v { font-weight: 600; }
            .score-row { display: grid; grid-template-columns: 1fr 1.5fr 0.8fr; gap: 12px; margin-top: 10px; padding-top: 10px; border-top: 1px solid #ea580c22; align-items: center; }
            .score-row .k { display: block; font-size: 10px; text-transform: uppercase; color: #666; }
            .score-row .v { display: block; font-size: 18px; font-weight: 700; color: #222; }
            .score-row .v.total { color: #ea580c; }
            .score-row .hint { display: block; font-size: 10px; color: #999; font-style: italic; }
            .footer { text-align: center; color: #999; font-size: 11px; margin-top: 24px; border-top: 1px solid #eee; padding-top: 8px; }
            @media print { body { padding: 16px; } .entry { page-break-inside: avoid; } }
          </style>
        </head>
        <body>
          <h1>Hazard Identification Risk Assessment</h1>
          <div class="subtitle">Generated ${escapeHtml(new Date().toLocaleString())} &middot; ${entries.length} total ${entries.length === 1 ? 'hazard' : 'hazards'}</div>
          <div class="summary">
            <div class="cell"><div class="label">Critical</div><div class="value" style="color:#991b1b">${counts.critical || 0}</div></div>
            <div class="cell"><div class="label">High</div><div class="value" style="color:#9a3412">${counts.high || 0}</div></div>
            <div class="cell"><div class="label">Medium</div><div class="value" style="color:#854d0e">${counts.medium || 0}</div></div>
            <div class="cell"><div class="label">Low</div><div class="value" style="color:#14532d">${counts.low || 0}</div></div>
          </div>
          ${rows}
          <div class="footer">
            EXRSIM &middot; Risk formula: Impact = sum of frequency + all 8 impact levels &middot;
            Trend = 1 × (# of Yes across 8 change checkboxes) &middot;
            Levels: Critical ≥ 32 &middot; High 20-31 &middot; Medium 10-19 &middot; Low &lt; 10
          </div>
        </body>
      </html>
    `;
    writeSanitizedHTML(win.document, html);
    win.focus();
    setTimeout(() => win.print(), 250);
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-orange-500 mb-2">HIRA - Hazard Identification Risk Assessment</h1>
          <p className="text-gray-400">Identify and assess emergency risks for your community</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handlePrintReport}
            variant="outline"
            className="border-gray-600 text-gray-200 hover:text-orange-400 hover:border-orange-500/50"
            data-testid="print-hira-report-btn"
          >
            <Printer className="h-4 w-4 mr-2" />
            Print HIRA Report
          </Button>
          <Button 
            onClick={onAddNew}
            className="bg-orange-500 hover:bg-orange-600 text-black font-semibold"
            data-testid="add-hira-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add HIRA Entry
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Card key={`pp-${i}`} className="bg-gray-800 border-gray-700">
              <CardContent className="p-4">
                <div className="space-y-2">
                  <div className="h-4 bg-gray-700 rounded animate-pulse w-1/3"></div>
                  <div className="h-3 bg-gray-700 rounded animate-pulse w-2/3"></div>
                  <div className="h-3 bg-gray-700 rounded animate-pulse w-1/2"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : entries.length === 0 ? (
        <Card className="bg-gray-800 border-gray-700 border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <AlertTriangle className="h-12 w-12 text-gray-500 mb-4" />
            <h3 className="text-lg font-semibold text-gray-300 mb-2">No HIRA Entries Yet</h3>
            <p className="text-gray-500 text-center mb-4">
              Start identifying and assessing hazards in your community to improve emergency preparedness.
            </p>
            <Button 
              onClick={onAddNew}
              className="bg-orange-500 hover:bg-orange-600 text-black font-semibold"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create First HIRA Entry
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* Search bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search hazards by name, description, type or notes…"
              className="pl-9 pr-9 bg-gray-800 border-gray-700 text-white placeholder-gray-500"
              data-testid="hira-search-input"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-orange-400 p-1"
                title="Clear search"
                data-testid="hira-search-clear"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-400">
              Showing {sortedEntries.length} of {entries.length} hazard{entries.length !== 1 ? 's' : ''}{searchQuery ? ` matching "${searchQuery}"` : ''} (sorted by risk level)
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setExpandedEntries(new Set(sortedEntries.map((e) => e.id)))}
                className="border-gray-600 text-gray-300 hover:text-orange-400 hover:border-orange-500/50"
                data-testid="hira-expand-all"
              >
                Expand All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setExpandedEntries(new Set())}
                className="border-gray-600 text-gray-300 hover:text-orange-400 hover:border-orange-500/50"
                data-testid="hira-collapse-all"
              >
                Collapse All
              </Button>
            </div>
          </div>
          {/* Super Admin only: helper — cross-agency list of HIRA entries missing hazard image */}
          {isSuperAdmin && missingImageEntries.length > 0 && (
            <Card className="bg-orange-500/10 border-orange-500/40" data-testid="hira-missing-images-banner">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center gap-3">
                  <AlertTriangle className="h-5 w-5 text-orange-400 flex-shrink-0" />
                  <div className="flex-1 min-w-[240px]">
                    <p className="text-sm text-orange-200 font-semibold">
                      {missingImageEntries.length} HIRA {missingImageEntries.length === 1 ? 'entry is' : 'entries are'} missing a hazard image
                    </p>
                    <p className="text-xs text-orange-100/70">
                      Cross-agency helper report. Open each entry to attach an image. Hazard cards without imagery use a generic fallback in ICS 202/205 briefings.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => setShowMissingImages((v) => !v)}
                    className="bg-orange-500 hover:bg-orange-600 text-black font-semibold"
                    data-testid="hira-missing-images-toggle"
                  >
                    {showMissingImages ? 'Hide list' : 'Show list'}
                  </Button>
                </div>
                {showMissingImages && (
                  <div className="mt-3 max-h-72 overflow-y-auto border border-orange-500/30 rounded" data-testid="hira-missing-images-list">
                    {loadingMissing && <div className="p-3 text-orange-200 text-sm">Loading…</div>}
                    {!loadingMissing && missingImageEntries.map((m) => (
                      <div key={m.id} className="flex items-center justify-between px-3 py-2 border-t border-orange-500/20 first:border-t-0 hover:bg-orange-500/5" data-testid={`hira-missing-row-${m.id}`}>
                        <div className="min-w-0 pr-2">
                          <div className="text-orange-100 text-sm font-medium truncate">{m.hazard || '(no name)'}</div>
                          <div className="text-orange-100/60 text-xs truncate">
                            {m.hazard_type || 'Uncategorised'} · {m.organization_name || 'Unassigned'}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setExpandedEntries((prev) => new Set(prev).add(m.id));
                            setTimeout(() => {
                              const el = document.querySelector(`[data-testid="hira-card-${m.id}"]`);
                              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            }, 50);
                          }}
                          className="border-orange-500/60 text-orange-200 hover:bg-orange-500/20"
                          data-testid={`hira-missing-open-${m.id}`}
                        >
                          Open
                        </Button>
                      </div>
                    ))}
                    {!loadingMissing && missingImageEntries.length === 0 && (
                      <div className="p-3 text-orange-200/70 text-sm">No entries missing images.</div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
          {/* Super Admin only: bulk-assign the unassigned legacy entries */}
          {isSuperAdmin && entries.some((e) => !e.organization_id) && (
            <Card className="bg-yellow-500/10 border-yellow-500/40" data-testid="hira-bulk-assign-banner">
              <CardContent className="p-4 flex flex-wrap items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-yellow-400 flex-shrink-0" />
                <div className="flex-1 min-w-[240px]">
                  <p className="text-sm text-yellow-200 font-semibold">
                    {entries.filter((e) => !e.organization_id).length} unassigned HIRA{' '}
                    {entries.filter((e) => !e.organization_id).length === 1 ? 'entry' : 'entries'}
                  </p>
                  <p className="text-xs text-yellow-100/70">
                    These pre-date agency scoping and are invisible to Agency Admin / User accounts. Pick an agency to migrate them into.
                  </p>
                </div>
                <div className="w-full sm:w-64">
                  <Select value={bulkTargetOrgId} onValueChange={setBulkTargetOrgId}>
                    <SelectTrigger className="bg-gray-800 border-gray-700 text-white" data-testid="hira-bulk-assign-select">
                      <SelectValue placeholder="Select target agency..." />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700 text-white">
                      {agencies.map((agency) => (
                        <SelectItem key={agency.id} value={agency.id}>{agency.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  size="sm"
                  disabled={!bulkTargetOrgId || bulkAssigning}
                  onClick={handleBulkAssignUnassigned}
                  className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold"
                  data-testid="hira-bulk-assign-btn"
                >
                  {bulkAssigning ? 'Assigning…' : 'Assign All Unassigned'}
                </Button>
              </CardContent>
            </Card>
          )}
          {sortedEntries.map((entry) => {
            const maxImpact = Math.max(entry.fatalities, entry.injuries, entry.evacuation, entry.property_damage);
            const riskLevel = getRiskLevel(entry.frequency, maxImpact, entry);
            const isExpanded = expandedEntries.has(entry.id);
            
            return (
              <Card key={entry.id} className="bg-gray-800 border-gray-700 hover:border-orange-500/50 transition-colors" data-testid={`hira-card-${entry.id}`}>
                <CardContent className={isExpanded ? 'p-6' : 'p-3'}>
                  <div className="flex items-center gap-3">
                    {/* Chevron toggle */}
                    <button
                      type="button"
                      onClick={() => toggleExpanded(entry.id)}
                      className="flex-shrink-0 text-gray-400 hover:text-orange-400 transition-colors"
                      title={isExpanded ? 'Collapse' : 'Expand'}
                      data-testid={`hira-toggle-${entry.id}`}
                    >
                      {isExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                    </button>
                    {/* Hazard Image thumbnail (lazy-loaded, falls back to icon) */}
                    <div className="flex-shrink-0" data-testid={`hira-thumb-${entry.id}`}>
                      <Avatar className={isExpanded ? 'w-16 h-16' : 'w-10 h-10'}>
                        <AvatarImage src={entry.hazard_image || thumbCache[entry.id]} alt={entry.name} />
                        <AvatarFallback className="bg-gray-600 text-orange-500 font-semibold">
                          <AlertTriangle className={isExpanded ? 'h-8 w-8' : 'h-5 w-5'} />
                        </AvatarFallback>
                      </Avatar>
                    </div>
                    
                    {/* Compact / expanded content */}
                    <div className="flex-1 min-w-0">
                      <button
                        type="button"
                        onClick={() => toggleExpanded(entry.id)}
                        className="w-full text-left"
                        data-testid={`hira-title-${entry.id}`}
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className={`${isExpanded ? 'text-xl' : 'text-base'} font-semibold text-white truncate`}>
                            {entry.name}
                          </h3>
                          <Badge className={riskLevel.color}>{riskLevel.level} Risk</Badge>
                          {isExpanded && (
                            <>
                              <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30">
                                {entry.disaster_type}
                              </Badge>
                              <span className="text-xs text-gray-500">Risk Score: {riskLevel.score}</span>
                            </>
                          )}
                          {!isExpanded && (
                            <span className="text-xs text-gray-500 truncate">
                              · {entry.disaster_type} · Score {riskLevel.score}
                            </span>
                          )}
                        </div>
                      </button>
                      
                      {isExpanded && (
                        <>
                          <p className="text-gray-400 mb-4 mt-2">{entry.description}</p>
                          
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <span className="text-orange-400 font-medium">Frequency:</span>
                              <span className="text-gray-300 ml-2">Level {entry.frequency}</span>
                            </div>
                            <div>
                              <span className="text-red-400 font-medium">Max Impact:</span>
                              <span className="text-gray-300 ml-2">Level {maxImpact}</span>
                            </div>
                            {entry.latitude && entry.longitude && (
                              <div>
                                <span className="text-green-400 font-medium">Location:</span>
                                <span className="text-gray-300 ml-2">{entry.latitude.toFixed(2)}, {entry.longitude.toFixed(2)}</span>
                              </div>
                            )}
                            <div>
                              <span className="text-purple-400 font-medium">Created:</span>
                              <span className="text-gray-300 ml-2">{new Date(entry.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                          
                          {entry.notes && (
                            <div className="mt-3 p-3 bg-gray-700/50 rounded">
                              <span className="text-blue-400 font-medium text-sm">Notes: </span>
                              <span className="text-gray-300 text-sm">{entry.notes}</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    
                    {/* Action Buttons */}
                    <div className={`flex ${isExpanded ? 'flex-col space-y-2' : 'flex-row space-x-2'} flex-shrink-0`}>
                      {/* ExCon and Event Buttons */}
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onExCon(entry)}
                          className="border-orange-500 text-orange-500 hover:bg-orange-500/10 hover:text-orange-400"
                          title="Exercise Control Builder"
                        >
                          <Settings className="h-4 w-4 mr-1" />
                          ExCon
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onEvent(entry)}
                          className="border-red-500 text-red-500 hover:bg-red-500/10 hover:text-red-400"
                          title="Real Event Builder"
                        >
                          <AlertTriangle className="h-4 w-4 mr-1" />
                          Real Event
                        </Button>
                      </div>
                      {/* Edit and Delete Buttons */}
                      <div className="flex space-x-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onEdit(entry)}
                          className="border-gray-600 text-gray-300 hover:text-orange-500 hover:border-orange-500/50"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => deleteEntry(entry.id)}
                          className="border-red-600 text-red-400 hover:text-red-300 hover:border-red-500/50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

const HIRAView = () => {
  const [view, setView] = useState('list'); // 'list', 'add', 'edit', 'excon', 'event'
  const [editingEntry, setEditingEntry] = useState(null);
  const [selectedHiraEntry, setSelectedHiraEntry] = useState(null);

  const handleAddNew = () => {
    setEditingEntry(null);
    setView('add');
  };

  const handleEdit = async (entry) => {
    // The HIRA list endpoint strips base64 hazard_images (Cloudflare 520 guard),
    // so the entry we got from the list has hazard_image=null. Refetch the
    // full single entry so the edit form sees the real image and does not
    // wipe it on the next save.
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/hira/${entry.id}`, { headers });
      setEditingEntry(response.data);
    } catch (error) {
      console.error('Error fetching full HIRA entry for edit:', error);
      // Fall back to the list snapshot so the user can still edit metadata.
      setEditingEntry(entry);
    }
    setView('edit');
  };

  const handleExCon = (entry) => {
    setSelectedHiraEntry(entry);
    setView('excon');
  };

  const handleEvent = (entry) => {
    setSelectedHiraEntry(entry);
    setView('event');
  };

  const handleBack = () => {
    setView('list');
    setEditingEntry(null);
    setSelectedHiraEntry(null);
  };

  const handleSave = () => {
    setView('list');
    setEditingEntry(null);
    // The list will automatically refresh via useEffect
  };

  if (view === 'excon') {
    // Retired the standalone 16-step ExConBuilder — the ExCon flow now reuses the
    // same 17-step ExerciseBuilderWizard as "Edit Exercise" for consistency.
    // We synthesise a blank exercise seeded from the HIRA entry; the wizard's
    // first Save is a POST (create), subsequent saves are PUTs (update).
    const seed = selectedHiraEntry
      ? {
          exercise_image: null,
          exercise_name: `ExCon - ${selectedHiraEntry.name || ''}`.trim(),
          exercise_type: '',
          exercise_description: `Exercise Control for ${selectedHiraEntry.name || ''}${selectedHiraEntry.disaster_type ? ' - ' + selectedHiraEntry.disaster_type : ''}`.trim(),
          location: '',
          latitude:
            selectedHiraEntry.latitude != null && !isNaN(parseFloat(selectedHiraEntry.latitude))
              ? parseFloat(selectedHiraEntry.latitude).toFixed(4)
              : '',
          longitude:
            selectedHiraEntry.longitude != null && !isNaN(parseFloat(selectedHiraEntry.longitude))
              ? parseFloat(selectedHiraEntry.longitude).toFixed(4)
              : '',
          scenario_name: selectedHiraEntry.name || '',
          scenario_description: selectedHiraEntry.description || '',
          scope_hazards: selectedHiraEntry.name || '',
          hira_id: selectedHiraEntry.id,
        }
      : null;
    return (
      <ExerciseBuilderWizard
        onBack={handleBack}
        editingExercise={seed}
        LeafletMapping={LeafletMapping}
        MSELView={MSELView}
      />
    );
  }

  if (view === 'event') {
    return (
      <EventBuilder
        onBack={handleBack}
        hiraEntry={selectedHiraEntry}
      />
    );
  }

  if (view === 'add' || view === 'edit') {
    return (
      <HIRAForm
        key={editingEntry?.id || 'new'}
        onBack={handleBack}
        onSave={handleSave}
        editingEntry={editingEntry}
      />
    );
  }

  return (
    <HIRAList
      onAddNew={handleAddNew}
      onEdit={handleEdit}
      onExCon={handleExCon}
      onEvent={handleEvent}
    />
  );
};

// Exercise Builder Step-by-Step Wizard
const ExerciseBuilder = () => {
  const [editingExercise, setEditingExercise] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialStep, setInitialStep] = useState(null);
  const [returnTo, setReturnTo] = useState(null);

  useEffect(() => {
    // Check URL parameters for exercise ID to edit
    // Handle parameters in hash format: #builder?exercise=<id>&step=<num>&returnTo=<view>
    const hash = window.location.hash;
    const queryStart = hash.indexOf('?');
    let exerciseId = null;
    
    if (queryStart !== -1) {
      const queryString = hash.substring(queryStart + 1);
      const urlParams = new URLSearchParams(queryString);
      exerciseId = urlParams.get('exercise');
      const stepParam = urlParams.get('step');
      const returnToParam = urlParams.get('returnTo');
      if (stepParam) setInitialStep(parseInt(stepParam));
      if (returnToParam) setReturnTo(returnToParam);
    }
    
    if (exerciseId) {
      setLoading(true);
      fetchExerciseForEdit(exerciseId);
    }
  }, []);

  const fetchExerciseForEdit = async (exerciseId) => {
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/exercise-builder/${exerciseId}`, { headers });
      setEditingExercise(response.data);
    } catch (error) {
      console.error('Error fetching exercise for edit:', error);
      console.error('Error details:', error.response?.data);
      alert('Error loading exercise for editing. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading exercise for editing...</p>
        </div>
      </div>
    );
  }

  return (
    <ExerciseBuilderWizard 
      onBack={() => {
        if (returnTo === 'excon-dashboard') {
          window.location.hash = '#excon-dashboard';
        } else {
          window.history.back();
        }
      }} 
      editingExercise={editingExercise}
      initialStep={initialStep}
      returnTo={returnTo}
      LeafletMapping={LeafletMapping}
      MSELView={MSELView}
    />
  );
};

// Scope Modal Component
const ScopeModal = ({ isOpen, onClose, onSave, initialData = null, exerciseId }) => {
  const { token } = useAuth(); // Get token from auth context
  const [scopeData, setScopeData] = useState({
    scope_description: '',
    scope_hazards: '',
    scope_geographic_area: '',
    scope_functions: '',
    scope_organizations: '',
    scope_personnel: ''
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (initialData) {
      setScopeData({
        scope_description: initialData.scope_description || '',
        scope_hazards: initialData.scope_hazards || '',
        scope_geographic_area: initialData.scope_geographic_area || '',
        scope_functions: initialData.scope_functions || '',
        scope_organizations: initialData.scope_organizations || '',
        scope_personnel: initialData.scope_personnel || ''
      });
    } else {
      setScopeData({
        scope_description: '',
        scope_hazards: '',
        scope_geographic_area: '',
        scope_functions: '',
        scope_organizations: '',
        scope_personnel: ''
      });
    }
  }, [initialData, isOpen]);

  const handleSave = async () => {
    console.log('🚀 ScopeModal handleSave called');
    console.log('📊 Current scopeData:', scopeData);
    console.log('🆔 Exercise ID:', exerciseId);
    
    setLoading(true);
    try {
      console.log('📡 Fetching current exercise data...');
      
      // First fetch the current exercise data to get all required fields
      const currentExerciseResponse = await axios.get(`${API}/exercise-builder/${exerciseId}`);
      const currentExercise = currentExerciseResponse.data;
      
      console.log('✅ Current exercise fetched:', currentExercise);
      
      // Merge the scope data with the existing exercise data
      // Ensure required fields have valid values
      const updatePayload = {
        ...currentExercise,
        // Provide defaults for required fields if they're empty
        exercise_type: currentExercise.exercise_type || 'Table Top',
        location: currentExercise.location || 'Not specified',
        start_time: currentExercise.start_time || '09:00',
        end_time: currentExercise.end_time || '17:00',
        // Update scope fields
        scope_description: scopeData.scope_description,
        scope_hazards: scopeData.scope_hazards,
        scope_geographic_area: scopeData.scope_geographic_area,
        scope_functions: scopeData.scope_functions,
        scope_organizations: scopeData.scope_organizations,
        scope_personnel: scopeData.scope_personnel
      };
      
      console.log('📦 Update payload prepared:', updatePayload);
      console.log('🔄 Sending PUT request to:', `${API}/exercise-builder/${exerciseId}`);
      
      const response = await axios.put(
        `${API}/exercise-builder/${exerciseId}`, 
        updatePayload,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      console.log('✅ Save successful! Response:', response.data);
      
      onSave(response.data);
      onClose();
    } catch (error) {
      console.error('❌ Error saving scope:', error);
      console.error('❌ Error details:', error.response?.data || error.message);
      // Show error message to user
      alert(`Error saving scope data: ${error.response?.data?.detail || error.message}. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-gray-800 border border-gray-700 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h2 className="text-2xl font-bold text-orange-500">
            {initialData ? 'Edit Exercise Scope' : 'Add Exercise Scope'}
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <Label className="text-gray-300">Scope Description *</Label>
            <Textarea
              value={scopeData.scope_description}
              onChange={(e) => setScopeData(prev => ({ ...prev, scope_description: e.target.value }))}
              className="bg-gray-700 border-gray-600 text-white mt-2"
              rows={3}
              placeholder="Describe the overall scope of this exercise..."
              required
            />
          </div>

          <div>
            <Label className="text-gray-300">Organizations</Label>
            <Textarea
              value={scopeData.scope_organizations}
              onChange={(e) => setScopeData(prev => ({ ...prev, scope_organizations: e.target.value }))}
              className="bg-gray-700 border-gray-600 text-white mt-2"
              rows={2}
              placeholder="List the organizations and agencies participating..."
            />
          </div>

          <div>
            <Label className="text-gray-300">Personnel</Label>
            <Textarea
              value={scopeData.scope_personnel}
              onChange={(e) => setScopeData(prev => ({ ...prev, scope_personnel: e.target.value }))}
              className="bg-gray-700 border-gray-600 text-white mt-2"
              rows={2}
              placeholder="Describe the personnel and roles involved..."
            />
          </div>
        </div>

        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-700">
          <Button
            variant="outline"
            onClick={onClose}
            className="border-gray-600 text-gray-300 hover:bg-gray-700"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading}
            className="bg-orange-500 hover:bg-orange-600 text-black"
          >
            {loading ? 'Saving...' : initialData ? 'Update Scope' : 'Add Scope'}
          </Button>
        </div>
      </div>
    </div>
  );
};


const ExerciseManagementDashboard = ({ 
  exerciseId, 
  scribeTemplates, 
  currentTemplate, 
  scribeFormData, 
  scribeFormLoading, 
  scribeTimeErrors,
  setCurrentTemplate, 
  setScribeFormData, 
  setScribeFormLoading, 
  setScribeTimeErrors,
  loadScribeTemplates 
}) => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const [exercise, setExercise] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const { locked: exerciseLocked, lockedByName: lockedBy, lockedAt: lockedWhen } = useExerciseLock(exerciseId);
  const canManageLock = user?.role === 'Super Admin' || user?.role === 'Agency Admin';
  
  // Sidebar collapse state
  const [exerciseSidebarCollapsed, setExerciseSidebarCollapsed] = useState(false);
  
  // Modal states
  const [scopeModalOpen, setScopeModalOpen] = useState(false);
  const [editingScope, setEditingScope] = useState(null);
  
  // Exercise Steps menu expansion state
  const [exerciseStepsExpanded, setExerciseStepsExpanded] = useState(false);
  
  // Improvement menu expansion state
  const [improvementExpanded, setImprovementExpanded] = useState(false);

  // Legacy ICS interface state (used by renderICSInterface below)
  const [activeICSMenu, setActiveICSMenu] = useState('dashboard');
  const [planningExpanded, setPlanningExpanded] = useState(false);
  const [resourcesUnitExpanded, setResourcesUnitExpanded] = useState(false);
  const [generalMessages, setGeneralMessages] = useState([]);
  const [positions, setPositions] = useState([]);

  // Evaluation state
  const [evaluationReports, setEvaluationReports] = useState([]);
  const [showAddEvaluation, setShowAddEvaluation] = useState(false);
  const [editingEvaluation, setEditingEvaluation] = useState(null);
  const [evaluationLoading, setEvaluationLoading] = useState(false);
  const [selectedEvaluationId, setSelectedEvaluationId] = useState(null);

  // Lessons Learned state
  const [lessonsLearned, setLessonsLearned] = useState([]);
  const [showAddLesson, setShowAddLesson] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [lessonsLoading, setLessonsLoading] = useState(false);
  const [selectedLessonId, setSelectedLessonId] = useState(null);
  const [expandedLessons, setExpandedLessons] = useState(() => new Set());

  // Locations state (shared with Participants and Resources)
  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);

  // Participants state for counting
  const [exerciseParticipants, setExerciseParticipants] = useState([]);

  // MSEL Injections state for Events count
  const [mselInjections, setMselInjections] = useState([]);

  // ICS Objectives count
  const [icsObjectivesCount, setIcsObjectivesCount] = useState(0);
  // Resources count
  const [exerciseResources, setExerciseResources] = useState([]);
  // Agency Coordination count (from Liaison Officer)
  const [agencyCoordinationCount, setAgencyCoordinationCount] = useState(0);

  // ExCon injection visibility — only ExCon team or Super Admin can see injections
  const [canViewInjections, setCanViewInjections] = useState(false);

  useEffect(() => {
    if (exerciseId) {
      fetchExercise();
      fetchMselInjections();
      fetchExerciseParticipants();
      fetchIcsObjectives();
      fetchExerciseResources();
      fetchAgencyCoordinationCount();
    }
  }, [exerciseId]);

  // Check if current user can view injections (ExCon team or Super Admin)
  useEffect(() => {
    const checkInjectionAccess = async () => {
      // Super Admin always has access
      if (user?.role === 'Super Admin') {
        setCanViewInjections(true);
        return;
      }
      if (!user || !exerciseId) return;

      try {
        const token = secureStorage.getToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/participants`, { headers });
        const participants = response.data || [];

        const exconPositions = ['exercise director', 'exercise evaluator', 'exercise observer'];

        // Check if this user has an ExCon position assigned to this exercise
        const userFullName = `${user.first_name || ''} ${user.last_name || ''}`.trim().toLowerCase();
        const userEmail = (user.email || '').toLowerCase();

        const isExconMember = participants.some(p => {
          const pName = (p.name || '').toLowerCase();
          const pEmail = (p.email || '').toLowerCase();
          const pPosition = (p.position || '').toLowerCase();
          const pAssigned = p.assigned_exercise || '';
          return (
            (pEmail === userEmail || pName === userFullName) &&
            pAssigned === exerciseId &&
            exconPositions.includes(pPosition)
          );
        });

        setCanViewInjections(isExconMember);
      } catch (error) {
        console.error('Error checking injection access:', error);
        setCanViewInjections(false);
      }
    };

    checkInjectionAccess();
  }, [user, exerciseId]);

  // Fetch MSEL injections for Events count
  const fetchMselInjections = async () => {
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/msel/${exerciseId}`, { headers });
      const injections = response.data || [];
      const sortedInjections = injections.sort((a, b) => a.event_number - b.event_number);
      setMselInjections(sortedInjections);
    } catch (error) {
      console.error('Error fetching MSEL injections for count:', error);
      setMselInjections([]);
    }
  };

  // Fetch participants for count - filter by assigned_exercise matching exerciseId
  const fetchExerciseParticipants = async () => {
    if (!exerciseId) return;
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/participants`, { headers });
      const allParticipants = response.data || [];
      // Filter participants where assigned_exercise matches current exerciseId AND involvedInExercise is true
      const filtered = allParticipants.filter(p => 
        p.involvedInExercise === true && 
        p.assigned_exercise === exerciseId
      );
      setExerciseParticipants(filtered);
    } catch (error) {
      console.error('Error fetching participants:', error);
      setExerciseParticipants([]);
    }
  };

  // Re-fetch participants when exerciseId changes
  useEffect(() => {
    if (exerciseId) {
      fetchExerciseParticipants();
    }
  }, [exerciseId]);

  // Fetch ICS Objectives count
  const fetchIcsObjectives = async () => {
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/hierarchical-objectives?exercise_id=${exerciseId}`, { headers });
      const data = Array.isArray(response.data) ? response.data : [];
      setIcsObjectivesCount(data.length);
    } catch (error) {
      console.error('Error fetching ICS objectives:', error);
      setIcsObjectivesCount(0);
    }
  };

  // Fetch exercise resources count
  const fetchExerciseResources = async () => {
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/exercises/${exerciseId}/resources`, { headers });
      setExerciseResources(response.data || []);
    } catch (error) {
      console.error('Error fetching resources:', error);
      setExerciseResources([]);
    }
  };

  // Fetch agency coordination count from Liaison Officer
  const fetchAgencyCoordinationCount = async () => {
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/liaison-officer?exercise_id=${exerciseId}`, { headers });
      const entries = response.data || [];
      const agencyEntries = entries.filter(e => e.category === 'agency-coordination');
      setAgencyCoordinationCount(agencyEntries.length);
    } catch (error) {
      console.error('Error fetching agency coordination:', error);
      setAgencyCoordinationCount(0);
    }
  };

  const fetchExercise = async () => {
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/exercise-builder/${exerciseId}`, { headers });
      setExercise(response.data);
    } catch (error) {
      console.error('Error fetching exercise:', error);
      console.error('Error details:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const fetchEvaluationReports = async () => {
    try {
      setEvaluationLoading(true);
      const response = await axios.get(`${API}/evaluation-reports/exercise/${exerciseId}`);
      setEvaluationReports(response.data);
    } catch (error) {
      console.error('Error fetching evaluation reports:', error);
    } finally {
      setEvaluationLoading(false);
    }
  };
  const fetchLessonsLearned = async () => {
    try {
      setLessonsLoading(true);
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/lessons-learned/exercise/${exerciseId}`, { headers });
      setLessonsLearned(response.data);
      // Every fetch resets to fully collapsed view (per Steve's UX request)
      setExpandedLessons(new Set());
    } catch (error) {
      console.error('Error fetching lessons learned:', error);
    } finally {
      setLessonsLoading(false);
    }
  };

  const fetchMapMarkers = async () => {
    setLocationsLoading(true);
    try {
      const url = exerciseId 
        ? `${API}/map-objects?type=marker&exercise_id=${exerciseId}`
        : `${API}/map-objects?type=marker`;
      const response = await axios.get(url);
      setLocations(response.data.map(marker => ({ id: marker.id, name: marker.name })));
    } catch (error) {
      console.error('Error fetching map markers:', error);
      setLocations([]);
    } finally {
      setLocationsLoading(false);
    }
  };

  // Load evaluation reports, lessons learned, and locations when exercise is loaded
  useEffect(() => {
    if (exerciseId) {
      fetchExercise();
      fetchEvaluationReports();
      fetchLessonsLearned();
      fetchMapMarkers();
    }
  }, [exerciseId]);

  const topLevelMenuItems = [
    { id: 'overview', title: 'Exercise Overview', icon: Shield },
    ...(user?.role === 'Super Admin' ? [{ id: 'audit_trail', title: 'Audit Trail', icon: Eye }] : []),
  ];

  const exerciseStepsMenuItems = [
    { id: 'scope', title: 'Scope', icon: Target },
    { id: 'purpose', title: 'Purpose', icon: Flag },
    { id: 'scenario', title: 'Scenario', icon: Map },
    { id: 'goals', title: 'Goals', icon: Trophy },
    { id: 'events', title: 'Events', icon: Calendar },
    { id: 'functions', title: 'Functions', icon: Settings },
    ...(canViewInjections ? [{ id: 'injections', title: 'Injections (MSEL)', icon: ClipboardList }] : []),
    { id: 'organizations', title: 'Organizations', icon: Building },
    { id: 'coordinators', title: 'Team Coordinators', icon: Users },
    { id: 'codewords', title: 'Code Words', icon: Key },
    { id: 'callsigns', title: 'Callsigns', icon: Radio },
    { id: 'frequencies', title: 'Communications', icon: Headphones },
    { id: 'assumptions', title: 'Assumptions', icon: MessageSquare },
    { id: 'artificialities', title: 'Artificialities', icon: AlertTriangle },
    { id: 'safety', title: 'Safety Concerns', icon: ShieldAlert },
  ];

  const improvementMenuItems = [
    { id: 'evaluations', title: 'Evaluations', icon: Star },
    { id: 'lessons_learned', title: 'Lessons Learned', icon: Lightbulb },
    ...(user?.role === 'Super Admin' ? [{ id: 'audit_trail', title: 'Audit Trail', icon: Eye }] : [])
  ];

  // Combined for compatibility with existing code
  const exerciseMenuItems = [
    ...topLevelMenuItems,
    { id: 'exercise', title: 'Exercise Details', icon: FileText },
    { id: 'objectives', title: 'Objectives', icon: CheckCircle },
    ...exerciseStepsMenuItems,
    ...improvementMenuItems
  ];

  const getExerciseStatus = () => {
    if (!exercise) return 'Unknown';
    
    // Check if all 18 steps are completed
    const isExerciseBuilderComplete = exercise.goals && exercise.goals.length > 0 &&
                                      exercise.objectives && exercise.objectives.length > 0 &&
                                      exercise.events && exercise.events.length > 0;
    
    const now = new Date();
    const startDate = new Date(exercise.start_date);
    const endDate = new Date(exercise.end_date);
    
    // If exercise builder is complete but exercise hasn't started yet
    if (isExerciseBuilderComplete && now < startDate) return 'Completed';
    
    // Original date-based status
    if (now < startDate) return 'Planning';
    if (now >= startDate && now <= endDate) return 'Active';
    if (now > endDate) return 'Completed';
    return 'Scheduled';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Planning': return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
      case 'Active': return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'Completed': return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      case 'Scheduled': return 'bg-purple-500/20 text-purple-300 border-purple-500/30';
      default: return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
    }
  };

  const handlePrintCompleteExercise = async () => {
    if (!exercise) return;
    
    try {
      console.log('Printing complete exercise from overview...');
      console.log('Exercise ID:', exercise.id);
      
      // Fetch MSEL data from separate collection
      let injections = [];
      
      console.log('=== FETCHING MSEL INJECTIONS FOR PRINT ===');
      console.log('API URL:', `${API}/msel/${exercise.id}`);
      
      try {
        const mselResponse = await axios.get(`${API}/msel/${exercise.id}`);
        injections = mselResponse.data || [];
        console.log('✅ Fetched MSEL injections:', injections.length, 'events');
        console.log('Injections data:', injections);
      } catch (error) {
        console.error('❌ Error fetching MSEL:', error);
        console.warn('⚠️ Continuing without MSEL data');
        injections = [];
      }
      
      // Merge MSEL data with exercise data (exclude injections if user cannot view them)
      const exerciseWithMSEL = {
        ...exercise,
        injections: canViewInjections ? injections : []
      };
      
      console.log('Exercise data with MSEL:', exerciseWithMSEL);
      
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        alert('Please allow pop-ups to print the exercise report');
        return;
      }
      
      // Generate print content with MSEL data
      let printContent = '';
      try {
        printContent = generateExercisePrintContent(exerciseWithMSEL);
      } catch (contentError) {
        console.error('Error generating print content:', contentError);
        printWindow.close();
        alert('Error generating report content: ' + contentError.message);
        return;
      }
      
      console.log('Content generated, writing to window...');
      writeSanitizedHTML(printWindow.document, printContent);
      console.log('Content written, triggering print...');
      
      setTimeout(() => {
        printWindow.print();
      }, 250);
    } catch (error) {
      console.error('Error in print function:', error);
      alert('Error generating print report: ' + error.message);
    }
  };
  
  const generateExercisePrintContent = (exerciseData) => {
    // Extract arrays from exercise data (matches 17-step wizard schema)
    const goals = exerciseData.goals || [];
    const functions = exerciseData.functions || [];
    const injections = exerciseData.injections || [];
    const organizations = exerciseData.organizations || [];
    const exerciseSites = exerciseData.exercise_sites || [];
    const coordinators = exerciseData.coordinators || [];
    const codeWords = exerciseData.codeWords || exerciseData.code_words || [];
    const callsigns = exerciseData.callsigns || [];
    const frequencies = exerciseData.frequencies || [];
    const assumptions = exerciseData.assumptions || [];
    const artificialities = exerciseData.artificialities || [];
    const safetyConcerns = exerciseData.safetyConcerns || exerciseData.safety_concerns || [];
    const standardsOfConduct = exerciseData.standardsOfConduct || exerciseData.standards_of_conduct || [];
    
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${exerciseData.exercise_name || 'Exercise Report'}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; color: #333; }
            h1 { color: #ea580c; text-align: center; border-bottom: 3px solid #ea580c; padding-bottom: 10px; }
            h2 { color: #ea580c; margin-top: 30px; border-bottom: 2px solid #ea580c; padding-bottom: 5px; }
            h3 { color: #666; margin-top: 20px; }
            .section { margin-bottom: 30px; page-break-inside: avoid; }
            .field { margin: 10px 0; }
            .label { font-weight: bold; color: #555; }
            .value { margin-left: 10px; }
            .list-item { margin: 15px 0; padding: 10px; background: #f9f9f9; border-left: 3px solid #ea580c; }
            @media print { 
              body { padding: 20px; }
              .section { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <h1>${exerciseData.exercise_name || 'Exercise Report'}</h1>
          
          <div class="section">
            <h2>Step 1: Exercise Details</h2>
            <div class="field"><span class="label">Exercise Name:</span> ${exerciseData.exercise_name || 'N/A'}</div>
            <div class="field"><span class="label">Exercise Type:</span> ${exerciseData.exercise_type || 'N/A'}</div>
            <div class="field"><span class="label">Description:</span> ${exerciseData.exercise_description || 'N/A'}</div>
            <div class="field"><span class="label">Location:</span> ${exerciseData.location || 'N/A'}</div>
            <div class="field"><span class="label">Latitude:</span> ${exerciseData.latitude || 'N/A'}</div>
            <div class="field"><span class="label">Longitude:</span> ${exerciseData.longitude || 'N/A'}</div>
            <div class="field"><span class="label">Zoom Level:</span> ${exerciseData.zoom_level || 'N/A'}</div>
            <div class="field"><span class="label">Start Date:</span> ${exerciseData.start_date || 'N/A'}</div>
            <div class="field"><span class="label">Start Time:</span> ${exerciseData.start_time || 'N/A'}</div>
            <div class="field"><span class="label">End Date:</span> ${exerciseData.end_date || 'N/A'}</div>
            <div class="field"><span class="label">End Time:</span> ${exerciseData.end_time || 'N/A'}</div>
            ${exerciseData.exercise_image ? `<div class="field" style="margin-top: 12px;"><img src="${exerciseData.exercise_image}" alt="Exercise" style="max-width: 480px; max-height: 320px; border-radius: 6px; border: 1px solid #ccc;"/></div>` : ''}
          </div>

          <div class="section">
            <h2>Step 2: Scenario</h2>
            <div class="field"><span class="label">Scenario Name:</span> ${exerciseData.scenario_name || 'N/A'}</div>
            <div class="field"><span class="label">Description:</span> ${exerciseData.scenario_description || 'N/A'}</div>
          </div>

          <div class="section">
            <h2>Step 3: Purpose</h2>
            <div class="field">${exerciseData.purpose_description || 'N/A'}</div>
          </div>

          <div class="section">
            <h2>Step 4: Scope</h2>
            <div class="field"><span class="label">Description:</span> ${exerciseData.scope_description || 'N/A'}</div>
            <div class="field"><span class="label">Hazards:</span> ${exerciseData.scope_hazards || 'N/A'}</div>
            <div class="field"><span class="label">Geographic Area:</span> ${exerciseData.scope_geographic_area || 'N/A'}</div>
            <div class="field"><span class="label">Functions:</span> ${exerciseData.scope_functions || 'N/A'}</div>
            <div class="field"><span class="label">Exercise Type:</span> ${exerciseData.scope_exercise_type || 'N/A'}</div>
          </div>

          <div class="section">
            <h2>Step 5: Goals &amp; Objectives</h2>
            ${goals.length > 0 ? goals.map((goal, idx) => {
              const objs = Array.isArray(goal.objectives) ? goal.objectives : [];
              const legacy = (goal.description || '').trim();
              const rows = objs.length > 0
                ? objs.map((o, i) => {
                    const n = (o.name || '').trim();
                    const d = (o.description || '').trim();
                    const nameHtml = n ? `<strong>${n}</strong>` : '<em style="color:#888;">(unnamed)</em>';
                    const descHtml = d ? `<div style="margin-top:2px;font-size:12px;color:#444;">${d}</div>` : '';
                    const ach = o.achieved ? ` <em style="color:#888;">(${o.achieved})</em>` : '';
                    return `<li style="margin-bottom:4px;"><span class="label">${i + 1}.</span> ${nameHtml}${ach}${descHtml}</li>`;
                  }).join('')
                : (legacy ? `<li>${legacy}</li>` : '');
              return `
                <div class="list-item">
                  <strong>Goal ${idx + 1}:</strong> ${goal.name || 'N/A'}
                  ${goal.broad_statement ? `<br/><span class="label">Broad Statement:</span> ${goal.broad_statement}` : ''}
                  ${rows ? `<br/><span class="label">Objectives:</span><ul style="margin:4px 0 0 22px; padding:0;">${rows}</ul>` : ''}
                </div>
              `;
            }).join('') : '<p>No goals added</p>'}
          </div>

          <div class="section">
            <h2>Step 6: Functions</h2>
            ${functions.length > 0 ? functions.map((func, idx) => `
              <div class="list-item">
                <strong>Function ${idx + 1}:</strong> ${func.name || 'N/A'}<br/>
                <span class="label">Description:</span> ${func.description || 'N/A'}
              </div>
            `).join('') : '<p>No functions added</p>'}
          </div>

          <div class="section">
            <h2>Step 7: Injections (MSEL)</h2>
            ${injections.length > 0 ? injections.map((inj, idx) => `
              <div class="list-item">
                <strong>Injection ${idx + 1}:</strong><br/>
                <span class="label">Event Number:</span> ${inj.event_number || 'N/A'}<br/>
                <span class="label">Scenario Time:</span> ${inj.scenario_time || 'N/A'}<br/>
                <span class="label">Event Type:</span> ${inj.event_type || 'N/A'}<br/>
                <span class="label">Inject Mode:</span> ${inj.inject_mode || 'N/A'}<br/>
                <span class="label">From:</span> ${inj.from_entity || 'N/A'}<br/>
                <span class="label">To:</span> ${inj.to_entity || 'N/A'}<br/>
                <span class="label">Message:</span> ${inj.message || 'N/A'}<br/>
                <span class="label">Expected Response:</span> ${inj.expected_response || 'N/A'}<br/>
                <span class="label">Objective/Capability/Task:</span> ${inj.objective_capability_task || 'N/A'}
              </div>
            `).join('') : '<p>No injections added</p>'}
          </div>

          <div class="section">
            <h2>Step 8: Organizations</h2>
            ${organizations.length > 0 ? organizations.map((org, idx) => `
              <div class="list-item">
                <strong>Organization ${idx + 1}:</strong> ${org.name || 'N/A'}<br/>
                <span class="label">Description:</span> ${org.description || 'N/A'}<br/>
                <span class="label">Home Base:</span> ${org.home_base || 'N/A'}<br/>
                <span class="label">Latitude:</span> ${org.latitude || 'N/A'} &nbsp; <span class="label">Longitude:</span> ${org.longitude || 'N/A'}<br/>
                <span class="label">Contact:</span> ${(org.contact_first_name || '') + ' ' + (org.contact_last_name || '') || 'N/A'}<br/>
                <span class="label">Phone:</span> ${org.contact_phone || 'N/A'} &nbsp; <span class="label">Email:</span> ${org.contact_email || 'N/A'}
                ${org.organization_image ? `<br/><img src="${org.organization_image}" alt="Org" style="max-width: 240px; margin-top: 6px; border-radius: 4px;"/>` : ''}
              </div>
            `).join('') : '<p>No organizations added</p>'}
          </div>

          <div class="section">
            <h2>Step 9: Exercise Sites</h2>
            ${exerciseSites.length > 0 ? exerciseSites.map((site, idx) => `
              <div class="list-item">
                <strong>Site ${idx + 1}:</strong> ${site.proposed_location || 'N/A'}<br/>
                <span class="label">Exercise Function:</span> ${site.exercise_function || 'N/A'}<br/>
                <span class="label">Participant Guidance:</span> ${site.participant_guidance || 'N/A'}
                ${site.image ? `<br/><img src="${site.image}" alt="Site" style="max-width: 320px; margin-top: 6px; border-radius: 4px;"/>` : ''}
              </div>
            `).join('') : '<p>No exercise sites added</p>'}
          </div>

          <div class="section">
            <h2>Step 10: Team Coordinators</h2>
            ${coordinators.length > 0 ? coordinators.map((coord, idx) => `
              <div class="list-item">
                <strong>Coordinator ${idx + 1}:</strong> ${coord.name || 'N/A'}<br/>
                <span class="label">Role:</span> ${coord.role || 'N/A'}<br/>
                <span class="label">Email:</span> ${coord.email || 'N/A'} &nbsp; <span class="label">Phone:</span> ${coord.phone || 'N/A'}<br/>
                <span class="label">Assigned To:</span> ${coord.assignedTo || 'N/A'}
              </div>
            `).join('') : '<p>No coordinators added</p>'}
          </div>

          <div class="section">
            <h2>Step 11: Code Words</h2>
            ${codeWords.length > 0 ? codeWords.map((code, idx) => `
              <div class="list-item">
                <strong>Code Word ${idx + 1}:</strong> ${code.word || 'N/A'}<br/>
                <span class="label">Meaning:</span> ${code.meaning || 'N/A'}
              </div>
            `).join('') : '<p>No code words added</p>'}
          </div>

          <div class="section">
            <h2>Step 12: Callsigns &amp; Frequencies</h2>
            <h3 style="margin-top:8px;">Callsigns</h3>
            ${callsigns.length > 0 ? callsigns.map((call, idx) => `
              <div class="list-item">
                <strong>Callsign ${idx + 1}:</strong> ${call.callsign || 'N/A'}<br/>
                <span class="label">Description:</span> ${call.description || call.assignment || 'N/A'}
              </div>
            `).join('') : '<p>No callsigns added</p>'}
            <h3 style="margin-top:8px;">Frequencies</h3>
            ${frequencies.length > 0 ? frequencies.map((f, idx) => `
              <div class="list-item">
                <strong>Frequency ${idx + 1}:</strong> ${f.frequency || 'N/A'}<br/>
                <span class="label">Type:</span> ${f.type || 'N/A'} &nbsp;
                <span class="label">Modulation:</span> ${f.modulation_type || 'N/A'} &nbsp;
                <span class="label">Primary/Backup:</span> ${f.primary_backup || 'N/A'}<br/>
                <span class="label">Description:</span> ${f.description || 'N/A'}
              </div>
            `).join('') : '<p>No frequencies added</p>'}
          </div>

          <div class="section">
            <h2>Step 13: Assumptions</h2>
            ${assumptions.length > 0 ? assumptions.map((assum, idx) => `
              <div class="list-item">
                <strong>Assumption ${idx + 1}:</strong> ${assum.assumption || 'N/A'}<br/>
                <span class="label">Description:</span> ${assum.description || 'N/A'}
              </div>
            `).join('') : '<p>No assumptions added</p>'}
          </div>

          <div class="section">
            <h2>Step 14: Artificialities</h2>
            ${artificialities.length > 0 ? artificialities.map((art, idx) => `
              <div class="list-item">
                <strong>Artificiality ${idx + 1}:</strong> ${art.artificiality || 'N/A'}<br/>
                <span class="label">Description:</span> ${art.description || 'N/A'}
              </div>
            `).join('') : '<p>No artificialities added</p>'}
          </div>

          <div class="section">
            <h2>Step 15: Safety Concerns</h2>
            ${safetyConcerns.length > 0 ? safetyConcerns.map((safety, idx) => `
              <div class="list-item">
                <strong>Safety Concern ${idx + 1}:</strong> ${safety.concern || 'N/A'}<br/>
                <span class="label">Description:</span> ${safety.description || 'N/A'}
              </div>
            `).join('') : '<p>No safety concerns added</p>'}
          </div>

          <div class="section">
            <h2>Step 16: Standard of Conduct</h2>
            ${standardsOfConduct.length > 0 ? standardsOfConduct.map((s, idx) => `
              <div class="list-item">
                <strong>Standard ${idx + 1}:</strong> ${s.name || 'N/A'}<br/>
                <span class="label">Conduct Expected:</span> ${s.conduct_expected || 'N/A'}
              </div>
            `).join('') : '<p>No standards of conduct added</p>'}
          </div>

          <div class="section">
            <h2>Step 17: Mapping</h2>
            <div class="field"><span class="label">Map Centre Latitude:</span> ${exerciseData.latitude || 'N/A'}</div>
            <div class="field"><span class="label">Map Centre Longitude:</span> ${exerciseData.longitude || 'N/A'}</div>
            <div class="field"><span class="label">Zoom Level:</span> ${exerciseData.zoom_level || 'N/A'}</div>
            <div class="field"><span class="label">Location:</span> ${exerciseData.location || 'N/A'}</div>
            <p style="margin-top:8px; color:#555;">Interactive map objects (markers, polylines, polygons) are stored with the exercise and are viewable inside the app.</p>
          </div>

          <div style="margin-top: 40px; text-align: center; color: #999; font-size: 12px;">
            <p>Generated: ${new Date().toLocaleString()}</p>
            <p>EXRSIM - Emergency Response Simulation Platform</p>
          </div>
        </body>
      </html>
    `;
  };

  const renderExerciseOverview = () => {
    if (!exercise) return null;
    
    const status = getExerciseStatus();
    
    return (
      <div className="space-y-6">
        {/* Locked-mode ribbon */}
        {exerciseLocked && (
          <ExerciseLockedRibbon
            lockedByName={lockedBy}
            lockedAt={lockedWhen}
            canManage={canManageLock}
          />
        )}
        {/* Exercise Header */}
        <div className={`${theme.colors.tertiary} border ${theme.colors.border} rounded-lg p-6`}>
          <div className="flex items-start justify-between mb-4">
            <div>
              <h1 className={`text-2xl font-bold ${theme.colors.textPrimary} mb-2`}>{exercise.exercise_name}</h1>
              <div className="flex items-center space-x-4">
                <Badge className={getStatusColor(status)}>
                  {status}
                </Badge>
                <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30">
                  {exercise.exercise_type}
                </Badge>
              </div>
            </div>
            {exercise.exercise_image && (
              <div className={`w-20 h-20 rounded-lg ${theme.colors.quaternary} overflow-hidden`}>
                <img src={exercise.exercise_image} alt={exercise.exercise_name} className="w-full h-full object-cover" />
              </div>
            )}
          </div>
          
          <p className={`${theme.colors.textSecondary} mb-4`}>{exercise.exercise_description}</p>
          
          {/* Exercise Lock (Agency Admin / Super Admin only) */}
          <div className="mb-3 flex items-center gap-2">
            <ExerciseLockButton
              exercise={exercise}
              user={user}
              testIdPrefix="management-exercise-lock"
              onChanged={fetchExercise}
            />
            {exercise.locked && (
              <span className="text-xs text-red-400" data-testid="management-exercise-lock-label">
                Locked{exercise.locked_by_name ? ` by ${exercise.locked_by_name}` : ''} — read-only
              </span>
            )}
          </div>
          
          {/* Dashboard Action Buttons */}
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <Button
              onClick={() => setActiveSection('injections')}
              variant="outline"
              className="border-teal-500/50 text-teal-500 hover:bg-teal-500/10"
              data-testid="dashboard-view-msel-btn"
            >
              <ClipboardList className="h-4 w-4 mr-2" />
              View MSEL
            </Button>
            <Button
              onClick={() => setActiveSection('evaluations')}
              variant="outline"
              className="border-yellow-500/50 text-yellow-500 hover:bg-yellow-500/10"
              data-testid="dashboard-evaluations-btn"
            >
              <Star className="h-4 w-4 mr-2" />
              Evaluations
            </Button>
            <Button
              onClick={handlePrintCompleteExercise}
              variant="outline"
              className="border-blue-500/50 text-blue-500 hover:bg-blue-500/10"
              data-testid="dashboard-print-exercise-btn"
            >
              <Printer className="h-4 w-4 mr-2" />
              Print Complete Exercise
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className={`${theme.colors.quaternary}/50 p-4 rounded-lg`}>
              <div className="flex items-center space-x-2 mb-2">
                <MapPin className="h-5 w-5 text-orange-400" />
                <span className={`text-sm font-medium ${theme.colors.textSecondary}`}>Location</span>
              </div>
              <p className={theme.colors.textPrimary}>{exercise.location || 'Not specified'}</p>
              {exercise.latitude && exercise.longitude && (
                <p className={`${theme.colors.textSecondary} text-sm mt-2`}>
                  📍 {exercise.latitude}°, {exercise.longitude}°
                </p>
              )}
            </div>
            
            <div className={`${theme.colors.quaternary}/50 p-4 rounded-lg`}>
              <div className="flex items-center space-x-2 mb-2">
                <Clock className="h-5 w-5 text-orange-400" />
                <span className={`text-sm font-medium ${theme.colors.textSecondary}`}>Duration</span>
              </div>
              <p className={theme.colors.textPrimary}>
                {new Date(exercise.start_date).toLocaleDateString()} - {new Date(exercise.end_date).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className={`${theme.colors.tertiary} ${theme.colors.border}`}>
            <CardContent className="p-4 text-center">
              <CheckCircle className="h-8 w-8 text-blue-400 mx-auto mb-2" />
              <div className={`text-2xl font-bold ${theme.colors.textPrimary}`}>{icsObjectivesCount}</div>
              <div className={`text-sm ${theme.colors.textMuted}`}>Objectives</div>
            </CardContent>
          </Card>
          
          <Card className={`${theme.colors.tertiary} ${theme.colors.border}`}>
            <CardContent className="p-4 text-center">
              <Users className="h-8 w-8 text-green-400 mx-auto mb-2" />
              <div className={`text-2xl font-bold ${theme.colors.textPrimary}`}>{exerciseParticipants?.length || 0}</div>
              <div className={`text-sm ${theme.colors.textMuted}`}>Participants</div>
            </CardContent>
          </Card>
          
          <Card className={`${theme.colors.tertiary} ${theme.colors.border}`}>
            <CardContent className="p-4 text-center">
              <Package className="h-8 w-8 text-teal-400 mx-auto mb-2" />
              <div className={`text-2xl font-bold ${theme.colors.textPrimary}`}>{exerciseResources?.length || 0}</div>
              <div className={`text-sm ${theme.colors.textMuted}`}>Resources</div>
            </CardContent>
          </Card>
          
          <Card className={`${theme.colors.tertiary} ${theme.colors.border}`}>
            <CardContent className="p-4 text-center">
              <Building className="h-8 w-8 text-purple-400 mx-auto mb-2" />
              <div className={`text-2xl font-bold ${theme.colors.textPrimary}`}>{agencyCoordinationCount}</div>
              <div className={`text-sm ${theme.colors.textMuted}`}>Organizations</div>
            </CardContent>
          </Card>
        </div>

        {/* Emergency Preparedness Features */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Exercise Timeline */}
          <Card className={`${theme.colors.tertiary} ${theme.colors.border}`}>
            <CardHeader>
              <CardTitle className="text-orange-500 flex items-center">
                <Clock className="h-5 w-5 mr-2" />
                Exercise Timeline
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                  <div>
                    <div className={`${theme.colors.textPrimary} font-medium`}>Exercise Start</div>
                    <div className={`text-sm ${theme.colors.textMuted}`}>
                      {new Date(exercise.start_date).toLocaleString()}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <div>
                    <div className={`${theme.colors.textPrimary} font-medium`}>Exercise End</div>
                    <div className={`text-sm ${theme.colors.textMuted}`}>
                      {new Date(exercise.end_date).toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Safety Information */}
          <Card className={`${theme.colors.tertiary} ${theme.colors.border}`}>
            <CardHeader>
              <CardTitle className="text-red-500 flex items-center">
                <ShieldAlert className="h-5 w-5 mr-2" />
                Safety Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className={`${theme.colors.textPrimary} font-medium`}>Safety Concerns: {exercise.safetyConcerns?.length || 0}</div>
                <div className={`text-sm ${theme.colors.textMuted}`}>
                  {exercise.safetyConcerns?.length > 0 ? 
                    "Safety protocols have been documented for this exercise." : 
                    "No specific safety concerns documented."
                  }
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                  onClick={() => setActiveSection('safety')}
                >
                  View Safety Details
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card className={`${theme.colors.tertiary} ${theme.colors.border}`}>
          <CardHeader>
            <CardTitle className="text-orange-500">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Button 
                className="bg-orange-500 hover:bg-orange-600 text-black"
                onClick={() => window.location.href = `#builder?exercise=${exercise.id}`}
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit Exercise
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderICSInterface = (generalMessages, setGeneralMessages, positions) => {
    const renderICSContent = (generalMessagesParam, setGeneralMessagesParam, positionsParam) => {
      switch (activeICSMenu) {
        case 'dashboard':
          return (
            <div className="p-6">
              <h1 className={`text-3xl font-bold ${theme.colors.textPrimary} mb-6`}>ICS Dashboard</h1>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
                  <CardHeader>
                    <CardTitle className="text-orange-500">Command Structure</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className={`${theme.colors.textMuted}`}>
                      Manage incident command structure and organizational assignments.
                    </p>
                  </CardContent>
                </Card>
                <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
                  <CardHeader>
                    <CardTitle className="text-blue-500">Planning Section</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className={`${theme.colors.textMuted}`}>
                      Access planning documentation, resources, and situation updates.
                    </p>
                  </CardContent>
                </Card>
                <Card className={`${theme.colors.cardBackground} ${theme.colors.border}`}>
                  <CardHeader>
                    <CardTitle className="text-green-500">Operations</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className={`${theme.colors.textMuted}`}>
                      Monitor operational activities and safety procedures.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          );
        
        // Add other ICS cases that were already implemented...
        // For now, return a placeholder for other cases
        default:
          return (
            <div className="p-6">
              <h2 className={`text-2xl font-bold ${theme.colors.textPrimary} mb-4`}>ICS Menu: {activeICSMenu}</h2>
              <p className={`${theme.colors.textMuted}`}>
                This ICS section is under development. Content for "{activeICSMenu}" will be implemented here.
              </p>
            </div>
          );
      }
    };

    return (
      <div className="flex h-screen">
        {/* ICS Sidebar */}
        <div className="w-80 bg-gray-900 border-r border-gray-700 overflow-y-auto">
          <div className="p-4">
            <h2 className="text-xl font-bold text-white mb-4">ICS Structure</h2>
            <div className="space-y-2">
              {/* Dashboard */}
              <Button
                variant={activeICSMenu === 'dashboard' ? 'secondary' : 'ghost'}
                className={`w-full justify-start ${
                  activeICSMenu === 'dashboard' 
                    ? 'bg-orange-500/20 text-orange-500' 
                    : 'text-orange-500 hover:bg-gray-800'
                }`}
              >
                <Home className="h-4 w-4 mr-2 text-orange-500" />
                Dashboard
              </Button>

              {/* Planning Section */}
              <div>
                <Button
                  variant="ghost"
                  className="w-full justify-start text-gray-300 hover:text-orange-500 hover:bg-gray-800"
                  onClick={() => setPlanningExpanded(!planningExpanded)}
                >
                  {planningExpanded ? (
                    <ChevronDown className="h-4 w-4 mr-2" />
                  ) : (
                    <ChevronRight className="h-4 w-4 mr-2" />
                  )}
                  <Users className="h-4 w-4 mr-2" />
                  Planning
                </Button>
                
                {planningExpanded && (
                  <div className="ml-6 mt-2 space-y-1">
                    {/* Resources Unit with submenu */}
                    <div>
                      <Button
                        variant="ghost"
                        className="w-full justify-start text-gray-300 hover:text-orange-500 hover:bg-gray-800 text-sm"
                        onClick={() => setResourcesUnitExpanded(!resourcesUnitExpanded)}
                      >
                        {resourcesUnitExpanded ? (
                          <ChevronDown className="h-3 w-3 mr-2" />
                        ) : (
                          <ChevronRight className="h-3 w-3 mr-2" />
                        )}
                        <Users className="h-3 w-3 mr-2" />
                        Resources Unit
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Other ICS sections can be added here */}
              <Button
                variant="ghost"
                className="w-full justify-start text-gray-300 hover:text-orange-500 hover:bg-gray-800"
              >
                <Shield className="h-4 w-4 mr-2" />
                Operations
              </Button>
              
              <Button
                variant="ghost"
                className="w-full justify-start text-gray-300 hover:text-orange-500 hover:bg-gray-800"
              >
                <Package className="h-4 w-4 mr-2" />
                Logistics
              </Button>
              
              <Button
                variant="ghost"
                className="w-full justify-start text-gray-300 hover:text-orange-500 hover:bg-gray-800"
              >
                <DollarSign className="h-4 w-4 mr-2" />
                Finance/Administration
              </Button>
            </div>
          </div>
        </div>

        {/* ICS Main Content */}
        <div className="flex-1 overflow-auto">
          {renderICSContent(generalMessages, setGeneralMessages, positions)}
        </div>
      </div>
    );
  };

  const renderSectionContent = () => {
    const content = (() => {
      if (activeSection === 'overview') {
        return renderExerciseOverview();
      }
      // Render specific management interfaces for each section
      switch (activeSection) {
        case 'scope':
          return renderScopeManagement();
        case 'goals':
          return renderGoalsManagement();
      case 'objectives':
        return renderObjectivesManagement();
      case 'events':
        return renderEventsManagement();
      case 'safety':
        return renderSafetyManagement();
      case 'evaluations':
        return renderEvaluationsManagement();
      case 'lessons_learned':
        return renderLessonsLearnedManagement();
      case 'injections':
        return <MSELView exerciseId={exercise?.id} />;
      case 'audit_trail':
        return <ExerciseAuditTrail exerciseId={exercise?.id} user={user} />;
      case 'ics':
        return renderICSInterface(generalMessages, setGeneralMessages, positions);
      default:
        return renderGenericSectionManagement();
      }
    })();
    // Wrap non-overview sections with the locked ribbon so the notice
    // travels with the user throughout the exercise.
    if (activeSection !== 'overview' && exerciseLocked) {
      return (
        <>
          <ExerciseLockedRibbon
            lockedByName={lockedBy}
            lockedAt={lockedWhen}
            canManage={canManageLock}
          />
          {content}
        </>
      );
    }
    return content;
  };

  const handleScopeUpdate = (updatedExercise) => {
    setExercise(updatedExercise);
  };

  const renderScopeManagement = () => {
    const hasScope = exercise?.scope_description || exercise?.scope_hazards || 
                    exercise?.scope_geographic_area || exercise?.scope_functions || 
                    exercise?.scope_organizations || exercise?.scope_personnel;

    // Print function for Scope
    const printScope = () => {
      const currentDateTime = new Date().toLocaleString();
      const printContent = `
        <html>
          <head>
            <title>Exercise Scope - ${exercise.name || 'Exercise'}</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              .header { border-bottom: 2px solid #333; margin-bottom: 20px; padding-bottom: 10px; }
              .scope-section { border: 1px solid #ddd; margin-bottom: 15px; padding: 15px; border-radius: 5px; }
              .scope-title { font-size: 16px; font-weight: bold; margin-bottom: 10px; color: #333; }
              .scope-content { margin-bottom: 10px; color: #666; line-height: 1.6; }
              .footer { 
                position: fixed; 
                bottom: 20px; 
                left: 20px; 
                right: 20px; 
                text-align: center; 
                font-size: 12px; 
                color: #666; 
                border-top: 1px solid #ddd; 
                padding-top: 10px; 
              }
              @media print { 
                @page { 
                  margin: 20px 20px 80px 20px; 
                  size: A4;
                }
                body { 
                  margin: 0; 
                  padding: 20px 20px 60px 20px; 
                  font-size: 12px;
                  line-height: 1.4;
                } 
                .no-print { display: none; }
                .footer { 
                  position: fixed; 
                  bottom: 20px; 
                  left: 20px; 
                  right: 20px;
                  height: 40px;
                  z-index: 999;
                }
                .report-item { 
                  page-break-inside: avoid; 
                  page-break-after: auto;
                  margin-bottom: 20px;
                }
                .section-title { 
                  page-break-after: avoid;
                }
                .assessment-grid { 
                  page-break-inside: avoid;
                }
              }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>Exercise Scope</h1>
              <h2>${exercise.name || 'Exercise Name'}</h2>
              <p>Exercise Type: ${exercise.exercise_type || 'N/A'}</p>
              <p>Generated on: ${new Date().toLocaleDateString()}</p>
            </div>
            <div class="scope-content">
              ${exercise.scope_description ? `
                <div class="scope-section">
                  <div class="scope-title">Description</div>
                  <div class="scope-content">${exercise.scope_description}</div>
                </div>
              ` : ''}
              ${exercise.scope_hazards ? `
                <div class="scope-section">
                  <div class="scope-title">Hazards</div>
                  <div class="scope-content">${exercise.scope_hazards}</div>
                </div>
              ` : ''}
              ${exercise.scope_geographic_area ? `
                <div class="scope-section">
                  <div class="scope-title">Geographic Area</div>
                  <div class="scope-content">${exercise.scope_geographic_area}</div>
                </div>
              ` : ''}
              ${exercise.scope_functions ? `
                <div class="scope-section">
                  <div class="scope-title">Functions</div>
                  <div class="scope-content">${exercise.scope_functions}</div>
                </div>
              ` : ''}
              ${exercise.scope_organizations ? `
                <div class="scope-section">
                  <div class="scope-title">Organizations</div>
                  <div class="scope-content">${exercise.scope_organizations}</div>
                </div>
              ` : ''}
              ${exercise.scope_personnel ? `
                <div class="scope-section">
                  <div class="scope-title">Personnel</div>
                  <div class="scope-content">${exercise.scope_personnel}</div>
                </div>
              ` : ''}
              ${!hasScope ? '<p>No scope information has been defined for this exercise.</p>' : ''}
            </div>
            <div class="footer">
              <p>Generated on: ${currentDateTime} | Powered by EXRSIM</p>
            </div>
          </body>
        </html>
      `;

      const printWindow = window.open('', '_blank');
      writeSanitizedHTML(printWindow.document, printContent);
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    };

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Exercise Scope</h1>
          <div className="flex space-x-3">
            {hasScope && (
              <Button 
                variant="outline"
                className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
                onClick={printScope}
              >
                <Printer className="h-4 w-4 mr-2" />
                Print Scope
              </Button>
            )}
            <Button 
              className="bg-orange-500 hover:bg-orange-600 text-black"
              onClick={() => {
                setEditingScope(hasScope ? exercise : null);
                setScopeModalOpen(true);
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              {hasScope ? 'Edit Scope' : 'Add Scope'}
            </Button>
          </div>
        </div>

        {/* Scope Display */}
        {hasScope ? (
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-6">
              <div className="space-y-6">
                {exercise.scope_description && (
                  <div>
                    <h3 className="text-lg font-semibold text-orange-400 mb-2">Description</h3>
                    <p className="text-gray-300">{exercise.scope_description}</p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {exercise.scope_hazards && (
                    <div>
                      <h4 className="text-md font-semibold text-white mb-2">Hazards</h4>
                      <div className="bg-gray-700/50 p-4 rounded-lg">
                        <p className="text-gray-300">{exercise.scope_hazards}</p>
                      </div>
                    </div>
                  )}

                  {exercise.scope_geographic_area && (
                    <div>
                      <h4 className="text-md font-semibold text-white mb-2">Geographic Area</h4>
                      <div className="bg-gray-700/50 p-4 rounded-lg">
                        <p className="text-gray-300">{exercise.scope_geographic_area}</p>
                      </div>
                    </div>
                  )}

                  {exercise.scope_functions && (
                    <div>
                      <h4 className="text-md font-semibold text-white mb-2">Functions</h4>
                      <div className="bg-gray-700/50 p-4 rounded-lg">
                        <p className="text-gray-300">{exercise.scope_functions}</p>
                      </div>
                    </div>
                  )}

                  {exercise.scope_organizations && (
                    <div>
                      <h4 className="text-md font-semibold text-white mb-2">Organizations</h4>
                      <div className="bg-gray-700/50 p-4 rounded-lg">
                        <p className="text-gray-300">{exercise.scope_organizations}</p>
                      </div>
                    </div>
                  )}

                  {exercise.scope_personnel && (
                    <div>
                      <h4 className="text-md font-semibold text-white mb-2">Personnel</h4>
                      <div className="bg-gray-700/50 p-4 rounded-lg">
                        <p className="text-gray-300">{exercise.scope_personnel}</p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-700">
                  <Button 
                    variant="outline"
                    className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                    onClick={() => {
                      setEditingScope(exercise);
                      setScopeModalOpen(true);
                    }}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit Scope
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-gray-800 border-gray-700 border-dashed">
            <CardContent className="p-12 text-center">
              <Target className="h-12 w-12 text-gray-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-300 mb-2">No Scope Defined</h3>
              <p className="text-gray-500 mb-4">
                Define the exercise scope including hazards, geographic area, functions, organizations, and personnel.
              </p>
              <Button 
                className="bg-orange-500 hover:bg-orange-600 text-black"
                onClick={() => {
                  setEditingScope(null);
                  setScopeModalOpen(true);
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add First Scope
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Exercise Details Context */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-orange-500">Exercise Context</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-400">Location:</span>
                <p className="text-white">{exercise?.location || 'Not specified'}</p>
              </div>
              <div>
                <span className="text-gray-400">Start Date:</span>
                <p className="text-white">{exercise?.start_date || 'Not set'}</p>
              </div>
              <div>
                <span className="text-gray-400">End Date:</span>
                <p className="text-white">{exercise?.end_date || 'Not set'}</p>
              </div>
              <div>
                <span className="text-gray-400">Exercise Type:</span>
                <p className="text-white">{exercise?.exercise_type || 'Not set'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderGoalsManagement = () => {
    // Print function for Goals
    const printGoals = () => {
      const currentDateTime = new Date().toLocaleString();
      const printContent = `
        <html>
          <head>
            <title>Exercise Goals - ${exercise.name || 'Exercise'}</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              .header { border-bottom: 2px solid #333; margin-bottom: 20px; padding-bottom: 10px; }
              .goal-item { border: 1px solid #ddd; margin-bottom: 15px; padding: 15px; border-radius: 5px; }
              .goal-title { font-size: 18px; font-weight: bold; margin-bottom: 10px; }
              .goal-description { margin-bottom: 10px; color: #666; }
              .goal-status { display: inline-block; padding: 4px 8px; border-radius: 4px; font-size: 12px; font-weight: bold; }
              .status-yes { background-color: #d4edda; color: #155724; }
              .status-partial { background-color: #fff3cd; color: #856404; }
              .status-no { background-color: #f8d7da; color: #721c24; }
              .footer { 
                position: fixed; 
                bottom: 20px; 
                left: 20px; 
                right: 20px; 
                text-align: center; 
                font-size: 12px; 
                color: #666; 
                border-top: 1px solid #ddd; 
                padding-top: 10px; 
              }
              @media print { 
                @page { 
                  margin: 20px 20px 80px 20px; 
                  size: A4;
                }
                body { 
                  margin: 0; 
                  padding: 20px 20px 60px 20px; 
                  font-size: 12px;
                  line-height: 1.4;
                } 
                .no-print { display: none; }
                .footer { 
                  position: fixed; 
                  bottom: 20px; 
                  left: 20px; 
                  right: 20px;
                  height: 40px;
                  z-index: 999;
                }
                .report-item { 
                  page-break-inside: avoid; 
                  page-break-after: auto;
                  margin-bottom: 20px;
                }
                .section-title { 
                  page-break-after: avoid;
                }
                .assessment-grid { 
                  page-break-inside: avoid;
                }
              }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>Exercise Goals</h1>
              <h2>${exercise.name || 'Exercise Name'}</h2>
              <p>Exercise Type: ${exercise.exercise_type || 'N/A'}</p>
              <p>Generated on: ${new Date().toLocaleDateString()}</p>
            </div>
            <div class="goals-content">
              ${exercise.goals && exercise.goals.length > 0 ? 
                exercise.goals.map((goal, index) => `
                  <div class="goal-item">
                    <div class="goal-title">${index + 1}. ${goal.name || 'Unnamed Goal'}</div>
                    <div class="goal-description">${goal.description || 'No description provided'}</div>
                    <div class="goal-status ${
                      goal.achieved === 'Yes' ? 'status-yes' :
                      goal.achieved === 'Partial' ? 'status-partial' :
                      'status-no'
                    }">
                      Status: ${goal.achieved || 'No'}
                    </div>
                  </div>
                `).join('') 
                : '<p>No goals have been defined for this exercise.</p>'
              }
            </div>
            <div class="footer">
              <p>Generated on: ${currentDateTime} | Powered by EXRSIM</p>
            </div>
          </body>
        </html>
      `;

      const printWindow = window.open('', '_blank');
      writeSanitizedHTML(printWindow.document, printContent);
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    };

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Exercise Goals</h1>
          <div className="flex space-x-3">
            <Button 
              variant="outline"
              className="border-green-500/50 text-green-400 hover:bg-green-500/10"
              onClick={printGoals}
            >
              <Printer className="h-4 w-4 mr-2" />
              Print Goals
            </Button>
            <Button 
              className="bg-orange-500 hover:bg-orange-600 text-black"
              onClick={() => {/* Add new goal logic */}}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Goal
            </Button>
          </div>
        </div>

        {/* Goals List */}
        <div className="space-y-4">
          {exercise.goals && exercise.goals.length > 0 ? (
            exercise.goals.map((goal, index) => (
              <Card key={goal.id || index} className="bg-gray-800 border-gray-700">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-2">{goal.name}</h3>
                      <p className="text-gray-300 mb-3">{goal.description}</p>
                      <Badge className={
                        goal.achieved === 'Yes' ? 'bg-green-500/20 text-green-300 border-green-500/30' :
                        goal.achieved === 'Partial' ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' :
                        'bg-red-500/20 text-red-300 border-red-500/30'
                      }>
                        Status: {goal.achieved}
                      </Badge>
                    </div>
                    <div className="flex space-x-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card className="bg-gray-800 border-gray-700 border-dashed">
              <CardContent className="p-12 text-center">
                <Trophy className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-300 mb-2">No Goals Yet</h3>
                <p className="text-gray-500 mb-4">Add exercise goals to track progress and success criteria.</p>
                <Button className="bg-orange-500 hover:bg-orange-600 text-black">
                  <Plus className="h-4 w-4 mr-2" />
                  Add First Goal
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  };

  const renderObjectivesManagement = () => {
    // Print function for Objectives
    const printObjectives = () => {
      const currentDateTime = new Date().toLocaleString();
      const printContent = `
        <html>
          <head>
            <title>Exercise Objectives - ${exercise.name || 'Exercise'}</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              .header { border-bottom: 2px solid #333; margin-bottom: 20px; padding-bottom: 10px; }
              .objective-item { border: 1px solid #ddd; margin-bottom: 15px; padding: 15px; border-radius: 5px; }
              .objective-title { font-size: 18px; font-weight: bold; margin-bottom: 10px; }
              .objective-description { margin-bottom: 10px; color: #666; }
              .objective-status { display: inline-block; padding: 4px 8px; border-radius: 4px; font-size: 12px; font-weight: bold; }
              .status-yes { background-color: #d4edda; color: #155724; }
              .status-partial { background-color: #fff3cd; color: #856404; }
              .status-no { background-color: #f8d7da; color: #721c24; }
              .footer { 
                position: fixed; 
                bottom: 20px; 
                left: 20px; 
                right: 20px; 
                text-align: center; 
                font-size: 12px; 
                color: #666; 
                border-top: 1px solid #ddd; 
                padding-top: 10px; 
              }
              @media print { 
                @page { 
                  margin: 20px 20px 80px 20px; 
                  size: A4;
                }
                body { 
                  margin: 0; 
                  padding: 20px 20px 60px 20px; 
                  font-size: 12px;
                  line-height: 1.4;
                } 
                .no-print { display: none; }
                .footer { 
                  position: fixed; 
                  bottom: 20px; 
                  left: 20px; 
                  right: 20px;
                  height: 40px;
                  z-index: 999;
                }
                .report-item { 
                  page-break-inside: avoid; 
                  page-break-after: auto;
                  margin-bottom: 20px;
                }
                .section-title { 
                  page-break-after: avoid;
                }
                .assessment-grid { 
                  page-break-inside: avoid;
                }
              }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>Exercise Objectives</h1>
              <h2>${exercise.name || 'Exercise Name'}</h2>
              <p>Exercise Type: ${exercise.exercise_type || 'N/A'}</p>
              <p>Generated on: ${new Date().toLocaleDateString()}</p>
            </div>
            <div class="objectives-content">
              ${exercise.objectives && exercise.objectives.length > 0 ? 
                exercise.objectives.map((objective, index) => `
                  <div class="objective-item">
                    <div class="objective-title">${index + 1}. ${objective.name || 'Unnamed Objective'}</div>
                    <div class="objective-description">${objective.description || 'No description provided'}</div>
                    <div class="objective-status ${
                      objective.achieved === 'Yes' ? 'status-yes' :
                      objective.achieved === 'Partial' ? 'status-partial' :
                      'status-no'
                    }">
                      Status: ${objective.achieved || 'No'}
                    </div>
                  </div>
                `).join('') 
                : '<p>No objectives have been defined for this exercise.</p>'
              }
            </div>
            <div class="footer">
              <p>Generated on: ${currentDateTime} | Powered by EXRSIM</p>
            </div>
          </body>
        </html>
      `;

      const printWindow = window.open('', '_blank');
      writeSanitizedHTML(printWindow.document, printContent);
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    };

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Exercise Objectives</h1>
          <div className="flex space-x-3">
            <Button 
              variant="outline"
              className="border-green-500/50 text-green-400 hover:bg-green-500/10"
              onClick={printObjectives}
            >
              <Printer className="h-4 w-4 mr-2" />
              Print Objectives
            </Button>
            <Button 
              className="bg-orange-500 hover:bg-orange-600 text-black"
              onClick={() => {/* Add new objective logic */}}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Objective
            </Button>
          </div>
        </div>

        {/* Objectives List */}
        <div className="space-y-4">
          {exercise.objectives && exercise.objectives.length > 0 ? (
            exercise.objectives.map((objective, index) => (
              <Card key={objective.id || index} className="bg-gray-800 border-gray-700">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-2">{objective.name}</h3>
                      <p className="text-gray-300 mb-3">{objective.description}</p>
                      <Badge className={
                        objective.achieved === 'Yes' ? 'bg-green-500/20 text-green-300 border-green-500/30' :
                        objective.achieved === 'Partial' ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30' :
                        'bg-red-500/20 text-red-300 border-red-500/30'
                      }>
                        Status: {objective.achieved}
                      </Badge>
                    </div>
                    <div className="flex space-x-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card className="bg-gray-800 border-gray-700 border-dashed">
              <CardContent className="p-12 text-center">
                <CheckCircle className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-300 mb-2">No Objectives Yet</h3>
                <p className="text-gray-500 mb-4">Add specific objectives to measure exercise success.</p>
                <Button className="bg-orange-500 hover:bg-orange-600 text-black">
                  <Plus className="h-4 w-4 mr-2" />
                  Add First Objective
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  };

  const renderEventsManagement = () => {
    // Print function for Events
    const printEvents = () => {
      const currentDateTime = new Date().toLocaleString();
      const printContent = `
        <html>
          <head>
            <title>Exercise Events - ${exercise.name || 'Exercise'}</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              .header { border-bottom: 2px solid #333; margin-bottom: 20px; padding-bottom: 10px; }
              .event-item { border: 1px solid #ddd; margin-bottom: 15px; padding: 15px; border-radius: 5px; }
              .event-title { font-size: 18px; font-weight: bold; margin-bottom: 10px; }
              .event-description { margin-bottom: 10px; color: #666; }
              .event-details { margin-bottom: 5px; font-size: 14px; }
              .footer { 
                position: fixed; 
                bottom: 20px; 
                left: 20px; 
                right: 20px; 
                text-align: center; 
                font-size: 12px; 
                color: #666; 
                border-top: 1px solid #ddd; 
                padding-top: 10px; 
              }
              @media print { 
                @page { 
                  margin: 20px 20px 80px 20px; 
                  size: A4;
                }
                body { 
                  margin: 0; 
                  padding: 20px 20px 60px 20px; 
                  font-size: 12px;
                  line-height: 1.4;
                } 
                .no-print { display: none; }
                .footer { 
                  position: fixed; 
                  bottom: 20px; 
                  left: 20px; 
                  right: 20px;
                  height: 40px;
                  z-index: 999;
                }
                .report-item { 
                  page-break-inside: avoid; 
                  page-break-after: auto;
                  margin-bottom: 20px;
                }
                .section-title { 
                  page-break-after: avoid;
                }
                .assessment-grid { 
                  page-break-inside: avoid;
                }
              }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>Exercise Events</h1>
              <h2>${exercise.name || 'Exercise Name'}</h2>
              <p>Exercise Type: ${exercise.exercise_type || 'N/A'}</p>
              <p>Generated on: ${new Date().toLocaleDateString()}</p>
            </div>
            <div class="events-content">
              ${exercise.events && exercise.events.length > 0 ? 
                exercise.events.map((event, index) => `
                  <div class="event-item">
                    <div class="event-title">${index + 1}. ${event.name || 'Unnamed Event'}</div>
                    <div class="event-description">${event.description || 'No description provided'}</div>
                    <div class="event-details"><strong>Time:</strong> ${event.time || 'Not specified'}</div>
                    <div class="event-details"><strong>Location:</strong> ${event.location || 'Not specified'}</div>
                    ${event.latitude && event.longitude ? `<div class="event-details"><strong>Coordinates:</strong> ${event.latitude}, ${event.longitude}</div>` : ''}
                  </div>
                `).join('') 
                : '<p>No events have been defined for this exercise.</p>'
              }
            </div>
            <div class="footer">
              <p>Generated on: ${currentDateTime} | Powered by EXRSIM</p>
            </div>
          </body>
        </html>
      `;

      const printWindow = window.open('', '_blank');
      writeSanitizedHTML(printWindow.document, printContent);
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    };

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Exercise Events</h1>
          <div className="flex space-x-3">
            <Button 
              variant="outline"
              className="border-green-500/50 text-green-400 hover:bg-green-500/10"
              onClick={printEvents}
            >
              <Printer className="h-4 w-4 mr-2" />
              Print Events
            </Button>
            <Button 
              className="bg-orange-500 hover:bg-orange-600 text-black"
              onClick={() => {/* Add new event logic */}}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Event
            </Button>
          </div>
        </div>

        {/* Events List */}
        <div className="space-y-4">
          {exercise.events && exercise.events.length > 0 ? (
            exercise.events.map((event, index) => (
              <Card key={event.id || index} className="bg-gray-800 border-gray-700">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-white mb-2">{event.name}</h3>
                      <p className="text-gray-300 mb-3">{event.description}</p>
                      <div className="flex space-x-4 text-sm text-gray-400">
                        <span>Start: {event.startTime || 'Not set'}</span>
                        <span>End: {event.endTime || 'Not set'}</span>
                        <span>Tier: {event.tierScale || 'Not set'}</span>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card className="bg-gray-800 border-gray-700 border-dashed">
              <CardContent className="p-12 text-center">
                <Calendar className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-300 mb-2">No Events Yet</h3>
                <p className="text-gray-500 mb-4">Add events to create the exercise timeline and scenarios.</p>
                <Button className="bg-orange-500 hover:bg-orange-600 text-black">
                  <Plus className="h-4 w-4 mr-2" />
                  Add First Event
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  };

  const renderSafetyManagement = () => {
    // Print function for Safety
    const printSafety = () => {
      const currentDateTime = new Date().toLocaleString();
      const printContent = `
        <html>
          <head>
            <title>Exercise Safety - ${exercise.name || 'Exercise'}</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              .header { border-bottom: 2px solid #333; margin-bottom: 20px; padding-bottom: 10px; }
              .safety-item { border: 1px solid #ddd; margin-bottom: 15px; padding: 15px; border-radius: 5px; }
              .safety-title { font-size: 18px; font-weight: bold; margin-bottom: 10px; }
              .safety-description { margin-bottom: 10px; color: #666; }
              .footer { 
                position: fixed; 
                bottom: 20px; 
                left: 20px; 
                right: 20px; 
                text-align: center; 
                font-size: 12px; 
                color: #666; 
                border-top: 1px solid #ddd; 
                padding-top: 10px; 
              }
              @media print { 
                @page { 
                  margin: 20px 20px 80px 20px; 
                  size: A4;
                }
                body { 
                  margin: 0; 
                  padding: 20px 20px 60px 20px; 
                  font-size: 12px;
                  line-height: 1.4;
                } 
                .no-print { display: none; }
                .footer { 
                  position: fixed; 
                  bottom: 20px; 
                  left: 20px; 
                  right: 20px;
                  height: 40px;
                  z-index: 999;
                }
                .report-item { 
                  page-break-inside: avoid; 
                  page-break-after: auto;
                  margin-bottom: 20px;
                }
                .section-title { 
                  page-break-after: avoid;
                }
                .assessment-grid { 
                  page-break-inside: avoid;
                }
              }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>Exercise Safety Concerns</h1>
              <h2>${exercise.name || 'Exercise Name'}</h2>
              <p>Exercise Type: ${exercise.exercise_type || 'N/A'}</p>
              <p>Generated on: ${new Date().toLocaleDateString()}</p>
            </div>
            <div class="safety-content">
              ${exercise.safetyConcerns && exercise.safetyConcerns.length > 0 ? 
                exercise.safetyConcerns.map((safety, index) => `
                  <div class="safety-item">
                    <div class="safety-title">${index + 1}. ${safety.concern || 'Unnamed Safety Concern'}</div>
                    <div class="safety-description">${safety.description || 'No description provided'}</div>
                  </div>
                `).join('') 
                : '<p>No safety concerns have been defined for this exercise.</p>'
              }
            </div>
            <div class="footer">
              <p>Generated on: ${currentDateTime} | Powered by EXRSIM</p>
            </div>
          </body>
        </html>
      `;

      const printWindow = window.open('', '_blank');
      writeSanitizedHTML(printWindow.document, printContent);
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    };

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Safety Concerns</h1>
          <div className="flex space-x-3">
            <Button 
              variant="outline"
              className="border-green-500/50 text-green-400 hover:bg-green-500/10"
              onClick={printSafety}
            >
              <Printer className="h-4 w-4 mr-2" />
              Print Safety
            </Button>
            <Button 
              className="bg-red-500 hover:bg-red-600 text-white"
              onClick={() => {/* Add new safety concern logic */}}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Safety Concern
            </Button>
          </div>
        </div>

        {/* Safety Concerns List */}
        <div className="space-y-4">
          {exercise.safetyConcerns && exercise.safetyConcerns.length > 0 ? (
            exercise.safetyConcerns.map((concern, index) => (
              <Card key={concern.id || index} className="bg-gray-800 border-red-500/20">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <ShieldAlert className="h-5 w-5 text-red-400" />
                        <h3 className="text-lg font-semibold text-white">{concern.name}</h3>
                      </div>
                      <p className="text-gray-300 mb-3">{concern.description}</p>
                      <Badge className="bg-red-500/20 text-red-300 border-red-500/30">
                        Priority: High
                      </Badge>
                    </div>
                    <div className="flex space-x-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card className="bg-gray-800 border-gray-700 border-dashed">
              <CardContent className="p-12 text-center">
                <ShieldAlert className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-300 mb-2">No Safety Concerns</h3>
                <p className="text-gray-500 mb-4">Document safety concerns and mitigation measures for this exercise.</p>
                <Button className="bg-red-500 hover:bg-red-600 text-white">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Safety Concern
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  };

  const renderGenericSectionManagement = () => {
    const sectionTitle = exerciseMenuItems.find(item => item.id === activeSection)?.title || activeSection;
    const sectionData = exercise?.[activeSection] || [];
    
    // Print function for Generic Sections
    const printGenericSection = () => {
      const currentDateTime = new Date().toLocaleString();
      const printContent = `
        <html>
          <head>
            <title>Exercise ${sectionTitle} - ${exercise.name || 'Exercise'}</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              .header { border-bottom: 2px solid #333; margin-bottom: 20px; padding-bottom: 10px; }
              .section-item { border: 1px solid #ddd; margin-bottom: 15px; padding: 15px; border-radius: 5px; }
              .section-title { font-size: 18px; font-weight: bold; margin-bottom: 10px; }
              .section-description { margin-bottom: 10px; color: #666; }
              .section-details { margin-bottom: 5px; font-size: 14px; }
              .footer { 
                position: fixed; 
                bottom: 20px; 
                left: 20px; 
                right: 20px; 
                text-align: center; 
                font-size: 12px; 
                color: #666; 
                border-top: 1px solid #ddd; 
                padding-top: 10px; 
              }
              @media print { 
                @page { 
                  margin: 20px 20px 80px 20px; 
                  size: A4;
                }
                body { 
                  margin: 0; 
                  padding: 20px 20px 60px 20px; 
                  font-size: 12px;
                  line-height: 1.4;
                } 
                .no-print { display: none; }
                .footer { 
                  position: fixed; 
                  bottom: 20px; 
                  left: 20px; 
                  right: 20px;
                  height: 40px;
                  z-index: 999;
                }
                .report-item { 
                  page-break-inside: avoid; 
                  page-break-after: auto;
                  margin-bottom: 20px;
                }
                .section-title { 
                  page-break-after: avoid;
                }
                .assessment-grid { 
                  page-break-inside: avoid;
                }
              }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>Exercise ${sectionTitle}</h1>
              <h2>${exercise.name || 'Exercise Name'}</h2>
              <p>Exercise Type: ${exercise.exercise_type || 'N/A'}</p>
              <p>Generated on: ${new Date().toLocaleDateString()}</p>
            </div>
            <div class="section-content">
              ${sectionData && sectionData.length > 0 ? 
                sectionData.map((item, index) => `
                  <div class="section-item">
                    <div class="section-title">${index + 1}. ${item.name || item.word || item.concern || 'Unnamed Item'}</div>
                    <div class="section-description">${item.description || item.meaning || 'No description provided'}</div>
                    ${item.home_base ? `<div class="section-details"><strong>Home Base:</strong> ${item.home_base}</div>` : ''}
                    ${item.frequency ? `<div class="section-details"><strong>Frequency:</strong> ${item.frequency}</div>` : ''}
                    ${item.achieved ? `<div class="section-details"><strong>Status:</strong> ${item.achieved}</div>` : ''}
                    ${item.latitude && item.longitude ? `<div class="section-details"><strong>Coordinates:</strong> ${item.latitude}, ${item.longitude}</div>` : ''}
                  </div>
                `).join('') 
                : `<p>No ${sectionTitle.toLowerCase()} have been defined for this exercise.</p>`
              }
            </div>
            <div class="footer">
              <p>Generated on: ${currentDateTime} | Powered by EXRSIM</p>
            </div>
          </body>
        </html>
      `;

      const printWindow = window.open('', '_blank');
      writeSanitizedHTML(printWindow.document, printContent);
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    };

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">{sectionTitle}</h1>
          <div className="flex space-x-3">
            <Button 
              variant="outline"
              className="border-green-500/50 text-green-400 hover:bg-green-500/10"
              onClick={printGenericSection}
            >
              <Printer className="h-4 w-4 mr-2" />
              Print {sectionTitle}
            </Button>
            <Button 
              className="bg-orange-500 hover:bg-orange-600 text-black"
              onClick={() => {/* Add new item logic */}}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add {sectionTitle}
            </Button>
          </div>
        </div>

        {/* Current Data Display */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-orange-500">Current {sectionTitle} Data</CardTitle>
          </CardHeader>
          <CardContent>
            {Array.isArray(sectionData) && sectionData.length > 0 ? (
              <div className="space-y-4">
                {sectionData.map((item, index) => (
                  <div key={item.id || index} className="bg-gray-700/50 p-4 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-white font-medium mb-2">
                          {item.name || item.word || item.callsign || `${sectionTitle} ${index + 1}`}
                        </h3>
                        {item.description && (
                          <p className="text-gray-400 text-sm mb-2">{item.description}</p>
                        )}
                        {item.definition && (
                          <p className="text-gray-400 text-sm mb-2">Definition: {item.definition}</p>
                        )}
                        <div className="text-xs text-gray-500">
                          {Object.entries(item).filter(([key]) => !['id', 'name', 'description', 'definition'].includes(key))
                            .map(([key, value]) => `${key}: ${value}`).join(' | ')}
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="text-gray-400 mb-4">No {sectionTitle.toLowerCase()} data available</div>
                <Button 
                  className="bg-orange-500 hover:bg-orange-600 text-black"
                  onClick={() => {/* Add new item logic */}}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add First {sectionTitle}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* CRUD Interface Note */}
        <Card className="bg-blue-900/20 border-blue-500/30">
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Info className="h-5 w-5 text-blue-400" />
              <div className="text-blue-300 text-sm">
                Full CRUD interface for {sectionTitle.toLowerCase()} management will be implemented here. 
                Users will be able to create, edit, update, and delete {sectionTitle.toLowerCase()} with proper forms and validation.
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderEvaluationsManagement = () => {
    const handleAddNew = () => {
      setEditingEvaluation(null);
      setShowAddEvaluation(true);
    };

    const handleEdit = (report) => {
      setEditingEvaluation(report);
      setShowAddEvaluation(true);
    };

    const handleBack = () => {
      setShowAddEvaluation(false);
      setEditingEvaluation(null);
    };

    const handleSave = () => {
      setShowAddEvaluation(false);
      setEditingEvaluation(null);
      fetchEvaluationReports(); // Refresh the list
    };

    const handleDelete = async (reportId) => {
      if (window.confirm('Are you sure you want to delete this evaluation report?')) {
        try {
          const authToken = secureStorage.getToken();
          await axios.delete(`${API}/evaluation-reports/${reportId}`, {
            headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
          });
          setEvaluationReports(prev => prev.filter(report => report.id !== reportId));
        } catch (error) {
          console.error('Error deleting evaluation report:', error);
          const msg = error.response?.data?.detail || error.message || 'Delete failed';
          alert(`Could not delete: ${msg}`);
        }
      }
    };

    // Print function for Evaluations
    const printEvaluations = () => {
      if (evaluationReports.length === 0) {
        alert('No evaluation reports to print.');
        return;
      }

      let reportsToPrint = [];

      // If there's only one report, print it directly
      if (evaluationReports.length === 1) {
        reportsToPrint = evaluationReports;
      }
      // If there's a selected report, print only that one
      else if (selectedEvaluationId) {
        const selectedReport = evaluationReports.find(r => r.id === selectedEvaluationId);
        if (selectedReport) {
          reportsToPrint = [selectedReport];
        } else {
          alert('Selected evaluation report not found.');
          return;
        }
      }
      // If multiple reports and none selected, ask user which to print
      else {
        const reportOptions = evaluationReports.map((report, index) => 
          `${index + 1}. ${report.report_title} (by ${report.evaluator_name})`
        ).join('\n');
        
        const userChoice = prompt(
          `Multiple evaluation reports found. Which would you like to print?\n\n${reportOptions}\n\nEnter the number (1-${evaluationReports.length}) or 'all' to print all reports:`
        );

        if (!userChoice || userChoice.toLowerCase() === 'cancel') {
          return; // User cancelled
        }

        if (userChoice.toLowerCase() === 'all') {
          reportsToPrint = evaluationReports;
        } else {
          const choiceNum = parseInt(userChoice);
          if (choiceNum >= 1 && choiceNum <= evaluationReports.length) {
            reportsToPrint = [evaluationReports[choiceNum - 1]];
          } else {
            alert('Invalid selection. Please try again.');
            return;
          }
        }
      }

      const currentDateTime = new Date().toLocaleString();
      
      // Collect all unique images from reports to print
      const allImages = reportsToPrint.reduce((images, report) => {
        if (report.evaluation_images && report.evaluation_images.length > 0) {
          return images.concat(report.evaluation_images);
        }
        return images;
      }, []);
      
      const printContent = `
        <html>
          <head>
            <title>Exercise Evaluation Reports - ${exercise.name || 'Exercise'}</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; line-height: 1.6; }
              .header { border-bottom: 3px solid #333; margin-bottom: 30px; padding-bottom: 15px; }
              .header-images { margin: 15px 0; }
              .header-images img { width: 120px; height: 120px; object-fit: cover; margin-right: 15px; margin-bottom: 10px; border: 1px solid #ddd; border-radius: 4px; aspect-ratio: 1/1; }
              .report-item { border: 1px solid #ddd; margin-bottom: 25px; padding: 20px; border-radius: 8px; page-break-inside: avoid; }
              .report-title { font-size: 20px; font-weight: bold; margin-bottom: 15px; color: #333; }
              .section-title { font-size: 16px; font-weight: bold; margin: 20px 0 10px 0; color: #555; border-bottom: 1px solid #eee; padding-bottom: 5px; }
              .content { margin-bottom: 15px; color: #666; }
              .assessment-grid { display: block; margin: 10px 0; }
              .assessment-item { padding: 10px; border: 1px solid #ddd; border-radius: 4px; margin-bottom: 8px; page-break-inside: avoid; }
              .rating { font-weight: bold; color: #333; }
              .rating.excellent { color: #3b82f6; }
              .rating.above { color: #0ea5e9; }
              .rating.average { color: #22c55e; }
              .rating.below { color: #f59e0b; }
              .rating.unacceptable { color: #ef4444; }
              .rating.notassessed { color: #6b7280; font-style: italic; }
              .footer { 
                position: fixed; 
                bottom: 20px; 
                left: 20px; 
                right: 20px; 
                text-align: center; 
                font-size: 12px; 
                color: #666; 
                border-top: 1px solid #ddd; 
                padding-top: 10px; 
              }
              @media print { 
                @page { 
                  margin: 20px 20px 80px 20px; 
                  size: A4;
                }
                body { 
                  margin: 0; 
                  padding: 20px 20px 60px 20px; 
                  font-size: 12px;
                  line-height: 1.4;
                } 
                .no-print { display: none; }
                .footer { 
                  position: fixed; 
                  bottom: 20px; 
                  left: 20px; 
                  right: 20px;
                  height: 40px;
                  z-index: 999;
                }
                .report-item { 
                  page-break-inside: avoid; 
                  page-break-after: auto;
                  margin-bottom: 20px;
                }
                .section-title { 
                  page-break-after: avoid;
                }
                .assessment-grid { 
                  page-break-inside: avoid;
                }
              }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>Exercise Evaluation Reports</h1>
              <h2>${exercise.name || 'Exercise Name'}</h2>
              <p><strong>Exercise Type:</strong> ${exercise.exercise_type || 'N/A'}</p>
              <p><strong>Generated on:</strong> ${new Date().toLocaleDateString()}</p>
              
              ${allImages.length > 0 ? `
                <div class="header-images">
                  <h3>Supporting Images:</h3>
                  ${allImages.slice(0, 6).map(image => `<img src="${image}" alt="Evaluation Image" />`).join('')}
                </div>
              ` : ''}
            </div>
            <div class="reports-content">
              ${reportsToPrint.length > 0 ? 
                reportsToPrint.map((report, index) => `
                  <div class="report-item">
                    <div class="report-title">${report.report_title}</div>
                    <div class="content">
                      <strong>Evaluator:</strong> ${report.evaluator_name} ${report.evaluator_organization ? '(' + report.evaluator_organization + ')' : ''}<br>
                      <strong>Evaluation Date:</strong> ${report.evaluation_date}
                    </div>
                    
                    ${report.exercise_overview ? `
                      <div class="section-title">Exercise Overview</div>
                      <div class="content">${report.exercise_overview}</div>
                    ` : ''}
                    
                    ${report.summary_of_findings ? `
                      <div class="section-title">Summary of Findings</div>
                      <div class="content">${report.summary_of_findings}</div>
                    ` : ''}
                    
                    ${report.strengths ? `
                      <div class="section-title">Strengths</div>
                      <div class="content">${report.strengths}</div>
                    ` : ''}
                    
                    ${report.areas_for_improvement ? `
                      <div class="section-title">Areas for Improvement</div>
                      <div class="content">${report.areas_for_improvement}</div>
                    ` : ''}
                    
                    <div class="section-title">Overall Rating</div>
                    <div class="content">
                      <strong style="padding: 8px 16px; border: 2px solid #ddd; border-radius: 8px; background-color: ${
                        calculateOverallRatingHelper(report) === 'Excellent' ? '#3b82f6' :
                        calculateOverallRatingHelper(report) === 'Above Average' ? '#0ea5e9' :
                        calculateOverallRatingHelper(report) === 'Average' ? '#22c55e' :
                        calculateOverallRatingHelper(report) === 'Below Average' ? '#f59e0b' :
                        calculateOverallRatingHelper(report) === 'Not Assessed' ? '#6b7280' : '#ef4444'
                      }; color: white;">Overall Rating: ${calculateOverallRatingHelper(report)}</strong>
                      <br><small style="color: #666; font-style: italic;">Calculated as the average of the assessment areas that have been rated. Areas marked "Not Assessed" are excluded.</small>
                    </div>
                    
                    <div class="section-title">Key Areas Assessment</div>
                    <div class="assessment-grid">
                      <div class="assessment-item">
                        <strong>Command and Control</strong><br>
                        <span class="rating ${(report.command_and_control?.rating || '').toLowerCase().replace(' ', '')}">Rating: ${report.command_and_control?.rating || 'Average'}</span><br>
                        ${report.command_and_control?.comments || 'No comments'}
                      </div>
                      <div class="assessment-item">
                        <strong>Operations</strong><br>
                        <span class="rating ${(report.operations?.rating || '').toLowerCase().replace(' ', '')}">Rating: ${report.operations?.rating || 'Average'}</span><br>
                        ${report.operations?.comments || 'No comments'}
                      </div>
                      <div class="assessment-item">
                        <strong>Planning</strong><br>
                        <span class="rating ${(report.planning?.rating || '').toLowerCase().replace(' ', '')}">Rating: ${report.planning?.rating || 'Average'}</span><br>
                        ${report.planning?.comments || 'No comments'}
                      </div>
                      <div class="assessment-item">
                        <strong>Logistics</strong><br>
                        <span class="rating ${(report.logistics?.rating || '').toLowerCase().replace(' ', '')}">Rating: ${report.logistics?.rating || 'Average'}</span><br>
                        ${report.logistics?.comments || 'No comments'}
                      </div>
                      <div class="assessment-item">
                        <strong>Finance / Admin</strong><br>
                        <span class="rating ${(report.finance_admin?.rating || '').toLowerCase().replace(' ', '')}">Rating: ${report.finance_admin?.rating || 'Average'}</span><br>
                        ${report.finance_admin?.comments || 'No comments'}
                      </div>
                      <div class="assessment-item">
                        <strong>Communication</strong><br>
                        <span class="rating ${(report.communication?.rating || '').toLowerCase().replace(' ', '')}">Rating: ${report.communication?.rating || 'Average'}</span><br>
                        ${report.communication?.comments || 'No comments'}
                      </div>
                      <div class="assessment-item">
                        <strong>Resource Management</strong><br>
                        <span class="rating ${(report.resource_management?.rating || '').toLowerCase().replace(' ', '')}">Rating: ${report.resource_management?.rating || 'Average'}</span><br>
                        ${report.resource_management?.comments || 'No comments'}
                      </div>
                      <div class="assessment-item">
                        <strong>Safety and Security</strong><br>
                        <span class="rating ${(report.safety_and_security?.rating || '').toLowerCase().replace(' ', '')}">Rating: ${report.safety_and_security?.rating || 'Average'}</span><br>
                        ${report.safety_and_security?.comments || 'No comments'}
                      </div>
                      <div class="assessment-item">
                        <strong>Training and Readiness</strong><br>
                        <span class="rating ${(report.training_and_readiness?.rating || '').toLowerCase().replace(' ', '')}">Rating: ${report.training_and_readiness?.rating || 'Average'}</span><br>
                        ${report.training_and_readiness?.comments || 'No comments'}
                      </div>
                    </div>
                    
                    ${report.key_findings_narrative ? `
                      <div class="section-title">Key Findings (Narrative)</div>
                      <div class="content">${report.key_findings_narrative}</div>
                    ` : ''}
                    
                    ${report.recommendations ? `
                      <div class="section-title">Recommendations</div>
                      <div class="content">${report.recommendations}</div>
                    ` : ''}
                    
                    ${report.appendices ? `
                      <div class="section-title">Appendices (Notes)</div>
                      <div class="content">${report.appendices}</div>
                    ` : ''}
                    
                    ${report.appendix_files && report.appendix_files.length > 0 ? `
                      <div class="section-title">Appendices (Attached Files)</div>
                      <ul class="content">
                        ${report.appendix_files.map(f => `<li>${f.name || 'file'}${f.size ? ` (${(f.size/1024).toFixed(1)} KB)` : ''}</li>`).join('')}
                      </ul>
                    ` : ''}
                  </div>
                `).join('') 
                : '<p>No evaluation reports available for this exercise.</p>'
              }
            </div>
            <div class="footer">
              <p>Generated on: ${currentDateTime} | Powered by EXRSIM</p>
            </div>
          </body>
        </html>
      `;

      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        alert('The print window was blocked by your browser. Please allow pop-ups for this site and try again.');
        return;
      }
      writeSanitizedHTML(printWindow.document, printContent);
      printWindow.focus();

      // Wait for images (base64) and layout before invoking print. Do NOT
      // call printWindow.close() right after print() — Chrome/Edge close
      // the popup before the print dialog can open, resulting in a blank
      // preview or nothing being printed. Let onafterprint close it.
      const doPrint = () => {
        try {
          printWindow.onafterprint = () => { try { printWindow.close(); } catch(_) {} };
          printWindow.print();
        } catch (e) {
          console.error('Print failed:', e);
        }
      };

      const imgs = Array.from(printWindow.document.images || []);
      if (imgs.length === 0) {
        setTimeout(doPrint, 300);
      } else {
        let remaining = imgs.length;
        let fired = false;
        const trigger = () => { if (!fired) { fired = true; doPrint(); } };
        imgs.forEach(img => {
          if (img.complete) {
            remaining -= 1;
            if (remaining <= 0) trigger();
          } else {
            const done = () => { remaining -= 1; if (remaining <= 0) trigger(); };
            img.addEventListener('load', done);
            img.addEventListener('error', done);
          }
        });
        setTimeout(trigger, 3000);
      }
    };

    if (showAddEvaluation) {
      return (
        <EvaluationReportForm
          key={editingEvaluation?.id || 'new-evaluation'}
          exerciseId={exerciseId}
          editingReport={editingEvaluation}
          onBack={handleBack}
          onSave={handleSave}
        />
      );
    }

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${theme.colors.textPrimary}`}>Exercise Evaluations</h1>
            {evaluationReports.length > 1 && (
              <p className={`text-sm ${theme.colors.textMuted} mt-1`}>
                Click on a report to select it for printing, or use "Print Evaluations" to choose
              </p>
            )}
          </div>
          <div className="flex space-x-3">
            <Button 
              variant="outline"
              className="border-green-500/50 text-green-400 hover:bg-green-500/10"
              onClick={printEvaluations}
              title={evaluationReports.length > 1 ? "Click a report to select it for printing, or print all reports" : "Print evaluation report"}
            >
              <Printer className="h-4 w-4 mr-2" />
              {selectedEvaluationId ? "Print Selected" : "Print Evaluations"}
            </Button>
            <Button 
              className="bg-orange-500 hover:bg-orange-600 text-black"
              onClick={handleAddNew}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Evaluation
            </Button>
          </div>
        </div>

        {evaluationLoading ? (
          <Card className={`${theme.colors.tertiary} ${theme.colors.border}`}>
            <CardContent className="p-12 text-center">
              <div className={theme.colors.textMuted}>Loading evaluation reports...</div>
            </CardContent>
          </Card>
        ) : evaluationReports.length === 0 ? (
          <Card className={`${theme.colors.tertiary} ${theme.colors.border} border-dashed`}>
            <CardContent className="p-12 text-center">
              <Star className={`h-12 w-12 ${theme.colors.textMuted} mx-auto mb-4`} />
              <h3 className={`text-lg font-semibold ${theme.colors.textSecondary} mb-2`}>No Evaluations Yet</h3>
              <p className={`${theme.colors.textMuted} mb-4`}>Add exercise evaluations to track performance and effectiveness.</p>
              <Button 
                className="bg-orange-500 hover:bg-orange-600 text-black"
                onClick={handleAddNew}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add First Evaluation
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {evaluationReports.map((report) => (
              <Card 
                key={report.id} 
                className={`${theme.colors.tertiary} ${theme.colors.border} cursor-pointer transition-all ${
                  selectedEvaluationId === report.id 
                    ? 'border-orange-500 ring-2 ring-orange-500/20 bg-orange-500/5' 
                    : `${theme.colors.hover}`
                }`}
                onClick={() => setSelectedEvaluationId(selectedEvaluationId === report.id ? null : report.id)}
              >
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <CardTitle className={`${theme.colors.textPrimary} text-lg`}>{report.report_title}</CardTitle>
                        {(() => {
                          const overall = calculateOverallRatingHelper(report);
                          const badgeClass =
                            overall === 'Excellent' ? 'border-blue-500 text-blue-400 bg-blue-500/10' :
                            overall === 'Above Average' ? 'border-sky-400 text-sky-400 bg-sky-400/10' :
                            overall === 'Average' ? 'border-green-500 text-green-400 bg-green-500/10' :
                            overall === 'Below Average' ? 'border-orange-500 text-orange-400 bg-orange-500/10' :
                            overall === 'Not Assessed' ? 'border-gray-400 text-gray-300 bg-gray-500/10 italic' :
                            'border-red-500 text-red-400 bg-red-500/10';
                          return (
                            <Badge
                              variant="outline"
                              className={`text-xs px-2 py-1 ${badgeClass}`}
                              data-testid={`evaluation-overall-rating-${report.id}`}
                            >
                              Overall Rating: {overall}
                            </Badge>
                          );
                        })()}
                        {selectedEvaluationId === report.id && (
                          <Badge variant="outline" className="border-orange-500 text-orange-400 bg-orange-500/10 text-xs">
                            Selected for Print
                          </Badge>
                        )}
                      </div>
                      <CardDescription className={theme.colors.textMuted}>
                        By {report.evaluator_name} {report.evaluator_organization && `(${report.evaluator_organization})`} • {report.evaluation_date}
                      </CardDescription>
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-orange-500 border-orange-500 hover:bg-orange-500 hover:text-black"
                        onClick={(e) => { e.stopPropagation(); handleEdit(report); }}
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-400 border-red-400 hover:bg-red-400 hover:text-white"
                        onClick={(e) => { e.stopPropagation(); handleDelete(report.id); }}
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {report.summary_of_findings && (
                    <div>
                      <h4 className={`font-semibold ${theme.colors.textPrimary} mb-2`}>Summary of Findings</h4>
                      <p className={`${theme.colors.textSecondary} text-sm`}>{report.summary_of_findings}</p>
                    </div>
                  )}
                  
                  <div>
                    <h4 className={`font-semibold ${theme.colors.textPrimary} mb-2`}>Key Areas Assessment</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                      {[
                        { label: 'Command & Control', data: report.command_and_control },
                        { label: 'Operations', data: report.operations },
                        { label: 'Planning', data: report.planning },
                        { label: 'Logistics', data: report.logistics },
                        { label: 'Finance / Admin', data: report.finance_admin },
                        { label: 'Communication', data: report.communication },
                        { label: 'Resource Management', data: report.resource_management },
                        { label: 'Safety & Security', data: report.safety_and_security },
                        { label: 'Training & Readiness', data: report.training_and_readiness }
                      ].map((area, index) => (
                        <div key={`analytic-${area.label}`} className={`${theme.colors.quaternary} p-2 rounded text-center`}>
                          <div className={`text-xs ${theme.colors.textMuted} mb-1`}>{area.label}</div>
                          <div className={`text-xs font-semibold ${
                            area.data?.rating === 'Excellent' ? 'text-blue-400' :
                            area.data?.rating === 'Above Average' ? 'text-sky-400' :
                            area.data?.rating === 'Average' ? 'text-green-400' :
                            area.data?.rating === 'Below Average' ? 'text-orange-400' :
                            area.data?.rating === 'Unacceptable' ? 'text-red-400' :
                            area.data?.rating === 'Not Assessed' ? 'text-gray-400 italic' :
                            theme.colors.textMuted
                          }`}>
                            {area.data?.rating || 'Not Rated'}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Helper function to create print functions for improvement sections
  const createPrintFunction = (sectionName, sectionTitle) => {
    return () => {
      const currentDateTime = new Date().toLocaleString();
      const printContent = `
        <html>
          <head>
            <title>${sectionTitle} - ${exercise.name || 'Exercise'}</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              .header { border-bottom: 2px solid #333; margin-bottom: 20px; padding-bottom: 10px; }
              .item { border: 1px solid #ddd; margin-bottom: 15px; padding: 15px; border-radius: 5px; }
              .item-title { font-size: 18px; font-weight: bold; margin-bottom: 10px; }
              .item-description { margin-bottom: 10px; color: #666; }
              .footer { 
                position: fixed; 
                bottom: 20px; 
                left: 20px; 
                right: 20px; 
                text-align: center; 
                font-size: 12px; 
                color: #666; 
                border-top: 1px solid #ddd; 
                padding-top: 10px; 
              }
              @media print { 
                @page { 
                  margin: 20px 20px 80px 20px; 
                  size: A4;
                }
                body { 
                  margin: 0; 
                  padding: 20px 20px 60px 20px; 
                  font-size: 12px;
                  line-height: 1.4;
                } 
                .no-print { display: none; }
                .footer { 
                  position: fixed; 
                  bottom: 20px; 
                  left: 20px; 
                  right: 20px;
                  height: 40px;
                  z-index: 999;
                }
                .report-item { 
                  page-break-inside: avoid; 
                  page-break-after: auto;
                  margin-bottom: 20px;
                }
                .section-title { 
                  page-break-after: avoid;
                }
                .assessment-grid { 
                  page-break-inside: avoid;
                }
              }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>${sectionTitle}</h1>
              <h2>${exercise.name || 'Exercise Name'}</h2>
              <p>Exercise Type: ${exercise.exercise_type || 'N/A'}</p>
              <p>Generated on: ${new Date().toLocaleDateString()}</p>
            </div>
            <div class="content">
              <p>${sectionTitle} data for this exercise will be displayed here.</p>
            </div>
            <div class="footer">
              <p>Generated on: ${currentDateTime} | Powered by EXRSIM</p>
            </div>
          </body>
        </html>
      `;

      const printWindow = window.open('', '_blank');
      writeSanitizedHTML(printWindow.document, printContent);
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    };
  };

// Lessons Learned Form Component  
// LessonsLearnedForm moved to components/LessonsLearned.js

  // DOTMPLFICC options with colors (shared between form and management)
  const dotmplficcOptions = [
    { value: 'Doctrine', label: 'Doctrine', color: '#3b82f6' }, // Blue
    { value: 'Organization', label: 'Organization', color: '#10b981' }, // Emerald
    { value: 'Training', label: 'Training', color: '#f59e0b' }, // Amber
    { value: 'Maint and Equip', label: 'Maint and Equip', color: '#8b5cf6' }, // Violet
    { value: 'Personnel', label: 'Personnel', color: '#06b6d4' }, // Cyan
    { value: 'Leadership', label: 'Leadership', color: '#ef4444' }, // Red
    { value: 'Facilities', label: 'Facilities', color: '#84cc16' }, // Lime
    { value: 'Interoperability', label: 'Interoperability', color: '#f97316' }, // Orange
    { value: 'Communications', label: 'Communications', color: '#ec4899' }, // Pink
    { value: 'Command and Control', label: 'Command and Control', color: '#6366f1' } // Indigo
  ];

  const renderLessonsLearnedManagement = () => {
    // Lessons Learned handlers
    const handleAddNewLesson = () => {
      setEditingLesson(null);
      setShowAddLesson(true);
    };

    const handleEditLesson = (lesson) => {
      setEditingLesson(lesson);
      setShowAddLesson(true);
    };

    const handleDeleteLesson = async (lessonId) => {
      if (window.confirm('Are you sure you want to delete this lesson learned?')) {
        try {
          const token = secureStorage.getToken();
          const headers = token ? { Authorization: `Bearer ${token}` } : {};
          
          const response = await fetch(`${API_BASE_URL}/api/lessons-learned/${lessonId}`, {
            method: 'DELETE',
            headers
          });

          if (response.ok) {
            await fetchLessonsLearned();
          } else {
            throw new Error('Failed to delete lesson learned');
          }
        } catch (error) {
          console.error('Error deleting lesson learned:', error);
          alert('Failed to delete lesson learned. Please try again.');
        }
      }
    };

    const handleSaveLesson = async (savedLesson) => {
      setShowAddLesson(false);
      setEditingLesson(null);
      await fetchLessonsLearned();
      // fetch already resets expandedLessons; keep newly saved entry collapsed too
    };

    const toggleLessonExpanded = (lessonId) => {
      setExpandedLessons(prev => {
        const next = new Set(prev);
        if (next.has(lessonId)) next.delete(lessonId); else next.add(lessonId);
        return next;
      });
    };

    const expandAllLessons = () => {
      setExpandedLessons(new Set(lessonsLearned.map(l => l.id)));
    };

    const collapseAllLessons = () => {
      setExpandedLessons(new Set());
    };

    const handleBackFromForm = () => {
      setShowAddLesson(false);
      setEditingLesson(null);
    };

    // Print function for Lessons Learned
    const printLessonsLearned = () => {
      if (lessonsLearned.length === 0) {
        alert('No lessons learned to print.');
        return;
      }

      let lessonsToPrint = [];

      // If there's only one lesson, print it directly
      if (lessonsLearned.length === 1) {
        lessonsToPrint = lessonsLearned;
      }
      // If there's a selected lesson, print only that one
      else if (selectedLessonId) {
        const selectedLesson = lessonsLearned.find(l => l.id === selectedLessonId);
        if (selectedLesson) {
          lessonsToPrint = [selectedLesson];
        } else {
          alert('Selected lesson learned not found.');
          return;
        }
      }
      // If multiple lessons and none selected, ask user which to print
      else {
        const lessonOptions = lessonsLearned.map((lesson, index) => 
          `${index + 1}. ${lesson.name} (Serial #${lesson.serial_number})`
        ).join('\n');
        
        const userChoice = prompt(
          `Multiple lessons learned found. Which would you like to print?\n\n${lessonOptions}\n\nEnter the number (1-${lessonsLearned.length}) or 'all' to print all lessons:`
        );

        if (!userChoice || userChoice.toLowerCase() === 'cancel') {
          return; // User cancelled
        }

        if (userChoice.toLowerCase() === 'all') {
          lessonsToPrint = lessonsLearned;
        } else {
          const choiceNum = parseInt(userChoice);
          if (choiceNum >= 1 && choiceNum <= lessonsLearned.length) {
            lessonsToPrint = [lessonsLearned[choiceNum - 1]];
          } else {
            alert('Invalid selection. Please try again.');
            return;
          }
        }
      }

      const currentDateTime = new Date().toLocaleString();
      
      // Collect all unique images from lessons to print
      const allImages = lessonsToPrint.reduce((images, lesson) => {
        if (lesson.lesson_images && lesson.lesson_images.length > 0) {
          return images.concat(lesson.lesson_images);
        }
        return images;
      }, []);
      
      const printContent = `
        <html>
          <head>
            <title>Exercise Lessons Learned - ${exercise.name || 'Exercise'}</title>
            <style>
              @page { 
                margin: 20px 20px 80px 20px; 
                size: A4;
              }
              body { 
                font-family: Arial, sans-serif; 
                margin: 0; 
                padding: 20px 20px 60px 20px; 
                font-size: 12px;
                line-height: 1.4;
              }
              .header { border-bottom: 3px solid #333; margin-bottom: 30px; padding-bottom: 15px; }
              .header-images { margin: 15px 0; }
              .header-images img { width: 120px; height: 120px; object-fit: cover; margin-right: 15px; margin-bottom: 10px; border: 1px solid #ddd; border-radius: 4px; aspect-ratio: 1/1; }
              .lesson-item { 
                border: 1px solid #ddd; 
                margin-bottom: 25px; 
                padding: 20px; 
                border-radius: 8px; 
                page-break-inside: avoid;
                page-break-after: auto;
              }
              .lesson-title { font-size: 20px; font-weight: bold; margin-bottom: 15px; color: #333; }
              .section-title { 
                font-size: 16px; 
                font-weight: bold; 
                margin: 20px 0 10px 0; 
                color: #555; 
                border-bottom: 1px solid #eee; 
                padding-bottom: 5px;
                page-break-after: avoid;
              }
              .content { margin-bottom: 15px; color: #666; }
              .priority { 
                padding: 4px 8px; 
                border-radius: 4px; 
                font-weight: bold; 
                color: white; 
              }
              .priority.pri-1 { background-color: #ef4444; }
              .priority.pri-2 { background-color: #f59e0b; }
              .priority.pri-3 { background-color: #22c55e; }
              .priority.pri-4 { background-color: #3b82f6; }
              .dotmplficc { 
                padding: 4px 8px; 
                border-radius: 4px; 
                font-weight: bold; 
                color: white; 
                margin-left: 10px;
              }
              .dotmplficc.doctrine { background-color: #3b82f6; }
              .dotmplficc.organization { background-color: #10b981; }
              .dotmplficc.training { background-color: #f59e0b; }
              .dotmplficc.maint { background-color: #8b5cf6; }
              .dotmplficc.personnel { background-color: #06b6d4; }
              .dotmplficc.leadership { background-color: #ef4444; }
              .dotmplficc.facilities { background-color: #84cc16; }
              .dotmplficc.interoperability { background-color: #f97316; }
              .dotmplficc.communications { background-color: #ec4899; }
              .dotmplficc.command { background-color: #6366f1; }
              .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin: 10px 0; }
              .grid-item { padding: 10px; border: 1px solid #ddd; border-radius: 4px; }
              .footer { 
                position: fixed; 
                bottom: 20px; 
                left: 20px; 
                right: 20px;
                text-align: center; 
                font-size: 12px; 
                color: #666; 
                border-top: 1px solid #ddd; 
                padding-top: 10px;
                height: 40px;
                z-index: 999;
              }
              .no-print { display: none; }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>Exercise Lessons Learned</h1>
              <h2>${exercise.name || 'Exercise Name'}</h2>
              <p><strong>Exercise Type:</strong> ${exercise.exercise_type || 'N/A'}</p>
              <p><strong>Generated on:</strong> ${new Date().toLocaleDateString()}</p>
              
              ${allImages.length > 0 ? `
                <div class="header-images">
                  <h3>Supporting Images:</h3>
                  ${allImages.slice(0, 6).map(image => `<img src="${image}" alt="Lesson Image" />`).join('')}
                </div>
              ` : ''}
            </div>
            <div class="lessons-content">
              ${lessonsToPrint.length > 0 ? 
                lessonsToPrint.map((lesson, index) => `
                  <div class="lesson-item">
                    <div class="lesson-title">${lesson.name}</div>
                    <div class="content">
                      <strong>Serial Number:</strong> ${lesson.serial_number || 'N/A'}<br>
                      <strong>Priority:</strong> <span class="priority ${lesson.priority.toLowerCase().replace(' ', '-')}">${lesson.priority}</span><br>
                      <strong>Date:</strong> ${lesson.date}<br>
                      <strong>DOTMPLFICC:</strong> <span class="dotmplficc ${lesson.dotmplficc.toLowerCase().replace(/\s+/g, '').replace('and', '')}">${lesson.dotmplficc}</span>
                    </div>
                    
                    ${lesson.references ? `
                      <div class="section-title">References</div>
                      <div class="content">${lesson.references}</div>
                    ` : ''}
                    
                    <div class="section-title">Observations and Recommended Actions</div>
                    ${lesson.issues_observation ? `
                      <div class="content">
                        <strong>Issues/Observation:</strong><br>${lesson.issues_observation}
                      </div>
                    ` : ''}
                    ${lesson.recommendations ? `
                      <div class="content">
                        <strong>Recommendations:</strong><br>${lesson.recommendations}
                      </div>
                    ` : ''}
                    
                    <div class="section-title">Analysis and Actions</div>
                    <div class="grid">
                      ${lesson.additional_comments ? `
                        <div class="grid-item">
                          <strong>Additional Comments:</strong><br>${lesson.additional_comments}
                        </div>
                      ` : ''}
                      ${lesson.recommended_actions ? `
                        <div class="grid-item">
                          <strong>Recommended Actions:</strong><br>${lesson.recommended_actions}
                        </div>
                      ` : ''}
                      ${lesson.final_authority_remarks ? `
                        <div class="grid-item">
                          <strong>Final Authority Remarks:</strong><br>${lesson.final_authority_remarks}
                        </div>
                      ` : ''}
                      ${lesson.testing_done_by || lesson.procedures_written_by || lesson.implement_new_ll_by ? `
                        <div class="grid-item">
                          <strong>Timeline:</strong><br>
                          ${lesson.testing_done_by ? `Testing done by: ${lesson.testing_done_by}<br>` : ''}
                          ${lesson.procedures_written_by ? `Procedures written by: ${lesson.procedures_written_by}<br>` : ''}
                          ${lesson.implement_new_ll_by ? `Implement new LL by: ${lesson.implement_new_ll_by}` : ''}
                        </div>
                      ` : ''}
                    </div>
                  </div>
                `).join('') : 
                '<div class="lesson-item"><p>No lessons learned found.</p></div>'
              }
            </div>
            <div class="footer">
              <p>Generated on ${currentDateTime} | Page <span id="pageNumber"></span></p>
            </div>
          </body>
        </html>
      `;

      const printWindow = window.open('', '_blank');
      writeSanitizedHTML(printWindow.document, printContent);
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    };

    // Show form if adding/editing
    if (showAddLesson) {
      return (
        <LessonsLearnedForm
          exerciseId={exerciseId}
          editingLesson={editingLesson}
          onBack={handleBackFromForm}
          onSave={handleSaveLesson}
          dotmplficcOptions={dotmplficcOptions}
        />
      );
    }

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Lessons Learned</h1>
            {lessonsLearned.length > 1 && (
              <p className="text-sm text-gray-400 mt-1">
                Click on a lesson to select it for printing, or use "Print Lessons Learned" to choose
              </p>
            )}
          </div>
          <div className="flex space-x-3">
            <Button
              variant="outline"
              className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
              onClick={expandAllLessons}
              disabled={lessonsLearned.length === 0}
              data-testid="lessons-expand-all-btn"
            >
              <ChevronDown className="h-4 w-4 mr-2" />
              Expand All
            </Button>
            <Button
              variant="outline"
              className="border-gray-500/50 text-gray-300 hover:bg-gray-500/10"
              onClick={collapseAllLessons}
              disabled={lessonsLearned.length === 0}
              data-testid="lessons-collapse-all-btn"
            >
              <ChevronRight className="h-4 w-4 mr-2" />
              Collapse All
            </Button>
            <Button 
              variant="outline"
              className="border-green-500/50 text-green-400 hover:bg-green-500/10"
              onClick={printLessonsLearned}
              title={lessonsLearned.length > 1 ? "Click a lesson to select it for printing, or print all lessons" : "Print lessons learned"}
            >
              <Printer className="h-4 w-4 mr-2" />
              {selectedLessonId ? "Print Selected" : "Print Lessons Learned"}
            </Button>
            <Button 
              className="bg-orange-500 hover:bg-orange-600 text-black"
              onClick={handleAddNewLesson}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Lesson Learned
            </Button>
          </div>
        </div>

        {lessonsLoading ? (
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-12 text-center">
              <div className="text-gray-400">Loading lessons learned...</div>
            </CardContent>
          </Card>
        ) : lessonsLearned.length === 0 ? (
          <Card className="bg-gray-800 border-gray-700 border-dashed">
            <CardContent className="p-12 text-center">
              <Lightbulb className="h-12 w-12 text-gray-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-300 mb-2">No Lessons Learned Yet</h3>
              <p className="text-gray-500 mb-4">Document key lessons and insights from the exercise.</p>
              <Button 
                className="bg-orange-500 hover:bg-orange-600 text-black"
                onClick={handleAddNewLesson}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add First Lesson
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {lessonsLearned.map((lesson) => {
              const isExpanded = expandedLessons.has(lesson.id);
              const priorityColor =
                lesson.priority === 'Pri 1' ? '#ef4444' :
                lesson.priority === 'Pri 2' ? '#f59e0b' :
                lesson.priority === 'Pri 3' ? '#22c55e' : '#3b82f6';
              const statusColor =
                lesson.observation_status === 'worked_well' ? '#16a34a' :
                lesson.observation_status === 'needs_improvement' ? '#dc2626' : '#6b7280';
              return (
              <Card 
                key={lesson.id} 
                className={`bg-gray-800 border-gray-700 transition-all overflow-hidden ${
                  selectedLessonId === lesson.id 
                    ? 'border-orange-500 ring-2 ring-orange-500/20 bg-orange-500/5' 
                    : 'hover:border-gray-600 hover:bg-gray-750'
                }`}
                style={{ borderLeft: `8px solid ${priorityColor}` }}
                data-testid={`lesson-card-${lesson.id}`}
              >
                <CardHeader
                  className="cursor-pointer px-6 py-4 sm:px-8"
                  onClick={() => toggleLessonExpanded(lesson.id)}
                  data-testid={`lesson-card-header-${lesson.id}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1 flex items-start gap-3">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); toggleLessonExpanded(lesson.id); }}
                        className="mt-1 text-gray-400 hover:text-orange-400 flex-shrink-0"
                        data-testid={`lesson-toggle-${lesson.id}`}
                        aria-label={isExpanded ? 'Collapse lesson' : 'Expand lesson'}
                      >
                        {isExpanded ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <CardTitle className="text-white text-lg">{lesson.name}</CardTitle>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedLessonId(selectedLessonId === lesson.id ? null : lesson.id);
                            }}
                            className="text-xs underline text-orange-300 hover:text-orange-400"
                            data-testid={`lesson-select-print-${lesson.id}`}
                          >
                            {selectedLessonId === lesson.id ? 'Unselect' : 'Select for Print'}
                          </button>
                          {selectedLessonId === lesson.id && (
                            <Badge variant="outline" className="border-orange-500 text-orange-400 bg-orange-500/10 text-xs">
                              Selected for Print
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center justify-between gap-4 text-sm text-gray-400 flex-wrap">
                          <div className="flex items-center gap-4 flex-wrap">
                            <span>Serial #{lesson.serial_number}</span>
                            <Badge 
                              className="text-white"
                              style={{ backgroundColor: priorityColor }}
                            >
                              {lesson.priority}
                            </Badge>
                            <span>{lesson.date}</span>
                            <Badge 
                              className="text-white"
                              style={{
                                backgroundColor: dotmplficcOptions.find(opt => opt.value === lesson.dotmplficc)?.color || '#6b7280'
                              }}
                            >
                              {lesson.dotmplficc}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2 ml-auto">
                            <span className="text-xs uppercase tracking-wide text-gray-500">Status</span>
                            {lesson.observation_status ? (
                              <Badge 
                                className={`text-white ${
                                  lesson.observation_status === 'worked_well' ? 'bg-green-600' :
                                  lesson.observation_status === 'needs_improvement' ? 'bg-red-600' : 'bg-gray-600'
                                }`}
                              >
                                {lesson.observation_status === 'worked_well' ? 'Worked Well' : 
                                 lesson.observation_status === 'needs_improvement' ? 'Needs Improvement' : lesson.observation_status}
                              </Badge>
                            ) : (
                              <Badge className="text-white bg-gray-600">Not Set</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-orange-500 border-orange-500 hover:bg-orange-500 hover:text-black"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditLesson(lesson);
                        }}
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-400 border-red-400 hover:bg-red-400 hover:text-white"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteLesson(lesson.id);
                        }}
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                {isExpanded && (
                  <CardContent className="space-y-3 px-6 sm:px-8 pb-6">
                    {lesson.references && (
                      <div>
                        <h4 className="font-semibold text-white text-sm mb-1">References</h4>
                        <p className="text-gray-300 text-sm">{lesson.references}</p>
                      </div>
                    )}
                    
                    {lesson.issues_observation && (
                      <div>
                        <h4 className="font-semibold text-white text-sm mb-1">Issues/Observation</h4>
                        <p className="text-gray-300 text-sm whitespace-pre-wrap">{lesson.issues_observation}</p>
                      </div>
                    )}
                    
                    {lesson.recommendations && (
                      <div>
                        <h4 className="font-semibold text-white text-sm mb-1">Recommendations</h4>
                        <p className="text-gray-300 text-sm whitespace-pre-wrap">{lesson.recommendations}</p>
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderDeficienciesManagement = () => {
    const printDeficiencies = createPrintFunction('deficiencies', 'Exercise Deficiencies');

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Exercise Deficiencies</h1>
          <div className="flex space-x-3">
            <Button 
              variant="outline"
              className="border-green-500/50 text-green-400 hover:bg-green-500/10"
              onClick={printDeficiencies}
            >
              <Printer className="h-4 w-4 mr-2" />
              Print Deficiencies
            </Button>
            <Button 
              className="bg-orange-500 hover:bg-orange-600 text-black"
              onClick={() => {/* Add new deficiency logic */}}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Deficiency
            </Button>
          </div>
        </div>

        <Card className="bg-gray-800 border-gray-700 border-dashed">
          <CardContent className="p-12 text-center">
            <AlertCircle className="h-12 w-12 text-gray-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-300 mb-2">No Deficiencies Recorded</h3>
            <p className="text-gray-500 mb-4">Track areas that need improvement or fell short of expectations.</p>
            <Button className="bg-orange-500 hover:bg-orange-600 text-black">
              <Plus className="h-4 w-4 mr-2" />
              Add First Deficiency
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderNearMissesManagement = () => {
    const printNearMisses = createPrintFunction('nearmisses', 'Exercise Near Misses');

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Near Misses</h1>
          <div className="flex space-x-3">
            <Button 
              variant="outline"
              className="border-green-500/50 text-green-400 hover:bg-green-500/10"
              onClick={printNearMisses}
            >
              <Printer className="h-4 w-4 mr-2" />
              Print Near Misses
            </Button>
            <Button 
              className="bg-orange-500 hover:bg-orange-600 text-black"
              onClick={() => {/* Add new near miss logic */}}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Near Miss
            </Button>
          </div>
        </div>

        <Card className="bg-gray-800 border-gray-700 border-dashed">
          <CardContent className="p-12 text-center">
            <Zap className="h-12 w-12 text-gray-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-300 mb-2">No Near Misses Reported</h3>
            <p className="text-gray-500 mb-4">Document incidents that almost occurred but were avoided.</p>
            <Button className="bg-orange-500 hover:bg-orange-600 text-black">
              <Plus className="h-4 w-4 mr-2" />
              Add First Near Miss
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderCommentsManagement = () => {
    const printComments = createPrintFunction('comments', 'Exercise Comments');

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">Exercise Comments</h1>
          <div className="flex space-x-3">
            <Button 
              variant="outline"
              className="border-green-500/50 text-green-400 hover:bg-green-500/10"
              onClick={printComments}
            >
              <Printer className="h-4 w-4 mr-2" />
              Print Comments
            </Button>
            <Button 
              className="bg-orange-500 hover:bg-orange-600 text-black"
              onClick={() => {/* Add new comment logic */}}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Comment
            </Button>
          </div>
        </div>

        <Card className="bg-gray-800 border-gray-700 border-dashed">
          <CardContent className="p-12 text-center">
            <MessageCircle className="h-12 w-12 text-gray-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-300 mb-2">No Comments Yet</h3>
            <p className="text-gray-500 mb-4">Add general observations and feedback about the exercise.</p>
            <Button className="bg-orange-500 hover:bg-orange-600 text-black">
              <Plus className="h-4 w-4 mr-2" />
              Add First Comment
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  };


  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className={theme.colors.textMuted}>Loading exercise...</p>
        </div>
      </div>
    );
  }

  if (!exercise) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="text-center">
          <ShieldAlert className={`h-12 w-12 ${theme.colors.textMuted} mx-auto mb-4`} />
          <h3 className={`text-lg font-semibold ${theme.colors.textSecondary} mb-2`}>Exercise Not Found</h3>
          <p className={`${theme.colors.textMuted} mb-4`}>The requested exercise could not be loaded.</p>
          <Button 
            variant="outline"
            onClick={() => window.location.href = '#dashboard'}
            className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
          >
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${theme.colors.primary} ${theme.colors.textPrimary}`}>
      <div className="flex">
        {/* Exercise-Specific Sidebar */}
        <div className={`${exerciseSidebarCollapsed ? 'w-16' : 'w-80'} ${theme.colors.secondary} border-r ${theme.colors.borderAccent} h-screen sticky top-0 transition-all duration-300 relative`}>
          {/* Collapse/Expand Toggle Button */}
          <button
            onClick={() => setExerciseSidebarCollapsed(!exerciseSidebarCollapsed)}
            className="absolute -right-3 top-20 z-10 bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded-full p-1 shadow-lg transition-colors"
            title={exerciseSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {exerciseSidebarCollapsed ? (
              <ChevronRight className="h-4 w-4 text-orange-400" />
            ) : (
              <ChevronLeft className="h-4 w-4 text-orange-400" />
            )}
          </button>
          
          <div className="p-4">
            <Button 
              variant="ghost" 
              className={`w-full ${exerciseSidebarCollapsed ? 'justify-center px-2' : 'justify-start'} text-orange-500 hover:text-orange-400 mb-4`}
              onClick={() => window.location.href = '#dashboard'}
              title={exerciseSidebarCollapsed ? 'Back to Dashboard' : ''}
            >
              <ArrowLeft className={`h-4 w-4 ${exerciseSidebarCollapsed ? '' : 'mr-2'} flex-shrink-0`} />
              {!exerciseSidebarCollapsed && 'Back to Dashboard'}
            </Button>
            
            {!exerciseSidebarCollapsed && (
              <div className="mb-6">
                <h2 className={`text-lg font-semibold ${theme.colors.textPrimary} mb-2`}>{exercise.exercise_name}</h2>
                <Badge className={getStatusColor(getExerciseStatus())}>
                  {getExerciseStatus()}
                </Badge>
              </div>
            )}
            
            <ScrollArea className="h-[calc(100vh-200px)]">
              <div className="space-y-1">
                {/* Top Level Menu Items */}
                {topLevelMenuItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Button
                      key={item.id}
                      variant={activeSection === item.id ? "secondary" : "ghost"}
                      className={`w-full ${exerciseSidebarCollapsed ? 'justify-center px-2' : 'justify-start'} text-left ${
                        activeSection === item.id
                          ? "bg-orange-500/20 text-orange-300 border-orange-500/50"
                          : `${theme.colors.textSecondary} hover:text-orange-500 ${theme.colors.hover}`
                      }`}
                      onClick={() => setActiveSection(item.id)}
                      title={exerciseSidebarCollapsed ? item.title : ''}
                    >
                      <Icon className={`h-4 w-4 ${exerciseSidebarCollapsed ? '' : 'mr-3'} flex-shrink-0`} />
                      {!exerciseSidebarCollapsed && (
                        <span className={activeSection === item.id ? "text-black font-bold truncate" : "truncate"}>{item.title}</span>
                      )}
                    </Button>
                  );
                })}
                
                {/* Improvement section moved to ICS sidebar — below Analytics */}
              </div>
            </ScrollArea>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          <div className="p-6">
            {renderSectionContent()}
          </div>
        </div>
      </div>
      
      {/* Modals */}
      <ScopeModal 
        isOpen={scopeModalOpen}
        onClose={() => setScopeModalOpen(false)}
        onSave={handleScopeUpdate}
        initialData={editingScope}
        exerciseId={exerciseId}
      />
    </div>
  );
};

const TwoFactorSetup = ({ onComplete, token }) => {
  const API = API_BASE_URL + '/api';
  const toast = useToast();
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [setupLoading, setSetupLoading] = useState(true);

  useEffect(() => {
    // Fetch 2FA setup data
    const setup2FA = async () => {
      try {
        // Use token from props or fallback to localStorage
        const authToken = token || secureStorage.getToken();
        
        if (!authToken) {
          toast.error('Authentication token missing. Please login again.');
          setSetupLoading(false);
          return;
        }
        
        const response = await axios.post(`${API}/auth/setup-2fa`, {}, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        setQrCode(response.data.qr_code);
        setSecret(response.data.manual_entry_key);
      } catch (error) {
        console.error('Error setting up 2FA:', error);
        if (error.response?.status === 401) {
          toast.error('Session expired. Please register or login again.');
        } else {
          toast.error('Failed to generate 2FA setup. Please try again.');
        }
      } finally {
        setSetupLoading(false);
      }
    };
    setup2FA();
  }, [token]);

  const handleVerify = async () => {
    if (verifyCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setLoading(true);
    try {
      // Use token from props or fallback to localStorage
      const authToken = token || secureStorage.getToken();
      
      if (!authToken) {
        toast.error('Authentication token missing. Please login again.');
        return;
      }
      
      const response = await axios.post(
        `${API}/auth/verify-2fa`,
        { token: verifyCode },
        { headers: { Authorization: `Bearer ${authToken}` }}
      );
      
      if (response.data.success) {
        toast.success('Two-factor authentication enabled successfully!');
        onComplete();
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please register or login again.');
      } else {
        toast.error(error.response?.data?.detail || 'Invalid authentication code. Please try again.');
      }
      setVerifyCode('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl bg-gray-900 border-gray-800">
        <CardHeader>
          <div className="flex items-center justify-center mb-4">
            <ShieldAlert className="h-12 w-12 text-blue-500" />
          </div>
          <CardTitle className="text-center text-2xl">Set Up Two-Factor Authentication</CardTitle>
          <CardDescription className="text-center">
            Secure your account with an additional layer of protection
          </CardDescription>
        </CardHeader>
        <CardContent>
          {setupLoading ? (
            <div className="text-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin text-blue-500 mx-auto mb-3" />
              <p className="text-gray-400">Generating your authentication setup...</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Instructions */}
              <div className="bg-blue-900/20 border border-blue-800 rounded-lg p-4">
                <h3 className="font-semibold text-blue-400 mb-2 flex items-center gap-2">
                  <Info className="h-5 w-5" />
                  How to set up 2FA
                </h3>
                <ol className="text-sm text-gray-300 space-y-1 list-decimal list-inside">
                  <li>Download Google Authenticator or Microsoft Authenticator on your phone</li>
                  <li>Scan the QR code below with the authenticator app</li>
                  <li>Enter the 6-digit code shown in the app to verify</li>
                </ol>
              </div>

              {/* QR Code */}
              <div className="space-y-3">
                <h4 className="font-semibold text-center">Scan this QR Code</h4>
                {qrCode && (
                  <div className="flex justify-center p-6 bg-white rounded-lg">
                    <img src={qrCode} alt="2FA QR Code" className="w-64 h-64" />
                  </div>
                )}
              </div>

              {/* Manual Entry */}
              <div className="bg-gray-800 p-4 rounded-lg">
                <p className="text-sm text-gray-400 mb-2 flex items-center gap-2">
                  <Key className="h-4 w-4" />
                  Can't scan? Enter this key manually:
                </p>
                <div className="bg-gray-700 p-3 rounded flex items-center justify-between">
                  <code className="text-blue-400 text-sm break-all select-text cursor-text" 
                        title="Click to select, then Ctrl+C to copy">
                    {secret}
                  </code>
                  <button
                    onClick={() => copyToClipboardUtil(
                      secret,
                      () => toast.success('Key copied to clipboard!'),
                      () => toast.error('Copy failed. Please select and copy the key manually.')
                    )}
                    className="ml-2 p-2 hover:bg-gray-600 rounded flex-shrink-0"
                    title="Copy to clipboard"
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  💡 Tip: Click the key to select it, then press Ctrl+C (or Cmd+C on Mac) to copy
                </p>
              </div>

              {/* Verification Code Input */}
              <div className="space-y-3">
                <Label htmlFor="verify-code" className="text-center block">
                  Enter the 6-digit code from your authenticator app
                </Label>
                <Input
                  id="verify-code"
                  type="text"
                  maxLength="6"
                  placeholder="123456"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                  className="bg-gray-800 border-gray-700 text-center text-3xl tracking-widest font-mono"
                  autoFocus
                />
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  onClick={handleVerify}
                  disabled={verifyCode.length !== 6 || loading}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Verify and Enable 2FA
                    </>
                  )}
                </Button>
              </div>

              {/* Security Note */}
              <div className="bg-yellow-900/20 border border-yellow-800 rounded-lg p-3">
                <p className="text-xs text-yellow-200 flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>Important:</strong> Two-factor authentication is mandatory for all users. 
                    You must complete this setup to access EXRSIM.
                  </span>
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Cross-fading background cascade used on Login & Register auth screens.
// Cycles through disaster + first-responder imagery every ~6s with a 1.5s crossfade.
const AUTH_BACKGROUND_IMAGES = [
  'https://customer-assets-lxgj4vgw.emergentagent.net/job_211cc8d9-19d2-4aa5-981f-fcb9f7f7b69b/artifacts/w0ezde8s_flood_damage.jpg',
  'https://customer-assets-lxgj4vgw.emergentagent.net/job_211cc8d9-19d2-4aa5-981f-fcb9f7f7b69b/artifacts/pzfdtrcm_tornado_destruction.jpg',
  'https://customer-assets-lxgj4vgw.emergentagent.net/job_211cc8d9-19d2-4aa5-981f-fcb9f7f7b69b/artifacts/7g024vvr_firefighters.webp',
  'https://customer-assets-lxgj4vgw.emergentagent.net/job_211cc8d9-19d2-4aa5-981f-fcb9f7f7b69b/artifacts/axm9cuct_fighting_blaze.jpg',
  'https://customer-assets-lxgj4vgw.emergentagent.net/job_211cc8d9-19d2-4aa5-981f-fcb9f7f7b69b/artifacts/wcnu16em_fireman_pointing.jpg'
];

const AuthBackgroundCascade = () => {
  const [activeIdx, setActiveIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => {
      setActiveIdx((i) => (i + 1) % AUTH_BACKGROUND_IMAGES.length);
    }, 6000);
    return () => clearInterval(t);
  }, []);
  return (
    <>
      {AUTH_BACKGROUND_IMAGES.map((url, i) => (
        <div
          key={url}
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `url('${url}')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            opacity: i === activeIdx ? 1 : 0,
            transition: 'opacity 1500ms ease-in-out',
            zIndex: 0,
          }}
          data-testid={`auth-bg-slide-${i}`}
        />
      ))}
      {/* Dark overlay for card readability — sits above the cascade */}
      <div className="absolute inset-0 bg-black/60 pointer-events-none" aria-hidden="true" style={{ zIndex: 1 }} />
    </>
  );
};

const LoginPage = () => {
  const API = API_BASE_URL + '/api';
  const { theme } = useTheme();
  const toast = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  
  // 2FA related states
  const [loginStep, setLoginStep] = useState(1); // 1 = email/password, 2 = 2FA code, 3 = 2FA setup
  const [totpCode, setTotpCode] = useState('');
  const [userId, setUserId] = useState('');
  const [requires2FASetup, setRequires2FASetup] = useState(false);
  const [authToken, setAuthToken] = useState(null);
  const [rememberMe, setRememberMe] = useState(true); // Default ON — keeps user signed in across tabs/reboots
  
  // Failed login attempts tracking
  const [failedAttempts, setFailedAttempts] = useState(0);
  const MAX_FAILED_ATTEMPTS = 3;
  
  // Forgot Password states
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState(1); // 1 = enter email, 2 = enter code & new password
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  // Email validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {};
    
    if (!email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!password) {
      newErrors.pwd = 'Password is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (loginStep === 1) {
        // Step 1: Verify email/password
        if (!validateForm()) {
          toast.error('Please fix the form errors');
          setLoading(false);
          return;
        }
        
        const response = await axios.post(`${API}/auth/login`, {
          email,
          password
        });
        
        // Check if login is successful (user has access_token - 2FA disabled)
        if (response.data.access_token && response.data.user) {
          // Direct login success - no 2FA required
          secureStorage.setToken(response.data.access_token, rememberMe);
          secureStorage.setPreference('exrsim-user', JSON.stringify(response.data.user));
          
          toast.success('Login successful! Welcome back.');
          
          // Reload to trigger auth check
          window.location.reload();
        } else if (response.data.requires_2fa_setup) {
          // User needs to set up 2FA
          const token = response.data.temp_token;
          
          // Store token in localStorage first
          secureStorage.setToken(token, rememberMe);
          
          // Small delay to ensure localStorage is written
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Then set state to trigger redirect
          setAuthToken(token);
          setRequires2FASetup(true);
          setUserId(response.data.user_id);
          setLoginStep(3);
          toast.success(response.data.message);
        } else if (response.data.requires_2fa_code) {
          // User has 2FA enabled, show code input
          setLoginStep(2);
          setUserId(response.data.user_id);
          toast.success(response.data.message);
        }
      } else if (loginStep === 2) {
        // Step 2: Verify TOTP code
        if (totpCode.length !== 6) {
          toast.error('Please enter a 6-digit code');
          setLoading(false);
          return;
        }
        
        const response = await axios.post(`${API}/auth/login-2fa`, {
          email,
          password,
          totp_code: totpCode
        });
        
        // Store token and user data with correct keys
        secureStorage.setToken(response.data.access_token);
        secureStorage.setPreference('exrsim-user', JSON.stringify(response.data.user));
        
        toast.success('Login successful! Welcome back.');
        
        // Reload to trigger auth check
        window.location.reload();
      }
    } catch (error) {
      console.error('Login error:', error);
      
      // Track failed login attempts ONLY for the exact "Incorrect email or
      // password" reply from /auth/login. The previous, looser matcher
      // ('password' anywhere in detail) also fired on session-expiry style
      // errors and pushed users into forced-reset for no reason.
      const errorDetail = error.response?.data?.detail || '';
      const isCredentialFailure = /incorrect email or password/i.test(errorDetail);
      if (isCredentialFailure) {
        const newFailedAttempts = failedAttempts + 1;
        setFailedAttempts(newFailedAttempts);
        
        if (newFailedAttempts >= MAX_FAILED_ATTEMPTS) {
          // Redirect to forgot password after 3 failed attempts
          toast.warning(`Too many failed attempts (${newFailedAttempts}). Please reset your password.`);
          setResetEmail(email); // Pre-fill email for convenience
          setShowForgotPassword(true);
          setFailedAttempts(0); // Reset counter
          return;
        } else {
          const remainingAttempts = MAX_FAILED_ATTEMPTS - newFailedAttempts;
          toast.error(`Incorrect password. ${remainingAttempts} attempt${remainingAttempts !== 1 ? 's' : ''} remaining before password reset.`);
          return;
        }
      }
      
      toast.error(error.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Forgot Password Handlers
  const handleForgotPasswordRequest = async (e) => {
    e.preventDefault();
    
    if (!resetEmail.trim()) {
      toast.error('Please enter your email address');
      return;
    }
    
    if (!validateEmail(resetEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setResetLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, {
        email: resetEmail
      });
      
      toast.success('Reset code sent! Check the response for the code.');
      // In production, remove this - code would be sent via email
      if (response.data.reset_code) {
        setResetCode(response.data.reset_code);
        toast.info(`Your reset code is: ${response.data.reset_code} (expires in 15 minutes)`);
      }
      setForgotPasswordStep(2);
    } catch (error) {
      console.error('Forgot password error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process request. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    if (!resetCode.trim()) {
      toast.error('Please enter the reset code');
      return;
    }
    
    if (resetCode.length !== 6) {
      toast.error('Reset code must be 6 digits');
      return;
    }
    
    if (!newPassword) {
      toast.error('Please enter a new password');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setResetLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email: resetEmail,
        reset_code: resetCode,
        new_password: newPassword
      });
      
      toast.success('Password reset successful! You can now login with your new password.');
      
      // Reset all forgot password states and close modal
      setShowForgotPassword(false);
      setForgotPasswordStep(1);
      setResetEmail('');
      setResetCode('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Password reset error:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset password. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  const closeForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotPasswordStep(1);
    setResetEmail('');
    setResetCode('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handle2FAComplete = () => {
    // After 2FA setup is complete, reload the page
    toast.success('2FA setup complete! Please login again.');
    window.location.reload();
  };

  // Show 2FA setup screen
  if (loginStep === 3 && requires2FASetup) {
    return <TwoFactorSetup onComplete={handle2FAComplete} token={authToken} />;
  }

  if (showRegister) {
    return <RegisterPage onBackToLogin={() => setShowRegister(false)} />;
  }

  return (
    <div
      className={`min-h-screen ${theme.colors.textPrimary} flex items-center justify-center p-4 relative overflow-hidden`}
      data-testid="auth-page-background"
    >
      <AuthBackgroundCascade />
      <Card className="w-full max-w-md bg-black/85 border-gray-800 backdrop-blur-sm relative z-10 shadow-2xl shadow-black/60">
        <CardHeader>
          <div className="flex justify-center mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_exrsim-next/artifacts/5c39g0n0_EXRSIM_Small.jpg" 
              alt="EXRSIM Logo" 
              className="h-16 w-auto sm:h-20"
            />
          </div>
          <CardTitle className="text-center text-xl sm:text-2xl">Welcome to EXRSIM</CardTitle>
          <CardDescription className="text-center text-sm sm:text-base">
            Login to access your incident management platform
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            {loginStep === 1 ? (
              // Step 1: Email and Password
              <>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (errors.email) setErrors({ ...errors, email: '' });
                      // Reset failed attempts when email changes
                      setFailedAttempts(0);
                    }}
                    className={`bg-gray-900 border-gray-700 ${errors.email ? 'border-red-500' : ''}`}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {errors.email}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (errors.pwd) setErrors({ ...errors, pwd: '' });
                    }}
                    className={`bg-gray-900 border-gray-700 ${errors.pwd ? 'border-red-500' : ''}`}
                  />
                  {errors.pwd && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {errors.pwd}
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer select-none" data-testid="remember-me-label">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-600 bg-gray-800 text-orange-500 focus:ring-orange-500 focus:ring-offset-0 cursor-pointer"
                      data-testid="remember-me-checkbox"
                    />
                    Keep me signed in
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowForgotPassword(true)}
                    className="text-sm text-orange-500 hover:text-orange-400"
                  >
                    Forgot Password?
                  </button>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-orange-600 hover:bg-orange-700"
                  disabled={loading}
                >
                  {loading ? 'Logging in...' : 'Login'}
                </Button>

                <div className="text-center pt-4">
                  <button
                    type="button"
                    onClick={() => setShowRegister(true)}
                    className="text-sm text-orange-600 hover:text-orange-700"
                  >
                    Don't have an account? Register here
                  </button>
                </div>
              </>
            ) : (
              // Step 2: Two-Factor Authentication Code
              <>
                <div className="text-center mb-4">
                  <div className="flex justify-center mb-3">
                    <ShieldAlert className="h-12 w-12 text-blue-500" />
                  </div>
                  <h3 className="text-lg font-semibold mb-1">Two-Factor Authentication</h3>
                  <p className="text-sm text-gray-400">
                    Enter the 6-digit code from your authenticator app
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="totp-code" className="sr-only">Authentication Code</Label>
                  <Input
                    id="totp-code"
                    type="text"
                    maxLength="6"
                    placeholder="123456"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                    className="bg-gray-900 border-gray-700 text-center text-3xl tracking-widest font-mono"
                    autoFocus
                  />
                  <p className="text-xs text-gray-500 text-center">
                    Open your authenticator app to get the code
                  </p>
                </div>
                
                <Button
                  type="submit"
                  disabled={totpCode.length !== 6 || loading}
                  className="w-full bg-orange-600 hover:bg-orange-700"
                >
                  {loading ? 'Verifying...' : 'Verify Code'}
                </Button>
                
                <button
                  type="button"
                  onClick={() => {
                    setLoginStep(1);
                    setTotpCode('');
                    setErrors({});
                  }}
                  className="text-sm text-gray-400 hover:text-gray-300 w-full text-center"
                >
                  ← Back to login
                </button>
              </>
            )}
          </form>
        </CardContent>
      </Card>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md bg-gray-900 border-gray-700">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="text-white flex items-center gap-2">
                  <Key className="h-5 w-5 text-orange-500" />
                  Reset Password
                </CardTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={closeForgotPassword}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {forgotPasswordStep === 1 ? (
                // Step 1: Enter email
                <form onSubmit={handleForgotPasswordRequest} className="space-y-4">
                  <p className="text-sm text-gray-400 mb-4">
                    Enter your email address and we'll send you a code to reset your password.
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="reset-email" className="text-gray-300">Email Address</Label>
                    <Input
                      id="reset-email"
                      type="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="bg-gray-800 border-gray-600 text-white"
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-orange-600 hover:bg-orange-700"
                    disabled={resetLoading}
                  >
                    {resetLoading ? 'Sending...' : 'Send Reset Code'}
                  </Button>
                  <button
                    type="button"
                    onClick={closeForgotPassword}
                    className="text-sm text-gray-400 hover:text-gray-300 w-full text-center mt-2"
                  >
                    ← Back to login
                  </button>
                </form>
              ) : (
                // Step 2: Enter code and new password
                <form onSubmit={handlePasswordReset} className="space-y-4">
                  <p className="text-sm text-gray-400 mb-4">
                    Enter the 6-digit code and your new password.
                  </p>
                  <div className="space-y-2">
                    <Label htmlFor="reset-code" className="text-gray-300">Reset Code</Label>
                    <Input
                      id="reset-code"
                      type="text"
                      value={resetCode}
                      onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Enter 6-digit code"
                      className="bg-gray-800 border-gray-600 text-white text-center text-xl tracking-widest"
                      maxLength={6}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="new-password" className="text-gray-300">New Password</Label>
                    <Input
                      id="new-password"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password (min 8 characters)"
                      className="bg-gray-800 border-gray-600 text-white"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-password" className="text-gray-300">Confirm Password</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                      className="bg-gray-800 border-gray-600 text-white"
                      required
                    />
                    {confirmPassword && newPassword !== confirmPassword && (
                      <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        Passwords do not match
                      </p>
                    )}
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-orange-600 hover:bg-orange-700"
                    disabled={resetLoading || newPassword !== confirmPassword}
                  >
                    {resetLoading ? 'Resetting...' : 'Reset Password'}
                  </Button>
                  <button
                    type="button"
                    onClick={() => setForgotPasswordStep(1)}
                    className="text-sm text-gray-400 hover:text-gray-300 w-full text-center mt-2"
                  >
                    ← Request new code
                  </button>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// Register Component
const RegisterPage = ({ onBackToLogin }) => {
  const API = API_BASE_URL + '/api';
  const { theme } = useTheme();
  const toast = useToast();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone_number: '',
    role: 'User',
    agency_code: ''
  });
  const [agencyVerified, setAgencyVerified] = useState(false);
  const [agencyInfo, setAgencyInfo] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [authToken, setAuthToken] = useState(null);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  // Email validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Password validation
  const validatePassword = (password) => {
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasMinLength = password.length >= 8;
    
    return {
      isValid: hasUpperCase && hasLowerCase && hasNumber && hasMinLength,
      hasUpperCase,
      hasLowerCase,
      hasNumber,
      hasMinLength
    };
  };

  const verifyAgencyCode = async () => {
    if (!formData.agency_code || formData.agency_code.length < 6) {
      toast.error('Please enter a valid agency code');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API}/agencies/code/${formData.agency_code.toUpperCase()}`);
      setAgencyInfo(response.data);
      setAgencyVerified(true);
      toast.success(`Agency verified: ${response.data.agency_name} (${response.data.slots_remaining} slots remaining)`);
    } catch (error) {
      setAgencyVerified(false);
      setAgencyInfo(null);
      toast.error(error.response?.data?.detail || 'Invalid agency code');
    } finally {
      setLoading(false);
    }
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {};
    
    // Agency code validation
    if (!formData.agency_code.trim()) {
      newErrors.agency_code = 'Agency code is required';
    } else if (!agencyVerified) {
      newErrors.agency_code = 'Please verify your agency code';
    }
    
    if (!formData.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }
    
    if (!formData.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    const passwordValidation = validatePassword(formData.password);
    if (!formData.password) {
      newErrors.pwd = 'Password is required';
    } else if (!passwordValidation.isValid) {
      newErrors.pwd = 'Password must be at least 8 characters with uppercase, lowercase, and number';
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPwd = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPwd = 'Passwords do not match';
    }
    
    if (formData.phone_number && !/^\d{3}-\d{3}-\d{4}$/.test(formData.phone_number)) {
      newErrors.phone_number = 'Phone must be in 555-123-4567 format';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      toast.error('Please fix the form errors');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/register`, {
        email: formData.email,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name,
        phone_number: formData.phone_number,
        role: formData.role,
        agency_code: formData.agency_code
      });

      // Store token in state and localStorage
      const token = response.data.access_token;
      setAuthToken(token);
      secureStorage.setToken(token);
      secureStorage.setPreference('exrsim-user', JSON.stringify(response.data.user));

      toast.success('Account created successfully! Please set up 2FA.');
      
      // Show 2FA setup
      setShow2FASetup(true);
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handle2FAComplete = () => {
    // After 2FA setup is complete, redirect to login
    toast.success('2FA setup complete! Please login.');
    onBackToLogin();
  };

  // Show 2FA setup after successful registration
  if (show2FASetup) {
    return <TwoFactorSetup onComplete={handle2FAComplete} token={authToken} />;
  }

  return (
    <div
      className={`min-h-screen ${theme.colors.textPrimary} flex items-center justify-center p-4 relative overflow-hidden`}
      data-testid="auth-page-background"
    >
      <AuthBackgroundCascade />
      <Card className="w-full max-w-md bg-black/85 border-gray-800 backdrop-blur-sm relative z-10 shadow-2xl shadow-black/60">
        <CardHeader>
          <div className="flex justify-center mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_exrsim-next/artifacts/5c39g0n0_EXRSIM_Small.jpg" 
              alt="EXRSIM Logo" 
              className="h-16 w-auto sm:h-20"
            />
          </div>
          <CardTitle className="text-center text-xl sm:text-2xl">Create Account</CardTitle>
          <CardDescription className="text-center text-sm sm:text-base">
            Register to start using EXRSIM
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleRegister} className="space-y-4">
            {/* Agency Code Field */}
            <div className="space-y-2">
              <Label htmlFor="agency_code">Agency Registration Code</Label>
              <div className="flex gap-2">
                <Input
                  id="agency_code"
                  type="text"
                  value={formData.agency_code}
                  onChange={(e) => {
                    handleChange('agency_code', e.target.value.toUpperCase());
                    setAgencyVerified(false);
                    setAgencyInfo(null);
                  }}
                  placeholder="Enter agency code"
                  className={`bg-gray-900 border-gray-700 ${errors.agency_code ? 'border-red-500' : agencyVerified ? 'border-green-500' : ''}`}
                  disabled={loading}
                />
                <Button
                  type="button"
                  onClick={verifyAgencyCode}
                  disabled={loading || !formData.agency_code || formData.agency_code.length < 6}
                  className={`${agencyVerified ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'} whitespace-nowrap`}
                >
                  {agencyVerified ? (
                    <>
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Verified
                    </>
                  ) : (
                    'Verify'
                  )}
                </Button>
              </div>
              {errors.agency_code && (
                <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.agency_code}
                </p>
              )}
              {agencyInfo && agencyVerified && (
                <div className="bg-green-900/20 border border-green-700 rounded-lg p-3 mt-2">
                  <p className="text-green-400 text-sm font-semibold flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    {agencyInfo.agency_name}
                  </p>
                  <p className="text-gray-400 text-xs mt-1">
                    {agencyInfo.slots_remaining} of {agencyInfo.user_limit} slots available
                  </p>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name</Label>
                <Input
                  id="first_name"
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => handleChange('first_name', e.target.value)}
                  className={`bg-gray-900 border-gray-700 ${errors.first_name ? 'border-red-500' : ''}`}
                />
                {errors.first_name && (
                  <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.first_name}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name</Label>
                <Input
                  id="last_name"
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => handleChange('last_name', e.target.value)}
                  className={`bg-gray-900 border-gray-700 ${errors.last_name ? 'border-red-500' : ''}`}
                />
                {errors.last_name && (
                  <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.last_name}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className={`bg-gray-900 border-gray-700 ${errors.email ? 'border-red-500' : ''}`}
              />
              {errors.email && (
                <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.email}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone_number">Cell Phone Number (Optional)</Label>
              <Input
                id="phone_number"
                type="tel"
                placeholder="555-123-4567"
                maxLength={12}
                value={formData.phone_number}
                onChange={(e) => {
                  let val = e.target.value.replace(/[^0-9]/g, '');
                  if (val.length > 3 && val.length <= 6) val = val.slice(0, 3) + '-' + val.slice(3);
                  else if (val.length > 6) val = val.slice(0, 3) + '-' + val.slice(3, 6) + '-' + val.slice(6, 10);
                  handleChange('phone_number', val);
                }}
                className="bg-gray-900 border-gray-700"
              />
              <p className="text-xs text-gray-500">
                Format: 555-123-4567
              </p>
              {errors.phone_number && <p className="text-red-400 text-xs">{errors.phone_number}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                className={`bg-gray-900 border-gray-700 ${errors.pwd ? 'border-red-500' : ''}`}
              />
              {errors.pwd && (
                <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.pwd}
                </p>
              )}
              <p className="text-xs text-gray-500">
                Minimum 8 characters with uppercase, lowercase, and number
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={formData.confirmPassword}
                onChange={(e) => handleChange('confirmPassword', e.target.value)}
                className={`bg-gray-900 border-gray-700 ${errors.confirmPwd ? 'border-red-500' : ''}`}
              />
              {errors.confirmPwd && (
                <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.confirmPwd}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => handleChange('role', value)}
              >
                <SelectTrigger className="bg-gray-900 border-gray-700">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-gray-700">
                  <SelectItem value="User">User</SelectItem>
                  <SelectItem value="Viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              type="submit"
              className="w-full bg-orange-600 hover:bg-orange-700"
              disabled={loading}
            >
              {loading ? 'Creating Account...' : 'Register'}
            </Button>

            <div className="text-center pt-4">
              <button
                type="button"
                onClick={onBackToLogin}
                className="text-sm text-orange-600 hover:text-orange-700"
              >
                Already have an account? Login here
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Main App Component
function App() {
  // Standalone tracker page — has its own auth, no app shell needed
  const hash = window.location.hash.substring(1).replace(/^\//, ''); // strip leading /
  if (hash === 'tracker' || hash.startsWith('tracker/')) {
    return <ResourceTrackerPage />;
  }

  return (
    <AuthProvider>
      <AgencyContextProvider>
        <ThemeProvider>
          <ToastProvider>
            <AuthWrapper />
          </ToastProvider>
        </ThemeProvider>
      </AgencyContextProvider>
    </AuthProvider>
  );
}

// Auth Wrapper Component - handles authentication checks
function AuthWrapper() {
  const { theme } = useTheme();
  const { isAuthenticated, loading: authLoading } = useAuth();

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div className={`min-h-screen ${theme.colors.primary} ${theme.colors.textPrimary} flex items-center justify-center`}>
        <div className="text-center">
          <img 
            src="https://customer-assets.emergentagent.com/job_crisis-trainer/artifacts/v9rilei7_EXRSIM_Small.jpg"
            alt="EXRSIM"
            className="h-16 w-auto object-contain mx-auto mb-4 animate-pulse"
          />
          <p className="text-xl">Loading EXRSIM...</p>
        </div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // User is authenticated, show main app
  return <AppContent />;
}

function AppContent() {
  const { theme } = useTheme();
  const { user: currentUser } = useAuth();
  
  // Check for display mode routes (hash may include a query string, e.g. #display-weather?exercise=abc)
  const rawHash = window.location.hash.substring(1);
  const hash = rawHash.split('?')[0];
  if (hash === 'display-mapping') {
    return <DisplayMapping />;
  }
  if (hash === 'display-weather') {
    return <DisplayWeather />;
  }
  if (hash === 'display-objectives') {
    return <DisplayObjectives />;
  }
  if (hash === 'display-images') {
    return <DisplayImages />;
  }
  if (hash === 'display-briefing') {
    return <DisplayBriefing />;
  }
  if (hash === 'display-ics203') {
    return <DisplayICS203 />;
  }
  
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [managingExerciseId, setManagingExerciseId] = useState(null);
  const [editingExerciseId, setEditingExerciseId] = useState(null);
  const [editingStep, setEditingStep] = useState(null);
  const [editingReturnTo, setEditingReturnTo] = useState(null);
  const [currentExercise, setCurrentExercise] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [generalMessages, setGeneralMessages] = useState([]);
  const [canAccessExconDashboard, setCanAccessExconDashboard] = useState(false);

  // Check ExCon Dashboard access for route guarding
  useEffect(() => {
    if (currentUser?.role === 'Super Admin') {
      setCanAccessExconDashboard(true);
      return;
    }
    if (!currentUser) return;

    const checkExconAccess = async () => {
      try {
        const token = secureStorage.getToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/participants`, { headers });
        const allParticipants = response.data || [];
        const exconPositions = ['exercise director', 'exercise evaluator', 'exercise observer'];
        const userFullName = `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim().toLowerCase();
        const userEmail = (currentUser.email || '').toLowerCase();

        const hasExconRole = allParticipants.some(p => {
          const pName = (p.name || '').toLowerCase();
          const pEmail = (p.email || '').toLowerCase();
          const pPosition = (p.position || '').toLowerCase();
          return (pEmail === userEmail || pName === userFullName) && exconPositions.includes(pPosition);
        });
        setCanAccessExconDashboard(hasExconRole);
      } catch (error) {
        setCanAccessExconDashboard(false);
      }
    };
    checkExconAccess();
  }, [currentUser]);

  // ICS Positions array - moved here to be accessible by both ParticipantsList and ICS components
  const positions = [
    'not assigned',
    'Command - Incident Commander',
    'Command - Incident Commander (deputy)',
    'Command - Safety Officer',
    'Command - Information Officer',
    'Command - Liaison Officer',
    'Operations - Operations Chief',
    'Operations - Staging Area Manager',
    'Operations - Air Ops Branch Director',
    'Operations - Branch 1 Director',
    'Operations - Branch 2 Director',
    'Operations - Air Support Group Supervisor',
    'Operations - Helibase Supervisor',
    'Operations - Helispot Supervisor',
    'Operations - Fixed-Wing Coordinator',
    'Operations - Air Tactical Group Supervisor',
    'Operations - Fixed-Wing Coord (Tac)',
    'Operations - Air Tanker Coord',
    'Operations - Helicopter Coord',
    'Operations - Division Group 1a Supervisor',
    'Operations - Division Group 1b Supervisor',
    'Operations - Division Group 1c Supervisor',
    'Operations - Division Group 1d Supervisor',
    'Operations - Division Group 2a Supervisor',
    'Operations - Division Group 2b Supervisor',
    'Operations - Division Group 2c Supervisor',
    'Operations - Division Group 2d Supervisor',
    'Planning - Planning Chief',
    'Planning - Resources Unit Leader',
    'Planning - Situation Leader',
    'Planning - Documentation Unit Leader',
    'Planning - Demobilization Unit Leader',
    'Planning - Tech Special',
    'Logistics - Logistics Chief',
    'Logistics - Branch Support Director',
    'Logistics - Supply Unit Leader',
    'Logistics - Facilities Unit Leader',
    'Logistics - Ground Support Unit Leader',
    'Logistics - Branch Services Director',
    'Logistics - Communications Unit Director',
    'Logistics - Medical Unit Leader',
    'Logistics - Food Unit Leader',
    'Finance/Admin - Finance Chief',
    'Finance/Admin - Time Unit Leader',
    'Finance/Admin - Procurement Unit Leader',
    'Finance/Admin - Compensation/Claims Unit Leader',
    'Finance/Admin - Cost Unit Leader',
    'Exercise Evaluator',
    'Exercise Director',
    'Exercise Observer',
    'Team Coordinator',
    'Team Planner',
    'Team Facilitator',
    'Team Evaluator',
    'Team Task',
    'EXRSIM Staff',
    'Participant',
    'Staff Member',
    'Fire Chief',
    'Police Chief',
    'Fire Fighter',
    'Medical Supervisor',
    'Security Facilitator',
    'Umpire',
    'Scribe',
    'Assistant Exercise Controller',
    'Agency Representative',
    'Owner',
    'Actor',
    'VIP',
    'Media',
    'Visitor'
  ];
  
  // Fetch current exercise data when managingExerciseId changes
  useEffect(() => {
    const fetchCurrentExercise = async () => {
      if (managingExerciseId) {
        try {
          const token = secureStorage.getToken();
          const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
          const response = await fetch(`${API_BASE_URL}/api/exercise-builder/${managingExerciseId}`, { headers });
          if (response.ok) {
            const exerciseData = await response.json();
            setCurrentExercise(exerciseData);
            // Save to localStorage for display mode components
            secureStorage.setPreference('current-exercise-id', exerciseData.id);
            secureStorage.setPreference('current-exercise-name', exerciseData.exercise_name);
          } else {
            setCurrentExercise(null);
            secureStorage.removePreference('current-exercise-id');
            secureStorage.removePreference('current-exercise-name');
          }
        } catch (error) {
          console.error('Error fetching current exercise:', error);
          setCurrentExercise(null);
          secureStorage.removePreference('current-exercise-id');
          secureStorage.removePreference('current-exercise-name');
        }
      } else {
        setCurrentExercise(null);
        secureStorage.removePreference('current-exercise-id');
        secureStorage.removePreference('current-exercise-name');
      }
    };

    fetchCurrentExercise();
  }, [managingExerciseId]);

  // Fetch participants for ICS organization chart
  useEffect(() => {
    const fetchParticipants = async () => {
      try {
        const token = secureStorage.getToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/participants`, { headers });
        setParticipants(response.data);
      } catch (error) {
        console.error('Error fetching participants:', error);
        setParticipants([]);
      }
    };

    fetchParticipants();
  }, []);
  
  // Scribe form state
  const [scribeTemplates, setScribeTemplates] = useState([]);
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [scribeFormLoading, setScribeFormLoading] = useState(false);
  const [scribeTimeErrors, setScribeTimeErrors] = useState({});
  const [scribeFormData, setScribeFormData] = useState({
    scribe_name: '',
    scribe_signature: '',
    exercise_start_time: '',
    exercise_end_time: '',
    timeline_events: [],
    communications: [],
    decisions: [],
    issues: [],
    participant_observations: [],
    additional_notes: ''
  });

  // Evaluation state
  const [evaluationReports, setEvaluationReports] = useState([]);
  const [showAddEvaluation, setShowAddEvaluation] = useState(false);
  const [editingEvaluation, setEditingEvaluation] = useState(null);
  const [evaluationLoading, setEvaluationLoading] = useState(false);
  const [selectedEvaluationId, setSelectedEvaluationId] = useState(null);

  // Handle URL-based navigation for editing exercises
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.substring(1); // Remove the '#'
      
      // Scroll to top when navigation occurs
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      if (hash) {
        // Check for parameters in hash format: #builder?exercise=<id> or #manage?exercise=<id>
        const queryStart = hash.indexOf('?');
        let hashBase = hash;
        let exerciseId = null;
        let urlParams = null;
        
        if (queryStart !== -1) {
          hashBase = hash.substring(0, queryStart);
          const queryString = hash.substring(queryStart + 1);
          urlParams = new URLSearchParams(queryString);
          exerciseId = urlParams.get('exercise');
        }
        
        if (hashBase === 'manage' && exerciseId) {
          setActiveMenu('manage');
          setManagingExerciseId(exerciseId);
        } else if (hashBase === 'ics' && exerciseId) {
          setActiveMenu('ics');
          setManagingExerciseId(exerciseId);
          
          // Handle ICS submenu parameter
          if (urlParams) {
            const icsMenu = urlParams.get('menu');
            if (icsMenu) {
              // Store for ICS component to use
              window.icsMenuSelection = icsMenu;
            }
          }
        } else if (hashBase === 'builder') {
          // Redirect builder to exercises with inline editing
          if (exerciseId) {
            setActiveMenu('exercises');
            setEditingExerciseId(exerciseId);
            if (urlParams) {
              const stepParam = urlParams.get('step');
              const returnToParam = urlParams.get('returnTo');
              if (stepParam) setEditingStep(parseInt(stepParam));
              if (returnToParam) setEditingReturnTo(returnToParam);
            }
          } else {
            setActiveMenu('exercises');
          }
          setManagingExerciseId(null);
        } else if (hashBase && ['dashboard', 'exercises', 'msel', 'hira', 'excon-dashboard', 'participants', 'resources'].includes(hashBase)) {
          setActiveMenu(hashBase);
          setManagingExerciseId(null);
          // Don't clear editing if navigating to exercises with an editing state
          if (hashBase !== 'exercises' || !exerciseId) {
            setEditingExerciseId(null);
            setEditingStep(null);
            setEditingReturnTo(null);
          }
        } else if (!hashBase) {
          setActiveMenu('dashboard');
          setManagingExerciseId(null);
        }
      } else {
        setActiveMenu('dashboard');
        setManagingExerciseId(null);
      }
    };
      
      // Initial parse
      handleHashChange();
    
    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
    window.addEventListener('popstate', handleHashChange);
    
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
      window.removeEventListener('popstate', handleHashChange);
    };
  }, []);
  
  // Load scribe templates when exercise changes
  useEffect(() => {
    if (managingExerciseId && activeMenu === 'manage') {
      loadScribeTemplates();
    }
  }, [managingExerciseId, activeMenu]);

  const loadScribeTemplates = async () => {
    if (!managingExerciseId) return;
    
    try {
      const response = await fetch(`${API}/scribe-templates/exercise/${managingExerciseId}`);
      if (response.ok) {
        const templates = await response.json();
        setScribeTemplates(templates);
        if (templates.length > 0) {
          setCurrentTemplate(templates[0]);
          setScribeFormData(templates[0]);
        }
      }
    } catch (error) {
      console.error('Error loading scribe templates:', error);
    }
  };

  const renderContent = () => {
    switch (activeMenu) {
      case 'dashboard':
        return <Dashboard />;
      case 'exercises':
        return <ExerciseView 
          editingExerciseId={editingExerciseId}
          editingStep={editingStep}
          editingReturnTo={editingReturnTo}
          onClearEditing={() => {
            setEditingExerciseId(null);
            setEditingStep(null);
            setEditingReturnTo(null);
            window.location.hash = '#exercises';
          }}
        />;
      case 'hira':
        return <HIRAView />;
      case 'excon-dashboard':
        if (!canAccessExconDashboard) {
          return <div className="flex items-center justify-center h-full"><p className="text-gray-400">Access restricted to Exercise Directors, Evaluators, and Observers.</p></div>;
        }
        return <ExConDashboard 
          onBack={() => { setActiveMenu('hira'); window.location.hash = '#hira'; }}
          onNavigateToBuilder={(exerciseId, step) => {
            setEditingExerciseId(exerciseId);
            setEditingStep(step);
            setEditingReturnTo('excon-dashboard');
            setActiveMenu('exercises');
            window.location.hash = `#builder?exercise=${exerciseId}&step=${step}&returnTo=excon-dashboard`;
          }}
        />;
      case 'participants':
        return <ParticipantsView positions={positions} currentExercise={currentExercise} />;
      case 'resources':
        return <ResourcesView positions={positions} currentExercise={currentExercise} />;
      case 'tutorials':
        return <TutorialsView />;
      case 'images':
        return <ImageRepository currentExercise={currentExercise} theme={theme} />;
      case 'analytics':
        return (
          <AnalyticsDashboard 
            exerciseId={currentExercise?.id}
            exerciseName={currentExercise?.exercise_name}
            token={secureStorage.getToken()}
          />
        );
      case 'helpdesk':
        return <HelpDeskView currentUser={currentUser} />;
      case 'whitepaper':
        return <WhitepaperView />;
      case 'manage':
        return <ExerciseManagementDashboard 
          exerciseId={managingExerciseId} 
          scribeTemplates={scribeTemplates}
          currentTemplate={currentTemplate}
          scribeFormData={scribeFormData}
          scribeFormLoading={scribeFormLoading}
          scribeTimeErrors={scribeTimeErrors}
          setCurrentTemplate={setCurrentTemplate}
          setScribeFormData={setScribeFormData}
          setScribeFormLoading={setScribeFormLoading}
          setScribeTimeErrors={setScribeTimeErrors}
          loadScribeTemplates={loadScribeTemplates}
        />;
      case 'ics':
        return <ICSMainPanel currentExercise={currentExercise} participants={participants} positions={positions} theme={theme} />;
      default:
        return <Dashboard />;
    }
  };

  // Exercise management and ICS dashboard with navigation
  const socketUser = currentUser ? { id: currentUser.id, name: `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim(), email: currentUser.email } : null;
  const isManageOrIcs = activeMenu === 'manage' || activeMenu === 'ics';

  // Mobile drawer state for both main sidebar and ICS sidebar
  const [mainDrawerOpen, setMainDrawerOpen] = useState(false);
  const [icsDrawerOpen, setIcsDrawerOpen] = useState(false);
  const mobileNavValue = { mainDrawerOpen, setMainDrawerOpen, icsDrawerOpen, setIcsDrawerOpen };

  return (
    <SocketProvider apiUrl={API_BASE_URL} user={socketUser} exerciseId={currentExercise?.id}>
    <MobileNavContext.Provider value={mobileNavValue}>
    <div className={`min-h-screen ${theme.colors.primary} ${theme.colors.textPrimary}`}>
      <BrowserRouter>
        <div className="flex flex-col h-screen">
          <Navigation currentExercise={currentExercise} activeMenu={activeMenu} />
          <AgencyContextBanner />
          {isManageOrIcs ? (
            <main className={`flex-1 ${activeMenu === 'ics' ? 'overflow-hidden' : 'overflow-auto'}`}>
              {renderContent()}
            </main>
          ) : (
            <div className="flex flex-1 overflow-hidden">
              <Sidebar onMenuSelect={setActiveMenu} activeMenu={activeMenu} user={currentUser} />
              <main className={`flex-1 ${activeMenu === 'ics' ? 'overflow-hidden' : 'overflow-auto'}`}>
                {renderContent()}
              </main>
            </div>
          )}
        </div>
      </BrowserRouter>
    </div>
    </MobileNavContext.Provider>
    </SocketProvider>
  );
}

export default App;